import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from './api';
import {
  ATTACHMENT_LABELS,
  DEFAULT_SETTINGS,
  PRODUCT_CATEGORY_OPTIONS,
  formatProductCategory,
  INVOICE_DELIVERY_METHOD_LABELS,
  INVOICE_FORMAT_LABELS,
  INVOICE_ISSUE_METHOD_LABELS,
  LAUNCH_ANNOUNCEMENT_AUDIENCE_LABELS,
  LAUNCH_ANNOUNCEMENT_STATUS_LABELS,
  LAUNCH_ANNOUNCEMENT_STATUS_TONE,
  PAYMENT_METHOD_LABELS,
  PROACTIVE_ATTACHMENT_LABELS,
  labelProactiveAttachmentType,
  PROACTIVE_CHANNEL_LABELS,
  PROACTIVE_LIFECYCLE_LABELS,
  PROACTIVE_REVISION_ITEM_LABELS,
  PROACTIVE_STATUS_LABELS,
  PROACTIVE_STATUS_TONE,
  PRODUCT_MASTER_ANNOUNCEMENT_PROGRESS_LABELS,
  PRODUCT_MASTER_WORK_STATUS_LABELS,
  PRODUCT_MASTER_WORK_STATUS_TONE,
  VENDOR_APPLICATION_ATTACHMENT_LABELS,
  VENDOR_APPLICATION_BANK_TRANSFER_NOTICE,
  VENDOR_APPLICATION_CHECK_NOTICE,
  VENDOR_APPLICATION_DELIVERY_CYCLE_LABELS,
  VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS,
  VENDOR_APPLICATION_FREIGHT_RULE_LABELS,
  VENDOR_APPLICATION_INVOICE_DELIVERY_LABELS,
  VENDOR_APPLICATION_INVOICE_FORMAT_LABELS,
  VENDOR_APPLICATION_PAYMENT_METHOD_LABELS,
  VENDOR_APPLICATION_PAYMENT_TERM_LABELS,
  VENDOR_APPLICATION_RECONCILIATION_NOTICE,
  VENDOR_APPLICATION_RETURN_CONDITION_LABELS,
  VENDOR_APPLICATION_RETURN_FREIGHT_LABELS,
  VENDOR_APPLICATION_RETURN_HANDLING_LABELS,
  VENDOR_APPLICATION_WEEKDAY_LABELS,
  VENDOR_ACCOUNT_STATUS_LABELS,
  VENDOR_ACCOUNT_STATUS_TONE,
  VENDOR_PRODUCT_CATEGORY_LABELS,
  STATUS_LABELS,
  STORE_DELIVERY_METHOD_LABELS,
  STORE_RETURN_METHOD_LABELS,
  VENDOR_ATTACHMENT_LABELS,
  VENDOR_DOCUMENT_TYPE_LABELS,
  VENDOR_STATUS_LABELS,
  VENDOR_STATUS_TONE,
  VENDOR_TYPE_LABELS,
  WAREHOUSE_DELIVERY_METHOD_LABELS,
  WAREHOUSE_RETURN_METHOD_LABELS,
  CERTIFICATE_TYPE_LABELS,
  labelCertificateType,
} from './shared/constants';
import {
  formatTaipeiDate,
  formatProductCode,
  getQuantifiedContentWarnings,
  validateProductBarcodes,
} from './shared/domain';
import {
  normalizeProductTradeTermsPayload,
  normalizeSpecialTradeTermsFlag,
  shouldApplyAutosaveResponse,
} from './shared/trade-terms';
import { validateProposalCertificate } from './shared/validation';
import { humanizeValidationMessage } from './shared/validation-messages';
import {
  displayVendorValue,
  formatVendorCategories,
  formatVendorDate,
  formatVendorFileSize,
  formatVendorMoney,
  formatVendorReturnConditions,
  formatVendorTime,
  formatVendorWeekdays,
  labelVendorAttachmentType,
  labelVendorChangedField,
  labelVendorChangeRequestStatus,
  labelVendorDeliveryCycle,
  labelVendorDeliveryScope,
  labelVendorFreightRule,
  labelVendorInvoiceDelivery,
  labelVendorInvoiceFormat,
  labelVendorPaymentMethod,
  labelVendorPaymentTerm,
  labelVendorReturnFreight,
  labelVendorReturnHandling,
  labelVendorStatus,
  parseVendorCode,
  parseVendorReturnConditions,
  vendorOptions,
  vendorLabels,
} from './shared/vendor-labels';
import { canCreateVendorProposal, vendorProposalAccessMessage } from './shared/vendor-portal';
import type {
  AttachmentType,
  ProductCategory,
  ProductSubcategory,
  ProactiveChannelCode,
  ProactiveProposalStatus,
  QuotationRound,
  QuotationStatus,
  Store,
  Submission,
  SubmissionProduct,
  SystemSettings,
  Vendor,
  VendorAccountSummary,
  VendorApplication,
  VendorChangeRequest,
  AttachmentRequirement,
  VendorBankAccount,
  VendorBankDetails,
  VendorContact,
  VendorDeliverySettings,
  VendorFinancialTerms,
  VendorInvoiceSettings,
  VendorProposal,
  VendorProposalInvitation,
  VendorProposalVersion,
  VendorProposalProduct,
  VendorProfile,
  InviteToken,
  VendorReturnSettings,
  VendorSelfServiceProfile,
  VendorStatus,
  VendorType,
  LaunchAnnouncement,
  LaunchAllocationMode,
  LaunchProductCandidate,
  ProductMasterWorkItem,
  ProductMasterWorkStatus,
  User,
} from './shared/types';
import {
  canVendorDeleteProposal,
  canVendorEditProposal,
  canVendorRequestProposalChange,
  VENDOR_CHANGE_REQUEST_TYPE_LABELS,
  VENDOR_PLATFORM_NAME,
} from './shared/vendor-self-service';
import {
  Button,
  Card,
  DateText,
  EmptyState,
  Input,
  Loading,
  Money,
  PageHeader,
  RatingInput,
  Select,
  StatCard,
  StatusBadge,
  Table,
  Textarea,
  TurnstileWidget,
  useUnsavedPrompt,
} from './ui';

export type Notify = (message: string, type?: 'success' | 'error' | 'info') => void;
export interface PageProps {
  notify: Notify;
}

const PRODUCT_PAYMENT_TERM_LABELS: Record<string, string> = {
  PREPAID: '預付款',
  WEEKLY: '週結',
  CASH_ON_DELIVERY: '下貨收現',
  MONTHLY: '月結',
};
const PRODUCT_PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: '現金',
  CHECK: '支票',
  BANK_TRANSFER: '匯款',
  OTHER: '其他',
};
const PRODUCT_RETURN_LABELS: Record<string, string> = {
  WAREHOUSE: '總倉',
  STORE: '門市',
  BOTH: '總倉與門市皆可',
  RETURN: '退貨',
  EXCHANGE: '換貨',
  RETURN_OR_EXCHANGE: '退貨或換貨皆可',
  NONE: '不接受退換貨',
  NEAR_EXPIRY: '即期',
  DEFECT: '瑕疵',
  INTOLERANCE: '吃不合',
  OTHER: '其他',
  VENDOR: '廠商負擔',
  COMPANY: '我司負擔',
  BY_REASON: '依退換貨原因判定',
  AGREEMENT: '雙方協議',
};
const REVIEW_RETURN_ITEM_LABELS: Record<string, string> = {
  PRODUCT_BASIC: '商品基本資料',
  BARCODES: '條碼資料',
  PRICES: '價格資料',
  TRADE_TERMS: '交易條件',
  INITIAL_OFFER: '首批優惠',
  GIFTS: '贈品及搭贈',
  PROMOTION: '促銷支援',
  SALES_PLAN: '銷售／上市提案',
  ATTACHMENTS: '附件',
  OTHER: '其他',
};
const FORMAL_SUBMISSION_SOURCE_LABELS: Record<string, string> = {
  BUYER_INVITATION: '惠生邀請',
  VENDOR_PROACTIVE: '廠商主動提案',
  INTERNAL_SUGGESTION: '內部提案',
  OTHER: '其他來源',
};
const PROPOSAL_REVIEW_ACTION_LABELS: Record<string, string> = {
  SUBMIT: '廠商正式提交',
  RESUBMIT: '廠商重新提交',
  VENDOR_APPROVED_HANDOFF: '廠商資格核准轉審',
  REQUEST_REVISION: '要求補件',
  REQUEST_TRADE_TERMS_REVISION: '要求交易條件補件',
  ENTER_MEETING: '進入會議',
  APPROVE_LAUNCH: '決議上市',
  PAUSE: '暫緩',
  REJECT: '不採用',
  CREATE_PRODUCT_MASTER: '建立正式商品主檔',
};
const PROACTIVE_ACTIVE_STATUS_OPTIONS: ProactiveProposalStatus[] = [
  'DRAFT',
  'WAITING_VENDOR_APPROVAL',
  'WAITING_INITIAL_SCREEN',
  'REVISION_REQUIRED',
  'VENDOR_REVISING',
  'WAITING_MEETING',
  'APPROVED',
  'PAUSED',
  'NOT_ADOPTED',
  'MASTER_CREATED',
];
function displayProductTradeTerm(value: unknown): string {
  if (Array.isArray(value))
    return value.map((item) => PRODUCT_RETURN_LABELS[String(item)] ?? String(item)).join('、');
  const text = String(value ?? '').trim();
  return (
    PRODUCT_PAYMENT_TERM_LABELS[text] ??
    PRODUCT_PAYMENT_METHOD_LABELS[text] ??
    PRODUCT_RETURN_LABELS[text] ??
    (text || '尚未設定')
  );
}

const SPECIAL_TRADE_TERM_DETAIL_KEYS = [
  'special_payment_term',
  'special_monthly_payment_days',
  'special_payment_method',
  'special_return_scope',
  'special_return_handling_method',
  'special_return_conditions',
  'special_return_deadline',
  'special_return_description',
  'special_return_freight_responsibility',
  'special_reason',
] as const;

function hasEnteredSpecialTradeTerms(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const terms = value as Record<string, unknown>;
  return SPECIAL_TRADE_TERM_DETAIL_KEYS.some((key) => {
    const field = terms[key];
    if (Array.isArray(field)) return field.length > 0;
    return field !== null && field !== undefined && String(field).trim().length > 0;
  });
}

function canChangeSpecialTradeTerms(value: unknown, nextEnabled: boolean): boolean {
  if (
    nextEnabled ||
    !normalizeSpecialTradeTermsFlag(
      value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>).special_trade_terms
        : undefined,
    ) ||
    !hasEnteredSpecialTradeTerms(value)
  )
    return true;
  return window.confirm(
    '改為沿用廠商交易條件後，本商品已填寫的特殊交易條件將不再套用。確定要繼續嗎？',
  );
}

const VALIDATION_FIELD_LABELS: Record<string, string> = {
  product: '商品基本資料',
  company_name: '公司／廠商名稱',
  tax_id: '統一編號',
  phone: '公司電話',
  address: '公司地址',
  company_email: '公司 Email',
  principal_brands: '主要代理／品牌',
  supplied_product_categories: '供應商品類',
  vendor_closing_day: '廠商結帳日',
  daily_order_cutoff_time: '廠商每日訂單截止時間',
  payment_term: '付款條件',
  monthly_payment_days: '月結天數',
  payment_method: '付款方式',
  payment_method_other: '其他付款方式說明',
  payment_rule_acknowledged: '付款規則確認',
  delivery_scope: '配送地點',
  delivery_cycle: '廠商配送週期',
  delivery_weekdays: '固定配送星期',
  delivery_fixed_weekdays: '固定配送星期',
  delivery_cycle_other: '其他配送週期說明',
  delivery_minimum_order_amount: '最低訂購金額',
  delivery_minimum_order_quantity: '最低訂購數量',
  freight_rule: '廠商運費規則',
  freight_threshold_amount: '免運門檻',
  freight_fixed_amount: '固定運費金額',
  freight_other_note: '其他運費規則說明',
  return_scope: '退貨地點',
  return_handling_method: '退貨處理方式',
  return_condition_types: '退貨／換貨成立條件',
  return_conditions: '退貨／換貨成立條件',
  return_near_expiry_condition: '即期退換貨條件',
  return_defect_condition: '瑕疵退換貨條件',
  return_intolerance_condition: '吃不合退換貨條件',
  return_other_condition: '其他退換貨條件',
  return_condition_description: '退貨／換貨詳細說明',
  return_freight_responsibility: '退貨／換貨運費負擔',
  return_freight_rule_note: '運費負擔規則說明',
  invoice_format: '發票格式',
  invoice_format_other: '其他發票格式說明',
  invoice_delivery_method: '發票寄送方式',
  invoice_delivery_other: '其他發票寄送方式及原因',
  invoice_contact_name: '發票聯絡人姓名',
  invoice_contact_phone: '發票聯絡電話',
  invoice_email: '發票聯絡 Email',
  reconciliation_contact_name: '對帳聯絡人姓名',
  reconciliation_contact_phone: '對帳聯絡電話',
  reconciliation_email: '對帳 Email',
  reconciliation_terms_acknowledged: '對帳條件確認',
  contact_name: '聯絡人姓名',
  contact_phone: '聯絡人電話',
  contact_email: '聯絡人 Email',
  vendor_application_id: '申請 ID',
  email: '聯絡 Email',
  turnstile_token: '安全驗證',
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const withRequestId = (message: string) =>
      error.requestId ? `${message}（錯誤編號：${error.requestId}）` : message;
    const details = error.details as
      | {
          blocking_items?: string[];
          missing_fields?: string[];
          fieldErrors?: Record<string, string[]>;
        }
      | undefined;
    if (details?.blocking_items?.length)
      return withRequestId(`${error.message} ${details.blocking_items.join('、')}`);
    if (details?.missing_fields?.length)
      return withRequestId(
        `${error.message} 缺少：${details.missing_fields.map((item) => item.split(':').slice(1).join(':') || item).join('、')}`,
      );
    const fieldErrors = Object.entries(details?.fieldErrors ?? {}).flatMap(([field, messages]) =>
      messages.map((message) => {
        const sectionPrefix =
          field === 'delivery_weekdays' || field === 'delivery_fixed_weekdays'
            ? '配送資料尚未完整：'
            : field === 'return_condition_types' || field === 'return_conditions'
              ? '退貨條件尚未完整：'
              : '';
        const label = VALIDATION_FIELD_LABELS[field] ?? field;
        const humanized = humanizeValidationMessage(field, message, label);
        const detail =
          humanized === message || !humanized.includes(label)
            ? `${label}：${humanized}`
            : humanized;
        return `${sectionPrefix}${detail}`;
      }),
    );
    if (fieldErrors.length) return withRequestId(`${error.message} ${fieldErrors.join('；')}`);
    return withRequestId(error.message);
  }
  return error instanceof Error ? error.message : '操作失敗，請稍後再試。';
}

function productCategoryDisplay(
  product:
    | {
        product_category_code?: string | null;
        product_category_name?: string | null;
        category?: string | null;
      }
    | null
    | undefined,
): string {
  return formatProductCategory(
    product?.product_category_code,
    product?.product_category_name,
    product?.category ?? '',
  );
}

function ProductCategorySelect({
  categories,
  value,
  disabled = false,
  currentName = '',
  onChange,
}: {
  categories: ProductCategory[];
  value: string;
  disabled?: boolean;
  currentName?: string;
  onChange: (value: string) => void;
}) {
  const selected = categories.find((category) => category.code === value);
  const listId = 'product-category-options';
  const displayValue = selected
    ? formatProductCategory(selected.code, selected.name)
    : value && currentName
      ? formatProductCategory(value, currentName)
      : value;
  return (
    <>
      <Input
        label="商品分類"
        required
        disabled={disabled}
        list={listId}
        value={displayValue}
        placeholder="請搜尋商品分類代碼或名稱"
        onChange={(event) => {
          const raw = event.target.value.trim();
          const matched = categories.find(
            (category) =>
              raw === category.code ||
              raw === category.name ||
              raw === formatProductCategory(category.code, category.name),
          );
          onChange(matched?.code ?? raw.split('｜')[0].trim());
        }}
      />
      <datalist id={listId}>
        {categories.map((category) => (
          <option key={category.code} value={formatProductCategory(category.code, category.name)} />
        ))}
      </datalist>
    </>
  );
}

function ProductSubcategorySelect({
  subcategories,
  value,
  currentName = '',
  disabled = false,
  onChange,
}: {
  subcategories: ProductSubcategory[];
  value: string;
  currentName?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const selected = subcategories.find((item) => item.code === value);
  const listId = `product-subcategories-${value || 'options'}`;
  const displayValue = selected
    ? `${selected.code}｜${selected.name}`
    : value && currentName
      ? `${value}｜${currentName}`
      : value;
  return (
    <>
      <Input
        label="商品子分類"
        required
        disabled={disabled}
        list={listId}
        value={displayValue}
        placeholder="請搜尋子分類代碼或名稱"
        onChange={(event) => {
          const raw = event.target.value.trim();
          const matched = subcategories.find(
            (item) =>
              raw === item.code || raw === item.name || raw === `${item.code}｜${item.name}`,
          );
          onChange(matched?.code ?? raw.split('｜')[0].trim());
        }}
      />
      <datalist id={listId}>
        {subcategories.map((item) => (
          <option key={item.code} value={`${item.code}｜${item.name}`} />
        ))}
      </datalist>
    </>
  );
}

function useAsyncLoad<T>(loader: () => Promise<T>, deps: unknown[], notify?: Notify) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    loader()
      .then((value) => {
        if (active) setData(value);
      })
      .catch((error) => {
        if (active && notify) notify(errorMessage(error), 'error');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // The caller controls dependencies explicitly to keep load functions readable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, setData, loading };
}

export function DashboardPage({ notify }: PageProps) {
  const { data: summaryData, loading: summaryLoading } = useAsyncLoad(
    () => api.dashboard(),
    [],
    notify,
  );
  const { data: roundData, loading: roundLoading } = useAsyncLoad(
    () => api.rounds.list(),
    [],
    notify,
  );
  const summary = summaryData?.summary;
  const rounds = roundData?.rounds ?? [];
  return (
    <>
      <PageHeader
        eyebrow="OVERVIEW"
        title="早安，今天先掌握新品進度"
        description="從廠商填報、新品提案採購審核到會議決議，所有狀態與決策集中在同一個工作台。"
        actions={
          <>
            <Link to="/rounds/new">
              <Button>＋ 建立報價活動</Button>
            </Link>
            <Link to="/compare">
              <Button variant="secondary">開啟新品比較</Button>
            </Link>
          </>
        }
      />
      {summaryLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard
            label="進行中報價活動"
            value={summary?.active_round_count ?? 0}
            note={`共 ${summary?.round_count ?? 0} 個活動`}
          />
          <Link to="/proactive?status=WAITING_INITIAL_SCREEN">
            <StatCard
              label="待新品提案審核"
              value={summary?.pending_review_count ?? 0}
              tone="amber"
              note="點擊前往新品提案採購審核"
            />
          </Link>
          {typeof summary?.invitation_pending_review_count === 'number' ? (
            <Link to="/proactive?status=WAITING_INITIAL_SCREEN&proposal_source=BUYER_INVITATION">
              <StatCard
                label="惠生邀請待審"
                value={summary.invitation_pending_review_count}
                tone="amber"
                note="點擊前往新品提案採購審核"
              />
            </Link>
          ) : null}
          {typeof summary?.product_master_pending_count === 'number' ? (
            <Link to="/product-master-work-items">
              <StatCard
                label="待建立正式商品主檔"
                value={summary.product_master_pending_count}
                tone="amber"
                note="點擊前往建檔工作台"
              />
            </Link>
          ) : null}
          {typeof summary?.product_master_without_announcement_count === 'number' ? (
            <Link to="/product-master-work-items?quick=WITHOUT_ANNOUNCEMENT">
              <StatCard
                label="已建檔未製作公告"
                value={summary.product_master_without_announcement_count}
                tone="teal"
                note="點擊接續製作公告"
              />
            </Link>
          ) : null}
          {typeof summary?.monthly_product_master_completed_count === 'number' ? (
            <Link to="/product-master-work-items?quick=MONTHLY_MASTER_CREATED">
              <StatCard
                label="本月完成商品建檔"
                value={summary.monthly_product_master_completed_count}
                tone="teal"
                note="依建檔完成時間統計"
              />
            </Link>
          ) : null}
          <StatCard
            label="決議上市／已建主檔"
            value={summary?.approved_count ?? 0}
            tone="teal"
            note={`補件中 ${summary?.revision_count ?? 0} 筆`}
          />
          <Link to="/proactive">
            <StatCard
              label="待分類新品"
              value={summary?.unclassified_count ?? 0}
              tone="amber"
              note="尚未指定商品子分類"
            />
          </Link>
        </div>
      )}
      <div className="mt-6">
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-extrabold">近期報價活動</h2>
              <p className="mt-1 text-xs text-slate-500">依截止日排序，點擊進入活動管理</p>
            </div>
            <Link to="/rounds" className="text-sm font-bold text-teal-700">
              查看全部 →
            </Link>
          </div>
          {roundLoading ? (
            <Loading />
          ) : rounds.length === 0 ? (
            <EmptyState
              title="尚未建立報價活動"
              description="建立第一個新品徵集活動開始收集報價。"
            />
          ) : (
            <div className="space-y-3">
              {rounds.slice(0, 5).map((round) => (
                <Link
                  to={`/rounds/${round.id}/edit`}
                  key={round.id}
                  className="flex flex-col gap-3 rounded-2xl border border-slate-100 p-4 transition hover:border-teal-200 hover:bg-teal-50/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-bold text-ink">{round.name}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {formatProductCategory(
                        round.product_category_code,
                        round.product_category_name || round.category,
                      )}{' '}
                      · {round.vendor_count ?? 0} 家廠商 · {round.submission_count ?? 0} 筆填報
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={round.status} />
                    <span className="text-xs text-slate-500">
                      截止 <DateText value={round.due_date} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

export function RoundsPage({ notify }: PageProps) {
  const { data, loading, setData } = useAsyncLoad(() => api.rounds.list(), [], notify);
  const rounds = data?.rounds ?? [];
  const [roundAction, setRoundAction] = useState<string | null>(null);
  const canDeleteRound = (round: QuotationRound) =>
    !round.archived_at &&
    (round.submission_count ?? 0) === 0 &&
    ['DRAFT', 'WAITING_VENDOR'].includes(round.status);
  const archiveRound = async (round: QuotationRound) => {
    const reason = window.prompt('請填寫封存原因（可留白）：', '不再使用此報價活動。');
    if (reason === null) return;
    setRoundAction(`archive:${round.id}`);
    try {
      await api.rounds.archive(round.id, reason.trim());
      setData({
        rounds: rounds.map((item) =>
          item.id === round.id ? { ...item, archived_at: new Date().toISOString() } : item,
        ),
      });
      notify('報價活動已封存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setRoundAction(null);
    }
  };
  const deleteRound = async (round: QuotationRound) => {
    const confirmed = window.confirm(
      `確定要刪除此報價活動嗎？\n\n活動名稱：${round.name}\n開始日期：${round.start_date}\n截止日期：${round.due_date}\n目前狀態：${STATUS_LABELS[round.status] ?? round.status}\n邀請廠商：${round.vendor_count ?? 0} 家\n廠商提交：${round.submission_count ?? 0} 筆\n\n刪除後活動不會出現在一般列表，系統仍保留稽核紀錄。`,
    );
    if (!confirmed) return;
    setRoundAction(`delete:${round.id}`);
    try {
      await api.rounds.delete(round.id);
      setData({ rounds: rounds.filter((item) => item.id !== round.id) });
      notify('報價活動已刪除。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setRoundAction(null);
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="WORKFLOW"
        title="新品報價活動"
        description="設定新品徵集範圍、截止日期、指定廠商與適用門市。"
        actions={
          <Link to="/rounds/new">
            <Button>＋ 建立活動</Button>
          </Link>
        }
      />
      {loading ? (
        <Loading />
      ) : rounds.length === 0 ? (
        <EmptyState
          title="尚未建立報價活動"
          description="先建立一個活動，再產生廠商邀請連結。"
          action={
            <Link to="/rounds/new">
              <Button>建立第一個活動</Button>
            </Link>
          }
        />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">活動名稱</th>
                <th className="px-4 py-3">分類</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">截止日期</th>
                <th className="px-4 py-3">廠商 / 填報</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rounds.map((round) => (
                <tr key={round.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-4">
                    <Link
                      to={`/rounds/${round.id}/edit`}
                      className="font-bold text-teal-700 hover:underline"
                    >
                      {round.name}
                    </Link>
                    <p className="mt-1 max-w-xs whitespace-normal text-xs leading-5 text-slate-500">
                      {round.description || '未填寫說明'}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-slate-600">
                    {formatProductCategory(
                      round.product_category_code,
                      round.product_category_name || round.category,
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {round.archived_at ? (
                      <span className="inline-flex rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-700">
                        已封存
                      </span>
                    ) : (
                      <StatusBadge status={round.status} />
                    )}
                  </td>
                  <td className="px-4 py-4 text-slate-600">
                    <DateText value={round.due_date} />
                  </td>
                  <td className="px-4 py-4 text-slate-600">
                    {round.vendor_count ?? 0} 家 / {round.submission_count ?? 0} 筆
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Link to={`/rounds/${round.id}/edit`}>
                        <Button variant="secondary">查看 / 編輯</Button>
                      </Link>
                      {!round.archived_at ? (
                        <Button
                          variant="secondary"
                          onClick={() => void archiveRound(round)}
                          loading={roundAction === `archive:${round.id}`}
                        >
                          封存活動
                        </Button>
                      ) : null}
                      {canDeleteRound(round) ? (
                        <Button
                          variant="danger"
                          onClick={() => void deleteRound(round)}
                          loading={roundAction === `delete:${round.id}`}
                        >
                          刪除活動
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}

const emptyRoundForm = {
  name: '',
  product_category_code: '',
  description: '',
  start_date: new Date().toISOString().slice(0, 10),
  due_date: '',
  launch_date: '',
  vendor_ids: [] as string[],
  required_attachment_types: ['PRODUCT_FRONT', 'QUOTATION'] as string[],
};

export function RoundEditorPage({ notify }: PageProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [form, setForm] = useState(emptyRoundForm);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [roundDetail, setRoundDetail] = useState<Record<string, unknown> | null>(null);
  const [inviteCreating, setInviteCreating] = useState<string | null>(null);
  const [inviteRevoking, setInviteRevoking] = useState<string | null>(null);
  const [createdInvite, setCreatedInvite] = useState<{
    vendor_id: string;
    token: string;
    expires_at: string;
    invite_url: string;
  } | null>(null);
  useUnsavedPrompt(dirty);
  useEffect(() => {
    Promise.all([api.vendors.list(), api.productCategories.publicList()])
      .then(([vendorResult, categoryResult]) => {
        setVendors(vendorResult.vendors);
        setProductCategories(categoryResult.categories);
        if (!isNew && id)
          return api.rounds.detail(id).then((detail) => {
            const row = detail as any;
            setRoundDetail(row);
            setForm({
              name: row.name ?? '',
              product_category_code: row.product_category_code ?? '',
              description: row.description ?? '',
              start_date: row.start_date ?? '',
              due_date: row.due_date ?? '',
              launch_date: row.launch_date ?? '',
              vendor_ids: (row.vendors ?? []).map((vendor: any) => vendor.vendor_id),
              required_attachment_types: row.required_attachment_types ?? [],
            });
          });
      })
      .catch((error) => notify(errorMessage(error), 'error'))
      .finally(() => setLoading(false));
  }, [id, isNew, notify]);
  const update = (key: string, value: unknown) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };
  const toggle = (key: 'vendor_ids' | 'required_attachment_types', value: string) =>
    update(
      key,
      (form[key] as string[]).includes(value)
        ? (form[key] as string[]).filter((item) => item !== value)
        : [...(form[key] as string[]), value],
    );
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.due_date) {
      notify('請設定截止日期。', 'error');
      return;
    }
    if (!form.product_category_code) {
      notify('請選擇商品分類。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = isNew ? await api.rounds.create(form) : await api.rounds.update(id!, form);
      if (!isNew) setRoundDetail(result as Record<string, unknown>);
      notify(isNew ? '報價活動已建立。' : '報價活動已更新。');
      setDirty(false);
      navigate(isNew ? `/rounds/${(result as any).id}/edit` : `/rounds/${id}/edit`);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const inviteTokens =
    (roundDetail?.invite_tokens as Array<InviteToken & { submission_status?: string }>) ?? [];
  const createInvite = async (vendorId: string) => {
    if (!id || isNew) return;
    setInviteCreating(vendorId);
    try {
      const result = await api.invitations.create({ quotation_round_id: id, vendor_id: vendorId });
      setCreatedInvite({ vendor_id: vendorId, ...result });
      const detail = await api.rounds.detail(id);
      setRoundDetail(detail);
      notify('廠商專屬邀請已產生；明文 Token 僅在此刻顯示。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setInviteCreating(null);
    }
  };
  const revokeInvite = async (invite: InviteToken) => {
    if (!window.confirm('確定要撤銷此廠商邀請嗎？撤銷後原連結將無法使用。')) return;
    setInviteRevoking(invite.id);
    try {
      await api.invitations.revoke(invite.id);
      setRoundDetail((current) =>
        current
          ? {
              ...current,
              invite_tokens: inviteTokens.map((item) =>
                item.id === invite.id ? { ...item, revoked_at: new Date().toISOString() } : item,
              ),
            }
          : current,
      );
      notify('邀請已撤銷。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setInviteRevoking(null);
    }
  };
  if (loading) return <Loading />;
  return (
    <>
      <PageHeader
        eyebrow="WORKFLOW SETUP"
        title={isNew ? '建立報價活動' : '編輯報價活動'}
        description="欄位與指定對象會保存到 D1，後續可從活動內的邀請管理產生每家廠商的專屬 Token。"
        actions={
          <Link to="/rounds">
            <Button variant="secondary">返回活動列表</Button>
          </Link>
        }
      />
      <form onSubmit={save} className="space-y-6">
        <Card>
          <div className="grid gap-5 md:grid-cols-2">
            <Input
              label="活動名稱"
              required
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
              placeholder="例如：2026 Q3 保健新品徵集"
            />
            <ProductCategorySelect
              categories={productCategories}
              value={form.product_category_code}
              currentName={
                productCategories.find((category) => category.code === form.product_category_code)
                  ?.name ?? ''
              }
              onChange={(value) => update('product_category_code', value)}
            />
            <Textarea
              label="活動說明"
              className="md:col-span-2"
              value={form.description}
              onChange={(event) => update('description', event.target.value)}
              placeholder="請說明新品方向、評估重點與填報注意事項。"
            />
            <Input
              label="開始日期"
              type="date"
              required
              value={form.start_date}
              onChange={(event) => update('start_date', event.target.value)}
            />
            <Input
              label="截止日期"
              type="date"
              required
              value={form.due_date}
              onChange={(event) => update('due_date', event.target.value)}
            />
            <Input
              label="預計上市日期"
              type="date"
              value={form.launch_date}
              onChange={(event) => update('launch_date', event.target.value)}
            />
          </div>
        </Card>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <h2 className="mb-3 text-lg font-extrabold">指定廠商</h2>
            <p className="mb-4 text-xs leading-5 text-slate-500">
              只有被指定的廠商可以產生邀請 Token。
            </p>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {vendors.map((vendor) => (
                <label
                  key={vendor.id}
                  className="flex cursor-pointer items-start gap-3 rounded-xl p-2 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-teal-600"
                    checked={form.vendor_ids.includes(vendor.id)}
                    onChange={() => toggle('vendor_ids', vendor.id)}
                  />
                  <span>
                    <span className="block text-sm font-bold">{vendor.name}</span>
                    <span className="text-xs text-slate-500">{vendor.code}</span>
                  </span>
                </label>
              ))}
            </div>
            {!vendors.length && <p className="text-sm text-slate-500">請先建立廠商。</p>}
          </Card>
          <Card>
            <h2 className="mb-3 text-lg font-extrabold">必填附件</h2>
            <p className="mb-4 text-xs leading-5 text-slate-500">
              廠商提交前會由後端檢查是否上傳完成。
            </p>
            <div className="space-y-2">
              {(Object.keys(ATTACHMENT_LABELS) as AttachmentType[]).map((type) => (
                <label
                  key={type}
                  className="flex cursor-pointer items-start gap-3 rounded-xl p-2 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-teal-600"
                    checked={form.required_attachment_types.includes(type)}
                    onChange={() => toggle('required_attachment_types', type)}
                  />
                  <span className="text-sm font-semibold">{ATTACHMENT_LABELS[type]}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>
        {!isNew ? (
          <Card>
            <div className="mb-4 flex flex-col justify-between gap-2 md:flex-row md:items-start">
              <div>
                <h2 className="text-lg font-extrabold">活動內邀請管理</h2>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  每家廠商使用不同的安全 Token；明文只在產生當下回傳，資料庫僅保存雜湊。
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-500">
                已設定廠商 {form.vendor_ids.length} 家
              </span>
            </div>
            {form.vendor_ids.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {form.vendor_ids.map((vendorId) => {
                  const vendor = vendors.find((item) => item.id === vendorId);
                  const latestInvite = inviteTokens.find((item) => item.vendor_id === vendorId);
                  return (
                    <div key={vendorId} className="rounded-2xl border border-slate-100 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-bold">{vendor?.name ?? vendorId}</p>
                          <p className="mt-1 text-xs text-slate-500">{vendor?.code ?? '—'}</p>
                        </div>
                        {latestInvite?.revoked_at ? (
                          <span className="text-xs font-bold text-rose-700">已撤銷</span>
                        ) : latestInvite ? (
                          <span className="text-xs font-bold text-emerald-700">已邀請</span>
                        ) : (
                          <span className="text-xs font-bold text-slate-500">尚未邀請</span>
                        )}
                      </div>
                      {latestInvite ? (
                        <p className="mt-2 text-xs text-slate-500">
                          到期：
                          <DateText value={latestInvite.expires_at} withTime />
                          {latestInvite.submission_status
                            ? ` · ${STATUS_LABELS[latestInvite.submission_status as QuotationStatus] ?? latestInvite.submission_status}`
                            : ''}
                        </p>
                      ) : null}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => void createInvite(vendorId)}
                          loading={inviteCreating === vendorId}
                        >
                          {latestInvite && !latestInvite.revoked_at ? '重新產生邀請' : '產生邀請'}
                        </Button>
                        {latestInvite && !latestInvite.revoked_at ? (
                          <Button
                            variant="danger"
                            onClick={() => void revokeInvite(latestInvite)}
                            loading={inviteRevoking === latestInvite.id}
                          >
                            撤銷
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="尚未指定廠商" description="先在上方勾選廠商，再產生專屬邀請。" />
            )}
            {createdInvite ? (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="font-bold text-amber-900">邀請連結已產生，請立即複製保存</p>
                <p className="mt-2 break-all text-sm text-amber-950">{createdInvite.invite_url}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void navigator.clipboard?.writeText(createdInvite.invite_url);
                      notify('邀請連結已複製。', 'info');
                    }}
                  >
                    複製連結
                  </Button>
                  <span className="text-xs text-amber-800">
                    到期：
                    <DateText value={createdInvite.expires_at} withTime />
                  </span>
                </div>
              </div>
            ) : null}
          </Card>
        ) : null}
        <div className="flex justify-end gap-3">
          <Link to="/rounds">
            <Button type="button" variant="secondary">
              取消
            </Button>
          </Link>
          <Button type="submit" loading={saving}>
            儲存活動
          </Button>
        </div>
      </form>
    </>
  );
}

export function VendorsPageLegacy({ notify }: PageProps) {
  const { data, loading, setData } = useAsyncLoad(() => api.vendors.list(), [], notify);
  const vendors = data?.vendors ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({
    code: '',
    name: '',
    tax_id: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
  });
  const [saving, setSaving] = useState(false);
  const startEdit = (vendor: Vendor) => {
    setEditing(vendor.id);
    setForm({
      code: vendor.code,
      name: vendor.name,
      tax_id: vendor.tax_id ?? '',
      contact_name: vendor.contact_name ?? '',
      contact_email: vendor.contact_email ?? '',
      contact_phone: vendor.contact_phone ?? '',
      address: vendor.address ?? '',
    });
  };
  const reset = () => {
    setEditing(null);
    setForm({
      code: '',
      name: '',
      tax_id: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
    });
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const vendor = editing
        ? await api.vendors.update(editing, form)
        : await api.vendors.create(form);
      setData({
        vendors: editing
          ? vendors.map((item) => (item.id === editing ? vendor : item))
          : [vendor, ...vendors],
      });
      notify(editing ? '廠商資料已更新。' : '廠商已建立。');
      reset();
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="MASTER DATA"
        title="廠商管理"
        description="維護廠商聯絡資料與邀請對象。商務欄位由後端角色檢查，非 UI 隱藏。"
      />{' '}
      <div className="grid gap-6 xl:grid-cols-[1fr_1.6fr]">
        <Card>
          <h2 className="mb-4 text-lg font-extrabold">{editing ? '編輯廠商' : '新增廠商'}</h2>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="廠商代碼"
                required
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
              />
              <Input
                label="廠商名稱"
                required
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
              <Input
                label="統一編號"
                value={form.tax_id}
                onChange={(event) => setForm({ ...form, tax_id: event.target.value })}
              />
              <Input
                label="聯絡人"
                value={form.contact_name}
                onChange={(event) => setForm({ ...form, contact_name: event.target.value })}
              />
              <Input
                label="聯絡 Email"
                type="email"
                value={form.contact_email}
                onChange={(event) => setForm({ ...form, contact_email: event.target.value })}
              />
              <Input
                label="聯絡電話"
                value={form.contact_phone}
                onChange={(event) => setForm({ ...form, contact_phone: event.target.value })}
              />
            </div>
            <Textarea
              label="地址"
              value={form.address}
              onChange={(event) => setForm({ ...form, address: event.target.value })}
            />
            <div className="flex justify-end gap-2">
              {editing && (
                <Button type="button" variant="secondary" onClick={reset}>
                  取消編輯
                </Button>
              )}
              <Button loading={saving}>{editing ? '儲存變更' : '建立廠商'}</Button>
            </div>
          </form>
        </Card>
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-extrabold">廠商清單</h2>
              <p className="mt-1 text-xs text-slate-500">共 {vendors.length} 家</p>
            </div>
          </div>
          {loading ? (
            <Loading />
          ) : vendors.length === 0 ? (
            <EmptyState title="尚未建立廠商" />
          ) : (
            <Table>
              <thead>
                <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                  <th className="px-4 py-3">廠商</th>
                  <th className="px-4 py-3">聯絡人</th>
                  <th className="px-4 py-3">聯絡方式</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {vendors.map((vendor) => (
                  <tr key={vendor.id} className="border-t border-slate-100">
                    <td className="px-4 py-4">
                      <p className="font-bold">{vendor.name}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {vendor.code} {vendor.tax_id ? `· ${vendor.tax_id}` : ''}
                      </p>
                    </td>
                    <td className="px-4 py-4">{vendor.contact_name || '—'}</td>
                    <td className="px-4 py-4">
                      <p>{vendor.contact_email || '—'}</p>
                      <p className="text-xs text-slate-500">{vendor.contact_phone || ''}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button variant="secondary" onClick={() => startEdit(vendor)}>
                        編輯
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

type VendorMasterForm = {
  code: string;
  name: string;
  short_name: string;
  vendor_type: VendorType;
  tax_id: string;
  website: string;
  fax: string;
  address: string;
  second_contact_name: string;
  second_contact_phone: string;
  notes: string;
  internal_notes: string;
  vendor_status: VendorStatus;
  contacts: { primary: VendorContact; secondary: VendorContact };
  financial_terms: VendorFinancialTerms;
  delivery_settings: VendorDeliverySettings;
  return_settings: VendorReturnSettings;
  invoice_settings: VendorInvoiceSettings;
  bank_accounts: VendorBankAccount[];
};

function createEmptyVendorForm(): VendorMasterForm {
  return {
    code: '',
    name: '',
    short_name: '',
    vendor_type: 'OTHER',
    tax_id: '',
    website: '',
    fax: '',
    address: '',
    second_contact_name: '',
    second_contact_phone: '',
    notes: '',
    internal_notes: '',
    vendor_status: 'DRAFT',
    contacts: {
      primary: { contact_kind: 'PRIMARY', name: '', phone: '', email: '', title: '' },
      secondary: { contact_kind: 'SECONDARY', name: '', phone: '', email: '', title: '' },
    },
    financial_terms: {
      settlement_method_id: '',
      settlement_method_other_note: '',
      payment_terms_id: '',
      payment_terms_note: '',
      payment_method: '',
      payment_method_note: '',
    },
    delivery_settings: {
      warehouse_delivery_enabled: false,
      warehouse_delivery_methods: [],
      warehouse_delivery_cycle: '',
      warehouse_fixed_delivery_days: [],
      warehouse_order_cutoff_time: '',
      warehouse_average_delivery_days: null,
      warehouse_minimum_order_amount: null,
      warehouse_minimum_order_quantity: null,
      warehouse_free_shipping_threshold: null,
      warehouse_shipping_fee: null,
      warehouse_delivery_note: '',
      warehouse_receiving_note: '',
      store_delivery_enabled: false,
      store_delivery_methods: [],
      store_minimum_order_amount: null,
      store_minimum_order_quantity: null,
      store_free_shipping_threshold: null,
      store_shipping_fee: null,
      store_delivery_regions: '',
      store_available_stores: [],
      store_unavailable_stores: [],
      store_fixed_delivery_days: [],
      store_order_cutoff_time: '',
      store_average_delivery_days: null,
      store_delivery_note: '',
    },
    return_settings: {
      warehouse_return_enabled: 'NO',
      warehouse_return_methods: [],
      warehouse_return_notice_days: null,
      warehouse_return_application_method: '',
      warehouse_return_minimum_amount: null,
      warehouse_return_shipping_bearer: '',
      warehouse_return_slip_required: false,
      warehouse_vendor_approval_required: false,
      warehouse_photo_required: false,
      warehouse_good_return_allowed: false,
      warehouse_defect_return_allowed: false,
      warehouse_near_expiry_return_allowed: false,
      warehouse_near_expiry_return_months: null,
      warehouse_return_note: '',
      store_return_to_vendor_enabled: 'NO',
      store_return_methods: [],
      store_procurement_approval_required: false,
      store_warehouse_approval_required: false,
      store_vendor_approval_required: false,
      store_photo_required: false,
      store_reason_required: false,
      store_good_return_allowed: false,
      store_defect_return_allowed: false,
      store_near_expiry_return_allowed: false,
      store_return_deadline_days: null,
      store_shipping_bearer: '',
      store_return_note: '',
    },
    invoice_settings: {
      invoice_format: '',
      invoice_format_note: '',
      invoice_issue_method: '',
      invoice_delivery_methods: [],
      invoice_email: '',
      invoice_mailing_address: '',
      invoice_title: '',
      invoice_tax_id: '',
      invoice_note: '',
    },
    bank_accounts: [],
  };
}

function vendorToForm(vendor: Vendor): VendorMasterForm {
  const empty = createEmptyVendorForm();
  const primary = vendor.contacts?.find((contact) => contact.contact_kind === 'PRIMARY');
  const secondary = vendor.contacts?.find((contact) => contact.contact_kind === 'SECONDARY');
  return {
    ...empty,
    code: vendor.code,
    name: vendor.name,
    short_name: vendor.short_name ?? '',
    vendor_type: vendor.vendor_type ?? 'OTHER',
    tax_id: vendor.tax_id ?? '',
    website: vendor.website ?? '',
    fax: vendor.fax ?? '',
    address: vendor.address ?? '',
    second_contact_name: vendor.second_contact_name ?? '',
    second_contact_phone: vendor.second_contact_phone ?? '',
    notes: vendor.notes ?? '',
    internal_notes: vendor.internal_notes ?? '',
    vendor_status: vendor.vendor_status ?? 'DRAFT',
    contacts: {
      primary: {
        ...empty.contacts.primary,
        ...(primary ?? {}),
        name: primary?.name ?? vendor.contact_name ?? '',
        phone: primary?.phone ?? vendor.contact_phone ?? '',
        email: primary?.email ?? vendor.contact_email ?? '',
      },
      secondary: { ...empty.contacts.secondary, ...(secondary ?? {}) },
    },
    financial_terms: { ...empty.financial_terms, ...(vendor.financial_terms ?? {}) },
    delivery_settings: { ...empty.delivery_settings, ...(vendor.delivery_settings ?? {}) },
    return_settings: { ...empty.return_settings, ...(vendor.return_settings ?? {}) },
    invoice_settings: { ...empty.invoice_settings, ...(vendor.invoice_settings ?? {}) },
    bank_accounts: (vendor.bank_accounts ?? []).map((account) => ({
      ...account,
      account_number: account.account_number ?? '',
    })),
  };
}

function VendorChoiceList({
  labels,
  selected,
  onChange,
  disabled = false,
}: {
  labels: Record<string, string>;
  selected: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.entries(labels).map(([value, label]) => (
        <label
          key={value}
          className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${selected.includes(value) ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-100 bg-slate-50 text-slate-600'} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        >
          <input
            type="checkbox"
            disabled={disabled}
            checked={selected.includes(value)}
            onChange={() => onChange(value)}
            className="accent-teal-600"
          />
          {label}
        </label>
      ))}
    </div>
  );
}

function VendorBoolean({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-center gap-2 text-sm font-semibold ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
    >
      <input
        type="checkbox"
        disabled={disabled}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-teal-600"
      />
      {label}
    </label>
  );
}

export function VendorsPageLegacyMaster({ notify }: PageProps) {
  const { data, loading, setData } = useAsyncLoad(() => api.vendors.list(), [], notify);
  const { data: optionData, setData: setOptionData } = useAsyncLoad(
    () => api.vendors.options(),
    [],
    notify,
  );
  const { data: authData } = useAsyncLoad(() => api.auth.me(), [], notify);
  const vendors = data?.vendors ?? [];
  const user = authData?.user;
  const isFinance = user?.role === 'FINANCE';
  const isAdmin = user?.role === 'ADMIN';
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<VendorMasterForm>(createEmptyVendorForm);
  const [activeSection, setActiveSection] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [settlementFilter, setSettlementFilter] = useState('');
  const [storeDeliveryFilter, setStoreDeliveryFilter] = useState('');
  const [storeReturnFilter, setStoreReturnFilter] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [optionForm, setOptionForm] = useState({
    id: '',
    option_type: 'SETTLEMENT_METHOD' as 'SETTLEMENT_METHOD' | 'PAYMENT_TERM',
    code: '',
    name: '',
    sort_order: 0,
    active: true,
  });
  const [optionSaving, setOptionSaving] = useState(false);
  const [attachmentType, setAttachmentType] = useState('BUSINESS_LICENSE');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentSaving, setAttachmentSaving] = useState(false);
  useUnsavedPrompt(dirty);

  const settlementMethods = optionData?.settlement_methods ?? [];
  const paymentTerms = optionData?.payment_terms ?? [];
  const filteredVendors = useMemo(
    () =>
      vendors.filter(
        (vendor) =>
          (!query.trim() ||
            `${vendor.code} ${vendor.name} ${vendor.short_name ?? ''} ${vendor.tax_id ?? ''}`
              .toLowerCase()
              .includes(query.trim().toLowerCase())) &&
          (!statusFilter || vendor.vendor_status === statusFilter) &&
          (!settlementFilter ||
            vendor.financial_terms?.settlement_method_id === settlementFilter) &&
          (!storeDeliveryFilter ||
            String(Number(vendor.delivery_settings?.store_delivery_enabled ?? false)) ===
              storeDeliveryFilter) &&
          (!storeReturnFilter ||
            vendor.return_settings?.store_return_to_vendor_enabled === storeReturnFilter) &&
          (!invoiceFilter || vendor.invoice_settings?.invoice_format === invoiceFilter),
      ),
    [
      invoiceFilter,
      query,
      settlementFilter,
      statusFilter,
      storeDeliveryFilter,
      storeReturnFilter,
      vendors,
    ],
  );
  const patchForm = (updater: (current: VendorMasterForm) => VendorMasterForm) => {
    setDirty(true);
    setForm(updater);
  };
  const patchSection = (
    section: 'financial_terms' | 'delivery_settings' | 'return_settings' | 'invoice_settings',
    key: string,
    value: unknown,
  ) =>
    patchForm(
      (current) =>
        ({ ...current, [section]: { ...current[section], [key]: value } }) as VendorMasterForm,
    );
  const reset = () => {
    setEditing(null);
    setForm(createEmptyVendorForm());
    setActiveSection('basic');
    setDirty(false);
  };
  const startEdit = (vendor: Vendor) => {
    setEditing(vendor.id);
    setForm(vendorToForm(vendor));
    setActiveSection('basic');
    setDirty(false);
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isFinance && !editing) {
      notify('財務人員只能編輯既有廠商，不能建立廠商主檔。', 'error');
      return;
    }
    setSaving(true);
    try {
      const vendor = editing
        ? await api.vendors.update(editing, form)
        : await api.vendors.create(form);
      setData({
        vendors: editing
          ? vendors.map((item) => (item.id === vendor.id ? vendor : item))
          : [vendor, ...vendors],
      });
      setForm(vendorToForm(vendor));
      setEditing(vendor.id);
      setDirty(false);
      notify('廠商主檔已儲存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const changeStatus = async (status: VendorStatus) => {
    if (!editing) {
      notify('請先儲存廠商主檔，再變更狀態。', 'info');
      return;
    }
    if (dirty) {
      notify('請先儲存尚未儲存的內容。', 'error');
      return;
    }
    const reason = window.prompt('請填寫廠商狀態變更原因。', '');
    if (!reason?.trim()) {
      notify('狀態變更原因為必填。', 'error');
      return;
    }
    try {
      const vendor = await api.vendors.status(editing, status, reason.trim());
      setData({ vendors: vendors.map((item) => (item.id === vendor.id ? vendor : item)) });
      setForm(vendorToForm(vendor));
      notify(`廠商狀態已更新為「${VENDOR_STATUS_LABELS[status]}」。`);
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const toggleChoice = (
    section: 'delivery_settings' | 'return_settings' | 'invoice_settings',
    key: string,
    value: string,
  ) => {
    const current = form[section][key as keyof VendorMasterForm[typeof section]];
    const selected: string[] = Array.isArray(current) ? (current as string[]) : [];
    patchSection(
      section,
      key,
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  };
  const updateBank = (index: number, key: keyof VendorBankAccount, value: unknown) =>
    patchForm((current) => ({
      ...current,
      bank_accounts: current.bank_accounts.map((account, itemIndex) =>
        itemIndex === index ? { ...account, [key]: value } : account,
      ),
    }));
  const addBank = () =>
    patchForm((current) => ({
      ...current,
      bank_accounts: [
        ...current.bank_accounts,
        {
          bank_name: '',
          branch_name: '',
          bank_code: '',
          branch_code: '',
          account_name: '',
          account_number: '',
          is_primary: current.bank_accounts.length === 0,
          active: true,
        },
      ],
    }));
  const removeBank = (index: number) =>
    patchForm((current) => ({
      ...current,
      bank_accounts: current.bank_accounts.filter((_, itemIndex) => itemIndex !== index),
    }));
  const saveOption = async (event: React.FormEvent) => {
    event.preventDefault();
    setOptionSaving(true);
    try {
      const result = optionForm.id
        ? await api.vendors.updateOption(optionForm.id, optionForm)
        : await api.vendors.createOption(optionForm);
      const next =
        optionForm.option_type === 'SETTLEMENT_METHOD'
          ? {
              settlement_methods: optionData?.settlement_methods ?? [],
              payment_terms: optionData?.payment_terms ?? [],
            }
          : {
              settlement_methods: optionData?.settlement_methods ?? [],
              payment_terms: optionData?.payment_terms ?? [],
            };
      if (optionForm.option_type === 'SETTLEMENT_METHOD')
        next.settlement_methods = optionForm.id
          ? next.settlement_methods.map((item) => (item.id === result.id ? result : item))
          : [result, ...next.settlement_methods];
      else
        next.payment_terms = optionForm.id
          ? next.payment_terms.map((item) => (item.id === result.id ? result : item))
          : [result, ...next.payment_terms];
      setOptionData(next);
      setOptionForm({
        id: '',
        option_type: optionForm.option_type,
        code: '',
        name: '',
        sort_order: 0,
        active: true,
      });
      notify('選項已儲存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setOptionSaving(false);
    }
  };
  const editOption = (
    option: { id: string; code: string; name: string; sort_order: number; active: number },
    type: 'SETTLEMENT_METHOD' | 'PAYMENT_TERM',
  ) =>
    setOptionForm({
      id: option.id,
      option_type: type,
      code: option.code,
      name: option.name,
      sort_order: option.sort_order,
      active: Boolean(option.active),
    });
  const toggleOption = async (
    option: { id: string; code: string; name: string; sort_order: number; active: number },
    type: 'SETTLEMENT_METHOD' | 'PAYMENT_TERM',
  ) => {
    try {
      const result = await api.vendors.updateOption(option.id, {
        option_type: type,
        code: option.code,
        name: option.name,
        sort_order: option.sort_order,
        active: !option.active,
      });
      setOptionData({
        settlement_methods:
          type === 'SETTLEMENT_METHOD'
            ? settlementMethods.map((item) => (item.id === result.id ? result : item))
            : settlementMethods,
        payment_terms:
          type === 'PAYMENT_TERM'
            ? paymentTerms.map((item) => (item.id === result.id ? result : item))
            : paymentTerms,
      });
      notify(result.active ? '選項已啟用。' : '選項已停用。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const uploadAttachment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing || !attachmentFile) {
      notify('請先選擇檔案。', 'error');
      return;
    }
    setAttachmentSaving(true);
    try {
      await api.vendorAttachments.upload(editing, attachmentType, attachmentFile);
      const detail = await api.vendors.detail(editing);
      setData({ vendors: vendors.map((item) => (item.id === detail.id ? detail : item)) });
      setForm(vendorToForm(detail));
      setAttachmentFile(null);
      notify('廠商附件已上傳至 R2。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setAttachmentSaving(false);
    }
  };

  const sectionTabs = [
    ['basic', '基本資料'],
    ['contact', '聯絡資料'],
    ['finance', '財務條件'],
    ['warehouse-delivery', '總倉配送'],
    ['store-delivery', '門市配送'],
    ['warehouse-return', '總倉退貨'],
    ['store-return', '門市退貨'],
    ['invoice', '發票設定'],
    ['bank', '銀行帳戶'],
    ['attachments', '附件與紀錄'],
  ];
  const d = form.delivery_settings;
  const r = form.return_settings;
  const i = form.invoice_settings;
  const f = form.financial_terms;
  const statusActions: Array<{
    status: VendorStatus;
    label: string;
    variant?: 'primary' | 'secondary' | 'danger';
  }> =
    form.vendor_status === 'DRAFT'
      ? [{ status: 'ACTIVE', label: '啟用廠商' }]
      : form.vendor_status === 'ACTIVE'
        ? [
            { status: 'SUSPENDED', label: '暫停交易', variant: 'secondary' },
            { status: 'INACTIVE', label: '停用廠商', variant: 'danger' },
          ]
        : form.vendor_status === 'SUSPENDED'
          ? [
              { status: 'ACTIVE', label: '恢復啟用' },
              { status: 'INACTIVE', label: '停用廠商', variant: 'danger' },
            ]
          : [{ status: 'DRAFT', label: '重新建立', variant: 'secondary' }];

  return (
    <>
      <PageHeader
        eyebrow="MASTER DATA"
        title="廠商主檔與交易條件"
        description="基本、聯絡、財務、配送、退貨與發票資料集中管理；FINANCE 僅能維護財務、發票及銀行資訊。"
        actions={
          !isFinance ? (
            <Button type="button" onClick={reset}>
              ＋ 新增廠商
            </Button>
          ) : undefined
        }
      />
      <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>填寫規則：</strong>
        紅色「*」為必要欄位；草稿可先儲存，送出審核或啟用前會由後端再次檢查完整度。銀行帳號只保存加密值，畫面顯示遮罩。
      </div>
      <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
        <Card className="h-fit">
          <div className="mb-4 grid gap-3">
            <Input
              label="搜尋廠商"
              placeholder="名稱、代碼、統編"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Select
              label="狀態篩選"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">全部狀態</option>
              {Object.entries(VENDOR_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="結帳方式"
              value={settlementFilter}
              onChange={(event) => setSettlementFilter(event.target.value)}
            >
              <option value="">全部結帳方式</option>
              {settlementMethods.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
            <Select
              label="門市配送"
              value={storeDeliveryFilter}
              onChange={(event) => setStoreDeliveryFilter(event.target.value)}
            >
              <option value="">全部</option>
              <option value="1">可直送門市</option>
              <option value="0">不可直送門市</option>
            </Select>
            <Select
              label="門市退廠商"
              value={storeReturnFilter}
              onChange={(event) => setStoreReturnFilter(event.target.value)}
            >
              <option value="">全部</option>
              <option value="YES">允許</option>
              <option value="NO">不允許</option>
              <option value="BY_PRODUCT">依商品</option>
            </Select>
            <Select
              label="發票格式"
              value={invoiceFilter}
              onChange={(event) => setInvoiceFilter(event.target.value)}
            >
              <option value="">全部發票格式</option>
              {Object.entries(INVOICE_FORMAT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          {loading ? (
            <Loading />
          ) : filteredVendors.length === 0 ? (
            <EmptyState title="沒有符合條件的廠商" description="可建立新廠商或調整搜尋條件。" />
          ) : (
            <div className="space-y-2">
              {filteredVendors.map((vendor) => (
                <button
                  type="button"
                  key={vendor.id}
                  onClick={() => startEdit(vendor)}
                  className={`w-full rounded-2xl border p-3 text-left transition ${editing === vendor.id ? 'border-teal-300 bg-teal-50' : 'border-slate-100 hover:border-teal-200 hover:bg-slate-50'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0">
                      <strong className="block truncate text-sm text-ink">{vendor.name}</strong>
                      <span className="mt-1 block text-xs text-slate-500">
                        {vendor.code} · {vendor.tax_id || '未填統編'}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-bold ${VENDOR_STATUS_TONE[vendor.vendor_status ?? 'DRAFT']}`}
                    >
                      {VENDOR_STATUS_LABELS[vendor.vendor_status ?? 'DRAFT']}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>完整度 {vendor.completeness_percent ?? 0}%</span>
                    {vendor.missing_fields?.length ? (
                      <span className="text-rose-600">缺 {vendor.missing_fields.length} 項</span>
                    ) : (
                      <span className="text-emerald-700">資料完整</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
        <div className="min-w-0">
          {!editing && isFinance ? (
            <Card>
              <EmptyState
                title="請選擇要維護的廠商"
                description="FINANCE 角色不可建立廠商，只能編輯既有廠商的財務、發票及銀行資訊。"
              />
            </Card>
          ) : (
            <form onSubmit={save} className="space-y-6">
              <Card>
                <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
                      VENDOR MASTER
                    </p>
                    <h2 className="mt-1 text-xl font-extrabold">
                      {editing ? form.name || '編輯廠商' : '新增廠商草稿'}
                    </h2>
                    <p className="mt-2 text-xs text-slate-500">
                      {editing
                        ? `代碼 ${form.code || '儲存後自動產生'}`
                        : '儲存後系統會自動產生廠商代碼。'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold ${VENDOR_STATUS_TONE[form.vendor_status]}`}
                    >
                      {VENDOR_STATUS_LABELS[form.vendor_status]}
                    </span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                      完整度{' '}
                      {editing
                        ? (vendors.find((item) => item.id === editing)?.completeness_percent ?? 0)
                        : 0}
                      %
                    </span>
                    {editing && form.vendor_status === 'ACTIVE' ? (
                      <a
                        href={api.vendors.pdfUrl(editing)}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full bg-teal-700 px-3 py-1 text-xs font-bold text-white hover:bg-teal-800"
                      >
                        下載正式廠商 PDF
                      </a>
                    ) : null}
                  </div>
                </div>
                <div className="mt-5 flex gap-2 overflow-x-auto border-b border-slate-100 pb-1">
                  {sectionTabs.map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      onClick={() => setActiveSection(value)}
                      className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-bold ${activeSection === value ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-ink'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Card>
              {activeSection === 'basic' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">基本資料</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="廠商代碼"
                      hint="留白自動產生"
                      value={form.code}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, code: event.target.value }))
                      }
                    />
                    <Input
                      label="廠商名稱"
                      required
                      value={form.name}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, name: event.target.value }))
                      }
                    />
                    <Input
                      label="廠商簡稱"
                      required
                      value={form.short_name}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, short_name: event.target.value }))
                      }
                    />
                    <Input
                      label="統一編號"
                      required
                      value={form.tax_id}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, tax_id: event.target.value }))
                      }
                    />
                    <Select
                      label="廠商類型"
                      required
                      value={form.vendor_type}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({
                          ...current,
                          vendor_type: event.target.value as VendorType,
                        }))
                      }
                    >
                      {Object.entries(VENDOR_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="網站"
                      type="url"
                      value={form.website}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, website: event.target.value }))
                      }
                    />
                    <Input
                      label="傳真"
                      value={form.fax}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, fax: event.target.value }))
                      }
                    />
                    <Textarea
                      label="公司地址"
                      required
                      className="md:col-span-2"
                      value={form.address}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, address: event.target.value }))
                      }
                    />
                    <Textarea
                      label="對外備註"
                      className="md:col-span-2"
                      value={form.notes}
                      disabled={isFinance}
                      onChange={(event) =>
                        patchForm((current) => ({ ...current, notes: event.target.value }))
                      }
                    />
                    {!isFinance && (
                      <Textarea
                        label="內部備註"
                        className="md:col-span-2"
                        value={form.internal_notes}
                        onChange={(event) =>
                          patchForm((current) => ({
                            ...current,
                            internal_notes: event.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                </Card>
              )}
              {activeSection === 'contact' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">聯絡資料</h3>
                  <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <h4 className="mb-4 font-bold">主要聯絡人</h4>
                      <div className="grid gap-4">
                        <Input
                          label="姓名"
                          required
                          disabled={isFinance}
                          value={form.contacts.primary.name}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                primary: { ...current.contacts.primary, name: event.target.value },
                              },
                            }))
                          }
                        />
                        <Input
                          label="職稱"
                          disabled={isFinance}
                          value={form.contacts.primary.title ?? ''}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                primary: { ...current.contacts.primary, title: event.target.value },
                              },
                            }))
                          }
                        />
                        <Input
                          label="電話"
                          required
                          disabled={isFinance}
                          value={form.contacts.primary.phone}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                primary: { ...current.contacts.primary, phone: event.target.value },
                              },
                            }))
                          }
                        />
                        <Input
                          label="Email"
                          required
                          type="email"
                          disabled={isFinance}
                          value={form.contacts.primary.email}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                primary: { ...current.contacts.primary, email: event.target.value },
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <h4 className="mb-4 font-bold">次要聯絡人</h4>
                      <div className="grid gap-4">
                        <Input
                          label="姓名"
                          disabled={isFinance}
                          value={form.contacts.secondary.name}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                secondary: {
                                  ...current.contacts.secondary,
                                  name: event.target.value,
                                },
                              },
                            }))
                          }
                        />
                        <Input
                          label="職稱"
                          disabled={isFinance}
                          value={form.contacts.secondary.title ?? ''}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                secondary: {
                                  ...current.contacts.secondary,
                                  title: event.target.value,
                                },
                              },
                            }))
                          }
                        />
                        <Input
                          label="電話"
                          disabled={isFinance}
                          value={form.contacts.secondary.phone}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                secondary: {
                                  ...current.contacts.secondary,
                                  phone: event.target.value,
                                },
                              },
                            }))
                          }
                        />
                        <Input
                          label="Email"
                          type="email"
                          disabled={isFinance}
                          value={form.contacts.secondary.email}
                          onChange={(event) =>
                            patchForm((current) => ({
                              ...current,
                              contacts: {
                                ...current.contacts,
                                secondary: {
                                  ...current.contacts.secondary,
                                  email: event.target.value,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              )}
              {activeSection === 'finance' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">財務與交易條件</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Select
                      label="結帳方式"
                      required
                      value={f.settlement_method_id}
                      onChange={(event) =>
                        patchSection('financial_terms', 'settlement_method_id', event.target.value)
                      }
                    >
                      <option value="">請選擇</option>
                      {settlementMethods.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </Select>
                    <Select
                      label="付款條件"
                      required
                      value={f.payment_terms_id}
                      onChange={(event) =>
                        patchSection('financial_terms', 'payment_terms_id', event.target.value)
                      }
                    >
                      <option value="">請選擇</option>
                      {paymentTerms.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </Select>
                    <Select
                      label="付款方式"
                      required
                      value={f.payment_method}
                      onChange={(event) =>
                        patchSection('financial_terms', 'payment_method', event.target.value)
                      }
                    >
                      <option value="">請選擇</option>
                      {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="付款方式備註"
                      value={f.payment_method_note}
                      onChange={(event) =>
                        patchSection('financial_terms', 'payment_method_note', event.target.value)
                      }
                    />
                    <Textarea
                      label="特殊結帳方式說明"
                      value={f.settlement_method_other_note}
                      onChange={(event) =>
                        patchSection(
                          'financial_terms',
                          'settlement_method_other_note',
                          event.target.value,
                        )
                      }
                    />
                    <Textarea
                      label="特殊付款條件說明"
                      value={f.payment_terms_note}
                      onChange={(event) =>
                        patchSection('financial_terms', 'payment_terms_note', event.target.value)
                      }
                    />
                  </div>
                </Card>
              )}
              {activeSection === 'warehouse-delivery' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">總倉配送設定</h3>
                  <div className="space-y-5">
                    <VendorBoolean
                      label="可配送至總倉"
                      checked={d.warehouse_delivery_enabled}
                      disabled={isFinance}
                      onChange={(value) =>
                        patchSection('delivery_settings', 'warehouse_delivery_enabled', value)
                      }
                    />
                    {d.warehouse_delivery_enabled && (
                      <>
                        <div>
                          <p className="mb-2 text-sm font-semibold">配送方式</p>
                          <VendorChoiceList
                            labels={WAREHOUSE_DELIVERY_METHOD_LABELS}
                            selected={d.warehouse_delivery_methods}
                            disabled={isFinance}
                            onChange={(value) =>
                              toggleChoice('delivery_settings', 'warehouse_delivery_methods', value)
                            }
                          />
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <Input
                            label="配送週期／固定日"
                            disabled={isFinance}
                            value={d.warehouse_delivery_cycle}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'warehouse_delivery_cycle',
                                event.target.value,
                              )
                            }
                          />
                          <Input
                            label="訂單截止時間"
                            type="time"
                            disabled={isFinance}
                            value={d.warehouse_order_cutoff_time}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'warehouse_order_cutoff_time',
                                event.target.value,
                              )
                            }
                          />
                          <Input
                            label="平均交貨天數"
                            type="number"
                            min="0"
                            disabled={isFinance}
                            value={d.warehouse_average_delivery_days ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'warehouse_average_delivery_days',
                                event.target.value ? Number(event.target.value) : null,
                              )
                            }
                          />
                          <Input
                            label="最低訂購金額"
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={isFinance}
                            value={d.warehouse_minimum_order_amount ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'warehouse_minimum_order_amount',
                                event.target.value || null,
                              )
                            }
                          />
                          <Input
                            label="最低訂購數量"
                            type="number"
                            min="0"
                            disabled={isFinance}
                            value={d.warehouse_minimum_order_quantity ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'warehouse_minimum_order_quantity',
                                event.target.value ? Number(event.target.value) : null,
                              )
                            }
                          />
                          <Input
                            label="免運門檻／運費"
                            disabled={isFinance}
                            value={d.warehouse_free_shipping_threshold ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'warehouse_free_shipping_threshold',
                                event.target.value || null,
                              )
                            }
                          />
                        </div>
                        <Textarea
                          label="配送及收貨備註"
                          disabled={isFinance}
                          value={`${d.warehouse_delivery_note}${d.warehouse_receiving_note ? `\n收貨：${d.warehouse_receiving_note}` : ''}`}
                          onChange={(event) =>
                            patchSection(
                              'delivery_settings',
                              'warehouse_delivery_note',
                              event.target.value,
                            )
                          }
                        />
                      </>
                    )}
                  </div>
                </Card>
              )}
              {activeSection === 'store-delivery' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">門市配送設定</h3>
                  <div className="space-y-5">
                    <VendorBoolean
                      label="可直接配送至門市"
                      checked={d.store_delivery_enabled}
                      disabled={isFinance}
                      onChange={(value) =>
                        patchSection('delivery_settings', 'store_delivery_enabled', value)
                      }
                    />
                    {d.store_delivery_enabled && (
                      <>
                        <div>
                          <p className="mb-2 text-sm font-semibold">配送方式</p>
                          <VendorChoiceList
                            labels={STORE_DELIVERY_METHOD_LABELS}
                            selected={d.store_delivery_methods}
                            disabled={isFinance}
                            onChange={(value) =>
                              toggleChoice('delivery_settings', 'store_delivery_methods', value)
                            }
                          />
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <Input
                            label="配送區域"
                            disabled={isFinance}
                            value={d.store_delivery_regions}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'store_delivery_regions',
                                event.target.value,
                              )
                            }
                          />
                          <Input
                            label="訂單截止時間"
                            type="time"
                            disabled={isFinance}
                            value={d.store_order_cutoff_time}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'store_order_cutoff_time',
                                event.target.value,
                              )
                            }
                          />
                          <Input
                            label="平均交貨天數"
                            type="number"
                            min="0"
                            disabled={isFinance}
                            value={d.store_average_delivery_days ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'store_average_delivery_days',
                                event.target.value ? Number(event.target.value) : null,
                              )
                            }
                          />
                          <Input
                            label="最低訂購金額／數量"
                            disabled={isFinance}
                            value={d.store_minimum_order_amount ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'delivery_settings',
                                'store_minimum_order_amount',
                                event.target.value || null,
                              )
                            }
                          />
                        </div>
                        <Textarea
                          label="門市配送備註"
                          disabled={isFinance}
                          value={d.store_delivery_note}
                          onChange={(event) =>
                            patchSection(
                              'delivery_settings',
                              'store_delivery_note',
                              event.target.value,
                            )
                          }
                        />
                      </>
                    )}
                  </div>
                </Card>
              )}
              {activeSection === 'warehouse-return' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">總倉退貨設定</h3>
                  <div className="space-y-5">
                    <Select
                      label="總倉退貨"
                      required
                      disabled={isFinance}
                      value={r.warehouse_return_enabled}
                      onChange={(event) =>
                        patchSection(
                          'return_settings',
                          'warehouse_return_enabled',
                          event.target.value,
                        )
                      }
                    >
                      <option value="YES">可退貨</option>
                      <option value="NO">不可退貨</option>
                      <option value="BY_PRODUCT">依商品約定</option>
                    </Select>
                    {r.warehouse_return_enabled !== 'NO' && (
                      <>
                        <div>
                          <p className="mb-2 text-sm font-semibold">退貨方式</p>
                          <VendorChoiceList
                            labels={WAREHOUSE_RETURN_METHOD_LABELS}
                            selected={r.warehouse_return_methods}
                            disabled={isFinance}
                            onChange={(value) =>
                              toggleChoice('return_settings', 'warehouse_return_methods', value)
                            }
                          />
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <Input
                            label="退貨通知天數"
                            type="number"
                            min="0"
                            disabled={isFinance}
                            value={r.warehouse_return_notice_days ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'return_settings',
                                'warehouse_return_notice_days',
                                event.target.value ? Number(event.target.value) : null,
                              )
                            }
                          />
                          <Input
                            label="運費負擔"
                            disabled={isFinance}
                            value={r.warehouse_return_shipping_bearer}
                            onChange={(event) =>
                              patchSection(
                                'return_settings',
                                'warehouse_return_shipping_bearer',
                                event.target.value,
                              )
                            }
                          />
                          <Input
                            label="申請方式"
                            disabled={isFinance}
                            value={r.warehouse_return_application_method}
                            onChange={(event) =>
                              patchSection(
                                'return_settings',
                                'warehouse_return_application_method',
                                event.target.value,
                              )
                            }
                          />
                          <Textarea
                            label="退貨規則／備註"
                            disabled={isFinance}
                            value={r.warehouse_return_note}
                            onChange={(event) =>
                              patchSection(
                                'return_settings',
                                'warehouse_return_note',
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="flex flex-wrap gap-4">
                          <VendorBoolean
                            label="需退貨單"
                            disabled={isFinance}
                            checked={r.warehouse_return_slip_required}
                            onChange={(value) =>
                              patchSection(
                                'return_settings',
                                'warehouse_return_slip_required',
                                value,
                              )
                            }
                          />
                          <VendorBoolean
                            label="需廠商核准"
                            disabled={isFinance}
                            checked={r.warehouse_vendor_approval_required}
                            onChange={(value) =>
                              patchSection(
                                'return_settings',
                                'warehouse_vendor_approval_required',
                                value,
                              )
                            }
                          />
                          <VendorBoolean
                            label="需商品照片"
                            disabled={isFinance}
                            checked={r.warehouse_photo_required}
                            onChange={(value) =>
                              patchSection('return_settings', 'warehouse_photo_required', value)
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              )}
              {activeSection === 'store-return' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">門市退貨設定</h3>
                  <div className="space-y-5">
                    <Select
                      label="門市直接退廠商"
                      required
                      disabled={isFinance}
                      value={r.store_return_to_vendor_enabled}
                      onChange={(event) =>
                        patchSection(
                          'return_settings',
                          'store_return_to_vendor_enabled',
                          event.target.value,
                        )
                      }
                    >
                      <option value="YES">允許</option>
                      <option value="NO">不允許</option>
                      <option value="BY_PRODUCT">依商品約定</option>
                    </Select>
                    {r.store_return_to_vendor_enabled !== 'NO' && (
                      <>
                        <div>
                          <p className="mb-2 text-sm font-semibold">退貨方式</p>
                          <VendorChoiceList
                            labels={STORE_RETURN_METHOD_LABELS}
                            selected={r.store_return_methods}
                            disabled={isFinance}
                            onChange={(value) =>
                              toggleChoice('return_settings', 'store_return_methods', value)
                            }
                          />
                        </div>
                        <div className="flex flex-wrap gap-4">
                          <VendorBoolean
                            label="需採購核准"
                            disabled={isFinance}
                            checked={r.store_procurement_approval_required}
                            onChange={(value) =>
                              patchSection(
                                'return_settings',
                                'store_procurement_approval_required',
                                value,
                              )
                            }
                          />
                          <VendorBoolean
                            label="需總倉核准"
                            disabled={isFinance}
                            checked={r.store_warehouse_approval_required}
                            onChange={(value) =>
                              patchSection(
                                'return_settings',
                                'store_warehouse_approval_required',
                                value,
                              )
                            }
                          />
                          <VendorBoolean
                            label="需廠商核准"
                            disabled={isFinance}
                            checked={r.store_vendor_approval_required}
                            onChange={(value) =>
                              patchSection(
                                'return_settings',
                                'store_vendor_approval_required',
                                value,
                              )
                            }
                          />
                          <VendorBoolean
                            label="需照片"
                            disabled={isFinance}
                            checked={r.store_photo_required}
                            onChange={(value) =>
                              patchSection('return_settings', 'store_photo_required', value)
                            }
                          />
                          <VendorBoolean
                            label="需填退貨原因"
                            disabled={isFinance}
                            checked={r.store_reason_required}
                            onChange={(value) =>
                              patchSection('return_settings', 'store_reason_required', value)
                            }
                          />
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <Input
                            label="退貨期限（天）"
                            type="number"
                            min="0"
                            disabled={isFinance}
                            value={r.store_return_deadline_days ?? ''}
                            onChange={(event) =>
                              patchSection(
                                'return_settings',
                                'store_return_deadline_days',
                                event.target.value ? Number(event.target.value) : null,
                              )
                            }
                          />
                          <Input
                            label="運費負擔"
                            disabled={isFinance}
                            value={r.store_shipping_bearer}
                            onChange={(event) =>
                              patchSection(
                                'return_settings',
                                'store_shipping_bearer',
                                event.target.value,
                              )
                            }
                          />
                          <Textarea
                            label="門市退貨備註"
                            disabled={isFinance}
                            value={r.store_return_note}
                            onChange={(event) =>
                              patchSection(
                                'return_settings',
                                'store_return_note',
                                event.target.value,
                              )
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              )}
              {activeSection === 'invoice' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">發票設定</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Select
                      label="發票格式"
                      required
                      value={i.invoice_format}
                      onChange={(event) =>
                        patchSection('invoice_settings', 'invoice_format', event.target.value)
                      }
                    >
                      <option value="">請選擇</option>
                      {Object.entries(INVOICE_FORMAT_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <Select
                      label="發票開立方式"
                      required
                      value={i.invoice_issue_method}
                      onChange={(event) =>
                        patchSection('invoice_settings', 'invoice_issue_method', event.target.value)
                      }
                    >
                      <option value="">請選擇</option>
                      {Object.entries(INVOICE_ISSUE_METHOD_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <div className="md:col-span-2">
                      <p className="mb-2 text-sm font-semibold">
                        發票寄送方式 <span className="text-rose-600">*</span>
                      </p>
                      <VendorChoiceList
                        labels={INVOICE_DELIVERY_METHOD_LABELS}
                        selected={i.invoice_delivery_methods}
                        onChange={(value) =>
                          toggleChoice('invoice_settings', 'invoice_delivery_methods', value)
                        }
                      />
                    </div>
                    <Input
                      label="發票 Email"
                      type="email"
                      value={i.invoice_email}
                      onChange={(event) =>
                        patchSection('invoice_settings', 'invoice_email', event.target.value)
                      }
                    />
                    <Input
                      label="發票郵寄地址"
                      value={i.invoice_mailing_address}
                      onChange={(event) =>
                        patchSection(
                          'invoice_settings',
                          'invoice_mailing_address',
                          event.target.value,
                        )
                      }
                    />
                    <Input
                      label="發票抬頭"
                      value={i.invoice_title}
                      onChange={(event) =>
                        patchSection('invoice_settings', 'invoice_title', event.target.value)
                      }
                    />
                    <Input
                      label="發票統編"
                      value={i.invoice_tax_id || form.tax_id}
                      onChange={(event) =>
                        patchSection('invoice_settings', 'invoice_tax_id', event.target.value)
                      }
                    />
                    <Textarea
                      label="發票備註"
                      className="md:col-span-2"
                      value={i.invoice_note}
                      onChange={(event) =>
                        patchSection('invoice_settings', 'invoice_note', event.target.value)
                      }
                    />
                  </div>
                </Card>
              )}
              {activeSection === 'bank' && (
                <Card>
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-extrabold">銀行帳戶</h3>
                      <p className="mt-1 text-xs text-slate-500">
                        帳號以 AES-GCM 加密後保存；一般清單只顯示末四碼。
                      </p>
                    </div>
                    <Button type="button" variant="secondary" onClick={addBank}>
                      ＋ 新增帳戶
                    </Button>
                  </div>
                  {form.bank_accounts.length === 0 ? (
                    <EmptyState
                      title="尚未設定銀行帳戶"
                      description="可先儲存其他資料，再新增主要收款帳戶。"
                    />
                  ) : (
                    <div className="space-y-4">
                      {form.bank_accounts.map((account, index) => (
                        <div
                          key={account.id ?? index}
                          className="rounded-2xl border border-slate-100 bg-slate-50 p-4"
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <VendorBoolean
                              label="主要帳戶"
                              checked={account.is_primary}
                              onChange={(value) =>
                                patchForm((current) => ({
                                  ...current,
                                  bank_accounts: current.bank_accounts.map((item, itemIndex) => ({
                                    ...item,
                                    is_primary:
                                      itemIndex === index ? value : value ? false : item.is_primary,
                                  })),
                                }))
                              }
                            />
                            <Button type="button" variant="ghost" onClick={() => removeBank(index)}>
                              移除
                            </Button>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            <Input
                              label="銀行名稱"
                              value={account.bank_name}
                              onChange={(event) =>
                                updateBank(index, 'bank_name', event.target.value)
                              }
                            />
                            <Input
                              label="分行名稱"
                              value={account.branch_name}
                              onChange={(event) =>
                                updateBank(index, 'branch_name', event.target.value)
                              }
                            />
                            <Input
                              label="銀行代碼"
                              value={account.bank_code}
                              onChange={(event) =>
                                updateBank(index, 'bank_code', event.target.value)
                              }
                            />
                            <Input
                              label="分行代碼"
                              value={account.branch_code}
                              onChange={(event) =>
                                updateBank(index, 'branch_code', event.target.value)
                              }
                            />
                            <Input
                              label="戶名"
                              value={account.account_name}
                              onChange={(event) =>
                                updateBank(index, 'account_name', event.target.value)
                              }
                            />
                            <Input
                              label="帳號"
                              value={account.account_number || account.account_number_masked || ''}
                              onChange={(event) =>
                                updateBank(index, 'account_number', event.target.value)
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
              {activeSection === 'attachments' && (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">附件與操作紀錄</h3>
                  <p className="mb-4 text-sm text-slate-500">
                    廠商營業登記、匯款資料與其他證明文件會上傳至 R2，Object Key 使用
                    UUID，不直接使用原始檔名。
                  </p>
                  {!editing ? (
                    <EmptyState title="儲存廠商後才能管理附件" />
                  ) : (
                    <>
                      <form
                        onSubmit={uploadAttachment}
                        className="mb-5 grid gap-3 rounded-2xl bg-slate-50 p-4 md:grid-cols-[220px_1fr_auto] md:items-end"
                      >
                        <Select
                          label="附件類型"
                          disabled={isFinance}
                          value={attachmentType}
                          onChange={(event) => setAttachmentType(event.target.value)}
                        >
                          {Object.entries(VENDOR_ATTACHMENT_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </Select>
                        <label className="block space-y-1.5">
                          <span className="text-sm font-semibold text-ink">選擇檔案</span>
                          <input
                            type="file"
                            disabled={isFinance}
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.ppt,.pptx,.doc,.docx,.xls,.xlsx"
                            onChange={(event) => setAttachmentFile(event.target.files?.[0] ?? null)}
                            className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          />
                        </label>
                        <Button
                          type="submit"
                          loading={attachmentSaving}
                          disabled={isFinance || !attachmentFile}
                        >
                          上傳附件
                        </Button>
                      </form>
                      {(vendors.find((vendor) => vendor.id === editing)?.attachments ?? [])
                        .length === 0 ? (
                        <EmptyState title="尚未上傳附件" />
                      ) : (
                        <div className="space-y-2">
                          {(vendors.find((vendor) => vendor.id === editing)?.attachments ?? []).map(
                            (attachment) => (
                              <div
                                key={attachment.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-50 p-3 text-sm"
                              >
                                <a
                                  className="font-semibold text-teal-700 hover:underline"
                                  href={api.vendorAttachments.downloadUrl(editing, attachment.id)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {attachment.original_name}
                                </a>
                                <span className="text-xs text-slate-500">
                                  {VENDOR_ATTACHMENT_LABELS[attachment.type] ?? attachment.type} ·{' '}
                                  {Math.round(attachment.size_bytes / 1024)} KB
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Card>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  {editing &&
                    statusActions.map((action) => (
                      <Button
                        type="button"
                        key={action.status}
                        variant={action.variant ?? 'primary'}
                        onClick={() => changeStatus(action.status)}
                      >
                        {action.label}
                      </Button>
                    ))}
                </div>
                <div className="flex gap-2">
                  {editing && (
                    <Button type="button" variant="secondary" onClick={reset}>
                      取消編輯
                    </Button>
                  )}
                  <Button type="submit" loading={saving}>
                    {editing ? '儲存變更' : '儲存草稿'}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      {isAdmin && (
        <Card className="mt-6">
          <div className="mb-4">
            <h2 className="text-lg font-extrabold">結帳方式與付款條件選項</h2>
            <p className="mt-1 text-xs text-slate-500">
              停用只會隱藏在新表單，既有廠商歷史資料仍保留。
            </p>
          </div>
          <form
            onSubmit={saveOption}
            className="grid gap-3 md:grid-cols-[160px_160px_1fr_1fr_100px_auto]"
          >
            <Select
              label="選項類別"
              value={optionForm.option_type}
              onChange={(event) =>
                setOptionForm((current) => ({
                  ...current,
                  option_type: event.target.value as 'SETTLEMENT_METHOD' | 'PAYMENT_TERM',
                }))
              }
            >
              <option value="SETTLEMENT_METHOD">結帳方式</option>
              <option value="PAYMENT_TERM">付款條件</option>
            </Select>
            <Input
              label="代碼"
              required
              value={optionForm.code}
              onChange={(event) =>
                setOptionForm((current) => ({ ...current, code: event.target.value }))
              }
            />
            <Input
              label="顯示名稱"
              required
              value={optionForm.name}
              onChange={(event) =>
                setOptionForm((current) => ({ ...current, name: event.target.value }))
              }
            />
            <Input
              label="排序"
              type="number"
              value={optionForm.sort_order}
              onChange={(event) =>
                setOptionForm((current) => ({ ...current, sort_order: Number(event.target.value) }))
              }
            />
            <VendorBoolean
              label="啟用"
              checked={optionForm.active}
              onChange={(value) => setOptionForm((current) => ({ ...current, active: value }))}
            />
            <div className="flex items-end gap-2">
              <Button loading={optionSaving}>{optionForm.id ? '更新選項' : '新增選項'}</Button>
              {optionForm.id && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setOptionForm({
                      id: '',
                      option_type: optionForm.option_type,
                      code: '',
                      name: '',
                      sort_order: 0,
                      active: true,
                    })
                  }
                >
                  取消
                </Button>
              )}
            </div>
          </form>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {(
              [
                ['SETTLEMENT_METHOD', '結帳方式', settlementMethods],
                ['PAYMENT_TERM', '付款條件', paymentTerms],
              ] as const
            ).map(([type, label, options]) => (
              <div key={type}>
                <h3 className="mb-2 font-bold">{label}</h3>
                <div className="space-y-2">
                  {options.map((option) => (
                    <div
                      key={option.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 text-sm"
                    >
                      <span>
                        <strong>{option.name}</strong>
                        <span className="ml-2 text-xs text-slate-500">
                          {option.code} · 順序 {option.sort_order}
                        </span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span
                          className={`text-xs font-bold ${option.active ? 'text-emerald-700' : 'text-slate-400'}`}
                        >
                          {option.active ? '啟用' : '停用'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => editOption(option, type)}
                        >
                          編輯
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => toggleOption(option, type)}
                        >
                          {option.active ? '停用' : '啟用'}
                        </Button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

const VENDOR_APPLICATION_STATUS_LABELS: Record<VendorApplication['status'], string> = {
  DRAFT: '草稿',
  PENDING_REVIEW: '待審核',
  APPROVED: '已核准',
  REJECTED: '已退回',
};

const VENDOR_APPLICATION_STATUS_TONE: Record<VendorApplication['status'], string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  PENDING_REVIEW: 'bg-amber-100 text-amber-900',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-rose-100 text-rose-800',
};

const VENDOR_APPLICATION_REVIEW_FIELD_OPTIONS = [
  ['company_name', '公司名稱'],
  ['tax_id', '統一編號'],
  ['company_email', '公司 Email'],
  ['principal_brands', '主要代理／品牌'],
  ['supplied_product_categories', '供應商品類'],
  ['contact', '主要聯絡人'],
  ['payment_terms', '付款與交易條件'],
  ['delivery_terms', '配送條件'],
  ['return_terms', '退貨／換貨條件'],
  ['invoice_terms', '發票與對帳'],
  ['bank_account', '銀行與付款資料'],
] as const;

const VENDOR_APPLICATION_REVIEW_ACTION_LABELS: Record<string, string> = {
  APPROVE: '已核准',
  REQUEST_DOCUMENTS: '要求補件',
  RETURN_FOR_EDIT: '退回修改',
  SUSPEND: '暫緩處理',
  REJECT: '已退件',
};

function VendorApplicationStatusBadge({
  status,
  action,
}: {
  status: VendorApplication['status'];
  action?: VendorApplication['review_action'];
}) {
  const actionTone =
    action === 'SUSPEND'
      ? 'bg-orange-100 text-orange-900'
      : action === 'REQUEST_DOCUMENTS' || action === 'RETURN_FOR_EDIT' || action === 'REJECT'
        ? 'bg-rose-100 text-rose-800'
        : VENDOR_APPLICATION_STATUS_TONE[status];
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${actionTone}`}>
      {action
        ? (VENDOR_APPLICATION_REVIEW_ACTION_LABELS[action] ??
          VENDOR_APPLICATION_STATUS_LABELS[status])
        : VENDOR_APPLICATION_STATUS_LABELS[status]}
    </span>
  );
}

function applicationValue(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

function ReviewInfo({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap break-words leading-6 font-semibold">{value}</dd>
    </div>
  );
}

function applicationCategoryLabels(codes: string[] | undefined): string {
  if (!codes?.length) return '—';
  return codes
    .map((code) => {
      const option = PRODUCT_CATEGORY_OPTIONS.find((item) => item.code === code);
      return option ? `${option.code}｜${option.name}` : code;
    })
    .join('、');
}

function applicationLabel(labels: Record<string, string>, value: string | undefined): string {
  if (!value) return '—';
  return labels[value] ?? value;
}

function VendorApplicationFullDetails({ application }: { application: VendorApplication }) {
  const contact = application.contact;
  const deliveryWeekdays = application.delivery_weekdays?.length
    ? application.delivery_weekdays
    : application.delivery_fixed_weekdays;
  const returnConditionTypes = application.return_condition_types?.length
    ? application.return_condition_types
    : application.return_conditions;
  const invoiceContact = application.invoice_same_as_primary
    ? '同主要聯絡人'
    : `${applicationValue(application.invoice_contact_name)}／${applicationValue(application.invoice_contact_phone)}／${applicationValue(application.invoice_email)}`;
  const reconciliationContact = application.reconciliation_same_as_primary
    ? '同主要聯絡人'
    : `${applicationValue(application.reconciliation_contact_name)}／${applicationValue(application.reconciliation_contact_phone)}／${applicationValue(application.reconciliation_email)}`;
  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-3 text-sm font-extrabold text-ink">一、公司基本資料</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <ReviewInfo label="公司名稱" value={applicationValue(application.company_name)} />
          <ReviewInfo
            label="公司簡稱"
            value={applicationValue(application.company_short_name ?? application.short_name)}
          />
          <ReviewInfo label="統一編號" value={applicationValue(application.tax_id)} />
          <ReviewInfo label="公司電話" value={applicationValue(application.phone)} />
          <ReviewInfo label="公司 Email" value={applicationValue(application.company_email)} />
          <ReviewInfo label="公司網站" value={applicationValue(application.website)} />
          <ReviewInfo label="公司地址" value={applicationValue(application.address)} wide />
          <ReviewInfo label="傳真" value={applicationValue(application.company_fax)} />
          <ReviewInfo
            label="主要代理／品牌"
            value={applicationValue(application.principal_brands?.join('、'))}
            wide
          />
          <ReviewInfo
            label="供應商品類"
            value={applicationCategoryLabels(application.supplied_product_categories)}
            wide
          />
          <ReviewInfo label="公司簡介" value={applicationValue(application.company_intro)} wide />
          <ReviewInfo label="備註" value={applicationValue(application.notes)} wide />
        </dl>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-extrabold text-ink">二、主要聯絡人</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <ReviewInfo label="聯絡人姓名" value={applicationValue(contact?.contact_name)} />
          <ReviewInfo
            label="聯絡人職稱"
            value={applicationValue(contact?.contact_job_title ?? contact?.title)}
          />
          <ReviewInfo label="聯絡電話" value={applicationValue(contact?.phone)} />
          <ReviewInfo label="聯絡 Email" value={applicationValue(contact?.email)} />
          <ReviewInfo label="廠商結帳日" value={applicationValue(application.vendor_closing_day)} />
          <ReviewInfo
            label="廠商每日訂單截止時間"
            value={applicationValue(application.daily_order_cutoff_time)}
          />
        </dl>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-extrabold text-ink">三、付款與交易條件</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <ReviewInfo
            label="付款條件"
            value={applicationLabel(
              VENDOR_APPLICATION_PAYMENT_TERM_LABELS,
              application.payment_term || application.payment_terms,
            )}
          />
          <ReviewInfo label="月結天數" value={applicationValue(application.monthly_payment_days)} />
          <ReviewInfo
            label="付款方式"
            value={applicationLabel(
              VENDOR_APPLICATION_PAYMENT_METHOD_LABELS,
              application.payment_method,
            )}
          />
          <ReviewInfo
            label="其他付款方式"
            value={applicationValue(application.payment_method_other)}
          />
          <ReviewInfo
            label="週結說明"
            value={applicationValue(application.weekly_payment_note)}
            wide
          />
          <ReviewInfo
            label="付款規則確認"
            value={application.payment_rule_acknowledged ? '已確認' : '未確認'}
          />
          <ReviewInfo
            label="支票／匯款提示"
            value="支票兌現日為每月 30 日；匯款日為每月月底。"
            wide
          />
        </dl>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-extrabold text-ink">四、配送條件</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <ReviewInfo
            label="配送地點"
            value={applicationLabel(
              VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS,
              application.delivery_scope,
            )}
          />
          <ReviewInfo
            label="配送週期"
            value={applicationLabel(
              VENDOR_APPLICATION_DELIVERY_CYCLE_LABELS,
              application.delivery_cycle,
            )}
          />
          <ReviewInfo
            label="固定配送星期"
            value={
              deliveryWeekdays?.length
                ? deliveryWeekdays
                    .map((item) => VENDOR_APPLICATION_WEEKDAY_LABELS[item] ?? item)
                    .join('、')
                : '—'
            }
            wide
          />
          <ReviewInfo
            label="其他配送週期說明"
            value={applicationValue(application.delivery_cycle_other)}
            wide
          />
          <ReviewInfo
            label="最低訂購金額"
            value={applicationValue(application.delivery_minimum_order_amount)}
          />
          <ReviewInfo
            label="最低訂購數量"
            value={applicationValue(application.delivery_minimum_order_quantity)}
          />
          <ReviewInfo
            label="運費規則"
            value={applicationLabel(
              VENDOR_APPLICATION_FREIGHT_RULE_LABELS,
              application.freight_rule,
            )}
          />
          <ReviewInfo
            label="免運門檻／固定運費"
            value={applicationValue(
              application.freight_threshold_amount ?? application.freight_fixed_amount,
            )}
          />
          <ReviewInfo
            label="其他運費規則說明"
            value={applicationValue(application.freight_other_note)}
            wide
          />
        </dl>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-extrabold text-ink">五、退貨／換貨條件</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <ReviewInfo
            label="退貨地點"
            value={applicationLabel(
              VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS,
              application.return_scope,
            )}
          />
          <ReviewInfo
            label="退貨處理方式"
            value={applicationLabel(
              VENDOR_APPLICATION_RETURN_HANDLING_LABELS,
              application.return_handling_method,
            )}
          />
          <ReviewInfo
            label="成立條件"
            value={
              returnConditionTypes?.length
                ? returnConditionTypes
                    .map((item) => VENDOR_APPLICATION_RETURN_CONDITION_LABELS[item] ?? item)
                    .join('、')
                : '—'
            }
            wide
          />
          <ReviewInfo
            label="即期退換貨條件"
            value={applicationValue(application.return_near_expiry_condition)}
            wide
          />
          <ReviewInfo
            label="瑕疵退換貨條件"
            value={applicationValue(application.return_defect_condition)}
            wide
          />
          <ReviewInfo
            label="吃不合退換貨條件"
            value={applicationValue(application.return_intolerance_condition)}
            wide
          />
          <ReviewInfo
            label="其他退換貨條件"
            value={applicationValue(application.return_other_condition)}
            wide
          />
          <ReviewInfo
            label="退貨／換貨詳細說明"
            value={applicationValue(application.return_condition_description)}
            wide
          />
          <ReviewInfo
            label="退貨運費負擔"
            value={applicationLabel(
              VENDOR_APPLICATION_RETURN_FREIGHT_LABELS,
              application.return_freight_responsibility,
            )}
          />
          <ReviewInfo
            label="運費負擔規則說明"
            value={applicationValue(application.return_freight_rule_note)}
            wide
          />
        </dl>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-extrabold text-ink">六、發票與對帳</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <ReviewInfo
            label="發票格式"
            value={`${applicationLabel(VENDOR_APPLICATION_INVOICE_FORMAT_LABELS, application.invoice_format)}${application.invoice_format_other ? `（${application.invoice_format_other}）` : ''}`}
          />
          <ReviewInfo
            label="發票寄送方式"
            value={`${applicationLabel(VENDOR_APPLICATION_INVOICE_DELIVERY_LABELS, application.invoice_delivery_method)}${application.invoice_delivery_other ? `（${application.invoice_delivery_other}）` : ''}`}
          />
          <ReviewInfo label="發票聯絡人" value={invoiceContact} wide />
          <ReviewInfo label="對帳聯絡人" value={reconciliationContact} wide />
          <ReviewInfo label="對帳週期" value={applicationValue(application.reconciliation_cycle)} />
          <ReviewInfo
            label="對帳條件確認"
            value={application.reconciliation_terms_acknowledged ? '已確認' : '未確認'}
          />
          <ReviewInfo
            label="我司對帳作業原則"
            value={VENDOR_APPLICATION_RECONCILIATION_NOTICE}
            wide
          />
        </dl>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-extrabold text-ink">七、銀行與付款資料</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <ReviewInfo
            label="銀行名稱／代碼"
            value={`${applicationValue(application.bank_name)}／${applicationValue(application.bank_code)}`}
          />
          <ReviewInfo
            label="分行名稱／代碼"
            value={`${applicationValue(application.branch_name)}／${applicationValue(application.branch_code)}`}
          />
          <ReviewInfo label="戶名" value={applicationValue(application.bank_account_name)} />
          <ReviewInfo
            label="帳號（遮罩）"
            value={applicationValue(application.bank_account_number_masked)}
          />
        </dl>
        <p className="mt-3 text-xs text-slate-500">
          銀行帳號僅顯示遮罩值，完整帳號不會在審核頁呈現。
        </p>
      </section>
    </div>
  );
}

type UnifiedVendorMasterForm = {
  code: string;
  vendor_type: VendorType;
  vendor_status: VendorStatus;
  internal_notes: string;
  responsible_buyer_id: string;
  active_date: string;
  credit_trade_notes: string;
  risk_flag: string;
  profile: VendorProfile;
};

function createEmptyUnifiedVendorProfile(): VendorProfile {
  return {
    business: {
      company_name: '',
      company_short_name: null,
      tax_id: '',
      phone: '',
      address: '',
      company_email: '',
      website: '',
      company_fax: '',
      company_intro: '',
      notes: '',
      principal_brands: [''],
      supplied_product_categories: [],
    },
    contact: {
      contact_name: '',
      contact_job_title: null,
      contact_phone: '',
      contact_email: '',
      vendor_closing_day: '',
      daily_order_cutoff_time: '',
    },
    payment_terms: {
      payment_term: '',
      monthly_payment_days: null,
      payment_method: '',
      payment_method_other: '',
      weekly_payment_note: '',
      payment_rule_acknowledged: false,
    },
    delivery_terms: {
      delivery_scope: '',
      delivery_cycle: '',
      delivery_weekdays: [],
      delivery_cycle_other: '',
      vendor_closing_day: '',
      daily_order_cutoff_time: '',
      minimum_order_amount: null,
      minimum_order_quantity: null,
      freight_rule: '',
      freight_threshold_amount: null,
      freight_fixed_amount: null,
      freight_other_note: '',
    },
    return_terms: {
      return_scope: '',
      return_handling_method: '',
      return_condition_types: [],
      return_near_expiry_condition: '',
      return_defect_condition: '',
      return_intolerance_condition: '',
      return_other_condition: '',
      return_condition_description: '',
      return_freight_responsibility: '',
      return_freight_rule_note: '',
    },
    invoice_terms: {
      invoice_format: '',
      invoice_format_other: '',
      invoice_delivery_method: '',
      invoice_delivery_other: '',
      invoice_same_as_primary: true,
      invoice_contact_name: '',
      invoice_contact_phone: '',
      invoice_email: '',
      reconciliation_same_as_primary: true,
      reconciliation_contact_name: '',
      reconciliation_contact_phone: '',
      reconciliation_email: '',
      reconciliation_terms_acknowledged: false,
    },
    bank_details: [],
  };
}

function createEmptyUnifiedVendorMasterForm(): UnifiedVendorMasterForm {
  return {
    code: '',
    vendor_type: 'OTHER',
    vendor_status: 'DRAFT',
    internal_notes: '',
    responsible_buyer_id: '',
    active_date: '',
    credit_trade_notes: '',
    risk_flag: '',
    profile: createEmptyUnifiedVendorProfile(),
  };
}

function vendorToUnifiedVendorMasterForm(vendor: Vendor): UnifiedVendorMasterForm {
  const empty = createEmptyUnifiedVendorMasterForm();
  const profile = vendor.profile ?? empty.profile;
  const bankDetails = profile.bank_details?.length
    ? profile.bank_details
    : (vendor.bank_accounts ?? []).map((account) => ({
        id: account.id,
        bank_name: account.bank_name,
        bank_code: account.bank_code,
        branch_name: account.branch_name,
        branch_code: account.branch_code,
        bank_account_name: account.account_name,
        bank_account_number: account.account_number,
        bank_account_number_masked: account.account_number_masked,
      }));
  return {
    code: vendor.code,
    vendor_type: vendor.vendor_type ?? 'OTHER',
    vendor_status: vendor.vendor_status ?? 'DRAFT',
    internal_notes: vendor.internal_notes ?? '',
    responsible_buyer_id: vendor.responsible_buyer_id ?? '',
    active_date: vendor.active_date ?? '',
    credit_trade_notes: vendor.credit_trade_notes ?? '',
    risk_flag: vendor.risk_flag ?? '',
    profile: {
      business: {
        ...empty.profile.business,
        ...profile.business,
        principal_brands: profile.business?.principal_brands ?? [],
      },
      contact: { ...empty.profile.contact, ...profile.contact },
      payment_terms: { ...empty.profile.payment_terms, ...profile.payment_terms },
      delivery_terms: {
        ...empty.profile.delivery_terms,
        ...profile.delivery_terms,
        delivery_weekdays: profile.delivery_terms?.delivery_weekdays ?? [],
      },
      return_terms: {
        ...empty.profile.return_terms,
        ...profile.return_terms,
        return_condition_types: profile.return_terms?.return_condition_types ?? [],
      },
      invoice_terms: { ...empty.profile.invoice_terms, ...profile.invoice_terms },
      bank_details: bankDetails,
    },
  };
}

function changeRequestValueAtPath(value: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[key];
  }, value);
}

function changeRequestDisplayValue(path: string, value: unknown): string {
  if (path.endsWith('supplied_product_categories')) return formatVendorCategories(value);
  if (path.endsWith('delivery_weekdays')) return formatVendorWeekdays(value);
  if (path.endsWith('return_condition_types')) return formatVendorReturnConditions(value);
  if (path.endsWith('payment_term')) return labelVendorPaymentTerm(value);
  if (path.endsWith('payment_method')) return labelVendorPaymentMethod(value);
  if (path.endsWith('delivery_scope') || path.endsWith('return_scope'))
    return labelVendorDeliveryScope(value);
  if (path.endsWith('delivery_cycle')) return labelVendorDeliveryCycle(value);
  if (path.endsWith('freight_rule')) return labelVendorFreightRule(value);
  if (path.endsWith('return_handling_method')) return labelVendorReturnHandling(value);
  if (path.endsWith('return_freight_responsibility')) return labelVendorReturnFreight(value);
  if (path.endsWith('invoice_format')) return labelVendorInvoiceFormat(value);
  if (path.endsWith('invoice_delivery_method')) return labelVendorInvoiceDelivery(value);
  if (
    path.endsWith('minimum_order_amount') ||
    path.endsWith('freight_threshold_amount') ||
    path.endsWith('freight_fixed_amount')
  )
    return formatVendorMoney(value);
  if (path.endsWith('attachments')) {
    const attachments = Array.isArray(value) ? value : [];
    return attachments.length ? `${attachments.length} 個附件` : '未提供附件';
  }
  return displayVendorValue(value);
}

function changeRequestListItems(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    const record = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    return String(record.original_name ?? record.id ?? '附件');
  });
}

function isImportantChangeField(path: string): boolean {
  return [
    'payment_terms.',
    'delivery_terms.',
    'return_terms.',
    'invoice_terms.',
    'bank_details',
  ].some((prefix) => path.startsWith(prefix) || path === prefix);
}

function ChangeRequestAttachmentList({ value, vendorId }: { value: unknown; vendorId: string }) {
  const attachments = Array.isArray(value) ? value : [];
  if (!attachments.length) return <span className="text-slate-500">未提供附件</span>;
  return (
    <div className="space-y-2">
      {attachments.map((item, index) => {
        const attachment =
          item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
        const id = String(attachment.id ?? '');
        const name = String(attachment.original_name ?? `附件 ${index + 1}`);
        return (
          <div key={id || `${name}-${index}`} className="flex flex-wrap items-center gap-2">
            <span className="break-all">{name}</span>
            {id && (
              <>
                <a
                  className="text-xs font-bold text-teal-700 underline"
                  href={`/api/vendors/${encodeURIComponent(vendorId)}/attachments/${encodeURIComponent(id)}?preview=1`}
                  target="_blank"
                  rel="noreferrer"
                >
                  預覽
                </a>
                <a
                  className="text-xs font-bold text-teal-700 underline"
                  href={`/api/vendors/${encodeURIComponent(vendorId)}/attachments/${encodeURIComponent(id)}`}
                >
                  下載
                </a>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function VendorChangeRequestsPage({ notify }: PageProps) {
  const [requests, setRequests] = useState<VendorChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<VendorChangeRequest | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [fullVendor, setFullVendor] = useState<Vendor | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.vendorChangeRequests.list('PENDING');
      setRequests(result.requests);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setSelected(null);
    setFullVendor(null);
    setDetailLoading(true);
    try {
      const result = await api.vendorChangeRequests.detail(id);
      setSelected(result);
    } catch (error) {
      notify(errorMessage(error), 'error');
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadFullVendor = async () => {
    if (!selected) return;
    try {
      setFullVendor(await api.vendors.detail(selected.vendor_id));
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };

  const review = async (action: 'APPROVE' | 'REQUEST_INFO' | 'RETURN' | 'REJECT') => {
    if (!selected || !selected.review_detail_loaded) {
      notify('請先開啟並載入完整變更內容。', 'error');
      return;
    }
    if (action === 'APPROVE') {
      const summary = selected.changed_fields
        .map((field) => `・${labelVendorChangedField(field)}`)
        .join('\n');
      if (!window.confirm(`確定核准此次廠商資料變更嗎？\n\n${summary}`)) return;
    }
    const reason =
      action === 'APPROVE'
        ? ''
        : window.prompt(
            action === 'REQUEST_INFO'
              ? '請填寫要求廠商補充的內容：'
              : action === 'RETURN'
                ? '請填寫退回修改原因：'
                : '請填寫不同意原因：',
            '',
          );
    if (reason === null) return;
    if (action !== 'APPROVE' && !reason.trim()) {
      notify('此處理動作必須填寫說明。', 'error');
      return;
    }
    setReviewing(true);
    try {
      await api.vendorChangeRequests.review(selected.id, {
        action,
        reason: reason?.trim() ?? '',
      });
      notify(
        action === 'APPROVE'
          ? '變更已核准並套用至正式廠商主檔。'
          : action === 'REQUEST_INFO'
            ? '已要求廠商補充資料。'
            : action === 'RETURN'
              ? '變更申請已退回修改。'
              : '變更申請已標記為不同意。',
        'success',
      );
      setSelected(null);
      setSelectedId(null);
      await loadRequests();
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setReviewing(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={VENDOR_PLATFORM_NAME}
        title="待處理廠商資料變更"
        description="逐筆查看廠商提出的原資料與申請修改內容；未開啟詳情前，伺服器不允許核准。"
        actions={
          <Link className="text-sm font-bold text-teal-700 underline" to="/vendors">
            返回廠商管理
          </Link>
        }
      />
      <Card className="mb-6">
        {loading ? (
          <Loading />
        ) : requests.length === 0 ? (
          <EmptyState title="目前沒有待處理變更" description="新的廠商資料變更申請會出現在這裡。" />
        ) : (
          <Table>
            <thead className="bg-slate-50 text-xs font-bold text-slate-500">
              <tr>
                <th className="px-4 py-3">廠商</th>
                <th className="px-4 py-3">申請日期</th>
                <th className="px-4 py-3">變更項目</th>
                <th className="px-4 py-3">申請人／原因</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">最後更新</th>
                <th className="px-4 py-3">負責採購</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requests.map((item) => (
                <tr key={item.id} className="align-top">
                  <td className="px-4 py-4">
                    <p className="font-bold text-ink">{item.vendor_name || '未命名廠商'}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      統編：{item.vendor_tax_id || '未填寫'}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-slate-600">
                    {formatTaipeiDate(item.requested_at || item.created_at)}
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-bold text-ink">{item.changed_fields.length} 項</p>
                    <p className="mt-1 max-w-xs whitespace-normal text-xs leading-5 text-slate-500">
                      {item.changed_fields
                        .map((field) => labelVendorChangedField(field))
                        .join('、')}
                    </p>
                  </td>
                  <td className="max-w-xs whitespace-normal px-4 py-4">
                    <p className="text-sm text-ink">
                      {item.requested_by_name || item.requested_by || '廠商'}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {item.reason || '未填寫原因'}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800">
                      {labelVendorChangeRequestStatus(item.status)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">
                    {formatTaipeiDate(item.updated_at)}
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-600">
                    {item.responsible_buyer_name || '尚未指派'}
                  </td>
                  <td className="px-4 py-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void openDetail(item.id)}
                    >
                      查看變更內容
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {selectedId && (
        <Card className="border-teal-200">
          {detailLoading || !selected ? (
            <Loading label="正在載入完整變更內容…" />
          ) : (
            <>
              <div className="flex flex-col justify-between gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-start">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">
                    本次變更內容
                  </p>
                  <h2 className="mt-2 text-xl font-extrabold text-ink">
                    {selected.vendor_name || '廠商資料變更'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    統編：{selected.vendor_tax_id || '未填寫'}｜申請原因：
                    {selected.reason || '未填寫'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="ghost" onClick={() => setSelectedId(null)}>
                    關閉詳情
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => void loadFullVendor()}>
                    查看完整廠商資料
                  </Button>
                </div>
              </div>
              <div className="mt-5 rounded-xl border border-teal-100 bg-teal-50 px-4 py-3 text-sm leading-6 text-teal-900">
                <strong>本次變更項目：{selected.changed_fields.length} 項</strong>
                <span className="ml-2">
                  已載入原資料與申請修改內容；核准按鈕已具備伺服器端查看紀錄。
                </span>
              </div>
              <div className="mt-5 space-y-4">
                {selected.changed_fields.map((field) => {
                  const before = changeRequestValueAtPath(selected.original, field);
                  const after = changeRequestValueAtPath(selected.proposed, field);
                  const beforeItems = changeRequestListItems(before);
                  const afterItems = changeRequestListItems(after);
                  const isList = Array.isArray(before) && Array.isArray(after);
                  const added = isList
                    ? afterItems.filter((item) => !beforeItems.includes(item))
                    : [];
                  const removed = isList
                    ? beforeItems.filter((item) => !afterItems.includes(item))
                    : [];
                  return (
                    <div key={field} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-extrabold text-ink">
                          {labelVendorChangedField(field)}
                        </h3>
                        {isImportantChangeField(field) && (
                          <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-bold text-rose-700">
                            重要交易資料
                          </span>
                        )}
                      </div>
                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs font-bold text-slate-500">原資料</p>
                          <div className="mt-2 break-words text-sm leading-6 text-slate-700">
                            {field.endsWith('attachments') ? (
                              <ChangeRequestAttachmentList
                                value={before}
                                vendorId={selected.vendor_id}
                              />
                            ) : (
                              changeRequestDisplayValue(field, before)
                            )}
                          </div>
                        </div>
                        <div className="rounded-xl bg-amber-50 p-3">
                          <p className="text-xs font-bold text-amber-800">申請修改</p>
                          <div className="mt-2 break-words text-sm leading-6 text-amber-950">
                            {field.endsWith('attachments') ? (
                              <ChangeRequestAttachmentList
                                value={after}
                                vendorId={selected.vendor_id}
                              />
                            ) : (
                              changeRequestDisplayValue(field, after)
                            )}
                          </div>
                        </div>
                      </div>
                      {(added.length > 0 || removed.length > 0) && (
                        <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
                          {added.length > 0 && (
                            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                              新增：{added.join('、')}
                            </span>
                          )}
                          {removed.length > 0 && (
                            <span className="rounded-full bg-rose-100 px-3 py-1 text-rose-800">
                              移除：{removed.join('、')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {fullVendor && (
                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <h3 className="font-extrabold text-ink">完整廠商資料</h3>
                  <div className="mt-3 grid gap-3 text-sm md:grid-cols-3">
                    <p>
                      <span className="text-slate-500">公司名稱：</span>
                      {fullVendor.name}
                    </p>
                    <p>
                      <span className="text-slate-500">統一編號：</span>
                      {fullVendor.tax_id || '未填寫'}
                    </p>
                    <p>
                      <span className="text-slate-500">狀態：</span>
                      {labelVendorStatus(fullVendor.vendor_status)}
                    </p>
                    <p className="md:col-span-3">
                      <span className="text-slate-500">地址：</span>
                      {fullVendor.address || '未填寫'}
                    </p>
                  </div>
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-2 border-t border-slate-100 pt-5">
                <Button type="button" loading={reviewing} onClick={() => void review('APPROVE')}>
                  核准變更
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={reviewing}
                  onClick={() => void review('REQUEST_INFO')}
                >
                  要求補充
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={reviewing}
                  onClick={() => void review('RETURN')}
                >
                  退回修改
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  disabled={reviewing}
                  onClick={() => void review('REJECT')}
                >
                  不同意變更
                </Button>
              </div>
            </>
          )}
        </Card>
      )}
    </>
  );
}

export function VendorsPage({ notify }: PageProps) {
  const { data, setData, loading } = useAsyncLoad(() => api.vendors.list(), [], notify);
  const { data: authData } = useAsyncLoad(() => api.auth.me(), [], notify);
  const vendors = data?.vendors ?? [];
  const isFinance = authData?.user.role === 'FINANCE';
  const canEditMaster = authData?.user.role === 'ADMIN' || authData?.user.role === 'BUYER';
  const canEditBank = canEditMaster || isFinance;
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<UnifiedVendorMasterForm>(createEmptyUnifiedVendorMasterForm);
  const [activeSection, setActiveSection] = useState('business');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [migrationFilter, setMigrationFilter] = useState<
    '' | NonNullable<Vendor['master_migration_status']>
  >('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [attachmentType, setAttachmentType] = useState('BUSINESS_REGISTRATION');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentSaving, setAttachmentSaving] = useState(false);
  const [statusDraft, setStatusDraft] = useState<VendorStatus>('DRAFT');
  const [statusReason, setStatusReason] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [migrationConfirmReason, setMigrationConfirmReason] = useState('');
  const [migrationConfirming, setMigrationConfirming] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  useUnsavedPrompt(dirty);

  const filteredVendors = useMemo(
    () =>
      vendors.filter((vendor) => {
        const profile = vendor.profile;
        const search =
          `${vendor.code} ${vendor.name} ${vendor.tax_id ?? ''} ${vendor.short_name ?? ''}`.toLowerCase();
        return (
          (!query.trim() || search.includes(query.trim().toLowerCase())) &&
          (!statusFilter || vendor.vendor_status === statusFilter) &&
          (!accountFilter || vendor.account?.status === accountFilter) &&
          (!migrationFilter || vendor.master_migration_status === migrationFilter) &&
          (!categoryFilter ||
            profile?.business.supplied_product_categories.includes(categoryFilter as never))
        );
      }),
    [accountFilter, categoryFilter, migrationFilter, query, statusFilter, vendors],
  );

  const patchForm = (updater: (current: UnifiedVendorMasterForm) => UnifiedVendorMasterForm) => {
    setDirty(true);
    setForm(updater);
  };
  const updateTop = (key: keyof Omit<UnifiedVendorMasterForm, 'profile'>, value: unknown) =>
    patchForm((current) => ({ ...current, [key]: value }) as UnifiedVendorMasterForm);
  const updateProfile = (section: keyof VendorProfile, field: string, value: unknown) =>
    patchForm(
      (current) =>
        ({
          ...current,
          profile: {
            ...current.profile,
            [section]: {
              ...(current.profile[section] as object),
              [field]: value,
            },
          },
        }) as UnifiedVendorMasterForm,
    );
  const updateContact = (field: keyof VendorProfile['contact'], value: unknown) =>
    patchForm((current) => {
      const contact = { ...current.profile.contact, [field]: value };
      const invoice = current.profile.invoice_terms.invoice_same_as_primary
        ? {
            ...current.profile.invoice_terms,
            invoice_contact_name: contact.contact_name,
            invoice_contact_phone: contact.contact_phone,
            invoice_email: contact.contact_email,
          }
        : current.profile.invoice_terms;
      const reconciliation = current.profile.invoice_terms.reconciliation_same_as_primary
        ? {
            ...invoice,
            reconciliation_contact_name: contact.contact_name,
            reconciliation_contact_phone: contact.contact_phone,
            reconciliation_email: contact.contact_email,
          }
        : invoice;
      return {
        ...current,
        profile: { ...current.profile, contact, invoice_terms: reconciliation },
      };
    });
  const toggleArray = (
    section: 'business' | 'delivery_terms' | 'return_terms',
    field: string,
    item: string,
  ) =>
    patchForm((current) => {
      const group = current.profile[section] as unknown as Record<string, unknown>;
      const selected = Array.isArray(group[field]) ? (group[field] as string[]) : [];
      return {
        ...current,
        profile: {
          ...current.profile,
          [section]: {
            ...group,
            [field]: selected.includes(item)
              ? selected.filter((entry) => entry !== item)
              : [...selected, item],
          },
        },
      } as UnifiedVendorMasterForm;
    });
  const updateBrand = (index: number, value: string) =>
    patchForm((current) => ({
      ...current,
      profile: {
        ...current.profile,
        business: {
          ...current.profile.business,
          principal_brands: current.profile.business.principal_brands.map((brand, brandIndex) =>
            brandIndex === index ? value : brand,
          ),
        },
      },
    }));
  const addBrand = () =>
    patchForm((current) => ({
      ...current,
      profile: {
        ...current.profile,
        business: {
          ...current.profile.business,
          principal_brands: [...current.profile.business.principal_brands, ''],
        },
      },
    }));
  const removeBrand = (index: number) =>
    patchForm((current) => ({
      ...current,
      profile: {
        ...current.profile,
        business: {
          ...current.profile.business,
          principal_brands: current.profile.business.principal_brands.filter(
            (_, brandIndex) => brandIndex !== index,
          ),
        },
      },
    }));
  const updateNumber = (
    section: 'payment_terms' | 'delivery_terms',
    field: string,
    value: string,
  ) => updateProfile(section, field, value === '' ? null : Number(value));
  const reset = () => {
    setEditing(null);
    setForm(createEmptyUnifiedVendorMasterForm());
    setActiveSection('business');
    setStatusDraft('DRAFT');
    setStatusReason('');
    setMigrationConfirmReason('');
    setDirty(false);
  };
  const startEdit = async (vendor: Vendor) => {
    try {
      const detail = await api.vendors.detail(vendor.id);
      const accountResult = await api.vendorAccount.detail(detail.id);
      const detailWithAccount = { ...detail, account: accountResult.account };
      setEditing(detailWithAccount.id);
      setForm(vendorToUnifiedVendorMasterForm(detailWithAccount));
      setData({
        vendors: vendors.map((item) =>
          item.id === detailWithAccount.id ? detailWithAccount : item,
        ),
      });
      setActiveSection('business');
      setStatusDraft(detailWithAccount.vendor_status ?? 'DRAFT');
      setStatusReason('');
      setMigrationConfirmReason('');
      setDirty(false);
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isFinance && !editing) {
      notify('FINANCE 只能維護既有廠商主檔。', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        vendor_type: form.vendor_type,
        vendor_status: form.vendor_status,
        internal_notes: form.internal_notes,
        responsible_buyer_id: form.responsible_buyer_id || null,
        active_date: form.active_date || null,
        credit_trade_notes: form.credit_trade_notes,
        risk_flag: form.risk_flag,
        profile: form.profile,
      };
      const vendor = editing
        ? await api.vendors.update(editing, payload)
        : await api.vendors.create(payload);
      setData({
        vendors: editing
          ? vendors.map((item) => (item.id === vendor.id ? vendor : item))
          : [vendor, ...vendors],
      });
      setEditing(vendor.id);
      setForm(vendorToUnifiedVendorMasterForm(vendor));
      setStatusDraft(vendor.vendor_status ?? 'DRAFT');
      setStatusReason('');
      setMigrationConfirmReason('');
      setDirty(false);
      notify('新版廠商主檔已儲存。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const updateSameAsPrimary = (kind: 'invoice' | 'reconciliation', checked: boolean) =>
    patchForm((current) => {
      const contact = current.profile.contact;
      const invoice = current.profile.invoice_terms;
      if (kind === 'invoice')
        return {
          ...current,
          profile: {
            ...current.profile,
            invoice_terms: {
              ...invoice,
              invoice_same_as_primary: checked,
              ...(checked
                ? {
                    invoice_contact_name: contact.contact_name,
                    invoice_contact_phone: contact.contact_phone,
                    invoice_email: contact.contact_email,
                  }
                : {}),
            },
          },
        };
      return {
        ...current,
        profile: {
          ...current.profile,
          invoice_terms: {
            ...invoice,
            reconciliation_same_as_primary: checked,
            ...(checked
              ? {
                  reconciliation_contact_name: contact.contact_name,
                  reconciliation_contact_phone: contact.contact_phone,
                  reconciliation_email: contact.contact_email,
                }
              : {}),
          },
        },
      };
    });
  const updateBank = (index: number, field: keyof VendorBankDetails, value: unknown) =>
    patchForm((current) => ({
      ...current,
      profile: {
        ...current.profile,
        bank_details: current.profile.bank_details.map((account, accountIndex) =>
          accountIndex === index ? { ...account, [field]: value } : account,
        ),
      },
    }));
  const addBank = () =>
    patchForm((current) => ({
      ...current,
      profile: {
        ...current.profile,
        bank_details: [
          ...current.profile.bank_details,
          {
            bank_name: '',
            bank_code: '',
            branch_name: '',
            branch_code: '',
            bank_account_name: '',
            bank_account_number: '',
          },
        ],
      },
    }));
  const removeBank = (index: number) =>
    patchForm((current) => ({
      ...current,
      profile: {
        ...current.profile,
        bank_details: current.profile.bank_details.filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  const uploadAttachment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing || !attachmentFile) {
      notify('請先選擇廠商及附件檔案。', 'error');
      return;
    }
    setAttachmentSaving(true);
    try {
      await api.vendorAttachments.upload(editing, attachmentType, attachmentFile);
      const detail = await api.vendors.detail(editing);
      setData({ vendors: vendors.map((item) => (item.id === detail.id ? detail : item)) });
      setForm(vendorToUnifiedVendorMasterForm(detail));
      setAttachmentFile(null);
      notify('附件已更新。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setAttachmentSaving(false);
    }
  };

  const saveVendorStatus = async () => {
    if (!editing || !canEditMaster) return;
    if (dirty) {
      notify('請先儲存尚未儲存的主檔內容，再變更狀態。', 'error');
      return;
    }
    if (statusDraft === (form.vendor_status ?? 'DRAFT')) {
      notify('廠商狀態沒有變更。', 'info');
      return;
    }
    if (!statusReason.trim()) {
      notify('狀態變更原因為必填。', 'error');
      return;
    }
    if (!window.confirm(`確定將廠商狀態變更為「${VENDOR_STATUS_LABELS[statusDraft]}」嗎？`)) return;
    setStatusSaving(true);
    try {
      const vendor = await api.vendors.status(editing, statusDraft, statusReason.trim());
      setData({ vendors: vendors.map((item) => (item.id === vendor.id ? vendor : item)) });
      setForm(vendorToUnifiedVendorMasterForm(vendor));
      setStatusDraft(vendor.vendor_status ?? 'DRAFT');
      setStatusReason('');
      notify(
        `廠商狀態已更新為「${VENDOR_STATUS_LABELS[vendor.vendor_status ?? 'DRAFT']}」。`,
        'success',
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setStatusSaving(false);
    }
  };

  const confirmMigration = async () => {
    if (!editing || !canEditMaster) return;
    if (dirty) {
      notify('請先儲存已補齊的廠商主檔資料，再進行人工確認。', 'error');
      return;
    }
    if (selectedVendor?.master_migration_status !== 'NEEDS_MANUAL_CONFIRMATION') return;
    if (!migrationConfirmReason.trim()) {
      notify('請填寫人工確認原因。', 'error');
      return;
    }
    if (!window.confirm('確認已檢查並完成此廠商的新版資料轉換嗎？')) return;
    setMigrationConfirming(true);
    try {
      const vendor = await api.vendors.confirmMigration(editing, migrationConfirmReason.trim());
      setData({ vendors: vendors.map((item) => (item.id === vendor.id ? vendor : item)) });
      setForm(vendorToUnifiedVendorMasterForm(vendor));
      setMigrationConfirmReason('');
      setDirty(false);
      notify('廠商資料已完成人工確認，並已留下操作紀錄。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setMigrationConfirming(false);
    }
  };

  const updateAccountInList = (account: VendorAccountSummary) => {
    if (!editing) return;
    setData({
      vendors: vendors.map((item) => (item.id === editing ? { ...item, account } : item)),
    });
  };

  const updateVendorAccountStatus = async (
    action: 'RESET_PASSWORD' | 'UNLOCK' | 'DISABLE' | 'ENABLE',
  ) => {
    if (!editing || !canEditMaster) return;
    setAccountSaving(true);
    try {
      const result = await api.vendorAccount.action(editing, action);
      updateAccountInList(result.account);
      notify(
        action === 'RESET_PASSWORD'
          ? '密碼已重設，廠商下次以統編進入時可自行設定新密碼。'
          : '廠商登入帳號狀態已更新。',
        'success',
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setAccountSaving(false);
    }
  };

  const p = form.profile;
  const selectedVendor = editing ? vendors.find((vendor) => vendor.id === editing) : null;
  const sections = [
    ['business', '公司基本資料'],
    ['contact', '主要聯絡人'],
    ['payment', '付款條件'],
    ['delivery', '配送條件'],
    ['return', '退貨／換貨條件'],
    ['invoice', '發票與對帳'],
    ['bank', '銀行資料'],
    ['attachments', '附件'],
    ['internal', '內部管理資料'],
    ['account', '登入帳號'],
  ];

  return (
    <>
      <PageHeader
        eyebrow="VENDOR MASTER V2"
        title="廠商主檔與交易條件"
        description="正式廠商主檔與新廠商申請共用同一套資料結構；核准後直接承接申請資料，採購只需補充內部管理欄位。"
        actions={
          canEditMaster ? (
            <Button type="button" onClick={reset}>
              新增廠商主檔
            </Button>
          ) : undefined
        }
      />
      <div className="mb-5 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm leading-6 text-sky-900">
        <strong>資料來源規則：</strong>
        付款、配送、退貨、發票、銀行及附件資料均以新版廠商申請內容為準；廠商主檔不要求重新輸入申請資料。
      </div>
      {selectedVendor?.master_migration_status === 'NEEDS_MANUAL_CONFIRMATION' && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm leading-6 text-amber-900">
          <strong>待採購確認</strong>
          <p className="mt-1">
            {selectedVendor.master_migration_note ||
              '部分舊版資料無法自動對應新版欄位，請先補齊並檢查。'}
          </p>
          {(selectedVendor.master_migration_issues ?? []).length > 0 && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-white/70 p-3">
              <p className="font-bold">待處理欄位</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {selectedVendor.master_migration_issues?.map((issue) => (
                  <li key={issue}>
                    {issue.includes(':') ? issue.split(':').slice(1).join(':') : issue}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {canEditMaster && (
            <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
              <Textarea
                label="人工確認原因"
                required
                value={migrationConfirmReason}
                onChange={(event) => setMigrationConfirmReason(event.target.value)}
                placeholder="例如：已依舊版總倉／門市條件確認為總倉與門市皆可，並補齊退貨條件。"
              />
              <Button
                type="button"
                loading={migrationConfirming}
                onClick={() => void confirmMigration()}
              >
                確認資料
              </Button>
            </div>
          )}
        </div>
      )}
      {selectedVendor?.master_migration_status === 'CONFIRMED' && (
        <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900">
          <strong>資料已人工確認</strong>
          <span className="ml-2">
            {selectedVendor.master_migration_confirmed_by_name
              ? `確認人：${selectedVendor.master_migration_confirmed_by_name}`
              : ''}
            {selectedVendor.master_migration_confirmed_at
              ? `｜${selectedVendor.master_migration_confirmed_at}`
              : ''}
          </span>
          {selectedVendor.master_migration_confirmation_reason && (
            <p className="mt-1">確認原因：{selectedVendor.master_migration_confirmation_reason}</p>
          )}
        </div>
      )}
      {(canEditMaster || isFinance) && (
        <Card className="mb-5 border-amber-200 bg-amber-50">
          <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
            <div>
              <h2 className="text-lg font-extrabold text-amber-950">待處理廠商資料變更</h2>
              <p className="mt-1 text-sm text-amber-900">
                請逐筆開啟變更內容，確認原資料與申請修改資料後，再進行核准或退回處理。
              </p>
            </div>
            <Link
              to="/vendor-change-requests"
              className="inline-flex min-h-10 items-center justify-center rounded-xl bg-white px-4 py-2 text-sm font-bold text-amber-900 shadow-sm hover:bg-amber-100"
            >
              開啟待處理列表
            </Link>
          </div>
        </Card>
      )}
      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <Card className="h-fit">
          <div className="mb-4 grid gap-3">
            <Input
              label="搜尋廠商"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Select
              label="廠商狀態"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">全部狀態</option>
              {Object.entries(VENDOR_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="資料轉換狀態"
              value={migrationFilter}
              onChange={(event) => setMigrationFilter(event.target.value as typeof migrationFilter)}
            >
              <option value="">全部資料狀態</option>
              <option value="NEEDS_MANUAL_CONFIRMATION">待採購確認</option>
              <option value="CONFIRMED">已人工確認</option>
              <option value="AUTO_MIGRATED">已自動轉換</option>
            </Select>
            <Select
              label="登入帳號狀態"
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
            >
              <option value="">全部帳號狀態</option>
              {Object.entries(VENDOR_ACCOUNT_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="供應商品類"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="">全部分類</option>
              {Object.entries(VENDOR_PRODUCT_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          {loading ? (
            <Loading />
          ) : filteredVendors.length === 0 ? (
            <EmptyState
              title="目前沒有符合條件的廠商"
              description="請調整篩選條件或新增廠商主檔。"
            />
          ) : (
            <div className="space-y-2">
              {filteredVendors.map((vendor) => (
                <button
                  type="button"
                  key={vendor.id}
                  onClick={() => void startEdit(vendor)}
                  className={`w-full rounded-2xl border p-3 text-left transition ${editing === vendor.id ? 'border-teal-300 bg-teal-50' : 'border-slate-100 hover:border-teal-200 hover:bg-slate-50'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0">
                      <strong className="block truncate text-sm text-ink">{vendor.name}</strong>
                      <span className="mt-1 block text-xs text-slate-500">
                        {vendor.code} · {vendor.tax_id || '尚未設定統一編號'}
                      </span>
                    </span>
                    <span
                      className={`rounded-full px-2 py-1 text-[11px] font-bold ${VENDOR_STATUS_TONE[vendor.vendor_status ?? 'DRAFT']}`}
                    >
                      {VENDOR_STATUS_LABELS[vendor.vendor_status ?? 'DRAFT']}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                    <span>完整度 {vendor.completeness_percent ?? 0}%</span>
                    <span>
                      {vendor.approved_by_name
                        ? `原始申請核准人：${vendor.approved_by_name}`
                        : '尚無來源申請核准人'}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    {vendor.master_migration_status === 'NEEDS_MANUAL_CONFIRMATION' ? (
                      <span className="text-amber-700">待採購確認</span>
                    ) : vendor.master_migration_status === 'CONFIRMED' ? (
                      <span className="text-emerald-700">已人工確認</span>
                    ) : (
                      <span>
                        {vendor.profile?.business.supplied_product_categories.length ?? 0}{' '}
                        個供應類別
                      </span>
                    )}
                    {(vendor.missing_attachment_types?.length ?? 0) > 0 && (
                      <span className="font-semibold text-amber-700">
                        缺 {vendor.missing_attachment_types?.length} 項附件
                      </span>
                    )}
                  </div>
                  {vendor.account && (
                    <div className="mt-2">
                      <span
                        className={`rounded-full px-2 py-1 text-[11px] font-bold ${VENDOR_ACCOUNT_STATUS_TONE[vendor.account.status]}`}
                      >
                        登入：{VENDOR_ACCOUNT_STATUS_LABELS[vendor.account.status]}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>
        {!editing && isFinance ? (
          <Card>
            <EmptyState
              title="請選擇既有廠商主檔"
              description="FINANCE 可查看及維護付款、發票與銀行資料，但不能新增廠商。"
            />
          </Card>
        ) : (
          <form onSubmit={save} className="min-w-0 space-y-6">
            <Card>
              <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
                    UNIFIED PROFILE
                  </p>
                  <h2 className="mt-1 text-xl font-extrabold">
                    {p.business.company_name || '新增正式廠商'}
                  </h2>
                  <p className="mt-2 text-xs text-slate-500">
                    {editing ? `廠商代碼：${form.code}` : '核准申請後自動帶入，無需重新輸入。'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${VENDOR_STATUS_TONE[form.vendor_status]}`}
                  >
                    {VENDOR_STATUS_LABELS[form.vendor_status]}
                  </span>
                  {editing && selectedVendor?.vendor_status !== 'DRAFT' && (
                    <a
                      href={api.vendors.pdfUrl(editing)}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-teal-700 px-3 py-1 text-xs font-bold text-white"
                    >
                      開啟廠商 PDF
                    </a>
                  )}
                  <a
                    href="/audit"
                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700"
                  >
                    查看異動紀錄
                  </a>
                </div>
              </div>
              <div className="mt-5 flex gap-2 overflow-x-auto border-b border-slate-100 pb-1">
                {sections.map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() => setActiveSection(value)}
                    className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-bold ${activeSection === value ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-ink'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Card>

            {activeSection === 'business' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">1. 公司基本資料</h3>
                <p className="mb-4 text-sm text-slate-500">
                  前 9 個區塊與新廠商申請使用相同欄位與必填規則。
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="公司名稱"
                    required
                    disabled={isFinance}
                    value={p.business.company_name}
                    onChange={(event) =>
                      updateProfile('business', 'company_name', event.target.value)
                    }
                  />
                  <Input
                    label="公司簡稱"
                    disabled={isFinance}
                    value={p.business.company_short_name ?? ''}
                    onChange={(event) =>
                      updateProfile('business', 'company_short_name', event.target.value || null)
                    }
                  />
                  <Input
                    label="統一編號"
                    required
                    disabled={isFinance}
                    value={p.business.tax_id}
                    onChange={(event) => updateProfile('business', 'tax_id', event.target.value)}
                  />
                  <Input
                    label="公司電話"
                    required
                    disabled={isFinance}
                    value={p.business.phone}
                    onChange={(event) => updateProfile('business', 'phone', event.target.value)}
                  />
                  <Input
                    label="公司 Email"
                    required
                    type="email"
                    disabled={isFinance}
                    value={p.business.company_email}
                    onChange={(event) =>
                      updateProfile('business', 'company_email', event.target.value)
                    }
                  />
                  <Input
                    label="公司網站"
                    type="url"
                    disabled={isFinance}
                    value={p.business.website}
                    onChange={(event) => updateProfile('business', 'website', event.target.value)}
                  />
                  <Input
                    label="傳真"
                    disabled={isFinance}
                    value={p.business.company_fax}
                    onChange={(event) =>
                      updateProfile('business', 'company_fax', event.target.value)
                    }
                  />
                  <Textarea
                    label="公司地址"
                    required
                    className="md:col-span-2"
                    disabled={isFinance}
                    value={p.business.address}
                    onChange={(event) => updateProfile('business', 'address', event.target.value)}
                  />
                  <Textarea
                    label="公司簡介"
                    className="md:col-span-2"
                    disabled={isFinance}
                    value={p.business.company_intro}
                    onChange={(event) =>
                      updateProfile('business', 'company_intro', event.target.value)
                    }
                  />
                  <Textarea
                    label="備註"
                    className="md:col-span-2"
                    disabled={isFinance}
                    value={p.business.notes}
                    onChange={(event) => updateProfile('business', 'notes', event.target.value)}
                  />
                </div>
                <div className="mt-6 border-t border-slate-100 pt-5">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-extrabold">2. 主要代理／品牌 *</h4>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={isFinance}
                      onClick={addBrand}
                    >
                      新增品牌
                    </Button>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    請以多筆方式維護主要代理、經銷或自有品牌。
                  </p>
                  <div className="mt-3 space-y-3">
                    {p.business.principal_brands.map((brand, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          label={`品牌 ${index + 1}`}
                          required
                          value={brand}
                          disabled={isFinance}
                          onChange={(event) => updateBrand(index, event.target.value)}
                        />
                        {p.business.principal_brands.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={isFinance}
                            onClick={() => removeBrand(index)}
                          >
                            移除
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-6 border-t border-slate-100 pt-5">
                  <h4 className="font-extrabold">3. 供應商品類 *</h4>
                  <p className="mt-1 text-sm text-slate-500">可複選；資料庫只儲存分類代碼。</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {Object.entries(VENDOR_PRODUCT_CATEGORY_LABELS).map(([value, label]) => (
                      <label
                        key={value}
                        className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          disabled={isFinance}
                          checked={p.business.supplied_product_categories.includes(value as never)}
                          onChange={() =>
                            toggleArray('business', 'supplied_product_categories', value)
                          }
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {activeSection === 'contact' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">2. 主要聯絡人</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="聯絡人姓名"
                    required
                    disabled={isFinance}
                    value={p.contact.contact_name}
                    onChange={(event) => updateContact('contact_name', event.target.value)}
                  />
                  <Input
                    label="聯絡人職稱"
                    disabled={isFinance}
                    value={p.contact.contact_job_title ?? ''}
                    onChange={(event) =>
                      updateContact('contact_job_title', event.target.value || null)
                    }
                  />
                  <Input
                    label="聯絡電話"
                    required
                    disabled={isFinance}
                    value={p.contact.contact_phone}
                    onChange={(event) => updateContact('contact_phone', event.target.value)}
                  />
                  <Input
                    label="聯絡 Email"
                    required
                    type="email"
                    disabled={isFinance}
                    value={p.contact.contact_email}
                    onChange={(event) => updateContact('contact_email', event.target.value)}
                  />
                  <Input
                    label="廠商結帳日"
                    required
                    disabled={isFinance}
                    value={p.contact.vendor_closing_day}
                    onChange={(event) => updateContact('vendor_closing_day', event.target.value)}
                  />
                  <Input
                    label="廠商每日訂單截止時間"
                    required
                    type="time"
                    disabled={isFinance}
                    value={p.contact.daily_order_cutoff_time}
                    onChange={(event) =>
                      updateContact('daily_order_cutoff_time', event.target.value)
                    }
                  />
                </div>
              </Card>
            )}

            {activeSection === 'payment' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">3. 付款條件</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Select
                    label="付款條件"
                    required
                    disabled={isFinance}
                    value={p.payment_terms.payment_term}
                    onChange={(event) => {
                      const next = event.target.value;
                      updateProfile('payment_terms', 'payment_term', next);
                      if (next === 'CASH_ON_DELIVERY')
                        updateProfile('payment_terms', 'payment_method', 'CASH');
                    }}
                  >
                    {<option value="">請選擇付款條件</option>}
                    {Object.entries(VENDOR_APPLICATION_PAYMENT_TERM_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  {p.payment_terms.payment_term === 'MONTHLY' && (
                    <Input
                      label="月結天數"
                      required
                      type="number"
                      min="1"
                      disabled={isFinance}
                      value={p.payment_terms.monthly_payment_days ?? ''}
                      onChange={(event) =>
                        updateNumber('payment_terms', 'monthly_payment_days', event.target.value)
                      }
                    />
                  )}
                  <Select
                    label="付款方式"
                    required
                    disabled={isFinance || p.payment_terms.payment_term === 'CASH_ON_DELIVERY'}
                    value={p.payment_terms.payment_method}
                    onChange={(event) =>
                      updateProfile('payment_terms', 'payment_method', event.target.value)
                    }
                  >
                    <option value="">請選擇付款方式</option>
                    {Object.entries(VENDOR_APPLICATION_PAYMENT_METHOD_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  {p.payment_terms.payment_method === 'OTHER' && (
                    <Input
                      label="其他付款方式說明"
                      required
                      disabled={isFinance}
                      value={p.payment_terms.payment_method_other}
                      onChange={(event) =>
                        updateProfile('payment_terms', 'payment_method_other', event.target.value)
                      }
                    />
                  )}
                  <Textarea
                    label="週結說明"
                    className="md:col-span-2"
                    disabled={isFinance}
                    value={p.payment_terms.weekly_payment_note}
                    onChange={(event) =>
                      updateProfile('payment_terms', 'weekly_payment_note', event.target.value)
                    }
                  />
                </div>
                {p.payment_terms.payment_term === 'CASH_ON_DELIVERY' && (
                  <p className="mt-4 rounded-xl bg-sky-50 p-3 text-sm text-sky-900">
                    下貨收現之付款方式固定為現金。
                  </p>
                )}
                {p.payment_terms.payment_method === 'CHECK' && (
                  <label className="mt-4 block rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <strong>我司支票兌現日為每月30日。</strong>
                    <span className="mt-3 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={p.payment_terms.payment_rule_acknowledged}
                        disabled={isFinance}
                        onChange={(event) =>
                          updateProfile(
                            'payment_terms',
                            'payment_rule_acknowledged',
                            event.target.checked,
                          )
                        }
                      />
                      我已了解支票付款規則 *
                    </span>
                  </label>
                )}
                {p.payment_terms.payment_method === 'BANK_TRANSFER' && (
                  <label className="mt-4 block rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <strong>我司匯款日為每月月底；遇假日順延至第一個工作天。</strong>
                    <span className="mt-3 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={p.payment_terms.payment_rule_acknowledged}
                        disabled={isFinance}
                        onChange={(event) =>
                          updateProfile(
                            'payment_terms',
                            'payment_rule_acknowledged',
                            event.target.checked,
                          )
                        }
                      />
                      我已了解匯款付款規則 *
                    </span>
                  </label>
                )}
              </Card>
            )}

            {activeSection === 'delivery' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">4. 配送條件</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Select
                    label="配送地點"
                    required
                    disabled={isFinance}
                    value={p.delivery_terms.delivery_scope}
                    onChange={(event) =>
                      updateProfile('delivery_terms', 'delivery_scope', event.target.value)
                    }
                  >
                    <option value="">請選擇配送地點</option>
                    {Object.entries(VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  <Select
                    label="廠商配送週期"
                    required
                    disabled={isFinance}
                    value={p.delivery_terms.delivery_cycle}
                    onChange={(event) =>
                      updateProfile('delivery_terms', 'delivery_cycle', event.target.value)
                    }
                  >
                    <option value="">請選擇配送週期</option>
                    {Object.entries(VENDOR_APPLICATION_DELIVERY_CYCLE_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  <Input
                    label="最低訂購金額"
                    required
                    type="number"
                    min="0"
                    disabled={isFinance}
                    value={p.delivery_terms.minimum_order_amount ?? ''}
                    onChange={(event) =>
                      updateNumber('delivery_terms', 'minimum_order_amount', event.target.value)
                    }
                  />
                  <Input
                    label="最低訂購數量"
                    required
                    type="number"
                    min="0"
                    disabled={isFinance}
                    value={p.delivery_terms.minimum_order_quantity ?? ''}
                    onChange={(event) =>
                      updateNumber('delivery_terms', 'minimum_order_quantity', event.target.value)
                    }
                  />
                  <Select
                    label="廠商運費規則"
                    required
                    disabled={isFinance}
                    value={p.delivery_terms.freight_rule}
                    onChange={(event) =>
                      updateProfile('delivery_terms', 'freight_rule', event.target.value)
                    }
                  >
                    <option value="">請選擇運費規則</option>
                    {Object.entries(VENDOR_APPLICATION_FREIGHT_RULE_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  <Input
                    label="廠商每日訂單截止時間"
                    required
                    type="time"
                    disabled={isFinance}
                    value={p.delivery_terms.daily_order_cutoff_time}
                    onChange={(event) =>
                      updateProfile('delivery_terms', 'daily_order_cutoff_time', event.target.value)
                    }
                  />
                </div>
                {p.delivery_terms.delivery_cycle === 'FIXED_WEEKDAYS' && (
                  <div className="mt-4">
                    <p className="mb-2 text-sm font-semibold">
                      固定配送星期 * <span className="font-normal text-slate-500">（可複選）</span>
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
                      {Object.entries(VENDOR_APPLICATION_WEEKDAY_LABELS).map(([value, label]) => (
                        <label
                          key={value}
                          className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            disabled={isFinance}
                            checked={p.delivery_terms.delivery_weekdays.includes(value as never)}
                            onChange={() =>
                              toggleArray('delivery_terms', 'delivery_weekdays', value)
                            }
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {p.delivery_terms.delivery_cycle === 'OTHER' && (
                  <Textarea
                    className="mt-4"
                    label="其他配送週期說明"
                    required
                    disabled={isFinance}
                    value={p.delivery_terms.delivery_cycle_other}
                    onChange={(event) =>
                      updateProfile('delivery_terms', 'delivery_cycle_other', event.target.value)
                    }
                  />
                )}
                {p.delivery_terms.freight_rule === 'THRESHOLD' && (
                  <Input
                    className="mt-4 max-w-md"
                    label="免運門檻"
                    required
                    type="number"
                    min="0"
                    disabled={isFinance}
                    value={p.delivery_terms.freight_threshold_amount ?? ''}
                    onChange={(event) =>
                      updateNumber('delivery_terms', 'freight_threshold_amount', event.target.value)
                    }
                  />
                )}
                {p.delivery_terms.freight_rule === 'FIXED' && (
                  <Input
                    className="mt-4 max-w-md"
                    label="固定運費金額"
                    required
                    type="number"
                    min="0"
                    disabled={isFinance}
                    value={p.delivery_terms.freight_fixed_amount ?? ''}
                    onChange={(event) =>
                      updateNumber('delivery_terms', 'freight_fixed_amount', event.target.value)
                    }
                  />
                )}
                {p.delivery_terms.freight_rule === 'OTHER' && (
                  <Textarea
                    className="mt-4"
                    label="其他運費規則說明"
                    required
                    disabled={isFinance}
                    value={p.delivery_terms.freight_other_note}
                    onChange={(event) =>
                      updateProfile('delivery_terms', 'freight_other_note', event.target.value)
                    }
                  />
                )}
              </Card>
            )}

            {activeSection === 'return' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">5. 退貨／換貨條件</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Select
                    label="退貨地點"
                    required
                    disabled={isFinance}
                    value={p.return_terms.return_scope}
                    onChange={(event) =>
                      updateProfile('return_terms', 'return_scope', event.target.value)
                    }
                  >
                    <option value="">請選擇退貨地點</option>
                    {Object.entries(VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  <Select
                    label="退貨處理方式"
                    required
                    disabled={isFinance}
                    value={p.return_terms.return_handling_method}
                    onChange={(event) =>
                      updateProfile('return_terms', 'return_handling_method', event.target.value)
                    }
                  >
                    <option value="">請選擇處理方式</option>
                    {Object.entries(VENDOR_APPLICATION_RETURN_HANDLING_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  <Select
                    label="退貨／換貨運費負擔"
                    required
                    disabled={isFinance}
                    value={p.return_terms.return_freight_responsibility}
                    onChange={(event) =>
                      updateProfile(
                        'return_terms',
                        'return_freight_responsibility',
                        event.target.value,
                      )
                    }
                  >
                    <option value="">請選擇運費負擔</option>
                    {Object.entries(VENDOR_APPLICATION_RETURN_FREIGHT_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                </div>
                <div className="mt-4">
                  <p className="mb-2 text-sm font-semibold">
                    退貨／換貨成立條件 *{' '}
                    <span className="font-normal text-slate-500">（可複選）</span>
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
                    {Object.entries(VENDOR_APPLICATION_RETURN_CONDITION_LABELS).map(
                      ([value, label]) => (
                        <label
                          key={value}
                          className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            disabled={isFinance}
                            checked={p.return_terms.return_condition_types.includes(value as never)}
                            onChange={() =>
                              toggleArray('return_terms', 'return_condition_types', value)
                            }
                          />
                          {label}
                        </label>
                      ),
                    )}
                  </div>
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {p.return_terms.return_condition_types.includes('NEAR_EXPIRY') && (
                    <Textarea
                      label="即期退換貨條件"
                      required
                      disabled={isFinance}
                      value={p.return_terms.return_near_expiry_condition}
                      onChange={(event) =>
                        updateProfile(
                          'return_terms',
                          'return_near_expiry_condition',
                          event.target.value,
                        )
                      }
                    />
                  )}
                  {p.return_terms.return_condition_types.includes('DEFECT') && (
                    <Textarea
                      label="瑕疵退換貨條件"
                      required
                      disabled={isFinance}
                      value={p.return_terms.return_defect_condition}
                      onChange={(event) =>
                        updateProfile('return_terms', 'return_defect_condition', event.target.value)
                      }
                    />
                  )}
                  {p.return_terms.return_condition_types.includes('INTOLERANCE') && (
                    <Textarea
                      label="吃不合退換貨條件"
                      required
                      disabled={isFinance}
                      value={p.return_terms.return_intolerance_condition}
                      onChange={(event) =>
                        updateProfile(
                          'return_terms',
                          'return_intolerance_condition',
                          event.target.value,
                        )
                      }
                    />
                  )}
                  {p.return_terms.return_condition_types.includes('OTHER') && (
                    <Textarea
                      label="其他退換貨條件"
                      required
                      disabled={isFinance}
                      value={p.return_terms.return_other_condition}
                      onChange={(event) =>
                        updateProfile('return_terms', 'return_other_condition', event.target.value)
                      }
                    />
                  )}
                </div>
                <Textarea
                  className="mt-4"
                  label="退貨／換貨詳細說明"
                  required
                  disabled={isFinance}
                  value={p.return_terms.return_condition_description}
                  onChange={(event) =>
                    updateProfile(
                      'return_terms',
                      'return_condition_description',
                      event.target.value,
                    )
                  }
                />
                {p.return_terms.return_freight_responsibility === 'BY_REASON' && (
                  <Textarea
                    className="mt-4"
                    label="運費負擔規則說明"
                    required
                    disabled={isFinance}
                    value={p.return_terms.return_freight_rule_note}
                    onChange={(event) =>
                      updateProfile('return_terms', 'return_freight_rule_note', event.target.value)
                    }
                  />
                )}
              </Card>
            )}

            {activeSection === 'invoice' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">6. 發票與對帳</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <Select
                    label="發票格式"
                    required
                    disabled={isFinance}
                    value={p.invoice_terms.invoice_format}
                    onChange={(event) =>
                      updateProfile('invoice_terms', 'invoice_format', event.target.value)
                    }
                  >
                    <option value="">請選擇發票格式</option>
                    {Object.entries(VENDOR_APPLICATION_INVOICE_FORMAT_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  {p.invoice_terms.invoice_format === 'OTHER' && (
                    <Input
                      label="其他發票格式說明"
                      required
                      disabled={isFinance}
                      value={p.invoice_terms.invoice_format_other}
                      onChange={(event) =>
                        updateProfile('invoice_terms', 'invoice_format_other', event.target.value)
                      }
                    />
                  )}
                  <Select
                    label="發票寄送方式"
                    required
                    disabled={isFinance}
                    value={p.invoice_terms.invoice_delivery_method}
                    onChange={(event) =>
                      updateProfile('invoice_terms', 'invoice_delivery_method', event.target.value)
                    }
                  >
                    <option value="">請選擇寄送方式</option>
                    {Object.entries(VENDOR_APPLICATION_INVOICE_DELIVERY_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  {p.invoice_terms.invoice_delivery_method === 'OTHER' && (
                    <Textarea
                      label="其他發票寄送方式及原因"
                      required
                      disabled={isFinance}
                      value={p.invoice_terms.invoice_delivery_other}
                      onChange={(event) =>
                        updateProfile('invoice_terms', 'invoice_delivery_other', event.target.value)
                      }
                    />
                  )}
                </div>
                <div className="mt-5 grid gap-5 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-100 p-4">
                    <h4 className="font-extrabold">發票聯絡人 *</h4>
                    <label className="mt-3 flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={isFinance}
                        checked={p.invoice_terms.invoice_same_as_primary}
                        onChange={(event) => updateSameAsPrimary('invoice', event.target.checked)}
                      />
                      同主要聯絡人
                    </label>
                    {p.invoice_terms.invoice_same_as_primary ? (
                      <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                        {p.contact.contact_name}／{p.contact.contact_phone}／
                        {p.contact.contact_email}
                      </p>
                    ) : (
                      <div className="mt-4 grid gap-4">
                        {' '}
                        <Input
                          label="發票聯絡人姓名"
                          required
                          disabled={isFinance}
                          value={p.invoice_terms.invoice_contact_name}
                          onChange={(event) =>
                            updateProfile(
                              'invoice_terms',
                              'invoice_contact_name',
                              event.target.value,
                            )
                          }
                        />
                        <Input
                          label="發票聯絡電話"
                          required
                          disabled={isFinance}
                          value={p.invoice_terms.invoice_contact_phone}
                          onChange={(event) =>
                            updateProfile(
                              'invoice_terms',
                              'invoice_contact_phone',
                              event.target.value,
                            )
                          }
                        />
                        <Input
                          label="發票 Email"
                          required
                          type="email"
                          disabled={isFinance}
                          value={p.invoice_terms.invoice_email}
                          onChange={(event) =>
                            updateProfile('invoice_terms', 'invoice_email', event.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div className="rounded-2xl border border-slate-100 p-4">
                    <h4 className="font-extrabold">對帳聯絡人 *</h4>
                    <label className="mt-3 flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={isFinance}
                        checked={p.invoice_terms.reconciliation_same_as_primary}
                        onChange={(event) =>
                          updateSameAsPrimary('reconciliation', event.target.checked)
                        }
                      />
                      同主要聯絡人
                    </label>
                    {p.invoice_terms.reconciliation_same_as_primary ? (
                      <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                        {p.contact.contact_name}／{p.contact.contact_phone}／
                        {p.contact.contact_email}
                      </p>
                    ) : (
                      <div className="mt-4 grid gap-4">
                        <Input
                          label="對帳聯絡人姓名"
                          required
                          disabled={isFinance}
                          value={p.invoice_terms.reconciliation_contact_name}
                          onChange={(event) =>
                            updateProfile(
                              'invoice_terms',
                              'reconciliation_contact_name',
                              event.target.value,
                            )
                          }
                        />
                        <Input
                          label="對帳聯絡電話"
                          required
                          disabled={isFinance}
                          value={p.invoice_terms.reconciliation_contact_phone}
                          onChange={(event) =>
                            updateProfile(
                              'invoice_terms',
                              'reconciliation_contact_phone',
                              event.target.value,
                            )
                          }
                        />
                        <Input
                          label="對帳 Email"
                          required
                          type="email"
                          disabled={isFinance}
                          value={p.invoice_terms.reconciliation_email}
                          onChange={(event) =>
                            updateProfile(
                              'invoice_terms',
                              'reconciliation_email',
                              event.target.value,
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
                  <p className="font-bold">{VENDOR_APPLICATION_RECONCILIATION_NOTICE}</p>
                  <label className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={isFinance}
                      checked={p.invoice_terms.reconciliation_terms_acknowledged}
                      onChange={(event) =>
                        updateProfile(
                          'invoice_terms',
                          'reconciliation_terms_acknowledged',
                          event.target.checked,
                        )
                      }
                    />
                    我已閱讀並了解上述對帳條件 *
                  </label>
                </div>
              </Card>
            )}

            {activeSection === 'bank' && (
              <Card>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-extrabold">7. 銀行資料</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      完整銀行帳號僅提供 ADMIN、BUYER、FINANCE
                      查看；廠商及門市不會取得銀行區塊或明文帳號。
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addBank}
                    disabled={!canEditBank}
                  >
                    新增銀行資料
                  </Button>
                </div>
                {p.payment_terms.payment_method !== 'BANK_TRANSFER' && (
                  <p className="mb-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                    目前付款方式不要求銀行資料；如有需要仍可由採購或財務補充。
                  </p>
                )}
                {p.bank_details.length === 0 ? (
                  <EmptyState
                    title="尚未建立銀行資料"
                    description="付款方式為匯款時，正式啟用前必須補齊銀行資料。"
                  />
                ) : (
                  <div className="space-y-4">
                    {p.bank_details.map((account, index) => (
                      <div
                        key={account.id ?? index}
                        className="rounded-2xl border border-slate-100 bg-slate-50 p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <strong>銀行帳戶 {index + 1}</strong>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={!canEditBank}
                            onClick={() => removeBank(index)}
                          >
                            移除
                          </Button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <Input
                            label="銀行名稱"
                            disabled={!canEditBank}
                            value={account.bank_name}
                            onChange={(event) => updateBank(index, 'bank_name', event.target.value)}
                          />
                          <Input
                            label="銀行代碼"
                            disabled={!canEditBank}
                            value={account.bank_code}
                            onChange={(event) => updateBank(index, 'bank_code', event.target.value)}
                          />
                          <Input
                            label="分行名稱"
                            disabled={!canEditBank}
                            value={account.branch_name}
                            onChange={(event) =>
                              updateBank(index, 'branch_name', event.target.value)
                            }
                          />
                          <Input
                            label="分行代碼"
                            disabled={!canEditBank}
                            value={account.branch_code}
                            onChange={(event) =>
                              updateBank(index, 'branch_code', event.target.value)
                            }
                          />
                          <Input
                            label="戶名"
                            disabled={!canEditBank}
                            value={account.bank_account_name}
                            onChange={(event) =>
                              updateBank(index, 'bank_account_name', event.target.value)
                            }
                          />
                          <Input
                            label="銀行帳號"
                            type="password"
                            disabled={!canEditBank}
                            value={account.bank_account_number ?? ''}
                            placeholder={account.bank_account_number_masked ?? ''}
                            onChange={(event) =>
                              updateBank(index, 'bank_account_number', event.target.value)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {activeSection === 'attachments' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">8. 附件</h3>
                <p className="mb-4 text-sm text-slate-500">
                  核准新廠商申請時已直接承接原 R2 檔案，不要求廠商重新上傳。
                </p>
                {editing && (
                  <form
                    onSubmit={uploadAttachment}
                    className="mb-5 grid gap-3 rounded-2xl border border-dashed border-slate-200 p-4 md:grid-cols-[1fr_1fr_auto] md:items-end"
                  >
                    <Select
                      label="文件類型"
                      value={attachmentType}
                      onChange={(event) => setAttachmentType(event.target.value)}
                    >
                      {Object.entries(VENDOR_DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="補充附件"
                      type="file"
                      onChange={(event) => setAttachmentFile(event.target.files?.[0] ?? null)}
                    />
                    <Button type="submit" loading={attachmentSaving}>
                      上傳附件
                    </Button>
                  </form>
                )}
                {!editing ? (
                  <EmptyState
                    title="儲存後查看附件"
                    description="核准申請的附件會自動顯示在正式主檔。"
                  />
                ) : form.profile &&
                  (vendors.find((item) => item.id === editing)?.attachments ?? []).length === 0 ? (
                  <EmptyState
                    title="尚無附件"
                    description="若來源申請有附件，核准後會自動帶入；也可由內部人員補充。"
                  />
                ) : (
                  <div className="space-y-2">
                    {(vendors.find((item) => item.id === editing)?.attachments ?? []).map(
                      (attachment) => (
                        <div
                          key={attachment.id}
                          className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 p-3 text-sm"
                        >
                          <span className="min-w-0 break-words">
                            {attachment.type_label ??
                              VENDOR_DOCUMENT_TYPE_LABELS[attachment.type] ??
                              '其他附件'}
                            ｜{attachment.original_name}
                            <span className="ml-2 text-xs text-slate-500">
                              {attachment.source_application_attachment_id
                                ? '來源：新廠商申請'
                                : '主檔附件'}{' '}
                              · {Math.round(attachment.size_bytes / 1024)} KB
                            </span>
                          </span>
                          <a
                            className="font-bold text-teal-700"
                            href={`/api/vendors/${editing}/attachments/${attachment.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            預覽／下載
                          </a>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </Card>
            )}

            {activeSection === 'internal' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">9. 內部管理資料</h3>
                <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                  以下欄位只供 ADMIN、BUYER、FINANCE
                  內部使用，廠商不可查看。廠商狀態變更必須填寫原因並留下歷程。
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="廠商代碼"
                    required
                    disabled={!canEditMaster}
                    value={form.code}
                    onChange={(event) => updateTop('code', event.target.value)}
                  />
                  <Input
                    label="負責採購（使用者 ID）"
                    disabled={!canEditMaster}
                    value={form.responsible_buyer_id}
                    onChange={(event) => updateTop('responsible_buyer_id', event.target.value)}
                  />
                  <Input
                    label="啟用日期"
                    type="date"
                    disabled={!canEditMaster}
                    value={form.active_date}
                    onChange={(event) => updateTop('active_date', event.target.value)}
                  />
                  <Input
                    label="風險標記"
                    disabled={!canEditMaster}
                    value={form.risk_flag}
                    onChange={(event) => updateTop('risk_flag', event.target.value)}
                  />
                  <Textarea
                    label="內部備註"
                    className="md:col-span-2"
                    disabled={!canEditMaster}
                    value={form.internal_notes}
                    onChange={(event) => updateTop('internal_notes', event.target.value)}
                  />
                  <Textarea
                    label="信用／交易備註"
                    className="md:col-span-2"
                    disabled={!canEditMaster}
                    value={form.credit_trade_notes}
                    onChange={(event) => updateTop('credit_trade_notes', event.target.value)}
                  />
                </div>
                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid gap-4 md:grid-cols-[1fr_2fr_auto] md:items-end">
                    <Select
                      label="廠商狀態"
                      required
                      disabled={!canEditMaster || !editing}
                      value={statusDraft}
                      onChange={(event) => setStatusDraft(event.target.value as VendorStatus)}
                    >
                      {Object.entries(VENDOR_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="狀態變更原因"
                      required
                      disabled={!canEditMaster || !editing}
                      value={statusReason}
                      onChange={(event) => setStatusReason(event.target.value)}
                      placeholder="例如：完成申請核准、暫停交易或資料補正"
                    />
                    <Button
                      type="button"
                      loading={statusSaving}
                      disabled={
                        !canEditMaster ||
                        !editing ||
                        statusDraft === (form.vendor_status ?? 'DRAFT')
                      }
                      onClick={() => void saveVendorStatus()}
                    >
                      儲存狀態變更
                    </Button>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    原始申請核准人：{selectedVendor?.approved_by_name || '非由新廠商申請建立'}
                  </p>
                </div>
              </Card>
            )}

            {activeSection === 'account' && (
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">10. 廠商登入帳號</h3>
                <p className="mb-4 rounded-xl bg-sky-50 p-3 text-sm leading-6 text-sky-900">
                  廠商登入帳號預設使用統一編號；密碼只儲存雜湊值，內部人員不會看到廠商密碼。
                  既有廠商首次使用時可直接以統編自行設定密碼，不需要採購提供啟用碼或 Email OTP。
                </p>
                {!editing || !selectedVendor?.account ? (
                  <EmptyState
                    title="尚未建立廠商登入帳號"
                    description="請先儲存正式廠商主檔；廠商之後可從公開入口以統一編號自行設定密碼。"
                  />
                ) : (
                  <div className="space-y-5">
                    <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-bold text-slate-500">登入帳號</p>
                        <p className="mt-1 font-extrabold text-ink">
                          {selectedVendor.account.login_account}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">帳號狀態</p>
                        <span
                          className={`mt-1 inline-flex rounded-full px-2 py-1 text-xs font-bold ${VENDOR_ACCOUNT_STATUS_TONE[selectedVendor.account.status]}`}
                        >
                          {VENDOR_ACCOUNT_STATUS_LABELS[selectedVendor.account.status]}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">最後登入</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {selectedVendor.account.last_login_at ? (
                            <DateText value={selectedVendor.account.last_login_at} withTime />
                          ) : (
                            '尚未登入'
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">密碼最後變更</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {selectedVendor.account.password_changed_at ? (
                            <DateText value={selectedVendor.account.password_changed_at} withTime />
                          ) : (
                            '尚未設定密碼'
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">失敗次數</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {selectedVendor.account.failed_login_attempts}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">建立時間</p>
                        <p className="mt-1 text-sm text-slate-700">
                          <DateText value={selectedVendor.account.created_at} withTime />
                        </p>
                      </div>
                    </div>
                    {canEditMaster && (
                      <div className="flex flex-wrap gap-3">
                        {selectedVendor.account.status !== 'DISABLED' && (
                          <Button
                            type="button"
                            variant="secondary"
                            loading={accountSaving}
                            onClick={() => {
                              if (
                                window.confirm(
                                  '確定要重設此廠商的登入密碼嗎？重設後原密碼將立即失效，廠商下次登入時需重新設定密碼。',
                                )
                              )
                                void updateVendorAccountStatus('RESET_PASSWORD');
                            }}
                          >
                            重設廠商密碼
                          </Button>
                        )}
                        {selectedVendor.account.status === 'LOCKED' && (
                          <Button
                            type="button"
                            variant="ghost"
                            loading={accountSaving}
                            onClick={() => void updateVendorAccountStatus('UNLOCK')}
                          >
                            解鎖帳號
                          </Button>
                        )}
                        {selectedVendor.account.status === 'DISABLED' ? (
                          <Button
                            type="button"
                            variant="ghost"
                            loading={accountSaving}
                            onClick={() => void updateVendorAccountStatus('ENABLE')}
                          >
                            重新啟用帳號
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            loading={accountSaving}
                            onClick={() => {
                              if (window.confirm('確定停用此廠商登入帳號嗎？'))
                                void updateVendorAccountStatus('DISABLE');
                            }}
                          >
                            停用帳號
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            <Card>
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <p className="text-sm text-slate-500">
                  儲存前請確認所有新版必填欄位；Server-side validation 會再次檢查條件式欄位。
                </p>
                <div className="flex gap-3">
                  <Button type="button" variant="secondary" onClick={reset}>
                    取消
                  </Button>
                  <Button type="submit" loading={saving}>
                    儲存廠商主檔
                  </Button>
                </div>
              </div>
            </Card>
          </form>
        )}
      </div>
    </>
  );
}

export function VendorApplicationsPage({ notify }: PageProps) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<VendorApplication['status'] | ''>(
    'PENDING_REVIEW',
  );
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [applications, setApplications] = useState<VendorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<VendorApplication | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [decisionReason, setDecisionReason] = useState('');
  const [reviewAction, setReviewAction] = useState<
    'REQUEST_DOCUMENTS' | 'RETURN_FOR_EDIT' | 'SUSPEND' | 'REJECT'
  >('RETURN_FOR_EDIT');
  const [reviewInternalReason, setReviewInternalReason] = useState('');
  const [reviewVendorReason, setReviewVendorReason] = useState('');
  const [requestedFields, setRequestedFields] = useState<string[]>([]);
  const [requestedAttachmentTypes, setRequestedAttachmentTypes] = useState<string[]>([]);
  const [reviewDueAt, setReviewDueAt] = useState('');
  const [decisionSaving, setDecisionSaving] = useState(false);
  useUnsavedPrompt(
    Boolean(
      decisionReason ||
      reviewInternalReason ||
      reviewVendorReason ||
      requestedFields.length ||
      requestedAttachmentTypes.length ||
      reviewDueAt,
    ),
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.vendorApplications
      .list({
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(appliedQuery ? { query: appliedQuery } : {}),
      })
      .then((result) => {
        if (active) setApplications(result.applications);
      })
      .catch((error) => {
        if (active) notify(errorMessage(error), 'error');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [appliedQuery, notify, refreshKey, statusFilter]);

  useEffect(() => {
    if (!id) {
      setSelected(null);
      setDetailLoading(false);
      setDecisionReason('');
      setReviewInternalReason('');
      setReviewVendorReason('');
      setRequestedFields([]);
      setRequestedAttachmentTypes([]);
      setReviewDueAt('');
      return;
    }
    let active = true;
    setDetailLoading(true);
    api.vendorApplications
      .detail(id)
      .then((application) => {
        if (active) {
          setSelected(application);
          setDecisionReason('');
          setReviewInternalReason('');
          setReviewVendorReason(
            application.review_vendor_reason ?? application.review_reason ?? '',
          );
          setRequestedFields(application.requested_review_fields ?? []);
          setRequestedAttachmentTypes(application.requested_attachment_types ?? []);
          setReviewDueAt(application.review_due_at?.slice(0, 10) ?? '');
          if (
            application.review_action === 'REQUEST_DOCUMENTS' ||
            application.review_action === 'RETURN_FOR_EDIT' ||
            application.review_action === 'SUSPEND' ||
            application.review_action === 'REJECT'
          )
            setReviewAction(application.review_action);
        }
      })
      .catch((error) => {
        if (active) notify(errorMessage(error), 'error');
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, notify]);

  const search = () => {
    setAppliedQuery(query.trim());
    setRefreshKey((value) => value + 1);
  };

  const approve = async () => {
    if (!selected || !window.confirm('確定核准此廠商申請並建立廠商主檔嗎？')) return;
    setDecisionSaving(true);
    try {
      const result = await api.vendorApplications.approve(selected.id, decisionReason.trim());
      notify(`申請已核准，已建立廠商主檔「${result.vendor.code}」。`);
      setDecisionReason('');
      setRefreshKey((value) => value + 1);
      navigate('/vendor-applications');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setDecisionSaving(false);
    }
  };

  const reject = async () => {
    if (!selected) return;
    if (!decisionReason.trim()) {
      notify('退回申請前請填寫理由。', 'error');
      return;
    }
    if (!window.confirm('確定退回此廠商申請嗎？')) return;
    setDecisionSaving(true);
    try {
      await api.vendorApplications.reject(selected.id, decisionReason.trim());
      notify('廠商申請已退回。');
      setDecisionReason('');
      setRefreshKey((value) => value + 1);
      navigate('/vendor-applications');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setDecisionSaving(false);
    }
  };

  const toggleReviewValue = (values: string[], value: string, setter: (next: string[]) => void) =>
    setter(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);

  const submitReview = async () => {
    if (!selected) return;
    if (!window.confirm(`確定執行「${VENDOR_APPLICATION_REVIEW_ACTION_LABELS[reviewAction]}」嗎？`))
      return;
    setDecisionSaving(true);
    try {
      await api.vendorApplications.review(selected.id, {
        action: reviewAction,
        internal_reason: reviewInternalReason.trim(),
        vendor_visible_reason: reviewVendorReason.trim(),
        requested_fields: requestedFields,
        requested_attachment_types: requestedAttachmentTypes,
        review_due_at: reviewDueAt,
      });
      notify('廠商申請審核決議已儲存。');
      setRefreshKey((value) => value + 1);
      navigate('/vendor-applications');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setDecisionSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="VENDOR APPLICATION REVIEW"
        title="廠商申請審核"
        description="查看廠商主動註冊申請。核准後會自動建立廠商主檔，退回時必須留下原因。"
        actions={
          <Button onClick={search} loading={loading} variant="secondary">
            重新整理
          </Button>
        }
      />
      <div className="mb-5 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm leading-6 text-sky-900">
        廠商申請在這裡審核；核准建立的廠商主檔預設為「草稿」，後續仍須補齊主檔交易條件後才能啟用。
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)]">
        <Card>
          <div className="mb-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_auto]">
            <Input
              label="搜尋申請編號／公司／統一編號"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') search();
              }}
            />
            <Select
              label="申請狀態"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as VendorApplication['status'] | '')
              }
            >
              <option value="">全部狀態</option>
              {Object.entries(VENDOR_APPLICATION_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <div className="flex items-end">
              <Button className="w-full" onClick={search} loading={loading}>
                查詢
              </Button>
            </div>
          </div>
          {loading ? (
            <Loading />
          ) : applications.length === 0 ? (
            <EmptyState
              title="目前沒有廠商申請"
              description="廠商從公開註冊頁送出申請後，會出現在這裡。"
            />
          ) : (
            <Table>
              <thead>
                <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                  <th className="px-4 py-3">申請編號</th>
                  <th className="px-4 py-3">公司／聯絡人</th>
                  <th className="px-4 py-3">提交時間</th>
                  <th className="px-4 py-3">狀態</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {applications.map((application) => (
                  <tr
                    key={application.id}
                    className={`border-t border-slate-100 align-top ${id === application.id ? 'bg-teal-50/50' : ''}`}
                  >
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        className="text-left font-bold text-teal-700 hover:underline"
                        onClick={() => navigate(`/vendor-applications/${application.id}`)}
                      >
                        {application.application_no}
                      </button>
                      <p className="mt-1 text-xs text-slate-500">{application.tax_id}</p>
                    </td>
                    <td className="max-w-[220px] whitespace-normal break-words px-4 py-4">
                      <p className="font-bold">{application.company_name}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {application.contact?.contact_name || application.company_email}
                      </p>
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-600">
                      <DateText
                        value={application.submitted_at ?? application.created_at}
                        withTime
                      />
                    </td>
                    <td className="px-4 py-4">
                      <VendorApplicationStatusBadge
                        status={application.status}
                        action={application.review_action}
                      />
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button
                        variant="secondary"
                        onClick={() => navigate(`/vendor-applications/${application.id}`)}
                      >
                        查看
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card>
          {detailLoading ? (
            <Loading label="申請資料載入中…" />
          ) : !selected ? (
            <EmptyState title="請選擇一筆申請" description="點選左側申請編號查看完整資料。" />
          ) : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">
                    APPLICATION DETAIL
                  </p>
                  <h2 className="mt-2 text-xl font-extrabold text-ink">{selected.company_name}</h2>
                  <p className="mt-1 text-sm text-slate-500">{selected.application_no}</p>
                </div>
                <VendorApplicationStatusBadge
                  status={selected.status}
                  action={selected.review_action}
                />
                {selected.status === 'APPROVED' ? (
                  <a
                    href={api.vendorApplications.pdfUrl(selected.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-teal-700 px-3 py-1 text-xs font-bold text-white hover:bg-teal-800"
                  >
                    下載核准 PDF
                  </a>
                ) : null}
              </div>

              <VendorApplicationFullDetails application={selected} />

              <div className="hidden">
                <section>
                  <h3 className="mb-3 text-sm font-extrabold text-ink">公司資料</h3>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-slate-500">統一編號</dt>
                      <dd className="mt-1 font-semibold">{selected.tax_id}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">公司電話</dt>
                      <dd className="mt-1 font-semibold">{applicationValue(selected.phone)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">公司簡稱／公司傳真</dt>
                      <dd className="mt-1 font-semibold">
                        {applicationValue(selected.short_name)}／
                        {applicationValue(selected.company_fax)}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs text-slate-500">地址</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.address)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">公司 Email</dt>
                      <dd className="mt-1 break-all font-semibold">{selected.company_email}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">網站</dt>
                      <dd className="mt-1 break-all font-semibold">
                        {applicationValue(selected.website)}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs text-slate-500">公司簡介</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words leading-6">
                        {applicationValue(selected.company_intro)}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs text-slate-500">主要代理／品牌</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {selected.principal_brands?.join('、') ||
                          applicationValue(selected.primary_brand || selected.brands)}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs text-slate-500">供應商品類</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {selected.supplied_product_categories?.join('、') ||
                          applicationValue(selected.categories)}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section>
                  <h3 className="mb-3 text-sm font-extrabold text-ink">主要聯絡人</h3>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-slate-500">姓名／職稱</dt>
                      <dd className="mt-1 font-semibold">
                        {applicationValue(selected.contact?.contact_name)}／
                        {applicationValue(selected.contact?.title)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">電話</dt>
                      <dd className="mt-1 font-semibold">
                        {applicationValue(selected.contact?.phone)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Email</dt>
                      <dd className="mt-1 break-all font-semibold">
                        {applicationValue(selected.contact?.email)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">廠商結帳日／每日訂單截止時間</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.vendor_closing_day)}／
                        {applicationValue(selected.daily_order_cutoff_time)}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section>
                  <h3 className="mb-3 text-sm font-extrabold text-ink">交易條件</h3>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-slate-500">付款條件／付款方式</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.payment_term || selected.payment_terms)}／
                        {applicationValue(selected.payment_method)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">月結天數／付款方式補充</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.monthly_payment_days)}／
                        {applicationValue(
                          selected.payment_method_other || selected.weekly_payment_note,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">付款規則確認</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {selected.payment_rule_acknowledged ? '已確認' : '未確認'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">退貨／換貨處理</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.return_handling_method || selected.return_terms)}
                        ／{selected.return_conditions?.join('、') || '—'}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section>
                  <h3 className="mb-3 text-sm font-extrabold text-ink">配送與退貨</h3>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-slate-500">配送地點／配送週期</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.delivery_scope)}／
                        {applicationValue(selected.delivery_cycle)}
                        {selected.delivery_fixed_weekdays?.length
                          ? `（${selected.delivery_fixed_weekdays.join('、')}）`
                          : ''}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">最低訂購金額／數量</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.delivery_minimum_order_amount)}／
                        {applicationValue(selected.delivery_minimum_order_quantity)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">運費規則</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.freight_rule)}／
                        {applicationValue(
                          selected.freight_threshold_amount ||
                            selected.freight_fixed_amount ||
                            selected.freight_other_note,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">退貨地點／運費負擔</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.return_scope)}／
                        {applicationValue(selected.return_freight_responsibility)}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs text-slate-500">退貨／換貨詳細說明</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words leading-6">
                        {applicationValue(selected.return_condition_description)}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section>
                  <h3 className="mb-3 text-sm font-extrabold text-ink">發票／對帳／銀行</h3>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-slate-500">發票</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.invoice_format)}／
                        {applicationValue(selected.invoice_delivery_method)}
                        {selected.invoice_delivery_other
                          ? `（${selected.invoice_delivery_other}）`
                          : ''}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">發票聯絡人／對帳聯絡人</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(
                          selected.invoice_same_as_primary
                            ? '同主要聯絡人'
                            : selected.invoice_contact_name,
                        )}
                        ／
                        {applicationValue(
                          selected.reconciliation_same_as_primary
                            ? '同主要聯絡人'
                            : selected.reconciliation_contact_name,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">對帳 Email／確認</dt>
                      <dd className="mt-1 break-all font-semibold">
                        {applicationValue(selected.reconciliation_email)}／
                        {selected.reconciliation_terms_acknowledged ? '已確認' : '未確認'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">銀行</dt>
                      <dd className="mt-1 whitespace-pre-wrap break-words font-semibold">
                        {applicationValue(selected.bank_name)}／
                        {applicationValue(selected.branch_name)}／
                        {applicationValue(selected.bank_account_name)}／
                        {applicationValue(selected.bank_account_number_masked)}
                      </dd>
                    </div>
                  </dl>
                </section>
              </div>

              {selected.application_attachments?.length ? (
                <section>
                  <h3 className="mb-3 text-sm font-extrabold text-ink">申請附件</h3>
                  <div className="space-y-2">
                    {selected.application_attachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={
                          '/api/vendor-applications/' +
                          selected.id +
                          '/attachments/' +
                          attachment.id
                        }
                        className="block rounded-xl bg-slate-50 p-3 text-sm text-teal-700 hover:bg-teal-50"
                      >
                        <span className="block font-bold">
                          {VENDOR_APPLICATION_ATTACHMENT_LABELS[attachment.type] ?? attachment.type}
                        </span>
                        <span className="mt-1 block break-words text-slate-700">
                          {attachment.original_name}（下載）
                        </span>
                        <span className="mt-1 block text-xs text-slate-500">
                          {attachment.content_type} ·{' '}
                          {Math.max(1, Math.round(attachment.size_bytes / 1024))} KB · 上傳於{' '}
                          {attachment.created_at}
                        </span>
                      </a>
                    ))}
                  </div>
                </section>
              ) : null}

              {(selected.review_vendor_reason || selected.review_reason) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
                  <strong>廠商可見說明：</strong>
                  <span className="whitespace-pre-wrap">
                    {selected.review_vendor_reason || selected.review_reason}
                  </span>
                </div>
              )}

              {selected.review_internal_reason && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                  <strong>內部審核原因：</strong>
                  <span className="whitespace-pre-wrap">{selected.review_internal_reason}</span>
                </div>
              )}

              {(selected.requested_review_fields?.length ||
                selected.requested_attachment_types?.length ||
                selected.review_due_at) && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm leading-6 text-sky-900">
                  <strong>補件／修改要求：</strong>
                  {selected.requested_review_fields?.length ? (
                    <p>
                      欄位：
                      {selected.requested_review_fields
                        .map(
                          (item) =>
                            VENDOR_APPLICATION_REVIEW_FIELD_OPTIONS.find(
                              ([value]) => value === item,
                            )?.[1] ?? item,
                        )
                        .join('、')}
                    </p>
                  ) : null}
                  {selected.requested_attachment_types?.length ? (
                    <p>
                      附件：
                      {selected.requested_attachment_types
                        .map((item) => VENDOR_APPLICATION_ATTACHMENT_LABELS[item] ?? item)
                        .join('、')}
                    </p>
                  ) : null}
                  {selected.review_due_at ? <p>期限：{selected.review_due_at}</p> : null}
                </div>
              )}

              {(selected.resubmission_count ?? 0) > 0 && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                  此申請已重新送審 {selected.resubmission_count} 次
                  {selected.last_resubmitted_at && (
                    <span>（最近一次：{selected.last_resubmitted_at}）</span>
                  )}
                </div>
              )}

              {selected.vendor_id && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  已建立廠商主檔：<strong>{selected.vendor_code || selected.vendor_id}</strong>
                </div>
              )}

              {selected.status === 'PENDING_REVIEW' && (
                <>
                  <section className="border-t border-slate-100 pt-5">
                    <h3 className="mb-4 text-sm font-extrabold text-ink">審核決議</h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Select
                        label="審核動作"
                        value={reviewAction}
                        onChange={(event) =>
                          setReviewAction(
                            event.target.value as
                              'REQUEST_DOCUMENTS' | 'RETURN_FOR_EDIT' | 'SUSPEND' | 'REJECT',
                          )
                        }
                      >
                        <option value="REQUEST_DOCUMENTS">要求補件</option>
                        <option value="RETURN_FOR_EDIT">退回修改</option>
                        <option value="SUSPEND">暫緩處理</option>
                        <option value="REJECT">退件</option>
                      </Select>
                      <Input
                        label="補件／修改期限"
                        type="date"
                        value={reviewDueAt}
                        onChange={(event) => setReviewDueAt(event.target.value)}
                      />
                    </div>
                    <div className="mt-4">
                      <Textarea
                        label="核准備註（可選）"
                        value={decisionReason}
                        onChange={(event) => setDecisionReason(event.target.value)}
                        placeholder="核准時可補充內部備註。"
                        maxLength={2000}
                      />
                    </div>
                    {(reviewAction === 'REQUEST_DOCUMENTS' ||
                      reviewAction === 'RETURN_FOR_EDIT') && (
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div>
                          <p className="mb-2 text-sm font-bold text-ink">要求補正欄位</p>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {VENDOR_APPLICATION_REVIEW_FIELD_OPTIONS.map(([value, label]) => (
                              <label
                                key={value}
                                className="flex items-center gap-2 text-sm text-slate-700"
                              >
                                <input
                                  type="checkbox"
                                  checked={requestedFields.includes(value)}
                                  onChange={() =>
                                    toggleReviewValue(requestedFields, value, setRequestedFields)
                                  }
                                  className="accent-teal-600"
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="mb-2 text-sm font-bold text-ink">要求補交附件</p>
                          <div className="grid gap-2">
                            {Object.entries(VENDOR_APPLICATION_ATTACHMENT_LABELS).map(
                              ([value, label]) => (
                                <label
                                  key={value}
                                  className="flex items-center gap-2 text-sm text-slate-700"
                                >
                                  <input
                                    type="checkbox"
                                    checked={requestedAttachmentTypes.includes(value)}
                                    onChange={() =>
                                      toggleReviewValue(
                                        requestedAttachmentTypes,
                                        value,
                                        setRequestedAttachmentTypes,
                                      )
                                    }
                                    className="accent-teal-600"
                                  />
                                  {label}
                                </label>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <Textarea
                        label="廠商可見說明"
                        required
                        value={reviewVendorReason}
                        onChange={(event) => setReviewVendorReason(event.target.value)}
                        placeholder="請說明廠商需要補正或注意的內容。"
                        maxLength={2000}
                      />
                      <Textarea
                        label="內部審核原因"
                        value={reviewInternalReason}
                        onChange={(event) => setReviewInternalReason(event.target.value)}
                        placeholder="僅供內部審核紀錄使用。退件時必填。"
                        maxLength={2000}
                      />
                    </div>
                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      <Button variant="secondary" onClick={submitReview} loading={decisionSaving}>
                        儲存審核決議
                      </Button>
                      <Button onClick={approve} loading={decisionSaving}>
                        核准並建立廠商主檔
                      </Button>
                    </div>
                  </section>

                  <div className="hidden">
                    <section className="border-t border-slate-100 pt-5">
                      <Textarea
                        label="審核備註（核准可選；退回必填）"
                        value={decisionReason}
                        onChange={(event) => setDecisionReason(event.target.value)}
                        placeholder="請填寫核准備註或退回原因"
                        maxLength={2000}
                      />
                      <div className="mt-4 flex flex-wrap justify-end gap-2">
                        <Button variant="danger" onClick={reject} loading={decisionSaving}>
                          退回申請
                        </Button>
                        <Button onClick={approve} loading={decisionSaving}>
                          核准並建立廠商主檔
                        </Button>
                      </div>
                    </section>
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

export function InvitationsPage({ notify }: PageProps) {
  const {
    data: invitationData,
    loading,
    setData,
  } = useAsyncLoad(() => api.invitations.list(), [], notify);
  const { data: roundData } = useAsyncLoad(() => api.rounds.list(), [], notify);
  const { data: vendorData } = useAsyncLoad(() => api.vendors.list(), [], notify);
  const [roundId, setRoundId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [created, setCreated] = useState<{
    token: string;
    expires_at: string;
    invite_url: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const rounds = roundData?.rounds ?? [];
  const vendors = vendorData?.vendors ?? [];
  const invitations = invitationData?.invitations ?? [];
  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!roundId || !vendorId) {
      notify('請選擇報價活動與廠商。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.invitations.create({
        quotation_round_id: roundId,
        vendor_id: vendorId,
      });
      setCreated(result);
      notify('邀請連結已產生，請立即複製保存。');
      const refreshed = await api.invitations.list();
      setData(refreshed);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const revoke = async (id: string) => {
    try {
      await api.invitations.revoke(id);
      setData({
        invitations: invitations.map((item) =>
          item.id === id ? { ...item, revoked_at: new Date().toISOString() } : item,
        ),
      });
      notify('邀請 Token 已撤銷。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="ACCESS CONTROL"
        title="邀請連結管理"
        description="每家廠商使用不同的安全 Token；明文只在產生當下回傳，資料庫僅保存雜湊。"
      />
      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.5fr]">
        <Card>
          <h2 className="mb-4 text-lg font-extrabold">產生專屬邀請</h2>
          <form onSubmit={create} className="space-y-4">
            <Select
              label="報價活動"
              value={roundId}
              onChange={(event) => setRoundId(event.target.value)}
            >
              <option value="">請選擇活動</option>
              {rounds.map((round) => (
                <option key={round.id} value={round.id}>
                  {round.name}
                </option>
              ))}
            </Select>
            <Select
              label="廠商"
              value={vendorId}
              onChange={(event) => setVendorId(event.target.value)}
            >
              <option value="">請選擇廠商</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}（{vendor.code}）
                </option>
              ))}
            </Select>
            <Button className="w-full" loading={saving}>
              產生邀請 Token
            </Button>
          </form>
          {created && (
            <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="font-bold text-emerald-800">Token 已產生，請只分享以下網址</p>
              <div className="mt-3 break-all rounded-xl bg-white p-3 text-xs text-slate-700">
                {created.invite_url}
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigator.clipboard?.writeText(created.invite_url)}
                >
                  複製連結
                </Button>
                <span className="self-center text-xs text-emerald-700">
                  有效至 {formatTaipeiDate(created.expires_at)}
                </span>
              </div>
            </div>
          )}
        </Card>
        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-extrabold">已建立連結</h2>
            <p className="mt-1 text-xs text-slate-500">
              撤銷後 Token 立即失效，廠商無法再讀寫資料。
            </p>
          </div>
          {loading ? (
            <Loading />
          ) : invitations.length === 0 ? (
            <EmptyState title="尚未建立邀請" />
          ) : (
            <Table>
              <thead>
                <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                  <th className="px-4 py-3">活動 / 廠商</th>
                  <th className="px-4 py-3">狀態</th>
                  <th className="px-4 py-3">有效期限</th>
                  <th className="px-4 py-3">最後使用</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {invitations.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100">
                    <td className="px-4 py-4">
                      <p className="font-bold">{item.round_name}</p>
                      <p className="mt-1 text-xs text-slate-500">{item.vendor_name}</p>
                    </td>
                    <td className="px-4 py-4">
                      {item.revoked_at ? (
                        <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-bold text-rose-700">
                          已撤銷
                        </span>
                      ) : (
                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                          {item.proposal_count
                            ? `已建立 ${item.proposal_count} 筆提案${item.proposal_status ? `（${PROACTIVE_STATUS_LABELS[item.proposal_status] ?? item.proposal_status}）` : ''}`
                            : item.submission_status === 'VENDOR_SUBMITTED'
                              ? '已提交'
                              : '尚未填報'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-600">
                      <DateText value={item.expires_at} withTime />
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-600">
                      <DateText value={item.last_used_at} withTime />
                    </td>
                    <td className="px-4 py-4 text-right">
                      {!item.revoked_at && (
                        <Button variant="danger" onClick={() => revoke(item.id)}>
                          撤銷
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

function submissionLabel(submission: Submission): string {
  return submission.product?.name_zh || '尚未填寫商品名稱';
}

function replaceSubmission(list: Submission[], next: Submission): Submission[] {
  return list.map((item) => (item.id === next.id ? next : item));
}

export function BuyerReviewPage({ notify }: PageProps) {
  const { data, loading, setData } = useAsyncLoad(() => api.submissions.list(), [], notify);
  const submissions = data?.submissions ?? [];
  const [productCategoryCode, setProductCategoryCode] = useState('');
  const [productSubcategoryFilter, setProductSubcategoryFilter] = useState('');
  const [unclassifiedOnly, setUnclassifiedOnly] = useState(
    () => new URLSearchParams(window.location.search).get('unclassified') === '1',
  );
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [productSubcategories, setProductSubcategories] = useState<ProductSubcategory[]>([]);
  const [filterSubcategories, setFilterSubcategories] = useState<ProductSubcategory[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [batchSubcategoryCode, setBatchSubcategoryCode] = useState('');
  const [batchSubcategories, setBatchSubcategories] = useState<ProductSubcategory[]>([]);
  const [batchSaving, setBatchSaving] = useState(false);
  const [reviewCategoryCode, setReviewCategoryCode] = useState('');
  const [reviewSubcategoryCode, setReviewSubcategoryCode] = useState('');
  const [classificationReason, setClassificationReason] = useState('');
  const [review, setReview] = useState({
    required_fields_checked: false,
    attachments_checked: false,
    internal_comment: '',
    risk_level: '',
  });
  const [returnReason, setReturnReason] = useState('');
  const [returnItemCodes, setReturnItemCodes] = useState<string[]>([]);
  const [returnDueDate, setReturnDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [tradeTermsReason, setTradeTermsReason] = useState('');
  const [tradeTermsSaving, setTradeTermsSaving] = useState(false);
  const filteredSubmissions = useMemo(
    () =>
      submissions.filter(
        (item) =>
          (!productCategoryCode || item.product?.product_category_code === productCategoryCode) &&
          (!productSubcategoryFilter ||
            item.product?.product_subcategory_code === productSubcategoryFilter) &&
          (!unclassifiedOnly || !item.product?.product_subcategory_code),
      ),
    [submissions, productCategoryCode, productSubcategoryFilter, unclassifiedOnly],
  );
  useEffect(() => {
    api.productCategories
      .publicList()
      .then((result) => setProductCategories(result.categories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, []);
  useEffect(() => {
    const categoryCode = reviewCategoryCode;
    if (!categoryCode) {
      setProductSubcategories([]);
      return;
    }
    api.productSubcategories
      .list({ category_code: categoryCode, include_inactive: '1' })
      .then((result) => setProductSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [reviewCategoryCode, notify]);
  useEffect(() => {
    if (!productCategoryCode) {
      setFilterSubcategories([]);
      return;
    }
    api.productSubcategories
      .publicList(productCategoryCode)
      .then((result) => setFilterSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [productCategoryCode, notify]);
  const batchCategoryCode = useMemo(() => {
    const codes = new Set(
      batchIds.map(
        (id) => submissions.find((item) => item.id === id)?.product?.product_category_code ?? '',
      ),
    );
    return codes.size === 1 ? [...codes][0] : '';
  }, [batchIds, submissions]);
  useEffect(() => {
    if (!batchCategoryCode) {
      setBatchSubcategories([]);
      setBatchSubcategoryCode('');
      return;
    }
    api.productSubcategories
      .publicList(batchCategoryCode)
      .then((result) => setBatchSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [batchCategoryCode, notify]);
  useEffect(() => {
    const queryId = new URLSearchParams(window.location.search).get('submission');
    setSelectedId(
      queryId && filteredSubmissions.some((item) => item.id === queryId)
        ? queryId
        : (filteredSubmissions[0]?.id ?? ''),
    );
  }, [filteredSubmissions]);
  const selected = submissions.find((item) => item.id === selectedId) ?? null;
  useEffect(() => {
    if (selected?.buyer_review)
      setReview({
        required_fields_checked: Boolean(selected.buyer_review.required_fields_checked),
        attachments_checked: Boolean(selected.buyer_review.attachments_checked),
        internal_comment: selected.buyer_review.internal_comment,
        risk_level: selected.buyer_review.risk_level,
      });
    else
      setReview({
        required_fields_checked: false,
        attachments_checked: false,
        internal_comment: '',
        risk_level: '',
      });
    setReviewCategoryCode(selected?.product?.product_category_code ?? '');
    setReviewSubcategoryCode(selected?.product?.product_subcategory_code ?? '');
    setClassificationReason('');
    setReturnReason('');
    setReturnItemCodes([]);
    setReturnDueDate(selected?.revision_due_at?.slice(0, 10) ?? '');
  }, [selectedId, selected?.buyer_review]);
  const classificationChanged = Boolean(
    selected?.product &&
    (selected.product.product_category_code !== reviewCategoryCode ||
      selected.product.product_subcategory_code !== reviewSubcategoryCode),
  );
  const saveReview = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const result = await api.submissions.review(selected.id, {
        ...review,
        product_category_code: reviewCategoryCode,
        product_subcategory_code: reviewSubcategoryCode,
        product_subcategory_change_reason: classificationReason,
      } as any);
      setData({ submissions: replaceSubmission(submissions, result) });
      notify('採購審核已儲存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const reviewSpecialTradeTerms = async (decision: 'ACCEPT' | 'REJECT' | 'REQUEST_REVISION') => {
    if (!selected) return;
    if (decision !== 'ACCEPT' && !tradeTermsReason.trim()) {
      notify('拒絕或要求補件時必須填寫原因。', 'error');
      return;
    }
    setTradeTermsSaving(true);
    try {
      const result = await api.submissions.reviewTradeTerms(selected.id, {
        decision,
        reason: tradeTermsReason,
      });
      setData({ submissions: replaceSubmission(submissions, result) });
      setTradeTermsReason('');
      notify('特殊交易條件審核已更新。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setTradeTermsSaving(false);
    }
  };
  const batchAssign = async () => {
    if (!batchIds.length || !batchSubcategoryCode) {
      notify('請先勾選新品並選擇商品子分類。', 'error');
      return;
    }
    const selectedBatch = submissions.filter((item) => batchIds.includes(item.id));
    const categories = new Set(
      selectedBatch.map((item) => item.product?.product_category_code ?? ''),
    );
    if (categories.size !== 1) {
      notify('所選商品的商品分類不同，無法批次指定商品子分類。', 'error');
      return;
    }
    const reason = selectedBatch.some((item) =>
      ['MEETING_REVIEW', 'APPROVED', 'MASTER_CREATED'].includes(item.status),
    )
      ? (window.prompt('請填寫修改原因：', '') ?? '')
      : '';
    if (
      selectedBatch.some((item) =>
        ['MEETING_REVIEW', 'APPROVED', 'MASTER_CREATED'].includes(item.status),
      ) &&
      !reason.trim()
    )
      return;
    setBatchSaving(true);
    try {
      const result = await api.submissions.batchSubcategory({
        submission_ids: batchIds,
        product_subcategory_code: batchSubcategoryCode,
        reason,
      });
      const updated = new Map(result.submissions.map((item) => [item.id, item]));
      setData({ submissions: submissions.map((item) => updated.get(item.id) ?? item) });
      setBatchIds([]);
      setBatchSubcategoryCode('');
      notify('已批次指定商品子分類。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setBatchSaving(false);
    }
  };
  const requestRevision = async () => {
    if (!selected) return;
    const reason = window.prompt(
      '請填寫要通知廠商的補件內容：',
      selected.missing_items?.join('、') ?? '',
    );
    if (!reason?.trim()) return;
    const dueDate = window.prompt(
      '請輸入補件期限（YYYY-MM-DD，可留白）：',
      selected.revision_due_at?.slice(0, 10) ?? '',
    );
    if (dueDate === null) return;
    const trimmedDueDate = dueDate.trim();
    if (trimmedDueDate && !/^\d{4}-\d{2}-\d{2}$/.test(trimmedDueDate)) {
      notify('補件期限格式必須是 YYYY-MM-DD。', 'error');
      return;
    }
    setSaving(true);
    try {
      const revisionDueAt = trimmedDueDate ? `${trimmedDueDate}T23:59:59.999Z` : null;
      const result = await api.submissions.requestRevision(selected.id, reason, revisionDueAt);
      setData({ submissions: replaceSubmission(submissions, result) });
      notify('已要求廠商補件。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const returnSubmission = async () => {
    if (!selected) return;
    if (!returnReason.trim()) {
      notify('退件原因為必填欄位。', 'error');
      return;
    }
    if (!returnItemCodes.length) {
      notify('請至少選擇一個退件項目。', 'error');
      return;
    }
    if (returnDueDate && !/^\d{4}-\d{2}-\d{2}$/.test(returnDueDate)) {
      notify('退件期限格式必須是 YYYY-MM-DD。', 'error');
      return;
    }
    if (!window.confirm('確定要退回此新品提案修改嗎？退件後廠商可依指定項目修正並重新提交。'))
      return;
    setSaving(true);
    try {
      const result = await api.submissions.returnSubmission(selected.id, {
        reason: returnReason.trim(),
        item_codes: returnItemCodes,
        revision_due_at: returnDueDate ? `${returnDueDate}T23:59:59.999Z` : null,
      });
      setData({ submissions: replaceSubmission(submissions, result) });
      setReturnReason('');
      setReturnItemCodes([]);
      notify('新品提案已退回廠商修改。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const moveStatus = async (status: string) => {
    if (!selected) return;
    const reason = status === 'MEETING_REVIEW' ? '資料完成，進入會議審議。' : '';
    setSaving(true);
    try {
      const result = await api.submissions.status(selected.id, status, reason);
      setData({ submissions: replaceSubmission(submissions, result) });
      notify('新品狀態已更新。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="BUYER WORKBENCH"
        title="採購審核"
        description="確認必填資料與附件，留下只供內部看的採購意見、風險與補件要求。"
        actions={
          <Link to="/compare">
            <Button variant="secondary">比較新品</Button>
          </Link>
        }
      />
      {loading ? (
        <Loading />
      ) : (
        <div className="grid gap-6 xl:grid-cols-[360px_1fr]">
          <Card className="h-fit xl:sticky xl:top-24">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-extrabold">待處理清單</h2>
              <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">
                {filteredSubmissions.length}
              </span>
            </div>
            <Select
              className="mb-4"
              label="商品分類"
              value={productCategoryCode}
              onChange={(event) => {
                setProductCategoryCode(event.target.value);
                setProductSubcategoryFilter('');
                setUnclassifiedOnly(false);
              }}
            >
              <option value="">全部商品分類</option>
              {productCategories.map((category) => (
                <option key={category.code} value={category.code}>
                  {formatProductCategory(category.code, category.name)}
                </option>
              ))}
            </Select>
            <Select
              className="mb-4"
              label="商品子分類"
              value={productSubcategoryFilter}
              onChange={(event) => {
                setProductSubcategoryFilter(event.target.value);
                setUnclassifiedOnly(false);
              }}
              disabled={!productCategoryCode}
            >
              <option value="">全部商品子分類</option>
              {filterSubcategories.map((subcategory) => (
                <option key={subcategory.code} value={subcategory.code}>
                  {subcategory.code}｜{subcategory.name}
                </option>
              ))}
            </Select>
            <label className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                className="accent-teal-600"
                checked={unclassifiedOnly}
                onChange={(event) => {
                  setUnclassifiedOnly(event.target.checked);
                  if (event.target.checked) setProductSubcategoryFilter('');
                }}
              />
              只看待分類新品
            </label>
            {batchIds.length ? (
              <div className="mb-4 rounded-2xl border border-teal-100 bg-teal-50 p-3">
                <p className="text-sm font-bold text-teal-900">已勾選 {batchIds.length} 筆新品</p>
                {batchCategoryCode ? (
                  <>
                    <div className="mt-3">
                      <ProductSubcategorySelect
                        subcategories={batchSubcategories}
                        value={batchSubcategoryCode}
                        onChange={setBatchSubcategoryCode}
                      />
                    </div>
                    <Button className="mt-3 w-full" onClick={batchAssign} loading={batchSaving}>
                      批次指定商品子分類
                    </Button>
                  </>
                ) : (
                  <p className="mt-2 text-xs font-semibold text-rose-700">
                    所選商品的商品分類不同，無法批次指定商品子分類。
                  </p>
                )}
              </div>
            ) : null}
            <div className="mb-4">
              <Input
                aria-label="搜尋新品"
                placeholder="搜尋商品、廠商或活動"
                onChange={() => undefined}
              />
            </div>
            <div className="space-y-2">
              {filteredSubmissions.length === 0 ? (
                <EmptyState title="沒有新品資料" />
              ) : (
                filteredSubmissions.map((submission) => (
                  <button
                    key={submission.id}
                    type="button"
                    className={`w-full rounded-2xl border p-3 text-left transition ${selectedId === submission.id ? 'border-teal-300 bg-teal-50' : 'border-slate-100 hover:border-teal-200'}`}
                    onClick={() => setSelectedId(submission.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <input
                        type="checkbox"
                        className="mt-1 accent-teal-600"
                        checked={batchIds.includes(submission.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          event.stopPropagation();
                          setBatchIds((current) =>
                            event.target.checked
                              ? [...current, submission.id]
                              : current.filter((id) => id !== submission.id),
                          );
                        }}
                        aria-label={`勾選${submissionLabel(submission)}`}
                      />
                      <span className="min-w-0 font-bold text-ink">
                        {submissionLabel(submission)}
                      </span>
                      <StatusBadge status={submission.status} />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {submission.vendor_name} · {submission.round_name}
                    </p>
                    {submission.missing_items?.length ? (
                      <p className="mt-2 text-xs font-semibold text-orange-700">
                        缺件 {submission.missing_items.length} 項
                      </p>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </Card>
          {selected ? (
            <div className="space-y-6">
              <Card>
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-teal-700">
                      {selected.vendor_name}
                    </p>
                    <h2 className="mt-1 text-2xl font-extrabold">{submissionLabel(selected)}</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      {selected.round_name} · {productCategoryDisplay(selected.product)} · 提交於{' '}
                      <DateText value={selected.submitted_at} withTime />
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusBadge status={selected.status} />
                    {selected.status === 'BUYER_REVIEW' ||
                    selected.status === 'VENDOR_SUBMITTED' ? (
                      <Button variant="secondary" onClick={requestRevision} loading={saving}>
                        要求補件
                      </Button>
                    ) : null}
                    {selected.status === 'BUYER_REVIEW' ? (
                      <Button onClick={() => moveStatus('MEETING_REVIEW')} loading={saving}>
                        進入會議
                      </Button>
                    ) : null}
                  </div>
                </div>
                {selected.missing_items?.length ? (
                  <div className="mt-5 rounded-2xl border border-orange-200 bg-orange-50 p-4">
                    <p className="font-bold text-orange-900">流程阻擋：尚缺必要資料</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selected.missing_items.map((item) => (
                        <span
                          key={item}
                          className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-orange-800"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-orange-800">
                      補齊後，後端才會允許進入下一個狀態。
                    </p>
                  </div>
                ) : (
                  <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
                    必要資料已齊全，可依流程繼續審核。
                  </div>
                )}
              </Card>
              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">廠商與提案來源</h3>
                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <Info label="廠商名稱" value={selected.vendor_name} />
                    <Info label="廠商代碼" value={selected.vendor_code} />
                    <Info label="統一編號" value={selected.vendor_tax_id} />
                    <Info label="聯絡人" value={selected.vendor_contact_name} />
                    <Info label="聯絡電話" value={selected.vendor_contact_phone} />
                    <Info label="聯絡 Email" value={selected.vendor_contact_email} />
                    <Info label="公司地址" value={selected.vendor_address} />
                    <Info
                      label="提案來源"
                      value={
                        FORMAL_SUBMISSION_SOURCE_LABELS[selected.source_type ?? 'OTHER'] ??
                        selected.source_type ??
                        '其他來源'
                      }
                    />
                    <Info label="報價版本" value={selected.proposal_version ?? 'V1'} />
                    {selected.source_proposal_id ? (
                      <div className="rounded-xl bg-slate-50 p-3">
                        <p className="text-xs text-slate-500">原始主動提案</p>
                        <Link
                          className="mt-1 block break-all font-bold text-teal-700 hover:underline"
                          to={`/proactive/${selected.source_proposal_id}`}
                        >
                          查看原始提案
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </Card>
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">審核摘要</h3>
                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <Info label="提案編號" value={selected.id} />
                    <Info label="活動分類" value={selected.round_category} />
                    <Info
                      label="提交時間"
                      value={<DateText value={selected.submitted_at} withTime />}
                    />
                    <Info
                      label="最後更新"
                      value={<DateText value={selected.updated_at} withTime />}
                    />
                    <Info
                      label="缺件數量"
                      value={
                        selected.missing_items?.length
                          ? `${selected.missing_items.length} 項`
                          : '無'
                      }
                    />
                    <Info label="目前風險" value={selected.internal_risk_level || '未標示'} />
                  </div>
                </Card>
              </div>
              {['BUYER_REVIEW', 'VENDOR_SUBMITTED'].includes(selected.status) ? (
                <Card className="border-rose-200 bg-rose-50">
                  <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
                    <div>
                      <h3 className="text-lg font-extrabold text-rose-950">退件設定</h3>
                      <p className="mt-1 text-sm leading-6 text-rose-900">
                        退件與「要求補件」分開記錄；請指定廠商需要修正的項目，送出後會保留本次審核歷史。
                      </p>
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-rose-700">
                      退回後廠商可重新提交
                    </span>
                  </div>
                  <Textarea
                    className="mt-4"
                    label="退件原因"
                    required
                    value={returnReason}
                    onChange={(event) => setReturnReason(event.target.value)}
                    placeholder="請說明本次退件需要廠商修正的原因。"
                    maxLength={2000}
                  />
                  <div className="mt-4 rounded-2xl bg-white p-4">
                    <p className="text-sm font-bold text-slate-800">退件項目（至少選一項）</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {Object.entries(REVIEW_RETURN_ITEM_LABELS).map(([code, label]) => (
                        <label
                          key={code}
                          className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2 text-sm font-semibold text-slate-700"
                        >
                          <input
                            type="checkbox"
                            className="accent-rose-600"
                            checked={returnItemCodes.includes(code)}
                            onChange={(event) =>
                              setReturnItemCodes((current) =>
                                event.target.checked
                                  ? [...current, code]
                                  : current.filter((item) => item !== code),
                              )
                            }
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end">
                    <Input
                      label="修正期限（可選）"
                      type="date"
                      value={returnDueDate}
                      onChange={(event) => setReturnDueDate(event.target.value)}
                    />
                    <Button variant="danger" onClick={returnSubmission} loading={saving}>
                      退回廠商修改
                    </Button>
                  </div>
                </Card>
              ) : null}
              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">商品與報價</h3>
                  <div className="mb-5 grid gap-4 rounded-2xl border border-teal-100 bg-teal-50 p-4 md:grid-cols-2">
                    <ProductCategorySelect
                      categories={productCategories}
                      value={reviewCategoryCode}
                      currentName={selected.product?.product_category_name ?? ''}
                      onChange={(value) => {
                        setReviewCategoryCode(value);
                        setReviewSubcategoryCode('');
                        notify('商品分類已變更，請重新指定商品子分類。', 'info');
                      }}
                    />
                    <ProductSubcategorySelect
                      subcategories={productSubcategories}
                      value={reviewSubcategoryCode}
                      currentName={selected.product?.product_subcategory_name ?? ''}
                      disabled={!reviewCategoryCode}
                      onChange={setReviewSubcategoryCode}
                    />
                    {!reviewSubcategoryCode && reviewCategoryCode ? (
                      <p className="text-sm font-semibold text-rose-700 md:col-span-2">
                        請先指定商品子分類。
                      </p>
                    ) : null}
                    {classificationChanged ? (
                      <div className="md:col-span-2">
                        <Textarea
                          label="修改原因"
                          required={['MEETING_REVIEW', 'APPROVED', 'MASTER_CREATED'].includes(
                            selected.status,
                          )}
                          value={classificationReason}
                          onChange={(event) => setClassificationReason(event.target.value)}
                          placeholder="請說明商品分類或商品子分類異動原因。"
                        />
                      </div>
                    ) : null}
                  </div>
                  {selected.product ? (
                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <Info label="廠商商品編號" value={selected.product.vendor_product_code} />
                      <Info label="商品中文名稱" value={selected.product.name_zh} />
                      <Info label="商品英文名稱" value={selected.product.name_en} />
                      <Info
                        label="品牌 / 製造商"
                        value={`${selected.product.brand || '—'} / ${selected.product.manufacturer || '—'}`}
                      />
                      <Info label="原產地" value={selected.product.origin} />
                      <Info
                        label="規格 / 單位"
                        value={`${selected.product.specification || '—'} / ${selected.product.unit || '—'}`}
                      />
                      <Info label="箱入數" value={selected.product.case_pack ?? '—'} />
                      <Info label="商品分類" value={productCategoryDisplay(selected.product)} />
                      <Info
                        label="商品子分類"
                        value={
                          selected.product.product_subcategory_code
                            ? `${selected.product.product_subcategory_code}｜${selected.product.product_subcategory_name}`
                            : '尚未指定'
                        }
                      />
                      <Info
                        label="條碼"
                        value={
                          [
                            ...selected.product.barcodes,
                            selected.product.outer_barcode,
                            selected.product.inner_barcode,
                          ]
                            .filter(Boolean)
                            .join('、') || '—'
                        }
                      />
                      <Info
                        label="條碼聲明"
                        value={
                          selected.product.no_international_barcode
                            ? `無國際條碼：${selected.product.no_international_barcode_reason || '未填原因'}`
                            : '有國際條碼'
                        }
                      />
                      <Info
                        label="保存方式"
                        value={selected.product.storage_method || selected.product.storage}
                      />
                      <Info label="保存期限" value={selected.product.shelf_life} />
                      <Info
                        label="預計上市日期"
                        value={
                          selected.product.expected_launch_date ||
                          selected.product.planned_launch_date
                        }
                      />
                      <Info
                        label="未稅進價"
                        value={<Money value={selected.product.ex_tax_price} />}
                      />
                      <Info
                        label="含稅進價"
                        value={<Money value={selected.product.tax_included_price} />}
                      />
                      <Info
                        label="建議售價"
                        value={<Money value={selected.product.suggested_retail_price} />}
                      />
                      <Info label="稅率" value={`${selected.product.tax_rate ?? '—'}%`} />
                      <Info
                        label="最低訂購量"
                        value={selected.product.minimum_order_quantity ?? '—'}
                      />
                      <Info label="最小配送單位" value={selected.product.minimum_delivery_unit} />
                      <Info
                        label="最低訂購金額"
                        value={<Money value={selected.product.minimum_order_amount} />}
                      />
                      <Info label="交貨天數" value={selected.product.delivery_days ?? '—'} />
                      <Info
                        label="預估毛利率"
                        value={
                          selected.product.estimated_margin_rate === null ||
                          selected.product.estimated_margin_rate === undefined
                            ? '—'
                            : `${(selected.product.estimated_margin_rate * 100).toFixed(2)}%`
                        }
                      />
                    </div>
                  ) : (
                    <EmptyState title="廠商尚未填寫商品資料" />
                  )}
                </Card>
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">附件</h3>
                  {selected.attachments?.length ? (
                    <div className="space-y-2">
                      {selected.attachments.map((attachment) => (
                        <a
                          key={attachment.id}
                          href={api.attachments.downloadUrl(attachment.id)}
                          className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm hover:bg-teal-50"
                        >
                          <span className="min-w-0 truncate font-semibold">
                            {ATTACHMENT_LABELS[attachment.type] ?? attachment.type} ·{' '}
                            {attachment.original_name}
                          </span>
                          <span className="ml-3 shrink-0 text-xs text-teal-700">下載</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <EmptyState title="尚未上傳附件" description="請在補件要求中提醒廠商上傳。" />
                  )}
                </Card>
              </div>
              {selected.product ? (
                <div className="grid gap-6 lg:grid-cols-2">
                  <Card>
                    <h3 className="mb-4 text-lg font-extrabold">商品資訊與銷售／上市提案</h3>
                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <Info label="商品特色" value={selected.product.product_features} />
                      <Info label="成分" value={selected.product.ingredients} />
                      <Info label="使用方式" value={selected.product.usage} />
                      <Info label="注意事項" value={selected.product.precautions} />
                      <Info label="適用客群" value={selected.product.target_customers} />
                      <Info label="建議陳列位置" value={selected.product.display_location} />
                      <Info label="商品生命週期" value={selected.product.product_lifecycle} />
                      <Info label="效期要求" value={selected.product.expiry_requirement} />
                      <Info label="預計上市日期" value={selected.product.planned_launch_date} />
                    </div>
                  </Card>
                  <Card>
                    <h3 className="mb-4 text-lg font-extrabold">優惠與支援內容</h3>
                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <Info
                        label="首批優惠"
                        value={
                          selected.product.initial_order_offer_content ||
                          (selected.product.initial_order_offer_enabled ? '有' : '無')
                        }
                      />
                      <Info
                        label="贈品及搭贈"
                        value={
                          selected.product.gift_bundle_support_content ||
                          (selected.product.gift_bundle_support_enabled ? '有' : '無')
                        }
                      />
                      <Info
                        label="促銷支援"
                        value={
                          selected.product.promotion_support_enabled
                            ? `${selected.product.promotion_content || '有'}${selected.product.promotion_start_date ? `（${selected.product.promotion_start_date}～${selected.product.promotion_end_date || '未設定'}）` : ''}`
                            : '無'
                        }
                      />
                      <Info
                        label="試用／體驗支援"
                        value={
                          selected.product.trial_support_enabled
                            ? selected.product.trial_support_content || '有'
                            : '無'
                        }
                      />
                    </div>
                  </Card>
                </div>
              ) : null}
              {selected.product ? (
                <Card>
                  <h3 className="mb-4 text-lg font-extrabold">廠商交易條件 Snapshot</h3>
                  <p className="mb-4 text-sm text-slate-600">
                    正式提交時保存的交易條件版本，供本次審核與歷史追溯使用。
                  </p>
                  <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <Info
                      label="資料來源"
                      value={
                        selected.product.trade_terms?.source_type === 'VENDOR_APPLICATION'
                          ? '新廠商申請'
                          : selected.product.trade_terms?.source_type === 'VENDOR_MASTER'
                            ? '廠商主檔'
                            : selected.product.trade_terms?.source_type === 'PRODUCT_SPECIAL'
                              ? '本商品特殊條件'
                              : '尚無 Snapshot'
                      }
                    />
                    <Info
                      label="付款條件"
                      value={displayProductTradeTerm(selected.product.trade_terms?.payment_term)}
                    />
                    <Info
                      label="月結天數"
                      value={selected.product.trade_terms?.monthly_payment_days ?? '—'}
                    />
                    <Info
                      label="付款方式"
                      value={displayProductTradeTerm(selected.product.trade_terms?.payment_method)}
                    />
                    <Info
                      label="退貨地點"
                      value={displayProductTradeTerm(selected.product.trade_terms?.return_scope)}
                    />
                    <Info
                      label="退換貨方式"
                      value={displayProductTradeTerm(
                        selected.product.trade_terms?.return_handling_method,
                      )}
                    />
                    <Info
                      label="退換貨成立條件"
                      value={displayProductTradeTerm(
                        selected.product.trade_terms?.return_conditions,
                      )}
                    />
                    <Info
                      label="退換貨運費負擔"
                      value={displayProductTradeTerm(
                        selected.product.trade_terms?.return_freight_responsibility,
                      )}
                    />
                    <div className="sm:col-span-2 lg:col-span-4">
                      <Info
                        label="退換貨詳細說明"
                        value={selected.product.trade_terms?.return_description}
                      />
                    </div>
                  </div>
                </Card>
              ) : null}
              <Card>
                <h3 className="mb-4 text-lg font-extrabold">採購審核表</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm font-semibold">
                    <input
                      type="checkbox"
                      className="mt-1 accent-teal-600"
                      checked={review.required_fields_checked}
                      onChange={(event) =>
                        setReview({ ...review, required_fields_checked: event.target.checked })
                      }
                    />
                    必填欄位已檢查
                  </label>
                  <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm font-semibold">
                    <input
                      type="checkbox"
                      className="mt-1 accent-teal-600"
                      checked={review.attachments_checked}
                      onChange={(event) =>
                        setReview({ ...review, attachments_checked: event.target.checked })
                      }
                    />
                    附件已檢查
                  </label>
                  <Select
                    label="風險標示"
                    value={review.risk_level}
                    onChange={(event) => setReview({ ...review, risk_level: event.target.value })}
                  >
                    <option value="">未標示</option>
                    <option value="LOW">低風險</option>
                    <option value="MEDIUM">中風險</option>
                    <option value="HIGH">高風險</option>
                  </Select>
                </div>
                <Textarea
                  className="mt-4"
                  label="內部意見（廠商不可見）"
                  value={review.internal_comment}
                  onChange={(event) =>
                    setReview({ ...review, internal_comment: event.target.value })
                  }
                  placeholder="記錄價格、品質、陳列或採購判斷。"
                />
                <div className="mt-4 flex justify-end">
                  <Button onClick={saveReview} loading={saving}>
                    儲存審核
                  </Button>
                </div>
              </Card>
              {selected.product?.trade_terms_draft &&
              normalizeSpecialTradeTermsFlag(
                selected.product.trade_terms_draft.special_trade_terms,
              ) ? (
                <Card>
                  <h3 className="mb-2 text-lg font-extrabold">本商品特殊交易條件</h3>
                  <p className="text-sm text-slate-600">
                    目前狀態：
                    {selected.product.trade_terms_draft.special_status === 'PENDING_BUYER_REVIEW'
                      ? '待採購確認'
                      : selected.product.trade_terms_draft.special_status}
                  </p>
                  <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                    <Info
                      label="特殊付款條件"
                      value={displayProductTradeTerm(
                        selected.product.trade_terms_draft.special_payment_term,
                      )}
                    />
                    <Info
                      label="特殊付款方式"
                      value={displayProductTradeTerm(
                        selected.product.trade_terms_draft.special_payment_method,
                      )}
                    />
                    <Info
                      label="特殊退換貨條件"
                      value={displayProductTradeTerm(
                        selected.product.trade_terms_draft.special_return_conditions,
                      )}
                    />
                    <Info
                      label="特殊條件原因"
                      value={selected.product.trade_terms_draft.special_reason}
                    />
                    <Info
                      label="特殊退換貨說明"
                      value={selected.product.trade_terms_draft.special_return_description}
                    />
                  </div>
                  <Textarea
                    className="mt-4"
                    label="審核意見／要求補件原因"
                    value={tradeTermsReason}
                    onChange={(event) => setTradeTermsReason(event.target.value)}
                  />
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => void reviewSpecialTradeTerms('REJECT')}
                      loading={tradeTermsSaving}
                    >
                      拒絕特殊條件，沿用標準條件
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => void reviewSpecialTradeTerms('REQUEST_REVISION')}
                      loading={tradeTermsSaving}
                    >
                      要求廠商重新提出
                    </Button>
                    <Button
                      onClick={() => void reviewSpecialTradeTerms('ACCEPT')}
                      loading={tradeTermsSaving}
                    >
                      接受特殊條件
                    </Button>
                  </div>
                </Card>
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="請選擇要審核的新品"
              description="左側選擇一筆填報資料後查看詳細內容。"
            />
          )}
        </div>
      )}
    </>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap break-words font-bold text-ink">{value || '—'}</p>
    </div>
  );
}

export function StoreReviewPage({ notify }: PageProps) {
  const { data, loading, setData } = useAsyncLoad(() => api.submissions.list(), [], notify);
  const submissions = data?.submissions ?? [];
  const [selectedId, setSelectedId] = useState('');
  const [scores, setScores] = useState({
    attractiveness: 0,
    price_acceptance: 0,
    customer_fit: 0,
    display_feasibility: 0,
    sales_potential: 0,
    comment: '',
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setSelectedId((current) => current || submissions[0]?.id || '');
  }, [submissions]);
  const selected = submissions.find((item) => item.id === selectedId) ?? null;
  useEffect(() => {
    const existing = selected?.store_reviews?.[0];
    setScores(
      existing
        ? {
            attractiveness: existing.attractiveness,
            price_acceptance: existing.price_acceptance,
            customer_fit: existing.customer_fit,
            display_feasibility: existing.display_feasibility,
            sales_potential: existing.sales_potential,
            comment: existing.comment,
          }
        : {
            attractiveness: 0,
            price_acceptance: 0,
            customer_fit: 0,
            display_feasibility: 0,
            sales_potential: 0,
            comment: '',
          },
    );
  }, [selectedId, selected?.store_reviews]);
  const save = async () => {
    if (
      !selected ||
      Object.values(scores)
        .slice(0, 5)
        .some((value) => value === 0)
    ) {
      notify('請完成五項 1–5 分評分。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.submissions.storeReview(selected.id, scores as any);
      setData({ submissions: replaceSubmission(submissions, result) });
      notify('門市評分已儲存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="STORE VOICE"
        title="門市評分"
        description="依實際陳列與客群經驗評估新品。每個評分欄位都會保留在 D1，並計算平均分數。"
      />
      <div className="grid gap-6 xl:grid-cols-[360px_1fr]">
        {' '}
        <Card className="h-fit xl:sticky xl:top-24">
          <h2 className="mb-4 text-lg font-extrabold">待評估新品</h2>
          {loading ? (
            <Loading />
          ) : (
            <div className="space-y-2">
              {submissions.map((submission) => (
                <button
                  type="button"
                  key={submission.id}
                  onClick={() => setSelectedId(submission.id)}
                  className={`w-full rounded-2xl border p-3 text-left ${selectedId === submission.id ? 'border-teal-300 bg-teal-50' : 'border-slate-100 hover:border-teal-200'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-bold">{submissionLabel(submission)}</span>
                    <span className="text-amber-500">
                      {submission.store_average_score
                        ? `★ ${submission.store_average_score}`
                        : '未評'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{submission.vendor_name}</p>
                </button>
              ))}
            </div>
          )}
        </Card>
        {selected ? (
          <Card>
            <div className="flex flex-col justify-between gap-4 border-b border-slate-100 pb-5 md:flex-row md:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-teal-700">
                  {selected.vendor_name}
                </p>
                <h2 className="mt-1 text-2xl font-extrabold">{submissionLabel(selected)}</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {selected.product?.specification || '尚未填寫規格'} ·{' '}
                  <Money value={selected.product?.suggested_retail_price} />
                </p>
              </div>
              <StatusBadge status={selected.status} />
            </div>
            <div className="grid gap-5 py-6 md:grid-cols-2">
              <RatingInput
                label="商品吸引力"
                value={scores.attractiveness}
                onChange={(value) => setScores({ ...scores, attractiveness: value })}
              />
              <RatingInput
                label="售價接受度"
                value={scores.price_acceptance}
                onChange={(value) => setScores({ ...scores, price_acceptance: value })}
              />
              <RatingInput
                label="客群適合度"
                value={scores.customer_fit}
                onChange={(value) => setScores({ ...scores, customer_fit: value })}
              />
              <RatingInput
                label="陳列可行性"
                value={scores.display_feasibility}
                onChange={(value) => setScores({ ...scores, display_feasibility: value })}
              />
              <RatingInput
                label="預估銷售性"
                value={scores.sales_potential}
                onChange={(value) => setScores({ ...scores, sales_potential: value })}
              />
            </div>
            <Textarea
              label="門市意見"
              value={scores.comment}
              onChange={(event) => setScores({ ...scores, comment: event.target.value })}
              placeholder="例如：適合收銀台旁小型陳列，建議搭配試吃活動。"
            />
            <div className="mt-5 flex justify-end">
              <Button onClick={save} loading={saving}>
                儲存門市評分
              </Button>
            </div>
            <div className="mt-8 border-t border-slate-100 pt-5">
              <h3 className="text-lg font-extrabold">其他門市意見</h3>
              {selected.store_reviews?.length ? (
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {selected.store_reviews.map((review) => (
                    <div className="rounded-xl bg-slate-50 p-3" key={review.id}>
                      <div className="flex justify-between">
                        <strong className="text-sm">{review.store_name}</strong>
                        <span className="text-sm font-bold text-amber-600">
                          {(
                            (review.attractiveness +
                              review.price_acceptance +
                              review.customer_fit +
                              review.display_feasibility +
                              review.sales_potential) /
                            5
                          ).toFixed(1)}{' '}
                          / 5
                        </span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600">
                        {review.comment || '未留下文字意見'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-slate-500">尚無其他門市評分。</p>
              )}
            </div>
          </Card>
        ) : (
          <EmptyState title="請選擇要評估的新品" />
        )}
      </div>
    </>
  );
}

export function ComparePage({ notify }: PageProps) {
  const { data, loading } = useAsyncLoad(() => api.proactive.list(), [], notify);
  const proposals = data?.proposals ?? [];
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [vendor, setVendor] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [productSubcategories, setProductSubcategories] = useState<ProductSubcategory[]>([]);
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('updated');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  useEffect(() => {
    api.productCategories
      .publicList()
      .then((result) => setProductCategories(result.categories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, []);
  useEffect(() => {
    setSubcategory('');
    if (!category) {
      setProductSubcategories([]);
      return;
    }
    api.productSubcategories
      .publicList(category)
      .then((result) => setProductSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [category, notify]);
  const vendors = useMemo(
    () => [...new Set(proposals.map((item) => item.vendor_name).filter(Boolean))] as string[],
    [proposals],
  );
  const categories = useMemo(() => productCategories, [productCategories]);
  const filtered = useMemo(
    () =>
      proposals
        .filter(
          (item) =>
            (!vendor || item.vendor_name === vendor) &&
            (!category || item.product?.product_category_code === category) &&
            (!subcategory || item.product?.product_subcategory_code === subcategory) &&
            (!status || item.status === status),
        )
        .sort((left, right) => {
          if (sort === 'cost')
            return (
              (left.product?.ex_tax_price ?? Infinity) - (right.product?.ex_tax_price ?? Infinity)
            );
          if (sort === 'retail')
            return (
              (left.product?.suggested_retail_price ?? Infinity) -
              (right.product?.suggested_retail_price ?? Infinity)
            );
          if (sort === 'margin')
            return (
              (right.product?.estimated_margin_rate ?? -Infinity) -
              (left.product?.estimated_margin_rate ?? -Infinity)
            );
          return right.updated_at.localeCompare(left.updated_at);
        }),
    [proposals, vendor, category, subcategory, status, sort],
  );
  const selected = filtered.filter((item) => selectedIds.includes(item.id));
  const toggleSelect = (id: string) =>
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : current.length < 4
          ? [...current, id]
          : current,
    );
  return (
    <>
      <PageHeader
        eyebrow="DECISION SUPPORT"
        title="新品比較"
        description="依廠商、分類、狀態與成本指標篩選，選取最多四項並排比較；適合桌面或投影開會。"
        actions={
          <Link to="/meeting">
            <Button>進入會議模式</Button>
          </Link>
        }
      />
      <Card className="no-print mb-6">
        <div className="grid gap-3 md:grid-cols-5">
          <Select label="廠商" value={vendor} onChange={(event) => setVendor(event.target.value)}>
            <option value="">全部廠商</option>
            {vendors.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
          <Select
            label="商品子分類"
            value={subcategory}
            onChange={(event) => setSubcategory(event.target.value)}
            disabled={!category}
          >
            <option value="">全部子分類</option>
            {productSubcategories.map((item) => (
              <option key={item.code} value={item.code}>
                {item.code}｜{item.name}
              </option>
            ))}
          </Select>
          <Select
            label="商品分類"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="">全部分類</option>
            {categories.map((item) => (
              <option key={item.code} value={item.code}>
                {formatProductCategory(item.code, item.name)}
              </option>
            ))}
          </Select>
          <Select label="狀態" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部狀態</option>
            {PROACTIVE_ACTIVE_STATUS_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {PROACTIVE_STATUS_LABELS[key]}
              </option>
            ))}
          </Select>
          <Select label="排序" value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="updated">最近更新</option>
            <option value="cost">未稅進價低到高</option>
            <option value="retail">建議售價低到高</option>
            <option value="margin">預估毛利率高到低</option>
          </Select>
        </div>
      </Card>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              顯示 {filtered.length} 筆 · 已選 {selected.length} 筆（最多 4 筆）
            </p>
            <Button variant="secondary" onClick={() => window.print()}>
              列印比較表
            </Button>
          </div>
          <Card className="p-0">
            <Table>
              <thead>
                <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                  <th className="px-4 py-3">選取</th>
                  <th className="px-4 py-3">商品</th>
                  <th className="px-4 py-3">廠商</th>
                  <th className="px-4 py-3">狀態</th>
                  <th className="px-4 py-3">未稅進價</th>
                  <th className="px-4 py-3">建議售價</th>
                  <th className="px-4 py-3">毛利率</th>
                  <th className="px-4 py-3">缺件</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-teal-600"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        aria-label={`選取${item.product?.name_zh || item.proposal_no}`}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <p className="max-w-[220px] whitespace-normal break-words font-bold text-ink">
                        {item.product?.name_zh || item.proposal_no}
                      </p>
                      <p className="mt-1 max-w-[220px] whitespace-normal break-words text-xs text-slate-500">
                        {item.product?.specification || '—'} ·{' '}
                        {productCategoryDisplay(item.product)}
                      </p>
                      <p className="mt-1 max-w-[220px] whitespace-normal break-words text-xs text-slate-500">
                        子分類：
                        {item.product?.product_subcategory_code
                          ? `${item.product.product_subcategory_code}｜${item.product.product_subcategory_name}`
                          : '未設定'}
                      </p>
                    </td>
                    <td className="max-w-[160px] whitespace-normal break-words px-4 py-4 text-slate-600">
                      {item.vendor_name}
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-4">
                      <Money value={item.product?.ex_tax_price} />
                    </td>
                    <td className="px-4 py-4">
                      <Money value={item.product?.suggested_retail_price} />
                    </td>
                    <td className="px-4 py-4 font-bold text-teal-700">
                      {item.product?.estimated_margin_rate == null
                        ? '—'
                        : `${(item.product.estimated_margin_rate * 100).toFixed(1)}%`}
                    </td>
                    <td className="px-4 py-4">
                      {item.completeness_score < 100 ? (
                        <span className="font-bold text-orange-700">
                          ⚠ {item.completeness_score}%
                        </span>
                      ) : (
                        <span className="text-emerald-600">完整</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          {selected.length > 0 && (
            <Card className="mt-6 border-teal-100 bg-white">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-extrabold">並排比較</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    長文字會自動換行，投影時可使用瀏覽器全螢幕。
                  </p>
                </div>
                <Button variant="ghost" onClick={() => setSelectedIds([])}>
                  清除選取
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {selected.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 p-4"
                  >
                    <div className="mb-4 flex items-start justify-between gap-2">
                      <h3 className="break-words text-lg font-extrabold">
                        {item.product?.name_zh || item.proposal_no}
                      </h3>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-sm text-slate-500">{item.vendor_name}</p>
                    <dl className="mt-4 space-y-3 text-sm">
                      <Info label="商品分類" value={productCategoryDisplay(item.product)} />
                      <Info
                        label="商品子分類"
                        value={
                          item.product?.product_subcategory_code
                            ? `${item.product.product_subcategory_code}｜${item.product.product_subcategory_name}`
                            : '未設定'
                        }
                      />
                      <Info label="規格" value={item.product?.specification} />
                      <Info label="未稅進價" value={<Money value={item.product?.ex_tax_price} />} />
                      <Info
                        label="建議售價"
                        value={<Money value={item.product?.suggested_retail_price} />}
                      />
                      <Info
                        label="預估毛利率"
                        value={
                          item.product?.estimated_margin_rate == null
                            ? '—'
                            : `${(item.product.estimated_margin_rate * 100).toFixed(1)}%`
                        }
                      />
                      <Info
                        label="提案來源"
                        value={
                          FORMAL_SUBMISSION_SOURCE_LABELS[item.proposal_source ?? ''] ||
                          '廠商主動提案'
                        }
                      />
                    </dl>
                    {item.completeness_score < 100 ? (
                      <div className="mt-4 rounded-xl bg-orange-100 p-3 text-xs font-semibold text-orange-800">
                        資料完整度：{item.completeness_score}%
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </>
  );
}

export function MeetingPage({ notify }: PageProps) {
  const { data, loading, setData } = useAsyncLoad(
    () => api.proactive.list({ status: 'WAITING_MEETING' }),
    [],
    notify,
  );
  const candidates = data?.proposals ?? [];
  const [selectedId, setSelectedId] = useState('');
  const [selected, setSelected] = useState<VendorProposal | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [decision, setDecision] = useState<MeetingForm>({
    decision: 'APPROVED',
    notes: '',
    revision_due_at: '',
    reconsideration_date: '',
    rejection_reason_code: 'OTHER',
  });
  const [revisionItems, setRevisionItems] = useState<string[]>(['PRODUCT_BASIC']);
  const [saving, setSaving] = useState(false);
  const meetingToday = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  useEffect(() => {
    setSelectedId((current) => current || candidates[0]?.id || '');
  }, [candidates]);
  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    setDetailLoading(true);
    api.proactive
      .detail(selectedId)
      .then(setSelected)
      .catch((error) => notify(errorMessage(error), 'error'))
      .finally(() => setDetailLoading(false));
  }, [selectedId, notify]);
  const decide = async () => {
    if (!selected) return;
    if (decision.decision !== 'APPROVED' && !decision.notes.trim()) {
      notify(
        decision.decision === 'REVISION_REQUIRED'
          ? '請填寫補件說明。'
          : decision.decision === 'PAUSED'
            ? '請填寫暫緩原因。'
            : '請填寫不採用原因。',
        'error',
      );
      return;
    }
    if (decision.decision === 'REVISION_REQUIRED' && !decision.revision_due_at) {
      notify('請選擇補件期限。', 'error');
      return;
    }
    if (decision.decision === 'REVISION_REQUIRED' && !revisionItems.length) {
      notify('請至少選擇一項補件項目。', 'error');
      return;
    }
    if (decision.decision === 'REVISION_REQUIRED' && decision.revision_due_at < meetingToday) {
      notify('補件期限不得早於目前日期。', 'error');
      return;
    }
    setSaving(true);
    try {
      if (decision.decision === 'REVISION_REQUIRED') {
        await api.proactive.requestRevision(selected.id, {
          item_codes: revisionItems,
          explanation: decision.notes,
          due_at: decision.revision_due_at,
        });
      } else {
        await api.proactive.status(selected.id, {
          status: decision.decision,
          reason: decision.decision === 'APPROVED' ? '會議決議上市。' : decision.notes,
          reconsideration_date:
            decision.decision === 'PAUSED' ? decision.reconsideration_date || null : null,
          rejection_reason_code:
            decision.decision === 'NOT_ADOPTED' ? decision.rejection_reason_code : undefined,
          internal_rejection_reason: decision.decision === 'NOT_ADOPTED' ? decision.notes : '',
          vendor_visible_reason: decision.decision === 'NOT_ADOPTED' ? decision.notes : '',
        });
      }
      const refreshed = await api.proactive.list({ status: 'WAITING_MEETING' });
      setData(refreshed);
      setSelected(null);
      setSelectedId('');
      setDecision({
        decision: 'APPROVED',
        notes: '',
        revision_due_at: '',
        reconsideration_date: '',
        rejection_reason_code: 'OTHER',
      });
      notify('會議決議已記錄並更新新品提案狀態。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="min-h-[calc(100vh-8rem)]">
      <PageHeader
        eyebrow="MEETING ROOM"
        title="會議討論模式"
        description="用大字體快速查看候選新品、毛利、交易條件與缺件警示，會議中直接記錄決議。"
        actions={
          <Button variant="secondary" onClick={() => window.print()}>
            列印會議摘要
          </Button>
        }
      />
      {loading ? (
        <Loading />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          {' '}
          <Card className="no-print h-fit">
            <h2 className="mb-3 text-lg font-extrabold">候選新品</h2>
            {candidates.length ? (
              <div className="space-y-2">
                {candidates.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={`w-full rounded-xl p-3 text-left ${selectedId === item.id ? 'bg-ink text-white' : 'bg-slate-50 hover:bg-teal-50'}`}
                  >
                    <p className="font-bold">{item.product?.name_zh || item.proposal_no}</p>
                    <p
                      className={`mt-1 text-xs ${selectedId === item.id ? 'text-white/70' : 'text-slate-500'}`}
                    >
                      {item.vendor_name}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState
                title="尚無會議候選"
                description="從新品提案採購審核選擇「進入會議」後，案件會出現在這裡。"
              />
            )}
          </Card>
          {detailLoading ? (
            <Loading />
          ) : selected ? (
            <div className="space-y-6">
              <Card className="bg-ink text-white">
                <div className="flex flex-col justify-between gap-6 lg:flex-row">
                  <div className="max-w-3xl">
                    <p className="text-sm font-bold uppercase tracking-[0.2em] text-teal-300">
                      {selected.vendor_name} ·{' '}
                      {selected.quotation_round_name ||
                        FORMAL_SUBMISSION_SOURCE_LABELS[selected.proposal_source ?? ''] ||
                        '廠商主動提案'}
                    </p>
                    <h2 className="mt-3 break-words text-4xl font-extrabold leading-tight md:text-6xl">
                      {selected.product?.name_zh || selected.proposal_no}
                    </h2>
                    <p className="mt-4 break-words text-xl text-white/70">
                      {selected.product?.specification || '規格未填寫'}
                    </p>
                    <p className="mt-2 break-words text-base text-white/60">
                      {productCategoryDisplay(selected.product)} · 子分類：
                      {selected.product?.product_subcategory_code
                        ? `${selected.product.product_subcategory_code}｜${selected.product.product_subcategory_name}`
                        : '未設定'}
                    </p>
                  </div>
                  <div className="grid gap-3 text-center lg:w-64">
                    <div className="rounded-2xl bg-white/10 p-4">
                      <p className="text-xs text-white/60">預估毛利率</p>
                      <p className="mt-2 text-3xl font-extrabold text-teal-300">
                        {selected.product?.estimated_margin_rate == null
                          ? '—'
                          : `${(selected.product.estimated_margin_rate * 100).toFixed(1)}%`}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
              <div className="grid gap-6 xl:grid-cols-3">
                <Card>
                  <h3 className="text-xl font-extrabold">商業條件</h3>
                  <div className="mt-4 space-y-3 text-sm">
                    <Info label="商品分類" value={productCategoryDisplay(selected.product)} />
                    <Info
                      label="商品子分類"
                      value={
                        selected.product?.product_subcategory_code
                          ? `${selected.product.product_subcategory_code}｜${selected.product.product_subcategory_name}`
                          : '未設定'
                      }
                    />
                    <Info
                      label="未稅進價"
                      value={<Money value={selected.product?.ex_tax_price} />}
                    />
                    <Info
                      label="建議售價"
                      value={<Money value={selected.product?.suggested_retail_price} />}
                    />
                    <Info label="促銷支援" value={selected.product?.promotion_content || '無'} />
                    <Info
                      label="付款條件"
                      value={labelVendorPaymentTerm(selected.trade_terms_source?.payment_term)}
                    />
                    <Info
                      label="付款方式"
                      value={labelVendorPaymentMethod(selected.trade_terms_source?.payment_method)}
                    />
                  </div>
                </Card>
                <Card>
                  <h3 className="text-xl font-extrabold">審核資訊</h3>
                  <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-7 text-slate-600">
                    {selected.review_history?.at(-1)?.review_note || '尚無其他審核說明'}
                  </p>
                  {selected.missing_items?.length ? (
                    <p className="mt-4 rounded-xl bg-orange-50 p-3 text-sm font-semibold text-orange-800">
                      缺件：{selected.missing_items.join('、')}
                    </p>
                  ) : null}
                </Card>
                <Card>
                  <h3 className="text-xl font-extrabold">記錄會議決議</h3>
                  <div className="mt-4 space-y-4">
                    <Select
                      label="決議"
                      value={decision.decision}
                      onChange={(event) =>
                        setDecision({
                          ...decision,
                          decision: event.target.value as MeetingForm['decision'],
                        })
                      }
                    >
                      <option value="APPROVED">決議上市</option>
                      <option value="REVISION_REQUIRED">要求補件</option>
                      <option value="PAUSED">暫緩</option>
                      <option value="NOT_ADOPTED">不採用</option>
                    </Select>
                    {decision.decision === 'REVISION_REQUIRED' ? (
                      <>
                        <div className="grid gap-2">
                          {Object.entries(PROACTIVE_REVISION_ITEM_LABELS).map(([code, label]) => (
                            <label key={code} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={revisionItems.includes(code)}
                                onChange={() =>
                                  setRevisionItems((current) =>
                                    current.includes(code)
                                      ? current.filter((item) => item !== code)
                                      : [...current, code],
                                  )
                                }
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                        <Input
                          label="補件期限"
                          required
                          type="date"
                          min={meetingToday}
                          value={decision.revision_due_at}
                          onChange={(event) =>
                            setDecision({ ...decision, revision_due_at: event.target.value })
                          }
                        />
                      </>
                    ) : null}
                    {decision.decision === 'PAUSED' ? (
                      <Input
                        label="預計重新評估日期"
                        type="date"
                        min={meetingToday}
                        value={decision.reconsideration_date}
                        onChange={(event) =>
                          setDecision({ ...decision, reconsideration_date: event.target.value })
                        }
                      />
                    ) : null}
                    {decision.decision === 'NOT_ADOPTED' ? (
                      <Select
                        label="不採用原因"
                        value={decision.rejection_reason_code}
                        onChange={(event) =>
                          setDecision({ ...decision, rejection_reason_code: event.target.value })
                        }
                      >
                        <option value="DUPLICATE_PRODUCT">商品重複或已有同類商品</option>
                        <option value="MARGIN_TOO_LOW">毛利不符合採購條件</option>
                        <option value="MARKET_FIT_LOW">市場定位或客群不符</option>
                        <option value="DOCUMENT_INCOMPLETE">文件或法規資料不足</option>
                        <option value="MOQ_UNSUITABLE">最低訂購量或交易條件不合適</option>
                        <option value="DELIVERY_UNSUITABLE">交貨或配送條件不合適</option>
                        <option value="OTHER">其他原因</option>
                      </Select>
                    ) : null}
                    <Textarea
                      label={
                        decision.decision === 'REVISION_REQUIRED'
                          ? '補件說明'
                          : decision.decision === 'PAUSED'
                            ? '暫緩原因'
                            : decision.decision === 'NOT_ADOPTED'
                              ? '不採用原因（廠商可見）'
                              : '決議說明'
                      }
                      required={decision.decision !== 'APPROVED'}
                      value={decision.notes}
                      onChange={(event) => setDecision({ ...decision, notes: event.target.value })}
                      placeholder="記錄會議結論、後續負責人與時間。"
                    />
                    <Button className="w-full" loading={saving} onClick={decide}>
                      儲存決議
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          ) : (
            <EmptyState title="請選擇會議候選新品" />
          )}
        </div>
      )}
    </div>
  );
}

type MeetingForm = {
  decision: 'APPROVED' | 'REVISION_REQUIRED' | 'PAUSED' | 'NOT_ADOPTED';
  notes: string;
  revision_due_at: string;
  reconsideration_date: string;
  rejection_reason_code: string;
};

export function ReportsPage({ notify }: PageProps) {
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [productSubcategoryCode, setProductSubcategoryCode] = useState('');
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [productSubcategories, setProductSubcategories] = useState<ProductSubcategory[]>([]);
  const [vendorId, setVendorId] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [report, vendorResult] = await Promise.all([
        api.reports.list({
          ...(status ? { status } : {}),
          ...(category ? { product_category_code: category } : {}),
          ...(productSubcategoryCode ? { product_subcategory_code: productSubcategoryCode } : {}),
          ...(vendorId ? { vendor_id: vendorId } : {}),
          ...(query ? { q: query } : {}),
        }),
        api.vendors.list().catch(() => ({ vendors: [] })),
      ]);
      setRows(report.rows);
      setVendors(vendorResult.vendors);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    api.productCategories
      .publicList()
      .then((result) => setProductCategories(result.categories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, []);
  useEffect(() => {
    setProductSubcategoryCode('');
    if (!category) {
      setProductSubcategories([]);
      return;
    }
    api.productSubcategories
      .publicList(category)
      .then((result) => setProductSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [category, notify]);
  const params = {
    ...(status ? { status } : {}),
    ...(category ? { product_category_code: category } : {}),
    ...(productSubcategoryCode ? { product_subcategory_code: productSubcategoryCode } : {}),
    ...(vendorId ? { vendor_id: vendorId } : {}),
    ...(query ? { q: query } : {}),
  };
  const exportJson = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '新品報價報表.json';
    link.click();
    URL.revokeObjectURL(url);
  };
  const exportExcel = () => {
    const headers = [
      '報價活動',
      '商品分類代碼',
      '商品分類名稱',
      '商品子分類代碼',
      '商品子分類名稱',
      '廠商',
      '商品名稱',
      '規格',
      '未稅進價',
      '建議售價',
      '預估毛利率',
      '狀態',
      '缺件數',
    ];
    const escape = (value: unknown) =>
      String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
    const html = `<html><meta charset="utf-8"><table><tr>${headers.map((header) => `<th>${escape(header)}</th>`).join('')}</tr>${rows.map((row) => `<tr>${[row.round_name, row.product_category_code, row.product_category_name || row.category, row.product_subcategory_code, row.product_subcategory_name, row.vendor_name, row.product_name, row.specification, row.ex_tax_price, row.suggested_retail_price, row.margin_rate == null ? '' : `${(Number(row.margin_rate) * 100).toFixed(2)}%`, STATUS_LABELS[String(row.status) as QuotationStatus] ?? row.status, row.missing_count].map((value) => `<td>${escape(value)}</td>`).join('')}</tr>`).join('')}</table></html>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '新品報價報表.xls';
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <PageHeader
        eyebrow="REPORTING"
        title="報表中心"
        description="報表會套用目前的篩選條件，可匯出 CSV、Excel、JSON，或使用瀏覽器列印為 PDF。"
        actions={
          <>
            <Button variant="secondary" onClick={() => window.print()}>
              列印 / PDF
            </Button>
            <Button variant="secondary" onClick={exportJson}>
              JSON
            </Button>
            <Button variant="secondary" onClick={exportExcel}>
              Excel
            </Button>
            <a href={api.reports.csvUrl(params)}>
              <Button>CSV</Button>
            </a>
          </>
        }
      />
      <Card className="no-print mb-6">
        <div className="grid gap-3 md:grid-cols-5">
          <Input
            label="關鍵字"
            placeholder="商品、廠商、活動"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Select label="狀態" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部狀態</option>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            label="廠商"
            value={vendorId}
            onChange={(event) => setVendorId(event.target.value)}
          >
            <option value="">全部廠商</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </Select>
          <Select
            label="商品分類"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="">全部商品分類</option>
            {productCategories.map((item) => (
              <option key={item.code} value={item.code}>
                {formatProductCategory(item.code, item.name)}
              </option>
            ))}
          </Select>
          <Select
            label="商品子分類"
            value={productSubcategoryCode}
            onChange={(event) => setProductSubcategoryCode(event.target.value)}
            disabled={!category}
          >
            <option value="">全部商品子分類</option>
            {productSubcategories.map((subcategory) => (
              <option key={subcategory.code} value={subcategory.code}>
                {subcategory.code}｜{subcategory.name}
              </option>
            ))}
          </Select>
          <div className="flex items-end">
            <Button className="w-full" onClick={load} loading={loading}>
              套用篩選
            </Button>
          </div>
        </div>
      </Card>
      <Card>
        {loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <EmptyState title="沒有符合篩選條件的資料" description="調整篩選條件後重新查詢。" />
        ) : (
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">活動 / 商品</th>
                <th className="px-4 py-3">廠商</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">價格 / 毛利</th>
                <th className="px-4 py-3">缺件</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={String(row.submission_id)} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-4">
                    <p className="max-w-[280px] whitespace-normal break-words font-bold">
                      {(row.product_name as string) || '—'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.round_name as string} ·{' '}
                      {row.product_category_code
                        ? `${row.product_category_code}｜${row.product_category_name || ''}`
                        : (row.category as string)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      商品子分類：
                      {row.product_subcategory_code
                        ? `${row.product_subcategory_code}｜${row.product_subcategory_name || ''}`
                        : '未設定'}
                    </p>
                  </td>
                  <td className="max-w-[180px] whitespace-normal break-words px-4 py-4">
                    {row.vendor_name as string}
                  </td>
                  <td className="px-4 py-4">
                    <StatusBadge status={String(row.status) as QuotationStatus} />
                  </td>
                  <td className="px-4 py-4">
                    <Money value={row.ex_tax_price as number | null} />
                    <span className="mx-1 text-slate-300">/</span>
                    <Money value={row.suggested_retail_price as number | null} />
                    <p className="mt-1 text-xs font-bold text-teal-700">
                      {row.margin_rate == null
                        ? '毛利 —'
                        : `毛利 ${(Number(row.margin_rate) * 100).toFixed(1)}%`}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    {Number(row.missing_count) ? (
                      <span className="font-bold text-orange-700">
                        {String(row.missing_count)} 項
                      </span>
                    ) : (
                      <span className="text-emerald-600">完整</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

export function SettingsPage({ notify }: PageProps) {
  const { data, loading } = useAsyncLoad(() => api.settings.get(), [], notify);
  const [form, setForm] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (data?.settings) setForm(data.settings);
  }, [data?.settings]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const result = await api.settings.update(form);
      setForm(result.settings);
      notify('系統設定已儲存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  if (loading) return <Loading />;
  return (
    <>
      <PageHeader
        eyebrow="SYSTEM"
        title="系統設定"
        description="集中管理幣別、稅率、毛利計算方式與附件限制；毛利公式由共用函式依此設定計算。"
      />
      <Card className="max-w-3xl">
        <form onSubmit={save} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="預設幣別"
              value={form.currency}
              onChange={(event) => setForm({ ...form, currency: event.target.value })}
            />
            <Input
              label="預設稅率（%）"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={form.default_tax_rate}
              onChange={(event) =>
                setForm({ ...form, default_tax_rate: Number(event.target.value) })
              }
            />
            <Select
              label="毛利計算方式"
              value={form.margin_calculation}
              onChange={(event) =>
                setForm({
                  ...form,
                  margin_calculation: event.target.value as SystemSettings['margin_calculation'],
                })
              }
            >
              <option value="RETAIL_MINUS_COST">建議售價 − 未稅進價</option>
              <option value="RETAIL_MINUS_TAXED_COST">建議售價 − 含稅進價</option>
            </Select>
            <Input
              label="附件大小上限（MB）"
              type="number"
              min="1"
              max="50"
              value={form.max_attachment_size_mb}
              onChange={(event) =>
                setForm({ ...form, max_attachment_size_mb: Number(event.target.value) })
              }
            />
            <Input
              label="每筆附件數量上限"
              type="number"
              min="1"
              max="50"
              value={form.max_attachment_count}
              onChange={(event) =>
                setForm({ ...form, max_attachment_count: Number(event.target.value) })
              }
            />
          </div>
          <div className="mt-6 rounded-2xl border border-teal-100 bg-teal-50 p-4">
            <h2 className="text-lg font-extrabold text-teal-900">廠商主動提案設定</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="flex items-start gap-3 rounded-xl bg-white/70 p-3 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={form.proactive_enabled}
                  onChange={(event) =>
                    setForm({ ...form, proactive_enabled: event.target.checked })
                  }
                />
                啟用公開主動提案
              </label>
              <label className="flex items-start gap-3 rounded-xl bg-white/70 p-3 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={form.proactive_allow_new_vendors}
                  onChange={(event) =>
                    setForm({ ...form, proactive_allow_new_vendors: event.target.checked })
                  }
                />
                允許新廠商申請
              </label>
              <label className="flex items-start gap-3 rounded-xl bg-white/70 p-3 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={form.proactive_require_email_verification}
                  onChange={(event) =>
                    setForm({ ...form, proactive_require_email_verification: event.target.checked })
                  }
                />
                要求 Email 驗證
              </label>
              <Input
                label="每日全站提案上限"
                type="number"
                min="1"
                value={form.proactive_daily_max}
                onChange={(event) =>
                  setForm({ ...form, proactive_daily_max: Number(event.target.value) })
                }
              />
              <Input
                label="同一廠商每日提案上限"
                type="number"
                min="1"
                value={form.proactive_vendor_daily_max}
                onChange={(event) =>
                  setForm({ ...form, proactive_vendor_daily_max: Number(event.target.value) })
                }
              />
              <Input
                label="提交完整度門檻（%）"
                type="number"
                min="0"
                max="100"
                value={form.proactive_completeness_threshold}
                onChange={(event) =>
                  setForm({ ...form, proactive_completeness_threshold: Number(event.target.value) })
                }
              />
              <Input
                label="主動提案單檔大小上限（MB）"
                type="number"
                min="1"
                max="100"
                value={form.proactive_max_file_size_mb}
                onChange={(event) =>
                  setForm({ ...form, proactive_max_file_size_mb: Number(event.target.value) })
                }
              />
              <Input
                label="主動提案附件數量上限"
                type="number"
                min="1"
                max="100"
                value={form.proactive_max_file_count}
                onChange={(event) =>
                  setForm({ ...form, proactive_max_file_count: Number(event.target.value) })
                }
              />
            </div>
            <p className="mt-3 text-xs leading-5 text-teal-800">
              編號規則：{form.proactive_numbering_rule}。檔案格式由後端設定與 R2 binding 同時限制。
            </p>
          </div>
          <div className="rounded-2xl bg-teal-50 p-4 text-sm leading-6 text-teal-900">
            目前毛利計算會以同一個共用函式處理，報表、比較頁與會議模式使用同一規則。
          </div>
          <div className="flex justify-end">
            <Button loading={saving}>儲存設定</Button>
          </div>
        </form>
      </Card>
      <AttachmentRequirementSettings notify={notify} />
    </>
  );
}

type AttachmentRequirementScope = 'vendor-application' | 'product';
type AttachmentRequirementDraft = {
  label: string;
  sort_order: number;
  required: boolean;
  is_active: boolean;
};

function AttachmentRequirementTable({
  scope,
  requirements,
  loading,
  notify,
}: {
  scope: AttachmentRequirementScope;
  requirements: AttachmentRequirement[];
  loading: boolean;
  notify: Notify;
}) {
  const [drafts, setDrafts] = useState<Record<string, AttachmentRequirementDraft>>({});
  const [savingId, setSavingId] = useState('');
  useEffect(() => {
    setDrafts(
      Object.fromEntries(
        requirements.map((item) => [
          item.id,
          {
            label: item.label,
            sort_order: item.sort_order,
            required: Boolean(item.required),
            is_active: Boolean(item.is_active),
          },
        ]),
      ),
    );
  }, [requirements]);
  const updateDraft = (id: string, patch: Partial<AttachmentRequirementDraft>) =>
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const save = async (item: AttachmentRequirement) => {
    const draft = drafts[item.id];
    if (!draft) return;
    setSavingId(item.id);
    try {
      await api.attachmentRequirements.update(scope, item.id, draft);
      notify(`附件規則「${draft.label}」已更新。`);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSavingId('');
    }
  };
  if (loading) return <Loading />;
  if (!requirements.length) return <EmptyState title="尚未設定附件規則" />;
  return (
    <Table>
      <thead>
        <tr className="bg-slate-50 text-xs font-bold text-slate-500">
          <th className="px-4 py-3">分類</th>
          <th className="px-4 py-3">附件類型</th>
          <th className="min-w-[220px] px-4 py-3">顯示名稱</th>
          <th className="w-28 px-4 py-3">排序</th>
          <th className="px-4 py-3">必要</th>
          <th className="px-4 py-3">啟用</th>
          <th className="px-4 py-3" />
        </tr>
      </thead>
      <tbody>
        {requirements.map((item) => {
          const draft = drafts[item.id] ?? {
            label: item.label,
            sort_order: item.sort_order,
            required: Boolean(item.required),
            is_active: Boolean(item.is_active),
          };
          return (
            <tr key={item.id} className="border-t border-slate-100 align-middle">
              <td className="px-4 py-4 font-bold text-teal-700">{item.category_code}</td>
              <td className="px-4 py-4 text-xs font-semibold text-slate-600">{item.type}</td>
              <td className="min-w-[220px] px-4 py-4">
                <Input
                  aria-label={`${item.type} 顯示名稱`}
                  value={draft.label}
                  onChange={(event) => updateDraft(item.id, { label: event.target.value })}
                />
              </td>
              <td className="px-4 py-4">
                <Input
                  aria-label={`${item.type} 排序`}
                  type="number"
                  min="0"
                  value={draft.sort_order}
                  onChange={(event) =>
                    updateDraft(item.id, { sort_order: Number(event.target.value) })
                  }
                />
              </td>
              <td className="px-4 py-4">
                <input
                  type="checkbox"
                  checked={draft.required}
                  onChange={(event) => updateDraft(item.id, { required: event.target.checked })}
                  aria-label={`${item.type} 必要`}
                />
              </td>
              <td className="px-4 py-4">
                <input
                  type="checkbox"
                  checked={draft.is_active}
                  onChange={(event) => updateDraft(item.id, { is_active: event.target.checked })}
                  aria-label={`${item.type} 啟用`}
                />
              </td>
              <td className="px-4 py-4 text-right">
                <Button onClick={() => save(item)} loading={savingId === item.id}>
                  儲存
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function AttachmentRequirementSettings({ notify }: PageProps) {
  const vendor = useAsyncLoad(
    () => api.attachmentRequirements.list('vendor-application'),
    [],
    notify,
  );
  const product = useAsyncLoad(() => api.attachmentRequirements.list('product'), [], notify);
  return (
    <div className="mt-6 space-y-6">
      <Card>
        <PageHeader
          eyebrow="ATTACHMENT RULES"
          title="廠商申請附件規則"
          description="管理廠商正式送審前必須上傳的文件；category_code 為 * 代表所有申請。"
        />
        <AttachmentRequirementTable
          scope="vendor-application"
          requirements={vendor.data?.requirements ?? []}
          loading={vendor.loading}
          notify={notify}
        />
      </Card>
      <Card>
        <PageHeader
          eyebrow="ATTACHMENT RULES"
          title="新品提案附件規則"
          description="管理全域與商品分類專屬的必填附件；同一附件類型以分類專屬規則優先。"
        />
        <AttachmentRequirementTable
          scope="product"
          requirements={product.data?.requirements ?? []}
          loading={product.loading}
          notify={notify}
        />
      </Card>
    </div>
  );
}

export function ProductCategoriesPage({ notify }: PageProps) {
  const { data, loading, setData } = useAsyncLoad(() => api.productCategories.list(), [], notify);
  const categories = data?.categories ?? [];
  const [drafts, setDrafts] = useState<
    Record<string, { name: string; sort_order: number; is_active: boolean }>
  >({});
  const [savingId, setSavingId] = useState('');
  useEffect(() => {
    if (!data) return;
    setDrafts(
      Object.fromEntries(
        data.categories.map((category) => [
          category.id,
          {
            name: category.name,
            sort_order: category.sort_order,
            is_active: Boolean(category.is_active),
          },
        ]),
      ),
    );
  }, [data]);
  const updateDraft = (id: string, patch: Partial<(typeof drafts)[string]>) =>
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const save = async (category: ProductCategory) => {
    const draft = drafts[category.id];
    if (!draft) return;
    setSavingId(category.id);
    try {
      const result = await api.productCategories.update(category.id, draft);
      setData({
        categories: categories.map((item) => (item.id === category.id ? result.category : item)),
      });
      notify(`分類 ${category.code} 已更新。`);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSavingId('');
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="MASTER DATA"
        title="商品分類管理"
        description="管理新品提案使用的商品分類主檔。分類代碼固定且不可刪除，已被歷史資料使用的分類只能停用。"
      />
      <Card>
        <div className="mb-5 rounded-2xl border border-teal-100 bg-teal-50 p-4 text-sm leading-6 text-teal-900">
          <p className="font-bold">分類代碼是歷史資料的穩定識別值</p>
          <p className="mt-1">
            可以修改名稱、排序與啟用狀態；停用後不會出現在廠商填報下拉選單，但既有資料仍保留原分類。
          </p>
        </div>
        {loading ? (
          <Loading />
        ) : categories.length === 0 ? (
          <EmptyState title="尚未建立商品分類" />
        ) : (
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">代碼</th>
                <th className="px-4 py-3">顯示名稱</th>
                <th className="px-4 py-3">排序</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const draft = drafts[category.id] ?? {
                  name: category.name,
                  sort_order: category.sort_order,
                  is_active: Boolean(category.is_active),
                };
                return (
                  <tr key={category.id} className="border-t border-slate-100 align-middle">
                    <td className="px-4 py-4 font-extrabold text-teal-700">{category.code}</td>
                    <td className="min-w-[240px] px-4 py-4">
                      <Input
                        aria-label={`${category.code} 分類名稱`}
                        value={draft.name}
                        onChange={(event) => updateDraft(category.id, { name: event.target.value })}
                      />
                    </td>
                    <td className="w-32 px-4 py-4">
                      <Input
                        aria-label={`${category.code} 排序`}
                        type="number"
                        min="1"
                        value={draft.sort_order}
                        onChange={(event) =>
                          updateDraft(category.id, { sort_order: Number(event.target.value) })
                        }
                      />
                    </td>
                    <td className="px-4 py-4">
                      <label className="flex items-center gap-2 text-sm font-semibold">
                        <input
                          type="checkbox"
                          checked={draft.is_active}
                          onChange={(event) =>
                            updateDraft(category.id, { is_active: event.target.checked })
                          }
                        />
                        {draft.is_active ? '啟用' : '停用'}
                      </label>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button onClick={() => save(category)} loading={savingId === category.id}>
                        儲存
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

export function ProductSubcategoriesPage({ notify }: PageProps) {
  const { data: categoryData } = useAsyncLoad(() => api.productCategories.list(), [], notify);
  const { data, loading, setData } = useAsyncLoad(
    () => api.productSubcategories.list({ include_inactive: '1' }),
    [],
    notify,
  );
  const categories = categoryData?.categories ?? [];
  const subcategories = data?.subcategories ?? [];
  const [drafts, setDrafts] = useState<
    Record<
      string,
      {
        name: string;
        parent_subcategory_code: string;
        sort_order: number;
        is_active: boolean;
      }
    >
  >({});
  const [newForm, setNewForm] = useState({
    category_code: '',
    code: '',
    name: '',
    parent_subcategory_code: '',
    sort_order: 1,
  });
  const [savingId, setSavingId] = useState('');
  useEffect(() => {
    if (!data) return;
    setDrafts(
      Object.fromEntries(
        data.subcategories.map((subcategory) => [
          subcategory.id,
          {
            name: subcategory.name,
            parent_subcategory_code: subcategory.parent_subcategory_code ?? '',
            sort_order: subcategory.sort_order,
            is_active: Boolean(subcategory.is_active),
          },
        ]),
      ),
    );
  }, [data]);
  const updateDraft = (id: string, patch: Partial<(typeof drafts)[string]>) =>
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const save = async (subcategory: ProductSubcategory) => {
    const draft = drafts[subcategory.id];
    if (!draft) return;
    setSavingId(subcategory.id);
    try {
      const result = await api.productSubcategories.update(subcategory.id, draft);
      setData({
        subcategories: subcategories.map((item) =>
          item.id === subcategory.id ? result.subcategory : item,
        ),
      });
      notify(`子分類 ${subcategory.code} 已更新。`);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSavingId('');
    }
  };
  const create = async () => {
    if (!newForm.category_code || !newForm.code.trim() || !newForm.name.trim()) {
      notify('請完成商品分類、子分類代碼與名稱。', 'error');
      return;
    }
    setSavingId('new');
    try {
      const result = await api.productSubcategories.create({
        ...newForm,
        parent_subcategory_code: newForm.parent_subcategory_code || null,
        is_active: true,
      });
      setData({ subcategories: [...subcategories, result.subcategory] });
      setNewForm({
        category_code: newForm.category_code,
        code: '',
        name: '',
        parent_subcategory_code: '',
        sort_order: newForm.sort_order + 1,
      });
      notify('商品子分類已建立。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSavingId('');
    }
  };
  const parentsFor = (categoryCode: string, currentCode?: string) =>
    subcategories.filter(
      (item) => item.category_code === categoryCode && item.code !== currentCode,
    );
  return (
    <>
      <PageHeader
        eyebrow="MASTER DATA"
        title="商品子分類管理"
        description="子分類必須隸屬既有商品分類。代碼不可刪除，停用後保留歷史案件對應。"
      />
      <Card className="mb-6">
        <h2 className="mb-4 text-lg font-extrabold">新增商品子分類</h2>
        <div className="grid gap-4 md:grid-cols-5">
          <Select
            label="商品分類"
            value={newForm.category_code}
            onChange={(event) =>
              setNewForm({
                ...newForm,
                category_code: event.target.value,
                parent_subcategory_code: '',
              })
            }
          >
            <option value="">請選擇商品分類</option>
            {categories.map((category) => (
              <option key={category.code} value={category.code}>
                {formatProductCategory(category.code, category.name)}
              </option>
            ))}
          </Select>
          <Input
            label="子分類代碼"
            value={newForm.code}
            onChange={(event) => setNewForm({ ...newForm, code: event.target.value })}
            placeholder="例如 J12"
          />
          <Input
            label="顯示名稱"
            value={newForm.name}
            onChange={(event) => setNewForm({ ...newForm, name: event.target.value })}
          />
          <Select
            label="上層子分類"
            value={newForm.parent_subcategory_code}
            onChange={(event) =>
              setNewForm({ ...newForm, parent_subcategory_code: event.target.value })
            }
            disabled={!newForm.category_code}
          >
            <option value="">無</option>
            {parentsFor(newForm.category_code).map((parent) => (
              <option key={parent.code} value={parent.code}>
                {parent.code}｜{parent.name}
              </option>
            ))}
          </Select>
          <Input
            label="排序"
            type="number"
            min="0"
            value={newForm.sort_order}
            onChange={(event) => setNewForm({ ...newForm, sort_order: Number(event.target.value) })}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={create} loading={savingId === 'new'}>
            建立子分類
          </Button>
        </div>
      </Card>
      <Card>
        {loading ? (
          <Loading />
        ) : subcategories.length === 0 ? (
          <EmptyState title="尚未建立商品子分類" />
        ) : (
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">商品分類</th>
                <th className="px-4 py-3">代碼</th>
                <th className="px-4 py-3">顯示名稱</th>
                <th className="px-4 py-3">上層</th>
                <th className="px-4 py-3">排序</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {subcategories.map((subcategory) => {
                const draft = drafts[subcategory.id] ?? {
                  name: subcategory.name,
                  parent_subcategory_code: subcategory.parent_subcategory_code ?? '',
                  sort_order: subcategory.sort_order,
                  is_active: Boolean(subcategory.is_active),
                };
                const category = categories.find((item) => item.code === subcategory.category_code);
                return (
                  <tr key={subcategory.id} className="border-t border-slate-100 align-middle">
                    <td className="whitespace-nowrap px-4 py-4 text-sm font-semibold">
                      {formatProductCategory(subcategory.category_code, category?.name ?? '')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 font-extrabold text-teal-700">
                      {subcategory.code}
                    </td>
                    <td className="min-w-[240px] px-4 py-4">
                      <Input
                        aria-label={`${subcategory.code} 子分類名稱`}
                        value={draft.name}
                        onChange={(event) =>
                          updateDraft(subcategory.id, { name: event.target.value })
                        }
                      />
                    </td>
                    <td className="min-w-[180px] px-4 py-4">
                      <Select
                        aria-label={`${subcategory.code} 上層子分類`}
                        value={draft.parent_subcategory_code}
                        onChange={(event) =>
                          updateDraft(subcategory.id, {
                            parent_subcategory_code: event.target.value,
                          })
                        }
                      >
                        <option value="">無</option>
                        {parentsFor(subcategory.category_code, subcategory.code).map((parent) => (
                          <option key={parent.code} value={parent.code}>
                            {parent.code}｜{parent.name}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="w-28 px-4 py-4">
                      <Input
                        aria-label={`${subcategory.code} 排序`}
                        type="number"
                        min="0"
                        value={draft.sort_order}
                        onChange={(event) =>
                          updateDraft(subcategory.id, { sort_order: Number(event.target.value) })
                        }
                      />
                    </td>
                    <td className="px-4 py-4">
                      <label className="flex items-center gap-2 text-sm font-semibold">
                        <input
                          type="checkbox"
                          checked={draft.is_active}
                          onChange={(event) =>
                            updateDraft(subcategory.id, { is_active: event.target.checked })
                          }
                        />
                        {draft.is_active ? '啟用' : '停用'}
                      </label>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button
                        onClick={() => save(subcategory)}
                        loading={savingId === subcategory.id}
                      >
                        儲存
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

export function AuditPage({ notify }: PageProps) {
  const { data, loading } = useAsyncLoad(() => api.audit(), [], notify);
  const logs = data?.logs ?? [];
  return (
    <>
      <PageHeader
        eyebrow="TRACEABILITY"
        title="操作紀錄"
        description="重要狀態、審核、邀請、附件與設定變更都會留下不可由一般使用者修改的紀錄。"
      />
      {loading ? (
        <Loading />
      ) : logs.length === 0 ? (
        <EmptyState title="尚無操作紀錄" />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">時間</th>
                <th className="px-4 py-3">操作者</th>
                <th className="px-4 py-3">動作</th>
                <th className="px-4 py-3">資料</th>
                <th className="px-4 py-3">內容</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={String(log.id)} className="border-t border-slate-100 align-top">
                  <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">
                    <DateText value={String(log.created_at)} withTime />
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-bold">{String(log.actor_name ?? '系統 / 廠商 Token')}</p>
                    <p className="text-xs text-slate-500">{String(log.actor_role ?? '')}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-teal-700">
                      {String(log.action)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-600">
                    {String(log.entity_type)}
                    <br />
                    {String(log.entity_id ?? '—')}
                  </td>
                  <td className="max-w-[360px] whitespace-pre-wrap break-words px-4 py-4 text-xs leading-5 text-slate-600">
                    {JSON.stringify(log.metadata ?? {})}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}

const blankProduct: SubmissionProduct = {
  vendor_product_code: '',
  name_zh: '',
  name_en: '',
  brand: '',
  manufacturer: '',
  origin: '',
  product_category_code: '',
  product_category_name: '',
  product_subcategory_code: '',
  product_subcategory_name: '',
  category: '',
  specification: '',
  unit: '',
  case_pack: null,
  product_features: '',
  ingredients: '',
  usage: '',
  storage: '',
  shelf_life: '',
  expiry_requirement: '',
  precautions: '',
  target_customers: '',
  display_location: '',
  planned_launch_date: null,
  barcodes: [''],
  outer_barcode: '',
  inner_barcode: '',
  ex_tax_price: null,
  tax_included_price: null,
  tax_rate: 5,
  suggested_retail_price: null,
  promotional_price: null,
  minimum_order_quantity: null,
  minimum_delivery_unit: '',
  delivery_days: null,
  payment_method: '',
  return_terms: '',
  near_expiry_handling: '',
  first_order_offer: '',
  gifts: '',
};

export function VendorSubmissionPage({ notify }: PageProps) {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [loading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [round] = useState<{
    id: string;
    name: string;
    category: string;
    description: string;
    due_date: string;
    required_attachment_types: AttachmentType[];
  } | null>(null);
  const [vendor] = useState<Vendor | null>(null);
  const [productCategories] = useState<ProductCategory[]>([]);
  const [product, setProduct] = useState<SubmissionProduct>(blankProduct);
  const [dirty, setDirty] = useState(false);
  useUnsavedPrompt(dirty);
  useEffect(() => {
    if (token)
      navigate(`/vendor/proposal?invite_token=${encodeURIComponent(token)}`, { replace: true });
  }, [navigate, token]);
  const update = (key: keyof SubmissionProduct, value: unknown) => {
    setDirty(true);
    setProduct((current) => ({ ...current, [key]: value }));
  };
  const updateTradeTerms = (key: string, value: unknown) => {
    setDirty(true);
    setProduct((current) => ({
      ...current,
      trade_terms_draft: {
        ...(current.trade_terms_draft ?? current.trade_terms ?? {}),
        [key]: value,
      } as SubmissionProduct['trade_terms_draft'],
    }));
  };
  const save = async (): Promise<Submission | null> => {
    setSaving(true);
    try {
      const removedFields = new Set([
        'promotional_price',
        'delivery_days',
        'payment_method',
        'payment_terms',
        'return_terms',
        'near_expiry_handling',
        'expiry_requirement',
        'display_location',
        'trade_terms_draft',
      ]);
      const normalized = {
        ...Object.fromEntries(Object.entries(product).filter(([key]) => !removedFields.has(key))),
        barcodes: product.barcodes.filter(Boolean),
        trade_terms: normalizeProductTradeTermsPayload(
          product.trade_terms_draft ?? product.trade_terms,
        ),
      };
      const result = await api.vendorSubmission.save(token, normalized);
      setSubmission(result);
      setDirty(false);
      notify('新品資料草稿已儲存。');
      return result;
    } catch (error) {
      notify(errorMessage(error), 'error');
      setTurnstileToken('');
      setTurnstileResetKey((value) => value + 1);
      return null;
    } finally {
      setSaving(false);
    }
  };
  const upload = async (type: AttachmentType, file: File) => {
    if (!submission) return;
    try {
      await api.attachments.upload({ token, submissionId: submission.id, type, file });
      const refreshed = await api.vendorSubmission.get(token);
      setSubmission(refreshed.submission);
      notify('附件已上傳。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const submit = async () => {
    if (!submission) return;
    if (!turnstileToken) {
      notify('請先完成安全驗證。', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const saved = dirty ? await save() : submission;
      if (!saved) return;
      const result = await api.vendorSubmission.submit(
        token,
        saved.id,
        turnstileToken,
        crypto.randomUUID(),
      );
      setSubmission(result);
      setDirty(false);
      notify('新品報價已提交，採購人員可以開始審核。');
    } catch (error) {
      notify(errorMessage(error), 'error');
      setTurnstileToken('');
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  };
  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  if (!submission || !round || !vendor)
    return (
      <div className="grid min-h-screen place-items-center bg-sand p-6">
        <EmptyState
          title="無法開啟填報連結"
          description="請確認連結仍在有效期限內，或聯絡採購人員重新產生邀請。"
        />
      </div>
    );
  const locked = !['VENDOR_FILLING', 'REVISION_REQUIRED'].includes(submission.status);
  const tradeTerms = product.trade_terms_draft ?? product.trade_terms;
  return (
    <div className="min-h-screen bg-sand">
      <header className="border-b border-slate-100 bg-white px-4 py-4 md:px-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-2xl bg-teal-600 font-extrabold text-white">
              P
            </span>
            <div>
              <p className="text-sm font-extrabold tracking-wide">PHARMAFLOW</p>
              <p className="text-xs text-slate-500">廠商新品填報</p>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p>{vendor.name}</p>
            <p className="mt-1">
              截止{' '}
              <strong className="text-ink">
                <DateText value={round.due_date} />
              </strong>
            </p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 md:px-10 md:py-10">
        <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
              VENDOR PORTAL
            </p>
            <h1 className="mt-2 text-3xl font-extrabold text-ink">{round.name}</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">{round.description}</p>
          </div>
          <StatusBadge status={submission.status} />
        </div>
        {submission.status === 'REVISION_REQUIRED' && (
          <div className="mb-6 rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
            <p className="font-bold">採購人員要求補件</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {(submission.review_history ?? [])
                .filter((item) => ['REVISION_REQUESTED', 'RETURNED'].includes(item.review_result))
                .map((item) => (
                  <li key={item.id}>
                    {item.return_reason ||
                      item.request_revision_content ||
                      '請依審核紀錄補正資料。'}
                    {item.revision_due_at ? `（期限：${item.revision_due_at.slice(0, 10)}）` : ''}
                  </li>
                ))}
            </ul>
          </div>
        )}
        {locked && (
          <div className="mb-6 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
            此填報目前為唯讀狀態。若需要修改，請聯絡採購人員重新開啟補件。
          </div>
        )}
        <p className="mb-3 text-sm text-slate-500">
          <span className="font-bold text-rose-600">*</span> 為必填欄位
        </p>
        <div className="space-y-6">
          <Card>
            <h2 className="mb-5 text-xl font-extrabold">基本資料</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="廠商商品編號"
                disabled={locked}
                value={product.vendor_product_code}
                onChange={(event) => update('vendor_product_code', event.target.value)}
              />
              <Input
                label="商品中文名稱"
                required
                disabled={locked}
                value={product.name_zh}
                onChange={(event) => update('name_zh', event.target.value)}
              />
              <Input
                label="商品英文名稱"
                disabled={locked}
                value={product.name_en}
                onChange={(event) => update('name_en', event.target.value)}
              />
              <Input
                label="品牌"
                required
                disabled={locked}
                value={product.brand}
                onChange={(event) => update('brand', event.target.value)}
              />
              <Input
                label="製造商"
                disabled={locked}
                value={product.manufacturer}
                onChange={(event) => update('manufacturer', event.target.value)}
              />
              <Input
                label="產地"
                required
                disabled={locked}
                value={product.origin}
                onChange={(event) => update('origin', event.target.value)}
              />
              <ProductCategorySelect
                categories={productCategories}
                value={product.product_category_code}
                currentName={product.product_category_name}
                disabled={locked}
                onChange={(value) => update('product_category_code', value)}
              />
              <Input
                label="規格"
                required
                disabled={locked}
                value={product.specification}
                onChange={(event) => update('specification', event.target.value)}
              />
              <Input
                label="單位"
                required
                disabled={locked}
                value={product.unit}
                onChange={(event) => update('unit', event.target.value)}
              />
              <Input
                label="箱入數"
                type="number"
                min="0"
                required
                disabled={locked}
                value={product.case_pack ?? ''}
                onChange={(event) =>
                  update('case_pack', event.target.value ? Number(event.target.value) : null)
                }
              />
            </div>
          </Card>
          <Card>
            <h2 className="mb-5 text-xl font-extrabold">條碼</h2>
            <p className="mb-4 text-sm text-slate-500">
              國際條碼最多六組，系統會檢查長度與檢核碼，且不允許重複。
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Input
                  key={index}
                  label={`國際條碼 ${index + 1}`}
                  disabled={locked}
                  value={product.barcodes[index] ?? ''}
                  onChange={(event) => {
                    const next = [...product.barcodes];
                    next[index] = event.target.value;
                    update('barcodes', next);
                  }}
                />
              ))}
              <Input
                label="外箱條碼"
                disabled={locked}
                value={product.outer_barcode}
                onChange={(event) => update('outer_barcode', event.target.value)}
              />
              <Input
                label="內盒條碼"
                disabled={locked}
                value={product.inner_barcode}
                onChange={(event) => update('inner_barcode', event.target.value)}
              />
            </div>
          </Card>
          <Card>
            <h2 className="mb-5 text-xl font-extrabold">價格與交易條件</h2>
            <div className="grid gap-4 md:grid-cols-4">
              <Input
                label="未稅進價"
                required
                type="number"
                min="0"
                step="0.01"
                disabled={locked}
                value={product.ex_tax_price ?? ''}
                onChange={(event) =>
                  update('ex_tax_price', event.target.value ? Number(event.target.value) : null)
                }
              />
              <Input
                label="含稅進價"
                required
                type="number"
                min="0"
                step="0.01"
                disabled={locked}
                value={product.tax_included_price ?? ''}
                onChange={(event) =>
                  update(
                    'tax_included_price',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <Input
                label="稅率（%）"
                type="number"
                min="0"
                max="100"
                step="0.1"
                disabled={locked}
                value={product.tax_rate}
                onChange={(event) => update('tax_rate', Number(event.target.value))}
              />
              <Input
                label="建議售價"
                required
                type="number"
                min="0"
                step="0.01"
                disabled={locked}
                value={product.suggested_retail_price ?? ''}
                onChange={(event) =>
                  update(
                    'suggested_retail_price',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <Input
                label="最低訂購量"
                type="number"
                min="0"
                step="1"
                disabled={locked}
                value={product.minimum_order_quantity ?? ''}
                onChange={(event) =>
                  update(
                    'minimum_order_quantity',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <Input
                label="最小配送單位"
                required
                disabled={locked}
                value={product.minimum_delivery_unit}
                onChange={(event) => update('minimum_delivery_unit', event.target.value)}
              />
              <Input
                label="最低訂購金額"
                type="number"
                min="0"
                step="0.01"
                required
                disabled={locked}
                value={product.minimum_order_amount ?? ''}
                onChange={(event) =>
                  update(
                    'minimum_order_amount',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
            </div>
            <div className="mt-5 rounded-2xl border border-teal-100 bg-teal-50 p-4">
              <p className="font-bold text-teal-900">廠商交易條件</p>
              <p className="mt-1 text-sm text-teal-800">
                以下交易條件已依廠商主檔自動帶入，資料來源：
                {submission.trade_terms_source?.source_type === 'VENDOR_APPLICATION'
                  ? '新廠商申請'
                  : '廠商主檔'}
                。
              </p>
              <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                <p>
                  付款條件：{displayProductTradeTerm(submission.trade_terms_source?.payment_term)}
                </p>
                <p>
                  月結天數：
                  {submission.trade_terms_source?.monthly_payment_days ?? '不適用'}
                </p>
                <p>
                  付款方式：{displayProductTradeTerm(submission.trade_terms_source?.payment_method)}
                </p>
                <p>
                  退貨地點：{displayProductTradeTerm(submission.trade_terms_source?.return_scope)}
                </p>
                <p>
                  退換貨方式：
                  {displayProductTradeTerm(submission.trade_terms_source?.return_handling_method)}
                </p>
                <p>
                  退換貨條件：
                  {displayProductTradeTerm(submission.trade_terms_source?.return_conditions)}
                </p>
                <p className="md:col-span-2">
                  退換貨說明：{submission.trade_terms_source?.return_description || '尚未設定'}
                </p>
                <p>
                  退貨運費：
                  {displayProductTradeTerm(
                    submission.trade_terms_source?.return_freight_responsibility,
                  )}
                </p>
              </div>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <Select
                label="本商品是否有特殊交易條件？"
                required
                disabled={locked}
                value={
                  normalizeSpecialTradeTermsFlag(tradeTerms?.special_trade_terms) ? 'YES' : 'NO'
                }
                onChange={(event) => {
                  const nextEnabled = event.target.value === 'YES';
                  if (!canChangeSpecialTradeTerms(tradeTerms, nextEnabled)) return;
                  updateTradeTerms('special_trade_terms', nextEnabled);
                }}
              >
                <option value="NO">否，沿用廠商交易條件</option>
                <option value="YES">是，本商品有特殊交易條件</option>
              </Select>
              {tradeTerms && normalizeSpecialTradeTermsFlag(tradeTerms.special_trade_terms) && (
                <Textarea
                  label="特殊條件原因"
                  required
                  disabled={locked}
                  value={tradeTerms.special_reason ?? ''}
                  onChange={(event) => updateTradeTerms('special_reason', event.target.value)}
                />
              )}
            </div>
            {tradeTerms && normalizeSpecialTradeTermsFlag(tradeTerms.special_trade_terms) && (
              <div className="mt-4 grid gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 md:grid-cols-2">
                <Input
                  label="特殊付款條件"
                  required
                  disabled={locked}
                  value={tradeTerms.special_payment_term ?? ''}
                  onChange={(event) => updateTradeTerms('special_payment_term', event.target.value)}
                />
                <Input
                  label="特殊月結天數"
                  type="number"
                  min="1"
                  disabled={locked}
                  value={tradeTerms.special_monthly_payment_days ?? ''}
                  onChange={(event) =>
                    updateTradeTerms(
                      'special_monthly_payment_days',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="特殊付款方式"
                  required
                  disabled={locked}
                  value={tradeTerms.special_payment_method ?? ''}
                  onChange={(event) =>
                    updateTradeTerms('special_payment_method', event.target.value)
                  }
                />
                <Input
                  label="特殊退換貨期限"
                  disabled={locked}
                  value={tradeTerms.special_return_deadline ?? ''}
                  onChange={(event) =>
                    updateTradeTerms('special_return_deadline', event.target.value)
                  }
                />
                <Textarea
                  label="特殊退換貨詳細說明"
                  required
                  disabled={locked}
                  value={tradeTerms.special_return_description ?? ''}
                  onChange={(event) =>
                    updateTradeTerms('special_return_description', event.target.value)
                  }
                />
                <Textarea
                  label="特殊退換貨條件（請以逗號分隔）"
                  required
                  disabled={locked}
                  value={(tradeTerms.special_return_conditions ?? []).join('、')}
                  onChange={(event) =>
                    updateTradeTerms(
                      'special_return_conditions',
                      event.target.value
                        .split(/[、,，]/)
                        .map((item) => item.trim())
                        .filter(Boolean),
                    )
                  }
                />
              </div>
            )}
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <Select
                label="首批優惠"
                required
                disabled={locked}
                value={
                  product.initial_order_offer_enabled === 1
                    ? 'YES'
                    : product.initial_order_offer_enabled === 0
                      ? 'NO'
                      : ''
                }
                onChange={(event) => {
                  const value = event.target.value === 'YES';
                  update('initial_order_offer_enabled', value ? 1 : 0);
                  update(
                    'initial_order_offer',
                    value ? '有' : event.target.value === 'NO' ? '無' : '',
                  );
                }}
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.initial_order_offer_enabled === 1 && (
                <Textarea
                  label="首批優惠內容"
                  required
                  disabled={locked}
                  value={product.initial_order_offer_content ?? ''}
                  onChange={(event) => update('initial_order_offer_content', event.target.value)}
                />
              )}
              <Select
                label="贈品及搭贈"
                required
                disabled={locked}
                value={
                  product.gift_bundle_support_enabled === 1
                    ? 'YES'
                    : product.gift_bundle_support_enabled === 0
                      ? 'NO'
                      : ''
                }
                onChange={(event) => {
                  const value = event.target.value === 'YES';
                  update('gift_bundle_support_enabled', value ? 1 : 0);
                  update(
                    'gift_bundle_support',
                    value ? '有' : event.target.value === 'NO' ? '無' : '',
                  );
                }}
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.gift_bundle_support_enabled === 1 && (
                <Textarea
                  label="贈品及搭贈內容"
                  required
                  disabled={locked}
                  value={product.gift_bundle_support_content ?? ''}
                  onChange={(event) => update('gift_bundle_support_content', event.target.value)}
                />
              )}
              <Select
                label="促銷支援"
                required
                disabled={locked}
                value={
                  product.promotion_support_enabled === 1
                    ? 'YES'
                    : product.promotion_support_enabled === 0
                      ? 'NO'
                      : ''
                }
                onChange={(event) => {
                  const value = event.target.value === 'YES';
                  update('promotion_support_enabled', value ? 1 : 0);
                  update(
                    'promotion_support',
                    value ? '有' : event.target.value === 'NO' ? '無' : '',
                  );
                }}
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.promotion_support_enabled === 1 && (
                <>
                  <Textarea
                    label="促銷內容"
                    required
                    disabled={locked}
                    value={product.promotion_content ?? ''}
                    onChange={(event) => update('promotion_content', event.target.value)}
                  />
                  <Input
                    label="促銷開始日期"
                    required
                    type="date"
                    disabled={locked}
                    value={product.promotion_start_date ?? ''}
                    onChange={(event) => update('promotion_start_date', event.target.value || null)}
                  />
                  <Input
                    label="促銷結束日期"
                    required
                    type="date"
                    disabled={locked}
                    value={product.promotion_end_date ?? ''}
                    onChange={(event) => update('promotion_end_date', event.target.value || null)}
                  />
                </>
              )}
              <Select
                label="試用支援"
                required
                disabled={locked}
                value={
                  product.trial_support_enabled === 1
                    ? 'YES'
                    : product.trial_support_enabled === 0
                      ? 'NO'
                      : ''
                }
                onChange={(event) => {
                  const value = event.target.value === 'YES';
                  update('trial_support_enabled', value ? 1 : 0);
                  update('sample_support', value ? '有' : event.target.value === 'NO' ? '無' : '');
                }}
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.trial_support_enabled === 1 && (
                <Textarea
                  label="試用支援內容"
                  required
                  disabled={locked}
                  value={product.trial_support_content ?? ''}
                  onChange={(event) => update('trial_support_content', event.target.value)}
                />
              )}
            </div>
          </Card>
          <Card>
            <h2 className="mb-5 text-xl font-extrabold">商品資訊</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <Textarea
                label="商品特色"
                disabled={locked}
                value={product.product_features}
                onChange={(event) => update('product_features', event.target.value)}
              />
              <Textarea
                label="成分"
                disabled={locked}
                value={product.ingredients}
                onChange={(event) => update('ingredients', event.target.value)}
              />
              <Textarea
                label="使用方式"
                disabled={locked}
                value={product.usage}
                onChange={(event) => update('usage', event.target.value)}
              />
              <Textarea
                label="保存方式"
                disabled={locked}
                value={product.storage}
                onChange={(event) => update('storage', event.target.value)}
              />
              <Textarea
                label="保存期限"
                disabled={locked}
                value={product.shelf_life}
                onChange={(event) => update('shelf_life', event.target.value)}
              />
              <Textarea
                label="注意事項"
                disabled={locked}
                value={product.precautions}
                onChange={(event) => update('precautions', event.target.value)}
              />
              <Textarea
                label="適用客群"
                disabled={locked}
                value={product.target_customers}
                onChange={(event) => update('target_customers', event.target.value)}
              />
              <Input
                label="預計上市日期"
                type="date"
                disabled={locked}
                value={product.planned_launch_date ?? ''}
                onChange={(event) => update('planned_launch_date', event.target.value || null)}
              />
            </div>
          </Card>
          <Card>
            <h2 className="mb-5 text-xl font-extrabold">附件上傳</h2>
            <p className="mb-4 text-sm text-slate-500">
              允許 PDF、JPG、PNG、WEBP 或簡報檔，單檔上限由系統設定控制。檔案會以 UUID 儲存在 R2。
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {(Object.keys(ATTACHMENT_LABELS) as AttachmentType[]).map((type) => (
                <label key={type} className="rounded-2xl border border-dashed border-slate-200 p-4">
                  <span className="block text-sm font-bold">
                    {ATTACHMENT_LABELS[type]}
                    {round.required_attachment_types.includes(type) && (
                      <span className="ml-2 text-rose-600">必填</span>
                    )}
                  </span>
                  <input
                    type="file"
                    className="mt-3 block w-full text-sm"
                    disabled={locked}
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.ppt,.pptx"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void upload(type, file);
                    }}
                  />
                </label>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {submission.attachments?.map((attachment) => (
                <div
                  className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm"
                  key={attachment.id}
                >
                  <span>
                    {ATTACHMENT_LABELS[attachment.type] ?? attachment.type} ·{' '}
                    {attachment.original_name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {Math.round(attachment.size_bytes / 1024)} KB
                  </span>
                </div>
              ))}
            </div>
          </Card>
          <div className="flex flex-col justify-end gap-3 border-t border-slate-200 pt-6 sm:flex-row">
            <Button variant="secondary" disabled={locked} onClick={save} loading={saving}>
              儲存草稿
            </Button>
            {!locked && (
              <>
                <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileResetKey} />
                <Button onClick={submit} loading={submitting} disabled={submitting}>
                  提交新品報價
                </Button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const blankProactiveProduct: VendorProposalProduct = {
  vendor_product_code: '',
  name_zh: '',
  name_en: '',
  brand: '',
  manufacturer: '',
  origin: '',
  product_category_code: '',
  product_category_name: '',
  product_subcategory_code: '',
  product_subcategory_name: '',
  category: '',
  subcategory: '',
  specification: '',
  unit: '',
  case_pack: null,
  product_features: '',
  ingredients: '',
  usage: '',
  storage: '',
  storage_method: '',
  shelf_life: '',
  expiry_requirement: '',
  precautions: '',
  target_customers: '',
  display_location: '',
  planned_launch_date: null,
  expected_launch_date: null,
  launch_date_is_tentative: 0,
  lifecycle_type: 'NEW',
  product_lifecycle: 'NEW',
  product_lifecycle_other_note: '',
  already_listed_date: null,
  listed_duration_months: null,
  average_monthly_sales: null,
  annual_sales_range: '',
  main_regions: '',
  key_features: '',
  market_positioning: '',
  competitor_difference: '',
  suggested_price_rationale: '',
  market_sales: '',
  estimated_monthly_sales: null,
  suggested_store_type: '',
  display_method: '',
  marketing_support: '',
  launch_plan: '',
  barcodes: [''],
  outer_barcode: '',
  inner_barcode: '',
  no_international_barcode: 0,
  no_international_barcode_reason: '',
  no_international_barcode_other_reason: '',
  ex_tax_price: null,
  tax_included_price: null,
  tax_rate: 5,
  suggested_retail_price: null,
  promotional_price: null,
  minimum_order_quantity: null,
  minimum_order_amount: null,
  minimum_delivery_unit: '',
  delivery_days: null,
  payment_method: '',
  payment_terms: '',
  return_terms: '',
  near_expiry_handling: '',
  first_order_offer: '',
  initial_order_offer: '',
  initial_order_offer_enabled: 0,
  initial_order_offer_content: '',
  gifts: '',
  gift_bundle_support: '',
  gift_bundle_support_enabled: 0,
  gift_bundle_support_content: '',
  promotion_support: '',
  promotion_support_enabled: 0,
  promotion_content: '',
  promotion_start_date: null,
  promotion_end_date: null,
  display_support: '',
  sample_support: '',
  trial_support_enabled: 0,
  trial_support_content: '',
  certificate_status: '',
  certificate_type: '',
  certificate_number: '',
  certificate_other_description: '',
  certificate_attachment_id: null,
  certificate_attachment_url: null,
  certificate_attachment_name: null,
  certificate_not_required_reason: '',
};

function normalizeVendorProposalProduct(
  value: VendorProposalProduct | null | undefined,
): VendorProposalProduct {
  const candidate =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Partial<VendorProposalProduct>)
      : {};
  return {
    ...blankProactiveProduct,
    ...candidate,
    trade_terms: candidate.trade_terms ?? undefined,
    trade_terms_draft: candidate.trade_terms_draft ?? undefined,
    barcodes:
      Array.isArray(candidate.barcodes) && candidate.barcodes.length ? candidate.barcodes : [''],
  };
}

function ProactiveStatusBadge({ status }: { status: ProactiveProposalStatus }) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${PROACTIVE_STATUS_TONE[status] ?? 'bg-slate-100 text-slate-700'}`}
    >
      {PROACTIVE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function VendorPortalHeader({ children }: { children?: React.ReactNode }) {
  return (
    <header className="border-b border-slate-100 bg-white px-4 py-4 md:px-10">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
        <Link to="/vendor/proposal" className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-2xl bg-teal-600 font-extrabold text-white">
            P
          </span>
          <div>
            <p className="text-sm font-extrabold tracking-wide">PHARMAFLOW</p>
            <p className="text-xs text-slate-500">{VENDOR_PLATFORM_NAME}</p>
          </div>
        </Link>
        {children}
      </div>
    </header>
  );
}

function vendorApplicationToForm(application: VendorApplication) {
  return {
    company_name: application.company_name,
    tax_id: application.tax_id,
    phone: application.phone,
    address: application.address,
    website: application.website,
    company_email: application.company_email,
    company_intro: application.company_intro,
    brands: application.brands,
    categories: application.categories,
    delivery_regions: application.delivery_regions,
    minimum_order_amount:
      application.minimum_order_amount === null ? '' : String(application.minimum_order_amount),
    minimum_order_quantity:
      application.minimum_order_quantity === null ? '' : String(application.minimum_order_quantity),
    lead_time: application.lead_time,
    delivery_methods: application.delivery_methods,
    payment_method: application.payment_method,
    return_terms: application.return_terms,
    near_expiry_handling: application.near_expiry_handling,
    invoice_available: application.invoice_available === 1,
    product_liability_insurance: application.product_liability_insurance === 1,
    notes: application.notes,
    contact_name: application.contact?.contact_name ?? '',
    contact_title: application.contact?.title ?? '',
    contact_phone: application.contact?.phone ?? '',
    contact_email: application.contact?.email ?? application.company_email,
  };
}

export function VendorApplicationLegacyPage({ notify }: PageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRequested = searchParams.get('edit') === '1';
  const [form, setForm] = useState({
    company_name: '',
    tax_id: '',
    phone: '',
    address: '',
    website: '',
    company_email: '',
    company_intro: '',
    brands: '',
    categories: '',
    delivery_regions: '',
    minimum_order_amount: '',
    minimum_order_quantity: '',
    lead_time: '',
    delivery_methods: '',
    payment_method: '',
    return_terms: '',
    near_expiry_handling: '',
    invoice_available: false,
    product_liability_insurance: false,
    notes: '',
    contact_name: '',
    contact_title: '',
    contact_phone: '',
    contact_email: '',
  });
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(editRequested);
  const [editingApplication, setEditingApplication] = useState<VendorApplication | null>(null);
  const [dirty, setDirty] = useState(false);
  const [result, setResult] = useState<{
    application_id: string;
    application_no?: string;
    direct_access?: boolean;
    dev_code?: string;
    challenge_id?: string;
    resubmitted?: boolean;
  } | null>(null);
  useUnsavedPrompt(dirty);
  const update = (key: string, value: unknown) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };
  useEffect(() => {
    if (!editRequested) return;
    let active = true;
    api.vendorProactive
      .applicationDetail()
      .then((application) => {
        if (!active) return;
        if (application.status !== 'REJECTED') {
          notify('目前沒有可修改的退件申請。', 'error');
          return;
        }
        setEditingApplication(application);
        setForm(vendorApplicationToForm(application));
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof ApiError && error.status === 401)
          notify('請先登入廠商入口，再開啟退件申請。', 'error');
        else notify(errorMessage(error), 'error');
      })
      .finally(() => {
        if (active) setLoadingExisting(false);
      });
    return () => {
      active = false;
    };
  }, [editRequested, notify]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const input = {
        ...form,
        minimum_order_amount: form.minimum_order_amount ? Number(form.minimum_order_amount) : null,
        minimum_order_quantity: form.minimum_order_quantity
          ? Number(form.minimum_order_quantity)
          : null,
      };
      if (editingApplication) {
        const application = await api.vendorProactive.applicationResubmit(input);
        setEditingApplication(application);
        setDirty(false);
        setResult({
          application_id: application.id,
          application_no: application.application_no,
          direct_access: true,
          resubmitted: true,
        });
        notify('申請資料已更新並重新送交採購審核。', 'success');
        return;
      }
      const application = await api.vendorProactive.application(input);
      setDirty(false);
      if (application.direct_access) {
        setResult({ ...application, direct_access: true });
        notify('廠商申請已建立，可直接開始填寫新品；送出後由採購人員審核。');
        return;
      }
      const verification = await api.vendorProactive.requestVerification({
        mode: 'NEW_VENDOR',
        tax_id: form.tax_id,
        email: form.contact_email,
      });
      setResult({
        ...application,
        challenge_id: verification.challenge_id,
        dev_code: verification.dev_code,
      });
      notify('申請資料已保存，請使用 Email 驗證後開始建立新品提案。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  if (loadingExisting)
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  return (
    <div className="min-h-screen bg-sand">
      <VendorPortalHeader>
        <Link to="/vendor/proposal" className="text-sm font-bold text-teal-700">
          已有資料？前往提案入口 →
        </Link>
      </VendorPortalHeader>
      <main className="mx-auto max-w-5xl px-4 py-8 md:px-10">
        <div className="mb-8 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
            NEW VENDOR APPLICATION
          </p>
          <h1 className="mt-2 text-3xl font-extrabold text-ink md:text-4xl">
            {editingApplication ? '修改退件申請，重新送審' : '建立廠商資料，主動提出新品'}
          </h1>
          <p className="mt-3 leading-7 text-slate-600">
            {editingApplication
              ? '請依退件原因修正原申請資料，送出後會重新交由採購人員審核。'
              : '這是廠商主動新品提案入口，不代表本公司承諾採購。依系統設定，廠商可能需要 Email 驗證；申請與提案都會由採購人員進一步審核。'}
          </p>
        </div>
        {result ? (
          <Card className="max-w-2xl border-emerald-200">
            <h2 className="text-xl font-extrabold text-emerald-800">
              {result.resubmitted ? '已重新送審' : '資料已建立'}
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {result.resubmitted
                ? '採購人員會重新審核這份申請。'
                : result.direct_access
                  ? '您可以直接開始填寫新品提案。'
                  : '驗證信已寄送至聯絡 Email。'}
            </p>
            {result.dev_code && (
              <div className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-bold">本機開發驗證碼</p>
                <p className="mt-1 text-2xl font-extrabold tracking-[0.3em]">{result.dev_code}</p>
                <p className="mt-2 text-xs">正式環境不會在畫面顯示驗證碼。</p>
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={() =>
                  navigate(
                    result.direct_access
                      ? '/vendor/proposal'
                      : `/vendor/proposal?challenge_id=${encodeURIComponent(result.challenge_id ?? '')}`,
                  )
                }
              >
                {result.resubmitted
                  ? '回到廠商提案入口'
                  : result.direct_access
                    ? '開始填寫新品'
                    : '前往驗證並管理提案'}
              </Button>
              <Button variant="secondary" onClick={() => setResult(null)}>
                返回修改資料
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {editingApplication && (
              <Card className="mb-6 border-rose-200 bg-rose-50">
                <h2 className="text-lg font-extrabold text-rose-900">退件原因</h2>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-rose-900">
                  {editingApplication.review_reason ||
                    '採購人員未提供文字說明，請確認申請資料是否完整。'}
                </p>
                <p className="mt-3 text-xs text-rose-800">
                  第 {(editingApplication.resubmission_count ?? 0) + 1} 次重新送審
                </p>
              </Card>
            )}
            <form onSubmit={submit} className="space-y-6">
              <Card>
                <h2 className="mb-5 text-xl font-extrabold">公司基本資料</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="公司／廠商名稱"
                    required
                    value={form.company_name}
                    onChange={(event) => update('company_name', event.target.value)}
                  />
                  <Input
                    label="統一編號"
                    required
                    value={form.tax_id}
                    onChange={(event) => update('tax_id', event.target.value)}
                  />
                  <Input
                    label="公司電話"
                    required
                    value={form.phone}
                    onChange={(event) => update('phone', event.target.value)}
                  />
                  <Input
                    label="公司 Email"
                    type="email"
                    required
                    value={form.company_email}
                    onChange={(event) => update('company_email', event.target.value)}
                  />
                  <Input
                    label="公司網站"
                    value={form.website}
                    onChange={(event) => update('website', event.target.value)}
                  />
                  <Textarea
                    className="md:col-span-2"
                    label="公司地址"
                    required
                    value={form.address}
                    onChange={(event) => update('address', event.target.value)}
                  />
                  <Textarea
                    className="md:col-span-2"
                    label="公司簡介"
                    value={form.company_intro}
                    onChange={(event) => update('company_intro', event.target.value)}
                  />
                  <Textarea
                    label="品牌／代理品牌"
                    value={form.brands}
                    onChange={(event) => update('brands', event.target.value)}
                  />
                  <Textarea
                    label="主要商品分類"
                    value={form.categories}
                    onChange={(event) => update('categories', event.target.value)}
                  />
                  <Textarea
                    label="配送區域"
                    value={form.delivery_regions}
                    onChange={(event) => update('delivery_regions', event.target.value)}
                  />
                  <Textarea
                    label="配送方式"
                    value={form.delivery_methods}
                    onChange={(event) => update('delivery_methods', event.target.value)}
                  />
                </div>
              </Card>
              <Card>
                <h2 className="mb-5 text-xl font-extrabold">交易與服務條件</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="最低訂購金額"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.minimum_order_amount}
                    onChange={(event) => update('minimum_order_amount', event.target.value)}
                  />
                  <Input
                    label="最低訂購量"
                    type="number"
                    min="0"
                    step="1"
                    value={form.minimum_order_quantity}
                    onChange={(event) => update('minimum_order_quantity', event.target.value)}
                  />
                  <Input
                    label="交期"
                    value={form.lead_time}
                    onChange={(event) => update('lead_time', event.target.value)}
                  />
                  <Input
                    label="付款方式／條件"
                    value={form.payment_method}
                    onChange={(event) => update('payment_method', event.target.value)}
                  />
                  <Textarea
                    label="退貨條件"
                    value={form.return_terms}
                    onChange={(event) => update('return_terms', event.target.value)}
                  />
                  <Textarea
                    label="即期品處理方式"
                    value={form.near_expiry_handling}
                    onChange={(event) => update('near_expiry_handling', event.target.value)}
                  />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm font-semibold">
                    <input
                      type="checkbox"
                      checked={form.invoice_available}
                      onChange={(event) => update('invoice_available', event.target.checked)}
                    />
                    可提供發票
                  </label>
                  <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm font-semibold">
                    <input
                      type="checkbox"
                      checked={form.product_liability_insurance}
                      onChange={(event) =>
                        update('product_liability_insurance', event.target.checked)
                      }
                    />
                    具備產品責任險
                  </label>
                </div>
              </Card>
              <Card>
                <h2 className="mb-5 text-xl font-extrabold">主要聯絡人</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="聯絡人姓名"
                    required
                    value={form.contact_name}
                    onChange={(event) => update('contact_name', event.target.value)}
                  />
                  <Input
                    label="職稱"
                    value={form.contact_title}
                    onChange={(event) => update('contact_title', event.target.value)}
                  />
                  <Input
                    label="聯絡電話"
                    required
                    value={form.contact_phone}
                    onChange={(event) => update('contact_phone', event.target.value)}
                  />
                  <Input
                    label="聯絡 Email"
                    type="email"
                    required
                    value={form.contact_email}
                    onChange={(event) => update('contact_email', event.target.value)}
                  />
                  <Textarea
                    className="md:col-span-2"
                    label="其他備註"
                    value={form.notes}
                    onChange={(event) => update('notes', event.target.value)}
                  />
                </div>
              </Card>
              <div className="flex justify-end gap-3">
                <Link to="/vendor/proposal">
                  <Button type="button" variant="secondary">
                    取消
                  </Button>
                </Link>
                <Button loading={saving}>
                  {editingApplication ? '修改完成並重新送審' : '保存資料並繼續'}
                </Button>
              </div>
            </form>
          </>
        )}
      </main>
    </div>
  );
}

const APPLICATION_NUMBER_KEYS = [
  'minimum_order_amount',
  'minimum_order_quantity',
  'warehouse_average_delivery_days',
  'warehouse_minimum_order_amount',
  'warehouse_minimum_order_quantity',
  'warehouse_free_shipping_threshold',
  'warehouse_return_notice_days',
  'store_average_delivery_days',
  'store_minimum_order_amount',
  'store_minimum_order_quantity',
  'store_free_shipping_threshold',
  'store_return_deadline_days',
];

const APPLICATION_BOOLEAN_KEYS = [
  'warehouse_delivery_enabled',
  'warehouse_return_enabled',
  'warehouse_good_return_allowed',
  'warehouse_defect_return_allowed',
  'warehouse_near_expiry_return_allowed',
  'store_delivery_enabled',
  'store_return_to_vendor_enabled',
  'store_return_vendor_approval_required',
  'store_return_buyer_approval_required',
  'invoice_available',
  'product_liability_insurance',
];

const EMPTY_VENDOR_APPLICATION_FORM: Record<string, unknown> = {
  company_name: '',
  short_name: '',
  tax_id: '',
  phone: '',
  address: '',
  website: '',
  company_fax: '',
  company_email: '',
  company_intro: '',
  primary_category_code: '',
  primary_category_name: '',
  primary_brand: '',
  brands: '',
  categories: '',
  delivery_regions: '',
  minimum_order_amount: '',
  minimum_order_quantity: '',
  lead_time: '',
  delivery_methods: '',
  settlement_method: '',
  settlement_method_other: '',
  payment_terms: '',
  payment_terms_other: '',
  payment_method: '',
  return_terms: '',
  near_expiry_handling: '',
  warehouse_delivery_enabled: false,
  warehouse_receiving_method: '',
  warehouse_delivery_cycle: '',
  warehouse_order_cutoff_time: '',
  warehouse_average_delivery_days: '',
  warehouse_minimum_order_amount: '',
  warehouse_minimum_order_quantity: '',
  warehouse_free_shipping_threshold: '',
  warehouse_shipping_rule: '',
  warehouse_return_enabled: false,
  warehouse_return_method: '',
  warehouse_return_notice_days: '',
  warehouse_return_shipping_bearer: '',
  warehouse_good_return_allowed: false,
  warehouse_defect_return_allowed: false,
  warehouse_near_expiry_return_allowed: false,
  warehouse_near_expiry_return_rule: '',
  store_delivery_enabled: false,
  store_receiving_method: '不適用－不提供門市直送',
  store_delivery_cycle: '不適用－不提供門市直送',
  store_order_cutoff_time: '不適用－不提供門市直送',
  store_average_delivery_days: '0',
  store_minimum_order_amount: '0',
  store_minimum_order_quantity: '0',
  store_free_shipping_threshold: '0',
  store_shipping_rule: '不適用－不提供門市直送',
  store_return_to_vendor_enabled: false,
  store_return_method: '門市退貨依總倉流程辦理',
  store_return_deadline_days: '0',
  store_return_shipping_bearer: '依採購平台退貨流程',
  store_return_vendor_approval_required: false,
  store_return_buyer_approval_required: true,
  store_return_route: '門市→總倉→廠商',
  invoice_format: '',
  invoice_format_other: '',
  invoice_issue_method: '',
  invoice_delivery_method: '',
  invoice_contact_name: '',
  invoice_contact_phone: '',
  invoice_email: '',
  reconciliation_contact_name: '',
  reconciliation_contact_phone: '',
  reconciliation_email: '',
  reconciliation_cycle: '',
  bank_name: '',
  bank_code: '',
  branch_name: '',
  branch_code: '',
  bank_account_name: '',
  bank_account_number: '',
  invoice_available: false,
  product_liability_insurance: false,
  notes: '',
  contact_name: '',
  contact_title: '',
  contact_phone: '',
  contact_email: '',
};

function vendorApplicationToRequiredForm(application: VendorApplication): Record<string, unknown> {
  const source = application as unknown as Record<string, unknown>;
  const form = { ...EMPTY_VENDOR_APPLICATION_FORM };
  Object.keys(form).forEach((key) => {
    if (source[key] !== undefined && source[key] !== null) form[key] = source[key];
  });
  APPLICATION_NUMBER_KEYS.forEach((key) => {
    form[key] = source[key] === null || source[key] === undefined ? '' : String(source[key]);
  });
  APPLICATION_BOOLEAN_KEYS.forEach((key) => {
    form[key] = Number(source[key] ?? 0) === 1;
  });
  form.contact_name = application.contact?.contact_name ?? '';
  form.contact_title = application.contact?.title ?? '';
  form.contact_phone = application.contact?.phone ?? '';
  form.contact_email = application.contact?.email ?? application.company_email;
  form.primary_category_name = application.primary_category_name ?? '';
  form.bank_account_number = '';
  return form;
}

function vendorApplicationPayload(form: Record<string, unknown>, mode: 'DRAFT' | 'SUBMIT') {
  const payload: Record<string, unknown> = { ...form, submission_mode: mode };
  APPLICATION_NUMBER_KEYS.forEach((key) => {
    const value = String(form[key] ?? '').trim();
    payload[key] = value ? Number(value) : null;
  });
  APPLICATION_BOOLEAN_KEYS.forEach((key) => {
    payload[key] = Boolean(form[key]);
  });
  payload.minimum_order_amount = payload.warehouse_minimum_order_amount;
  payload.minimum_order_quantity = payload.warehouse_minimum_order_quantity;
  return payload;
}

export function VendorApplicationPage({ notify }: PageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRequested = searchParams.get('edit') === '1';
  const [form, setForm] = useState<Record<string, unknown>>({
    ...EMPTY_VENDOR_APPLICATION_FORM,
  });
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [requirements, setRequirements] = useState<AttachmentRequirement[]>([]);
  const [applicationId, setApplicationId] = useState('');
  const [editingApplication, setEditingApplication] = useState<VendorApplication | null>(null);
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editRequested);
  const [dirty, setDirty] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<{
    application_id: string;
    application_no?: string;
    direct_access?: boolean;
    challenge_id?: string;
    dev_code?: string;
    draft?: boolean;
    resubmitted?: boolean;
  } | null>(null);
  useUnsavedPrompt(dirty);

  const update = (key: string, value: unknown) => {
    setDirty(true);
    setSubmitError('');
    setForm((current) => ({ ...current, [key]: value }));
  };
  const value = (key: string) => String(form[key] ?? '');
  const booleanValue = (key: string) => Boolean(form[key]);
  const setBoolean = (key: string, enabled: boolean) => {
    update(key, enabled);
    if (key === 'store_delivery_enabled' && !enabled) {
      update('store_receiving_method', '不適用－不提供門市直送');
      update('store_delivery_cycle', '不適用－不提供門市直送');
      update('store_order_cutoff_time', '不適用－不提供門市直送');
      update('store_average_delivery_days', '0');
      update('store_minimum_order_amount', '0');
      update('store_minimum_order_quantity', '0');
      update('store_free_shipping_threshold', '0');
      update('store_shipping_rule', '不適用－不提供門市直送');
    }
  };
  const textField = (key: string, label: string, required = false, type = 'text') => (
    <Input
      label={label}
      required={required}
      type={type}
      value={value(key)}
      onChange={(event) => update(key, event.target.value)}
    />
  );
  const areaField = (key: string, label: string, required = false, className = '') => (
    <Textarea
      className={className}
      label={label}
      required={required}
      value={value(key)}
      onChange={(event) => update(key, event.target.value)}
    />
  );
  const boolField = (key: string, label: string) => (
    <Select
      label={label}
      required
      value={booleanValue(key) ? 'YES' : 'NO'}
      onChange={(event) => setBoolean(key, event.target.value === 'YES')}
    >
      <option value="">請選擇</option>
      <option value="YES">是</option>
      <option value="NO">否</option>
    </Select>
  );

  useEffect(() => {
    let active = true;
    Promise.all([
      api.productCategories.publicList(),
      api.vendorProactive.applicationAttachmentRequirements(),
    ])
      .then(([categoryResult, requirementResult]) => {
        if (!active) return;
        setCategories(categoryResult.categories);
        setRequirements(requirementResult.requirements);
      })
      .catch((error) => notify(errorMessage(error), 'error'));
    if (!editRequested) {
      setLoading(false);
      return () => {
        active = false;
      };
    }
    api.vendorProactive
      .applicationDetail()
      .then((application) => {
        if (!active) return;
        if (application.status !== 'REJECTED') {
          notify('目前沒有可修改的退件申請。', 'error');
          return;
        }
        setEditingApplication(application);
        setApplicationId(application.id);
        setForm(vendorApplicationToRequiredForm(application));
      })
      .catch((error) => notify(errorMessage(error), 'error'))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [editRequested, notify]);

  const rememberApplication = (id: string) => {
    setApplicationId(id);
  };

  const refreshApplication = async () => {
    try {
      const application = await api.vendorProactive.applicationDetail();
      setEditingApplication(application);
      setApplicationId(application.id);
      setForm(vendorApplicationToRequiredForm(application));
    } catch {
      // Email 驗證尚未完成時，公開端點沒有可讀取的 session；不影響附件上傳結果。
    }
  };

  const uploadFiles = async (id: string) => {
    for (const [type, file] of Object.entries(pendingFiles)) {
      await api.vendorProactive.uploadApplicationAttachment(id, type, file);
    }
    if (Object.keys(pendingFiles).length) {
      setPendingFiles({});
      await refreshApplication();
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    setSubmitError('');
    try {
      if (editingApplication) {
        notify('退件申請請使用「修改完成並重新送審」。', 'info');
        return;
      }
      const response = await api.vendorProactive.application(
        vendorApplicationPayload(form, 'DRAFT'),
      );
      rememberApplication(response.application_id);
      if (response.direct_access) await uploadFiles(response.application_id);
      setDirty(false);
      notify('草稿已儲存，可繼續補齊資料及附件。', 'success');
    } catch (error) {
      const message = errorMessage(error);
      setSubmitError(message);
      notify(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitApplication = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSubmitError('');
    try {
      let id = applicationId || editingApplication?.id;
      if (!id && !editingApplication) {
        const draft = await api.vendorProactive.application(
          vendorApplicationPayload(form, 'DRAFT'),
        );
        id = draft.application_id;
        rememberApplication(id);
        if (draft.direct_access) await uploadFiles(id);
      } else if (id) {
        await uploadFiles(id);
      }
      if (editingApplication) {
        const application = await api.vendorProactive.applicationResubmit(
          vendorApplicationPayload(form, 'SUBMIT'),
        );
        setDirty(false);
        setResult({
          application_id: application.id,
          application_no: application.application_no,
          direct_access: true,
          resubmitted: true,
        });
        notify('申請已修改並重新送審。', 'success');
        return;
      }
      const response = await api.vendorProactive.application(
        vendorApplicationPayload(form, 'SUBMIT'),
      );
      setDirty(false);
      if (response.direct_access) {
        setResult({ ...response, direct_access: true, resubmitted: Boolean(editingApplication) });
        await refreshApplication();
        notify(
          editingApplication
            ? '申請已修改並重新送審。'
            : response.draft
              ? '草稿已儲存。'
              : '廠商申請已送出。',
          'success',
        );
        return;
      }
      const verification = await api.vendorProactive.requestVerification({
        mode: 'NEW_VENDOR',
        tax_id: value('tax_id'),
        email: value('contact_email'),
      });
      setResult({
        ...response,
        challenge_id: verification.challenge_id,
        dev_code: verification.dev_code,
      });
      notify('申請已建立，請依 Email 驗證後繼續。', 'success');
    } catch (error) {
      const message = errorMessage(error);
      setSubmitError(message);
      notify(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const existingAttachments = editingApplication?.application_attachments ?? [];
  const availableAttachmentTypes = new Set([
    ...existingAttachments.map((attachment) => attachment.type),
    ...Object.keys(pendingFiles),
  ]);
  const missingAttachments = requirements.filter(
    (requirement) => !availableAttachmentTypes.has(requirement.type),
  );
  const bankRequired = /BANK|銀行|匯款/i.test(value('payment_method'));

  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  return (
    <div className="min-h-screen bg-sand">
      <VendorPortalHeader>
        <Link to="/vendor/proposal" className="text-sm font-bold text-teal-700">
          返回廠商入口
        </Link>
      </VendorPortalHeader>
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-10">
        <div className="mb-6 max-w-4xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
            NEW VENDOR APPLICATION
          </p>
          <h1 className="mt-2 text-3xl font-extrabold text-ink">
            {editingApplication ? '退件廠商申請修改與重新送審' : '新廠商申請'}
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            請依序填寫公司、交易、配送、發票及對帳資料。草稿可暫存；正式送出前必須完成所有標示為必填的欄位及附件。
          </p>
          <p className="mt-2 font-bold text-rose-600">* 為必填欄位</p>
        </div>
        {editingApplication && (
          <Card className="mb-6 border-rose-200 bg-rose-50">
            <h2 className="font-extrabold text-rose-900">退件原因</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-rose-900">
              {editingApplication.review_reason || '採購人員未提供文字原因，請補齊資料後重新送審。'}
            </p>
          </Card>
        )}
        {submitError && (
          <Card className="mb-6 border-rose-200 bg-rose-50">
            <h2 className="font-extrabold text-rose-900">請檢查欄位內容</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-rose-900">
              {submitError}
            </p>
          </Card>
        )}
        {result ? (
          <Card className="max-w-3xl border-emerald-200">
            <h2 className="text-xl font-extrabold text-emerald-800">
              {result.resubmitted ? '已重新送審' : result.draft ? '草稿已儲存' : '申請已送出'}
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {result.resubmitted
                ? '，採購人員將重新審核。'
                : result.direct_access
                  ? '，可直接返回廠商入口查看進度。'
                  : '，請完成 Email 驗證後查看申請進度。'}
            </p>
            {result.dev_code && (
              <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                測試驗證碼：<strong className="tracking-[0.2em]">{result.dev_code}</strong>
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => navigate('/vendor/proposal')}>返回廠商入口</Button>
              <Button variant="secondary" onClick={() => setResult(null)}>
                繼續編輯
              </Button>
            </div>
          </Card>
        ) : (
          <form onSubmit={submitApplication} className="space-y-6">
            <Card>
              <h2 className="mb-5 text-xl font-extrabold">一、公司基本資料</h2>
              <div className="grid gap-4 md:grid-cols-3">
                {textField('company_name', '公司正式名稱', true)}
                {textField('short_name', '公司簡稱', true)}
                {textField('tax_id', '統一編號', true)}
                {textField('phone', '公司電話', true)}
                {textField('company_fax', '公司傳真')}
                {textField('company_email', '公司 Email', true, 'email')}
                {textField('website', '公司網站')}
                <ProductCategorySelect
                  categories={categories}
                  value={value('primary_category_code')}
                  currentName={value('primary_category_name')}
                  onChange={(code) => {
                    update('primary_category_code', code);
                    update(
                      'primary_category_name',
                      categories.find((category) => category.code === code)?.name ?? '',
                    );
                  }}
                />
                {textField('primary_brand', '主要品牌', true)}
                {areaField('address', '公司地址', true, 'md:col-span-3')}
                {areaField('company_intro', '公司簡介', false, 'md:col-span-3')}
                {areaField('brands', '主要代理／品牌', false)}
                {areaField('categories', '供應商品類', false)}
                {areaField('delivery_regions', '可配送區域', false)}
                {areaField('delivery_methods', '一般配送方式', false)}
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">二、主要聯絡人</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {textField('contact_name', '主要聯絡人姓名', true)}
                {textField('contact_title', '職稱', true)}
                {textField('contact_phone', '聯絡電話', true)}
                {textField('contact_email', '聯絡 Email', true, 'email')}
                {areaField('notes', '補充說明', false, 'md:col-span-2')}
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">三、交易條件</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <Select
                  label="結帳方式"
                  required
                  value={value('settlement_method')}
                  onChange={(event) => update('settlement_method', event.target.value)}
                >
                  <option value="">請選擇</option>
                  <option value="月結">月結</option>
                  <option value="現金">現金</option>
                  <option value="銀行匯款">銀行匯款</option>
                  <option value="其他">其他</option>
                </Select>
                {textField('settlement_method_other', '結帳方式補充')}
                <Select
                  label="付款條件"
                  required
                  value={value('payment_terms')}
                  onChange={(event) => update('payment_terms', event.target.value)}
                >
                  <option value="">請選擇</option>
                  <option value="月結30天">月結 30 天</option>
                  <option value="月結60天">月結 60 天</option>
                  <option value="貨到付款">貨到付款</option>
                  <option value="其他">其他</option>
                </Select>
                {textField('payment_terms_other', '付款條件補充')}
                <Select
                  label="付款方式"
                  required
                  value={value('payment_method')}
                  onChange={(event) => update('payment_method', event.target.value)}
                >
                  <option value="">請選擇</option>
                  <option value="銀行匯款">銀行匯款</option>
                  <option value="支票">支票</option>
                  <option value="現金">現金</option>
                  <option value="其他">其他</option>
                </Select>
                {textField('lead_time', '一般交貨天數／交期', true)}
                {areaField('return_terms', '退貨條件', true)}
                {areaField('near_expiry_handling', '即期品處理方式', true)}
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">四、總倉配送與退貨</h2>
              <div className="grid gap-4 md:grid-cols-3">
                {boolField('warehouse_delivery_enabled', '是否提供總倉配送')}
                {textField('warehouse_receiving_method', '總倉收貨方式', true)}
                {textField('warehouse_delivery_cycle', '總倉配送週期', true)}
                {textField('warehouse_order_cutoff_time', '總倉訂單截止時間', true)}
                {textField('warehouse_average_delivery_days', '總倉平均到貨天數', true, 'number')}
                {textField('warehouse_minimum_order_amount', '總倉最低訂購金額', true, 'number')}
                {textField('warehouse_minimum_order_quantity', '總倉最低訂購數量', true, 'number')}
                {textField('warehouse_free_shipping_threshold', '總倉免運門檻', true, 'number')}
                {textField('warehouse_shipping_rule', '總倉運費規則', true)}
                {boolField('warehouse_return_enabled', '總倉是否可退貨')}
                {textField('warehouse_return_method', '總倉退貨方式', true)}
                {textField(
                  'warehouse_return_notice_days',
                  '總倉退貨通知期限（天）',
                  true,
                  'number',
                )}
                {textField('warehouse_return_shipping_bearer', '總倉退貨運費負擔', true)}
                {boolField('warehouse_good_return_allowed', '良品可退')}
                {boolField('warehouse_defect_return_allowed', '瑕疵品可退')}
                {boolField('warehouse_near_expiry_return_allowed', '即期品可退')}
                {areaField(
                  'warehouse_near_expiry_return_rule',
                  '即期品退貨規則',
                  true,
                  'md:col-span-3',
                )}
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">五、門市配送與退貨</h2>
              <p className="mb-4 text-sm text-slate-500">
                若不提供門市直送，系統會將配送欄位標示為不適用。
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                {boolField('store_delivery_enabled', '是否提供門市直送')}
                {textField('store_receiving_method', '門市收貨方式', true)}
                {textField('store_delivery_cycle', '門市配送週期', true)}
                {textField('store_order_cutoff_time', '門市訂單截止時間', true)}
                {textField('store_average_delivery_days', '門市平均到貨天數', true, 'number')}
                {textField('store_minimum_order_amount', '門市最低訂購金額', true, 'number')}
                {textField('store_minimum_order_quantity', '門市最低訂購數量', true, 'number')}
                {textField('store_free_shipping_threshold', '門市免運門檻', true, 'number')}
                {textField('store_shipping_rule', '門市運費規則', true)}
                {boolField('store_return_to_vendor_enabled', '門市是否可直接退回廠商')}
                {textField('store_return_method', '門市退貨方式', true)}
                {textField('store_return_deadline_days', '門市退貨期限（天）', true, 'number')}
                {textField('store_return_shipping_bearer', '門市退貨運費負擔', true)}
                {boolField('store_return_vendor_approval_required', '需廠商確認')}
                {boolField('store_return_buyer_approval_required', '需採購確認')}
                {textField('store_return_route', '門市退貨路徑')}
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">六、發票與對帳</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <Select
                  label="發票格式"
                  required
                  value={value('invoice_format')}
                  onChange={(event) => update('invoice_format', event.target.value)}
                >
                  <option value="">請選擇</option>
                  <option value="電子發票">電子發票</option>
                  <option value="紙本發票">紙本發票</option>
                  <option value="OTHER">其他</option>
                </Select>
                {textField('invoice_format_other', '發票格式補充')}
                {textField('invoice_issue_method', '發票開立方式', true)}
                {textField('invoice_delivery_method', '發票寄送方式', true)}
                {textField('invoice_contact_name', '發票聯絡人', true)}
                {textField('invoice_contact_phone', '發票聯絡電話', true)}
                {textField('invoice_email', '發票 Email', true, 'email')}
                {textField('reconciliation_contact_name', '對帳聯絡人', true)}
                {textField('reconciliation_contact_phone', '對帳聯絡電話', true)}
                {textField('reconciliation_email', '對帳 Email', true, 'email')}
                {textField('reconciliation_cycle', '對帳週期', true)}
                {boolField('invoice_available', '是否可提供發票')}
                {boolField('product_liability_insurance', '是否有產品責任保險')}
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">七、銀行資料</h2>
              <p className="mb-4 text-sm text-slate-500">
                僅在付款方式為銀行匯款時需要填寫；帳號會加密儲存。
              </p>
              {bankRequired ? (
                <div className="grid gap-4 md:grid-cols-3">
                  {textField('bank_name', '銀行名稱', true)}
                  {textField('bank_code', '銀行代碼', true)}
                  {textField('branch_name', '分行名稱', true)}
                  {textField('branch_code', '分行代碼', true)}
                  {textField('bank_account_name', '戶名', true)}
                  {textField('bank_account_number', '銀行帳號', true)}
                </div>
              ) : (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                  目前付款方式不需填寫銀行帳號。
                </p>
              )}
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">八、公司附件</h2>
              <p className="mb-4 text-sm text-slate-500">
                檔案會上傳至受控儲存空間；正式送審前必須完成所有標示「*【必填】」的附件。
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {requirements.map((requirement) => {
                  const existing = existingAttachments.find(
                    (item) => item.type === requirement.type,
                  );
                  const pending = pendingFiles[requirement.type];
                  return (
                    <label
                      key={requirement.id}
                      className="rounded-2xl border border-dashed border-slate-200 p-4"
                    >
                      <span className="block text-sm font-bold">
                        {requirement.label ||
                          VENDOR_APPLICATION_ATTACHMENT_LABELS[requirement.type] ||
                          requirement.type}
                        <span className="ml-2 text-rose-600">*【必填】</span>
                      </span>
                      <input
                        type="file"
                        className="mt-3 block w-full text-sm"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          const id = applicationId || editingApplication?.id;
                          if (!id) {
                            setPendingFiles((current) => ({
                              ...current,
                              [requirement.type]: file,
                            }));
                            setDirty(true);
                            notify('已選取附件，儲存草稿後會上傳。', 'info');
                            return;
                          }
                          void api.vendorProactive
                            .uploadApplicationAttachment(id, requirement.type, file)
                            .then(async () => {
                              await refreshApplication();
                              notify('附件已上傳。', 'success');
                            })
                            .catch((error) => notify(errorMessage(error), 'error'));
                        }}
                      />
                      {(existing || pending) && (
                        <p className="mt-2 text-xs text-emerald-700">
                          已完成：{pending?.name ?? existing?.original_name}
                        </p>
                      )}
                    </label>
                  );
                })}
              </div>
              {missingAttachments.length > 0 && (
                <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                  尚缺附件：{missingAttachments.map((item) => item.label).join('、')}
                </p>
              )}
              {existingAttachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {existingAttachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={api.vendorProactive.applicationAttachmentDownloadUrl(attachment.id)}
                      className="block rounded-xl bg-slate-50 p-3 text-sm text-teal-700 hover:bg-teal-50"
                    >
                      {attachment.original_name}（下載）
                    </a>
                  ))}
                </div>
              )}
            </Card>

            <div className="flex flex-col justify-end gap-3 border-t border-slate-200 pt-6 sm:flex-row">
              {!editingApplication && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void saveDraft()}
                  loading={saving}
                >
                  儲存草稿
                </Button>
              )}
              <Button type="submit" loading={saving} disabled={missingAttachments.length > 0}>
                {editingApplication ? '修改完成並重新送審' : '正式提交申請'}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

const VENDOR_APPLICATION_V2_NUMBER_KEYS = [
  'monthly_payment_days',
  'delivery_minimum_order_amount',
  'delivery_minimum_order_quantity',
  'freight_threshold_amount',
  'freight_fixed_amount',
] as const;

const VENDOR_APPLICATION_V2_BOOLEAN_KEYS = [
  'payment_rule_acknowledged',
  'invoice_same_as_primary',
  'reconciliation_same_as_primary',
  'reconciliation_terms_acknowledged',
] as const;

const VENDOR_APPLICATION_V2_ARRAY_KEYS = [
  'principal_brands',
  'supplied_product_categories',
  'delivery_weekdays',
  'return_condition_types',
] as const;

const EMPTY_VENDOR_APPLICATION_V2_FORM: Record<string, unknown> = {
  company_name: '',
  company_short_name: '',
  tax_id: '',
  phone: '',
  address: '',
  company_email: '',
  website: '',
  company_fax: '',
  company_intro: '',
  principal_brands: [''],
  supplied_product_categories: [],
  contact_name: '',
  contact_job_title: '',
  contact_phone: '',
  contact_email: '',
  vendor_closing_day: '',
  daily_order_cutoff_time: '',
  payment_term: '',
  monthly_payment_days: '',
  payment_method: '',
  payment_method_other: '',
  weekly_payment_note: '',
  payment_rule_acknowledged: false,
  delivery_scope: '',
  delivery_cycle: '',
  delivery_weekdays: [],
  delivery_cycle_other: '',
  delivery_minimum_order_amount: '',
  delivery_minimum_order_quantity: '',
  freight_rule: '',
  freight_threshold_amount: '',
  freight_fixed_amount: '',
  freight_other_note: '',
  return_scope: '',
  return_handling_method: '',
  return_condition_types: [],
  return_near_expiry_condition: '',
  return_defect_condition: '',
  return_intolerance_condition: '',
  return_other_condition: '',
  return_condition_description: '',
  return_freight_responsibility: '',
  return_freight_rule_note: '',
  invoice_format: '',
  invoice_format_other: '',
  invoice_delivery_method: '',
  invoice_delivery_other: '',
  invoice_same_as_primary: true,
  invoice_contact_name: '',
  invoice_contact_phone: '',
  invoice_email: '',
  reconciliation_same_as_primary: true,
  reconciliation_contact_name: '',
  reconciliation_contact_phone: '',
  reconciliation_email: '',
  reconciliation_terms_acknowledged: false,
  bank_name: '',
  bank_code: '',
  branch_name: '',
  branch_code: '',
  bank_account_name: '',
  bank_account_number: '',
  notes: '',
};

function vendorApplicationV2ToForm(application: VendorApplication): Record<string, unknown> {
  const source = application as unknown as Record<string, unknown>;
  const form = { ...EMPTY_VENDOR_APPLICATION_V2_FORM };
  Object.keys(form).forEach((key) => {
    if (source[key] !== undefined && source[key] !== null) form[key] = source[key];
  });
  const brands = application.principal_brands?.filter(Boolean) ?? [];
  form.principal_brands = brands.length
    ? brands
    : application.primary_brand
      ? [application.primary_brand]
      : [''];
  const suppliedCategories = application.supplied_product_categories?.filter(Boolean) ?? [];
  form.supplied_product_categories = suppliedCategories.length
    ? suppliedCategories
    : application.primary_category_code
      ? [application.primary_category_code]
      : [];
  form.company_short_name = application.company_short_name ?? application.short_name ?? '';
  form.contact_name = application.contact?.contact_name ?? '';
  form.contact_job_title =
    application.contact?.contact_job_title ?? application.contact?.title ?? '';
  form.contact_phone = application.contact?.phone ?? '';
  form.contact_email = application.contact?.email ?? application.company_email;
  VENDOR_APPLICATION_V2_NUMBER_KEYS.forEach((key) => {
    form[key] = source[key] === null || source[key] === undefined ? '' : String(source[key]);
  });
  VENDOR_APPLICATION_V2_BOOLEAN_KEYS.forEach((key) => {
    form[key] =
      Number(
        source[key] ??
          (key === 'invoice_same_as_primary' || key === 'reconciliation_same_as_primary' ? 1 : 0),
      ) === 1;
  });
  VENDOR_APPLICATION_V2_ARRAY_KEYS.forEach((key) => {
    form[key] = Array.isArray(source[key]) ? source[key] : [];
  });
  form.delivery_weekdays = application.delivery_weekdays?.length
    ? application.delivery_weekdays
    : (application.delivery_fixed_weekdays ?? []);
  form.return_condition_types = application.return_condition_types?.length
    ? application.return_condition_types
    : (application.return_conditions ?? []);
  form.bank_account_number = '';
  return form;
}

function vendorApplicationV2Payload(form: Record<string, unknown>, mode: 'DRAFT' | 'SUBMIT') {
  const payload: Record<string, unknown> = { ...form, submission_mode: mode };
  VENDOR_APPLICATION_V2_NUMBER_KEYS.forEach((key) => {
    const current = String(form[key] ?? '').trim();
    payload[key] = current === '' ? null : Number(current);
  });
  VENDOR_APPLICATION_V2_BOOLEAN_KEYS.forEach((key) => {
    payload[key] = Boolean(form[key]);
  });
  VENDOR_APPLICATION_V2_ARRAY_KEYS.forEach((key) => {
    const current = Array.isArray(form[key]) ? form[key] : [];
    payload[key] = current.map((item) => String(item).trim()).filter(Boolean);
  });
  payload.principal_brands = (payload.principal_brands as string[]).filter(Boolean);
  return payload;
}

export function VendorApplicationPageV2({ notify }: PageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRequested = searchParams.get('edit') === '1';
  const entryTaxId = searchParams.get('tax_id')?.replace(/\s|-/g, '').trim() ?? '';
  const [form, setForm] = useState<Record<string, unknown>>({
    ...EMPTY_VENDOR_APPLICATION_V2_FORM,
    tax_id: entryTaxId,
  });
  const [requirements, setRequirements] = useState<AttachmentRequirement[]>([]);
  const [applicationId, setApplicationId] = useState('');
  const [applicationRecord, setApplicationRecord] = useState<VendorApplication | null>(null);
  const [editingApplication, setEditingApplication] = useState<VendorApplication | null>(null);
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editRequested);
  const [dirty, setDirty] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<{
    application_id: string;
    application_no?: string;
    direct_access?: boolean;
    challenge_id?: string;
    dev_code?: string;
    draft?: boolean;
    resubmitted?: boolean;
  } | null>(null);
  useUnsavedPrompt(dirty);

  const value = (key: string) => String(form[key] ?? '');
  const arrayValue = (key: string) =>
    Array.isArray(form[key]) ? (form[key] as unknown[]).map(String) : [];
  const booleanValue = (key: string) => Boolean(form[key]);
  const update = (key: string, nextValue: unknown) => {
    setDirty(true);
    setSubmitError('');
    setForm((current) => ({ ...current, [key]: nextValue }));
  };
  const toggleArray = (key: string, item: string) => {
    const current = arrayValue(key);
    update(
      key,
      current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item],
    );
  };
  const textField = (
    key: string,
    label: string,
    required = false,
    type = 'text',
    className = '',
  ) => (
    <Input
      className={className}
      label={label}
      required={required}
      type={type}
      value={value(key)}
      onChange={(event) => update(key, event.target.value)}
    />
  );
  const areaField = (key: string, label: string, required = false, className = '', hint = '') => (
    <Textarea
      className={className}
      hint={hint}
      label={label}
      required={required}
      value={value(key)}
      onChange={(event) => update(key, event.target.value)}
    />
  );
  const selectField = (
    key: string,
    label: string,
    options: Record<string, string>,
    required = false,
  ) => (
    <Select
      label={label}
      required={required}
      value={value(key)}
      onChange={(event) => update(key, event.target.value)}
    >
      <option value="">請選擇</option>
      {Object.entries(options).map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>
          {optionLabel}
        </option>
      ))}
    </Select>
  );
  const checkboxList = (key: string, labels: Record<string, string>) => (
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.entries(labels).map(([item, label]) => (
        <label
          key={item}
          className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${arrayValue(key).includes(item) ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-100 bg-slate-50 text-slate-700'}`}
        >
          <input
            type="checkbox"
            checked={arrayValue(key).includes(item)}
            onChange={() => toggleArray(key, item)}
            className="h-4 w-4 accent-teal-600"
          />
          {label}
        </label>
      ))}
    </div>
  );
  const scopeChoices = (key: string, label: string) => (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold text-ink">
        {label}
        <span className="ml-1 text-rose-600">*</span>
      </legend>
      <div className="grid gap-2 sm:grid-cols-3">
        {Object.entries(VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS).map(([scope, scopeLabel]) => (
          <label
            key={scope}
            className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-3 text-sm ${value(key) === scope ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-100 bg-slate-50 text-slate-700'}`}
          >
            <input
              type="radio"
              name={key}
              value={scope}
              checked={value(key) === scope}
              onChange={(event) => update(key, event.target.value)}
              className="accent-teal-600"
            />
            {scopeLabel}
          </label>
        ))}
      </div>
    </fieldset>
  );

  useEffect(() => {
    let active = true;
    if (entryTaxId) setForm((current) => ({ ...current, tax_id: entryTaxId }));
    api.vendorProactive
      .applicationAttachmentRequirements()
      .then((result) => {
        if (active) setRequirements(result.requirements);
      })
      .catch((error) => {
        if (active) notify(errorMessage(error), 'error');
      });
    if (!editRequested) {
      api.vendorProactive
        .applicationDetail()
        .then((application) => {
          if (!active || application.status !== 'DRAFT') return;
          setApplicationRecord(application);
          setApplicationId(application.id);
          setForm(vendorApplicationV2ToForm(application));
        })
        .catch((error) => {
          if (!(error instanceof ApiError && [401, 404].includes(error.status)))
            notify(errorMessage(error), 'error');
        })
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => {
        active = false;
      };
    }
    api.vendorProactive
      .applicationDetail()
      .then((application) => {
        if (!active) return;
        if (application.status !== 'REJECTED') {
          notify('目前沒有可修改的退件申請。', 'error');
          return;
        }
        setEditingApplication(application);
        setApplicationRecord(application);
        setApplicationId(application.id);
        setForm(vendorApplicationV2ToForm(application));
      })
      .catch((error) => {
        if (active) notify(errorMessage(error), 'error');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [editRequested, entryTaxId, notify]);

  const rememberApplication = (id: string) => {
    setApplicationId(id);
  };
  const refreshApplication = async () => {
    try {
      const application = await api.vendorProactive.applicationDetail();
      setApplicationRecord(application);
      setApplicationId(application.id);
    } catch {
      // 未完成 Email 驗證時，公開端點尚未建立 session；不影響草稿資料。
    }
  };
  const uploadFiles = async (id: string) => {
    for (const [type, file] of Object.entries(pendingFiles))
      await api.vendorProactive.uploadApplicationAttachment(id, type, file);
    if (Object.keys(pendingFiles).length) {
      setPendingFiles({});
      await refreshApplication();
    }
  };
  const saveDraft = async () => {
    if (editingApplication) {
      notify('退件申請請使用「修改完成並重新送審」。', 'info');
      return;
    }
    setSaving(true);
    setSubmitError('');
    try {
      const response = await api.vendorProactive.application(
        vendorApplicationV2Payload(form, 'DRAFT'),
      );
      rememberApplication(response.application_id);
      if (response.direct_access) await uploadFiles(response.application_id);
      setDirty(false);
      notify('草稿已儲存，可繼續補齊資料及附件。', 'success');
    } catch (error) {
      const message = errorMessage(error);
      setSubmitError(message);
      notify(message, 'error');
    } finally {
      setSaving(false);
    }
  };
  const submitApplication = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSubmitError('');
    try {
      let id = applicationId || editingApplication?.id;
      if (editingApplication) {
        await uploadFiles(editingApplication.id);
        const application = await api.vendorProactive.applicationResubmit(
          vendorApplicationV2Payload(form, 'SUBMIT'),
        );
        setDirty(false);
        setResult({
          application_id: application.id,
          application_no: application.application_no,
          direct_access: true,
          resubmitted: true,
        });
        notify('申請已修改並重新送審。', 'success');
        return;
      }
      if (!id) {
        const draft = await api.vendorProactive.application(
          vendorApplicationV2Payload(form, 'DRAFT'),
        );
        id = draft.application_id;
        rememberApplication(id);
        if (draft.direct_access) await uploadFiles(id);
      } else {
        await uploadFiles(id);
      }
      const response = await api.vendorProactive.application(
        vendorApplicationV2Payload(form, 'SUBMIT'),
      );
      setDirty(false);
      if (response.direct_access) {
        setResult({ ...response, direct_access: true });
        await refreshApplication();
        notify(response.draft ? '草稿已儲存。' : '廠商申請已送出。', 'success');
        return;
      }
      const verification = await api.vendorProactive.requestVerification({
        mode: 'NEW_VENDOR',
        tax_id: value('tax_id'),
        email: value('contact_email'),
      });
      setResult({
        ...response,
        challenge_id: verification.challenge_id,
        dev_code: verification.dev_code,
      });
      notify('申請已建立，請依 Email 驗證後繼續。', 'success');
    } catch (error) {
      const message = errorMessage(error);
      setSubmitError(message);
      notify(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const existingAttachments = applicationRecord?.application_attachments ?? [];
  const availableAttachmentTypes = new Set([
    ...existingAttachments.map((attachment) => attachment.type),
    ...Object.keys(pendingFiles),
  ]);
  const missingAttachments = requirements.filter(
    (requirement) => requirement.required === 1 && !availableAttachmentTypes.has(requirement.type),
  );
  const bankRequired = value('payment_method') === 'BANK_TRANSFER';

  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  return (
    <div className="min-h-screen bg-sand">
      <VendorPortalHeader>
        <Link to="/vendor/proposal" className="text-sm font-bold text-teal-700">
          返回廠商入口
        </Link>
      </VendorPortalHeader>
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-10">
        <div className="mb-6 max-w-4xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
            NEW VENDOR APPLICATION V2
          </p>
          <h1 className="mt-2 text-3xl font-extrabold text-ink">
            {editingApplication ? '退件廠商申請修改與重新送審' : '新廠商申請'}
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            請依序完成公司、聯絡人、交易、配送退貨及發票對帳資料。資料未完成時可以儲存草稿，但不能正式送出申請。
          </p>
          <p className="mt-2 font-bold text-rose-600">
            * 為必填欄位，資料未完成時可儲存草稿，但無法正式送出申請。
          </p>
        </div>
        {editingApplication && (
          <Card className="mb-6 border-rose-200 bg-rose-50">
            <h2 className="font-extrabold text-rose-900">退件原因</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-rose-900">
              {editingApplication.review_reason || '採購人員未提供文字原因，請補齊資料後重新送審。'}
            </p>
          </Card>
        )}
        {submitError && (
          <Card className="mb-6 border-rose-200 bg-rose-50">
            <h2 className="font-extrabold text-rose-900">請檢查欄位內容</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-rose-900">
              {submitError}
            </p>
          </Card>
        )}
        {result ? (
          <Card className="max-w-3xl border-emerald-200">
            <h2 className="text-xl font-extrabold text-emerald-800">
              {result.resubmitted ? '已重新送審' : result.draft ? '草稿已儲存' : '申請已送出'}
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {result.resubmitted ? '採購人員將重新審核。' : '可返回廠商入口查看進度。'}
            </p>
            {result.dev_code && (
              <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                測試驗證碼：<strong className="tracking-[0.2em]">{result.dev_code}</strong>
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => navigate('/vendor/proposal')}>返回廠商入口</Button>
              <Button variant="secondary" onClick={() => setResult(null)}>
                繼續編輯
              </Button>
            </div>
          </Card>
        ) : (
          <form onSubmit={submitApplication} className="space-y-6">
            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 1｜公司基本資料</h2>
              <div className="grid gap-4 md:grid-cols-3">
                {textField('company_name', '公司名稱', true)}
                {textField('company_short_name', '公司簡稱')}
                {textField('tax_id', '統一編號', true)}
                {textField('phone', '公司電話', true)}
                {textField('company_email', '公司 Email', true, 'email')}
                {textField('website', '公司網站')}
                {textField('company_fax', '傳真')}
                {areaField('address', '公司地址', true, 'md:col-span-3')}
                {areaField('company_intro', '公司簡介', false, 'md:col-span-3')}
              </div>
              <div className="mt-5 border-t border-slate-100 pt-5">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-extrabold">
                      主要代理／品牌 <span className="text-rose-600">*</span>
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      請填寫目前主要代理、經銷或自有品牌；如有多個品牌請以多筆方式新增。
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      update('principal_brands', [...arrayValue('principal_brands'), ''])
                    }
                  >
                    ＋ 新增品牌
                  </Button>
                </div>
                <div className="space-y-2">
                  {arrayValue('principal_brands').map((brand, index) => (
                    <div key={`brand-${index}`} className="flex gap-2">
                      <Input
                        className="flex-1"
                        label={`品牌 ${index + 1}`}
                        required={index === 0}
                        value={brand}
                        onChange={(event) => {
                          const next = arrayValue('principal_brands');
                          next[index] = event.target.value;
                          update('principal_brands', next);
                        }}
                      />
                      {arrayValue('principal_brands').length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="mt-7"
                          onClick={() =>
                            update(
                              'principal_brands',
                              arrayValue('principal_brands').filter(
                                (_, itemIndex) => itemIndex !== index,
                              ),
                            )
                          }
                        >
                          移除
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <fieldset className="mt-5 border-t border-slate-100 pt-5">
                <legend className="mb-2 text-sm font-extrabold">
                  供應商品類 <span className="text-rose-600">*</span>
                </legend>
                <p className="mb-3 text-xs text-slate-500">
                  請勾選貴公司目前可以供應的商品類別，可複選。
                </p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {PRODUCT_CATEGORY_OPTIONS.map((category) => (
                    <label
                      key={category.code}
                      className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${arrayValue('supplied_product_categories').includes(category.code) ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-100 bg-slate-50 text-slate-700'}`}
                    >
                      <input
                        type="checkbox"
                        checked={arrayValue('supplied_product_categories').includes(category.code)}
                        onChange={() => toggleArray('supplied_product_categories', category.code)}
                        className="h-4 w-4 accent-teal-600"
                      />
                      {category.code}｜{category.name}
                    </label>
                  ))}
                </div>
              </fieldset>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 2｜主要聯絡人</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {textField('contact_name', '聯絡人姓名', true)}
                {textField('contact_job_title', '聯絡人職稱')}
                {textField('contact_phone', '聯絡電話', true)}
                {textField('contact_email', '聯絡 Email', true, 'email')}
                <Select
                  label="廠商結帳日"
                  required
                  value={value('vendor_closing_day')}
                  onChange={(event) => update('vendor_closing_day', event.target.value)}
                >
                  <option value="">請選擇</option>
                  {Array.from({ length: 31 }, (_, index) => String(index + 1)).map((day) => (
                    <option key={day} value={day}>
                      {day}日
                    </option>
                  ))}
                  <option value="月底">月底</option>
                </Select>
                {textField('daily_order_cutoff_time', '廠商每日訂單截止時間', true, 'time')}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                請填寫貴公司每月結帳截止日，供我司採購與對帳作業使用。每日訂單截止時間超過後，訂單將依下一配送週期處理。
              </p>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 3｜交易條件</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <Select
                  label="付款條件"
                  required
                  value={value('payment_term')}
                  onChange={(event) => {
                    const next = event.target.value;
                    update('payment_term', next);
                    if (next === 'CASH_ON_DELIVERY') update('payment_method', 'CASH');
                    else if (value('payment_term') === 'CASH_ON_DELIVERY')
                      update('payment_method', '');
                  }}
                >
                  <option value="">請選擇</option>
                  {Object.entries(VENDOR_APPLICATION_PAYMENT_TERM_LABELS).map(([term, label]) => (
                    <option key={term} value={term}>
                      {label}
                    </option>
                  ))}
                </Select>
                {value('payment_term') === 'MONTHLY' &&
                  textField('monthly_payment_days', '月結天數', true, 'number')}
                {value('payment_term') === 'WEEKLY' &&
                  areaField(
                    'weekly_payment_note',
                    '週結說明',
                    false,
                    '',
                    '可填每週結算日期或特殊說明',
                  )}
                <Select
                  label="付款方式"
                  required
                  value={value('payment_method')}
                  disabled={value('payment_term') === 'CASH_ON_DELIVERY'}
                  onChange={(event) => update('payment_method', event.target.value)}
                >
                  <option value="">請選擇</option>
                  {Object.entries(VENDOR_APPLICATION_PAYMENT_METHOD_LABELS).map(
                    ([method, label]) => (
                      <option key={method} value={method}>
                        {label}
                      </option>
                    ),
                  )}
                </Select>
                {value('payment_term') === 'CASH_ON_DELIVERY' && (
                  <p className="rounded-xl bg-sky-50 p-3 text-sm text-sky-900">
                    付款方式：現金。下貨收現之付款方式固定為現金。
                  </p>
                )}
                {value('payment_method') === 'OTHER' &&
                  textField('payment_method_other', '其他付款方式說明', true)}
              </div>
              {value('payment_method') === 'CHECK' && (
                <label className="mt-4 block rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-bold">{VENDOR_APPLICATION_CHECK_NOTICE}</p>
                  <span className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={booleanValue('payment_rule_acknowledged')}
                      onChange={(event) =>
                        update('payment_rule_acknowledged', event.target.checked)
                      }
                      className="h-4 w-4 accent-teal-600"
                    />
                    我已了解上述支票付款規則 <span className="text-rose-600">*</span>
                  </span>
                </label>
              )}
              {value('payment_method') === 'BANK_TRANSFER' && (
                <label className="mt-4 block rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-bold">{VENDOR_APPLICATION_BANK_TRANSFER_NOTICE}</p>
                  <span className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={booleanValue('payment_rule_acknowledged')}
                      onChange={(event) =>
                        update('payment_rule_acknowledged', event.target.checked)
                      }
                      className="h-4 w-4 accent-teal-600"
                    />
                    我已了解上述匯款付款規則 <span className="text-rose-600">*</span>
                  </span>
                </label>
              )}
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 4｜配送與退貨</h2>
              <div className="space-y-6">
                <section>
                  <h3 className="mb-4 text-base font-extrabold">配送</h3>
                  {scopeChoices('delivery_scope', '配送地點')}
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {selectField(
                      'delivery_cycle',
                      '廠商配送週期',
                      VENDOR_APPLICATION_DELIVERY_CYCLE_LABELS,
                      true,
                    )}
                    {textField('delivery_minimum_order_amount', '最低訂購金額', true, 'number')}
                    {textField('delivery_minimum_order_quantity', '最低訂購數量', true, 'number')}
                    {selectField(
                      'freight_rule',
                      '廠商運費規則',
                      VENDOR_APPLICATION_FREIGHT_RULE_LABELS,
                      true,
                    )}
                  </div>
                  {value('delivery_cycle') === 'FIXED_WEEKDAYS' && (
                    <div className="mt-4">
                      <p className="mb-2 text-sm font-semibold">
                        固定配送星期 <span className="text-rose-600">*</span>{' '}
                        <span className="font-normal text-slate-500">（可複選）</span>
                      </p>
                      {checkboxList('delivery_weekdays', VENDOR_APPLICATION_WEEKDAY_LABELS)}
                    </div>
                  )}
                  {value('delivery_cycle') === 'OTHER' && (
                    <div className="mt-4">
                      {areaField('delivery_cycle_other', '其他配送週期說明', true)}
                    </div>
                  )}
                  {value('freight_rule') === 'THRESHOLD' && (
                    <div className="mt-4 max-w-md">
                      {textField('freight_threshold_amount', '免運門檻', true, 'number')}
                    </div>
                  )}
                  {value('freight_rule') === 'FIXED' && (
                    <div className="mt-4 max-w-md">
                      {textField('freight_fixed_amount', '固定運費金額', true, 'number')}
                    </div>
                  )}
                  {value('freight_rule') === 'OTHER' && (
                    <div className="mt-4">
                      {areaField('freight_other_note', '其他運費規則說明', true)}
                    </div>
                  )}
                </section>
                <section className="border-t border-slate-100 pt-6">
                  <h3 className="mb-4 text-base font-extrabold">退貨</h3>
                  {scopeChoices('return_scope', '退貨地點')}
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {selectField(
                      'return_handling_method',
                      '退貨處理方式',
                      VENDOR_APPLICATION_RETURN_HANDLING_LABELS,
                      true,
                    )}
                    {selectField(
                      'return_freight_responsibility',
                      '退貨／換貨運費負擔',
                      VENDOR_APPLICATION_RETURN_FREIGHT_LABELS,
                      true,
                    )}
                  </div>
                  <div className="mt-4">
                    <p className="mb-2 text-sm font-semibold">
                      退貨／換貨成立條件 <span className="text-rose-600">*</span>{' '}
                      <span className="font-normal text-slate-500">（可複選）</span>
                    </p>
                    {checkboxList(
                      'return_condition_types',
                      VENDOR_APPLICATION_RETURN_CONDITION_LABELS,
                    )}
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {arrayValue('return_condition_types').includes('NEAR_EXPIRY') &&
                      areaField('return_near_expiry_condition', '即期退換貨條件', true)}
                    {arrayValue('return_condition_types').includes('DEFECT') &&
                      areaField('return_defect_condition', '瑕疵退換貨條件', true)}
                    {arrayValue('return_condition_types').includes('INTOLERANCE') &&
                      areaField('return_intolerance_condition', '吃不合退換貨條件', true)}
                    {arrayValue('return_condition_types').includes('OTHER') &&
                      areaField('return_other_condition', '其他退換貨條件', true)}
                  </div>
                  {areaField(
                    'return_condition_description',
                    '退貨／換貨詳細說明',
                    true,
                    'mt-4',
                    '請清楚說明可退貨或換貨的商品狀況、期限、數量限制及處理方式。',
                  )}
                  {value('return_freight_responsibility') === 'BY_REASON' && (
                    <div className="mt-4">
                      {areaField('return_freight_rule_note', '運費負擔規則說明', true)}
                    </div>
                  )}
                </section>
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 5｜發票與對帳</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {selectField(
                  'invoice_format',
                  '發票格式',
                  VENDOR_APPLICATION_INVOICE_FORMAT_LABELS,
                  true,
                )}
                {value('invoice_format') === 'OTHER' &&
                  textField('invoice_format_other', '其他發票格式說明', true)}
                {selectField(
                  'invoice_delivery_method',
                  '發票寄送方式',
                  VENDOR_APPLICATION_INVOICE_DELIVERY_LABELS,
                  true,
                )}
                {value('invoice_delivery_method') === 'OTHER' &&
                  areaField('invoice_delivery_other', '其他發票寄送方式及原因', true)}
              </div>
              <div className="mt-5 grid gap-5 md:grid-cols-2">
                <section className="rounded-2xl border border-slate-100 p-4">
                  <h3 className="font-extrabold">
                    發票聯絡人 <span className="text-rose-600">*</span>
                  </h3>
                  <label className="mt-3 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={booleanValue('invoice_same_as_primary')}
                      onChange={(event) => update('invoice_same_as_primary', event.target.checked)}
                      className="h-4 w-4 accent-teal-600"
                    />
                    同主要聯絡人
                  </label>
                  {!booleanValue('invoice_same_as_primary') && (
                    <div className="mt-4 grid gap-4">
                      {textField('invoice_contact_name', '發票聯絡人姓名', true)}
                      {textField('invoice_contact_phone', '發票聯絡電話', true)}
                      {textField('invoice_email', '發票聯絡 Email', true, 'email')}
                    </div>
                  )}
                  {booleanValue('invoice_same_as_primary') && (
                    <p className="mt-3 text-xs text-slate-500">
                      將同步主要聯絡人姓名、電話及 Email。
                    </p>
                  )}
                </section>
                <section className="rounded-2xl border border-slate-100 p-4">
                  <h3 className="font-extrabold">
                    對帳聯絡人 <span className="text-rose-600">*</span>
                  </h3>
                  <label className="mt-3 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={booleanValue('reconciliation_same_as_primary')}
                      onChange={(event) =>
                        update('reconciliation_same_as_primary', event.target.checked)
                      }
                      className="h-4 w-4 accent-teal-600"
                    />
                    同主要聯絡人
                  </label>
                  {!booleanValue('reconciliation_same_as_primary') && (
                    <div className="mt-4 grid gap-4">
                      {textField('reconciliation_contact_name', '對帳聯絡人姓名', true)}
                      {textField('reconciliation_contact_phone', '對帳聯絡電話', true)}
                      {textField('reconciliation_email', '對帳 Email', true, 'email')}
                    </div>
                  )}
                  {booleanValue('reconciliation_same_as_primary') && (
                    <p className="mt-3 text-xs text-slate-500">
                      將同步主要聯絡人姓名、電話及 Email。
                    </p>
                  )}
                </section>
              </div>
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
                <p className="font-bold">{VENDOR_APPLICATION_RECONCILIATION_NOTICE}</p>
                <label className="mt-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={booleanValue('reconciliation_terms_acknowledged')}
                    onChange={(event) =>
                      update('reconciliation_terms_acknowledged', event.target.checked)
                    }
                    className="h-4 w-4 accent-teal-600"
                  />
                  我已閱讀並了解上述對帳條件 <span className="text-rose-600">*</span>
                </label>
              </div>
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 6｜銀行與付款資料</h2>
              <p className="mb-4 text-sm text-slate-500">
                付款方式選擇匯款時，以下銀行資料為必填；帳號會加密儲存，畫面不會回傳完整帳號。
              </p>
              {bankRequired ? (
                <div className="grid gap-4 md:grid-cols-3">
                  {textField('bank_name', '銀行名稱', true)}
                  {textField('bank_code', '銀行代碼', true)}
                  {textField('branch_name', '分行名稱', true)}
                  {textField('branch_code', '分行代碼', true)}
                  {textField('bank_account_name', '戶名', true)}
                  {textField('bank_account_number', '銀行帳號', true)}
                </div>
              ) : (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                  目前付款方式不需填寫銀行帳號。
                </p>
              )}
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 7｜附件</h2>
              <p className="mb-4 text-sm text-slate-500">
                附件會上傳至受控儲存空間；正式送審前必須完成所有標示「*【必填】」的文件。
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {requirements.map((requirement) => {
                  const existing = existingAttachments.find(
                    (attachment) => attachment.type === requirement.type,
                  );
                  const pending = pendingFiles[requirement.type];
                  return (
                    <label
                      key={requirement.id}
                      className="rounded-2xl border border-dashed border-slate-200 p-4"
                    >
                      <span className="block text-sm font-bold">
                        {requirement.label ||
                          VENDOR_APPLICATION_ATTACHMENT_LABELS[requirement.type] ||
                          requirement.type}
                        {requirement.required === 1 && (
                          <span className="ml-2 text-rose-600">*【必填】</span>
                        )}
                      </span>
                      <input
                        type="file"
                        className="mt-3 block w-full text-sm"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          const id = applicationId;
                          if (!id) {
                            setPendingFiles((current) => ({
                              ...current,
                              [requirement.type]: file,
                            }));
                            setDirty(true);
                            notify('已選取附件，儲存草稿後會上傳。', 'info');
                            return;
                          }
                          void api.vendorProactive
                            .uploadApplicationAttachment(id, requirement.type, file)
                            .then(async () => {
                              await refreshApplication();
                              notify('附件已上傳。', 'success');
                            })
                            .catch((error) => notify(errorMessage(error), 'error'));
                        }}
                      />
                      {(existing || pending) && (
                        <p className="mt-2 text-xs text-emerald-700">
                          已完成：{pending?.name ?? existing?.original_name}
                        </p>
                      )}
                    </label>
                  );
                })}
              </div>
              {missingAttachments.length > 0 && (
                <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                  尚缺附件：{missingAttachments.map((item) => item.label).join('、')}
                </p>
              )}
              {existingAttachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {existingAttachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={api.vendorProactive.applicationAttachmentDownloadUrl(attachment.id)}
                      className="block rounded-xl bg-slate-50 p-3 text-sm text-teal-700 hover:bg-teal-50"
                    >
                      {attachment.original_name}（下載）
                    </a>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <h2 className="mb-5 text-xl font-extrabold">Step 8｜預覽與提交</h2>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <p>
                  <span className="text-slate-500">公司：</span>
                  {value('company_name') || '尚未填寫'}
                </p>
                <p>
                  <span className="text-slate-500">供應商品類：</span>
                  {arrayValue('supplied_product_categories').join('、') || '尚未選擇'}
                </p>
                <p>
                  <span className="text-slate-500">付款條件：</span>
                  {VENDOR_APPLICATION_PAYMENT_TERM_LABELS[value('payment_term')] || '尚未選擇'}
                </p>
                <p>
                  <span className="text-slate-500">配送地點：</span>
                  {VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS[value('delivery_scope')] || '尚未選擇'}
                </p>
                <p>
                  <span className="text-slate-500">退貨地點：</span>
                  {VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS[value('return_scope')] || '尚未選擇'}
                </p>
                <p>
                  <span className="text-slate-500">發票格式：</span>
                  {VENDOR_APPLICATION_INVOICE_FORMAT_LABELS[value('invoice_format')] || '尚未選擇'}
                </p>
              </div>
              <div className="mt-5 flex flex-col justify-end gap-3 border-t border-slate-200 pt-6 sm:flex-row">
                {!editingApplication && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void saveDraft()}
                    loading={saving}
                  >
                    儲存草稿
                  </Button>
                )}
                <Button type="submit" loading={saving} disabled={missingAttachments.length > 0}>
                  {editingApplication ? '修改完成並重新送審' : '正式提交申請'}
                </Button>
              </div>
            </Card>
          </form>
        )}
      </main>
    </div>
  );
}

function VendorProfileValue({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: unknown;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'md:col-span-2' : undefined}>
      <dt className="text-xs font-semibold text-slate-500">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap break-words text-sm text-ink">
        {displayVendorValue(value)}
      </dd>
    </div>
  );
}

type ImportantVendorChangeDraft = {
  payment_term: string;
  monthly_payment_days: string;
  payment_method: string;
  payment_method_other: string;
  delivery_scope: string;
  delivery_cycle: string;
  delivery_weekdays: string[];
  minimum_order_amount: string;
  minimum_order_quantity: string;
  freight_rule: string;
  freight_threshold_amount: string;
  freight_fixed_amount: string;
  freight_other_note: string;
  return_scope: string;
  return_handling_method: string;
  return_condition_types: string[];
  return_near_expiry_condition: string;
  return_defect_condition: string;
  return_intolerance_condition: string;
  return_other_condition: string;
  return_condition_description: string;
  return_freight_responsibility: string;
  return_freight_rule_note: string;
  invoice_format: string;
  invoice_format_other: string;
  invoice_delivery_method: string;
  invoice_delivery_other: string;
  bank_name: string;
  bank_code: string;
  branch_name: string;
  branch_code: string;
  bank_account_name: string;
  bank_account_number: string;
};

const emptyImportantVendorChangeDraft: ImportantVendorChangeDraft = {
  payment_term: '',
  monthly_payment_days: '',
  payment_method: '',
  payment_method_other: '',
  delivery_scope: '',
  delivery_cycle: '',
  delivery_weekdays: [],
  minimum_order_amount: '',
  minimum_order_quantity: '',
  freight_rule: '',
  freight_threshold_amount: '',
  freight_fixed_amount: '',
  freight_other_note: '',
  return_scope: '',
  return_handling_method: '',
  return_condition_types: [],
  return_near_expiry_condition: '',
  return_defect_condition: '',
  return_intolerance_condition: '',
  return_other_condition: '',
  return_condition_description: '',
  return_freight_responsibility: '',
  return_freight_rule_note: '',
  invoice_format: '',
  invoice_format_other: '',
  invoice_delivery_method: '',
  invoice_delivery_other: '',
  bank_name: '',
  bank_code: '',
  branch_name: '',
  branch_code: '',
  bank_account_name: '',
  bank_account_number: '',
};

function VendorOptionCheckboxGroup({
  label,
  hint,
  options,
  selected,
  onChange,
}: {
  label: string;
  hint?: string;
  options: readonly { value: string; label: string }[];
  selected: string[];
  onChange: (value: string) => void;
}) {
  return (
    <fieldset className="rounded-xl border border-slate-200 bg-slate-50 p-4 md:col-span-2">
      <legend className="px-1 text-sm font-semibold text-ink">
        {label} <span className="ml-1 text-rose-600">*</span>
        {hint && <span className="ml-2 text-xs font-normal text-slate-500">（{hint}）</span>}
      </legend>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 md:grid-cols-4">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-ink hover:border-teal-300"
          >
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => onChange(option.value)}
              className="h-4 w-4 accent-teal-600"
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function VendorSelfServicePanel({
  profile,
  changeRequests,
  onReload,
  notify,
}: {
  profile: VendorSelfServiceProfile | null;
  changeRequests: VendorChangeRequest[];
  onReload: () => Promise<void>;
  notify: Notify;
}) {
  const [contactDraft, setContactDraft] = useState({
    company_phone: '',
    company_address: '',
    company_email: '',
    website: '',
    company_fax: '',
    contact_name: '',
    contact_job_title: '',
    contact_phone: '',
    contact_email: '',
  });
  const [importantDraft, setImportantDraft] = useState<ImportantVendorChangeDraft>(
    emptyImportantVendorChangeDraft,
  );
  const [reason, setReason] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [savingChange, setSavingChange] = useState(false);

  useEffect(() => {
    if (!profile) return;
    const bank = profile.bank_details[0];
    setContactDraft({
      company_phone: profile.business.phone,
      company_address: profile.business.address,
      company_email: profile.business.company_email,
      website: profile.business.website,
      company_fax: profile.business.company_fax,
      contact_name: profile.contact.contact_name,
      contact_job_title: profile.contact.contact_job_title ?? '',
      contact_phone: profile.contact.contact_phone,
      contact_email: profile.contact.contact_email,
    });
    const currentWeekdays =
      profile.delivery_terms.delivery_cycle === 'FIXED_WEEKDAYS'
        ? profile.delivery_terms.delivery_weekdays.filter((value) =>
            vendorOptions.weekdays.some((option) => option.value === value),
          )
        : [];
    const currentReturnConditions = parseVendorReturnConditions(
      profile.return_terms.return_condition_types,
    ).filter((value) =>
      vendorOptions.returnConditionTypes.some((option) => option.value === value),
    );
    setImportantDraft({
      payment_term: profile.payment_terms.payment_term,
      monthly_payment_days: profile.payment_terms.monthly_payment_days?.toString() ?? '',
      payment_method: profile.payment_terms.payment_method,
      payment_method_other: profile.payment_terms.payment_method_other,
      delivery_scope: profile.delivery_terms.delivery_scope,
      delivery_cycle: parseVendorCode(
        profile.delivery_terms.delivery_cycle,
        vendorLabels.deliveryCycle,
      ),
      delivery_weekdays: currentWeekdays,
      minimum_order_amount: profile.delivery_terms.minimum_order_amount?.toString() ?? '',
      minimum_order_quantity: profile.delivery_terms.minimum_order_quantity?.toString() ?? '',
      freight_rule: parseVendorCode(profile.delivery_terms.freight_rule, vendorLabels.freightRule),
      freight_threshold_amount: profile.delivery_terms.freight_threshold_amount?.toString() ?? '',
      freight_fixed_amount: profile.delivery_terms.freight_fixed_amount?.toString() ?? '',
      freight_other_note: profile.delivery_terms.freight_other_note,
      return_scope: profile.return_terms.return_scope,
      return_handling_method: parseVendorCode(
        profile.return_terms.return_handling_method,
        vendorLabels.returnHandling,
      ),
      return_condition_types: currentReturnConditions,
      return_near_expiry_condition: profile.return_terms.return_near_expiry_condition,
      return_defect_condition: profile.return_terms.return_defect_condition,
      return_intolerance_condition: profile.return_terms.return_intolerance_condition,
      return_other_condition: profile.return_terms.return_other_condition,
      return_condition_description: profile.return_terms.return_condition_description,
      return_freight_responsibility: parseVendorCode(
        profile.return_terms.return_freight_responsibility,
        vendorLabels.returnFreight,
      ),
      return_freight_rule_note: profile.return_terms.return_freight_rule_note,
      invoice_format: parseVendorCode(
        profile.invoice_terms.invoice_format,
        vendorLabels.invoiceFormat,
      ),
      invoice_format_other: profile.invoice_terms.invoice_format_other,
      invoice_delivery_method: parseVendorCode(
        profile.invoice_terms.invoice_delivery_method,
        vendorLabels.invoiceDelivery,
      ),
      invoice_delivery_other: profile.invoice_terms.invoice_delivery_other,
      bank_name: bank?.bank_name ?? '',
      bank_code: bank?.bank_code ?? '',
      branch_name: bank?.branch_name ?? '',
      branch_code: bank?.branch_code ?? '',
      bank_account_name: bank?.bank_account_name ?? '',
      bank_account_number: bank?.bank_account_number ?? '',
    });
  }, [profile]);

  const updateContact = (key: keyof typeof contactDraft, value: string) =>
    setContactDraft((current) => ({ ...current, [key]: value }));
  const updateImportant = <K extends keyof ImportantVendorChangeDraft>(
    key: K,
    value: ImportantVendorChangeDraft[K],
  ) => {
    setImportantDraft((current) => {
      const next = { ...current, [key]: value };
      if (key === 'delivery_cycle' && value !== 'FIXED_WEEKDAYS') next.delivery_weekdays = [];
      if (key === 'freight_rule') {
        if (value !== 'THRESHOLD') next.freight_threshold_amount = '';
        if (value !== 'FIXED') next.freight_fixed_amount = '';
        if (value !== 'OTHER') next.freight_other_note = '';
      }
      if (
        key === 'return_freight_responsibility' &&
        !['BY_REASON', 'AGREEMENT', 'OTHER'].includes(String(value))
      )
        next.return_freight_rule_note = '';
      if (key === 'invoice_format' && value !== 'OTHER') next.invoice_format_other = '';
      if (key === 'invoice_delivery_method' && value !== 'OTHER') next.invoice_delivery_other = '';
      return next;
    });
  };
  const toggleImportantChoice = (
    key: 'delivery_weekdays' | 'return_condition_types',
    value: string,
  ) => {
    setImportantDraft((current) => {
      const selected = current[key];
      const nextValues = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
      const next = { ...current, [key]: nextValues };
      if (key === 'return_condition_types') {
        if (!nextValues.includes('NEAR_EXPIRY')) next.return_near_expiry_condition = '';
        if (!nextValues.includes('DEFECT')) next.return_defect_condition = '';
        if (!nextValues.includes('INTOLERANCE')) next.return_intolerance_condition = '';
        if (!nextValues.includes('OTHER')) next.return_other_condition = '';
      }
      return next;
    });
  };

  const saveContact = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingContact(true);
    try {
      await api.vendorProactive.profile.updateContact(contactDraft);
      await onReload();
      notify('一般聯絡資料已更新。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSavingContact(false);
    }
  };

  const submitImportantChange = async (event: React.FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < 5) {
      notify('請填寫資料變更原因。', 'error');
      return;
    }
    if (
      importantDraft.delivery_cycle === 'FIXED_WEEKDAYS' &&
      importantDraft.delivery_weekdays.length === 0
    ) {
      notify('固定配送星期至少要選擇一項。', 'error');
      return;
    }
    if (importantDraft.return_condition_types.length === 0) {
      notify('退貨／換貨成立條件至少要選擇一項。', 'error');
      return;
    }
    if (
      importantDraft.freight_rule === 'THRESHOLD' &&
      !importantDraft.freight_threshold_amount.trim()
    ) {
      notify('請填寫免運門檻金額。', 'error');
      return;
    }
    if (importantDraft.freight_rule === 'FIXED' && !importantDraft.freight_fixed_amount.trim()) {
      notify('請填寫固定運費金額。', 'error');
      return;
    }
    if (importantDraft.freight_rule === 'OTHER' && !importantDraft.freight_other_note.trim()) {
      notify('請填寫其他運費規則說明。', 'error');
      return;
    }
    const requiredReturnConditions: Array<[string, string]> = [
      ['NEAR_EXPIRY', importantDraft.return_near_expiry_condition],
      ['DEFECT', importantDraft.return_defect_condition],
      ['INTOLERANCE', importantDraft.return_intolerance_condition],
      ['OTHER', importantDraft.return_other_condition],
    ];
    const missingReturnCondition = requiredReturnConditions.find(
      ([code, value]) => importantDraft.return_condition_types.includes(code) && !value.trim(),
    );
    if (missingReturnCondition) {
      notify('請填寫所選退貨／換貨成立條件的詳細說明。', 'error');
      return;
    }
    if (
      ['BY_REASON', 'OTHER'].includes(importantDraft.return_freight_responsibility) &&
      !importantDraft.return_freight_rule_note.trim()
    ) {
      notify('請填寫退換貨運費負擔說明。', 'error');
      return;
    }
    if (importantDraft.invoice_format === 'OTHER' && !importantDraft.invoice_format_other.trim()) {
      notify('請填寫其他發票格式說明。', 'error');
      return;
    }
    if (
      importantDraft.invoice_delivery_method === 'OTHER' &&
      !importantDraft.invoice_delivery_other.trim()
    ) {
      notify('請填寫其他發票寄送方式說明。', 'error');
      return;
    }
    const proposed: Record<string, unknown> = {
      payment_terms: {
        payment_term: importantDraft.payment_term,
        monthly_payment_days: importantDraft.monthly_payment_days
          ? Number(importantDraft.monthly_payment_days)
          : null,
        payment_method: importantDraft.payment_method,
        payment_method_other: importantDraft.payment_method_other,
      },
      delivery_terms: {
        delivery_scope: importantDraft.delivery_scope,
        delivery_cycle: importantDraft.delivery_cycle,
        delivery_weekdays: importantDraft.delivery_weekdays,
        minimum_order_amount: importantDraft.minimum_order_amount
          ? Number(importantDraft.minimum_order_amount)
          : null,
        minimum_order_quantity: importantDraft.minimum_order_quantity
          ? Number(importantDraft.minimum_order_quantity)
          : null,
        freight_rule: importantDraft.freight_rule,
        freight_threshold_amount: importantDraft.freight_threshold_amount
          ? Number(importantDraft.freight_threshold_amount)
          : null,
        freight_fixed_amount: importantDraft.freight_fixed_amount
          ? Number(importantDraft.freight_fixed_amount)
          : null,
        freight_other_note: importantDraft.freight_other_note,
      },
      return_terms: {
        return_scope: importantDraft.return_scope,
        return_handling_method: importantDraft.return_handling_method,
        return_condition_types: importantDraft.return_condition_types,
        return_near_expiry_condition: importantDraft.return_near_expiry_condition,
        return_defect_condition: importantDraft.return_defect_condition,
        return_intolerance_condition: importantDraft.return_intolerance_condition,
        return_other_condition: importantDraft.return_other_condition,
        return_condition_description: importantDraft.return_condition_description,
        return_freight_responsibility: importantDraft.return_freight_responsibility,
        return_freight_rule_note: importantDraft.return_freight_rule_note,
      },
      invoice_terms: {
        invoice_format: importantDraft.invoice_format,
        invoice_format_other: importantDraft.invoice_format_other,
        invoice_delivery_method: importantDraft.invoice_delivery_method,
        invoice_delivery_other: importantDraft.invoice_delivery_other,
      },
    };
    const hasBank = [
      importantDraft.bank_name,
      importantDraft.branch_name,
      importantDraft.bank_account_name,
      importantDraft.bank_account_number,
    ].some(Boolean);
    if (hasBank)
      proposed.bank_details = [
        {
          ...(profile?.bank_details[0] ?? {}),
          bank_name: importantDraft.bank_name,
          bank_code: importantDraft.bank_code,
          branch_name: importantDraft.branch_name,
          branch_code: importantDraft.branch_code,
          bank_account_name: importantDraft.bank_account_name,
          bank_account_number: importantDraft.bank_account_number,
        },
      ];
    setSavingChange(true);
    try {
      await api.vendorProactive.profile.requestChange({ reason, proposed });
      setReason('');
      await onReload();
      notify('重要資料變更已送出，待內部人員審核後才會套用。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSavingChange(false);
    }
  };

  if (!profile)
    return (
      <Card>
        <Loading />
      </Card>
    );

  const currentBank = profile.bank_details[0];
  const comparisonRows = [
    {
      label: '付款條件',
      before: labelVendorPaymentTerm(profile.payment_terms.payment_term),
      after: labelVendorPaymentTerm(importantDraft.payment_term),
    },
    {
      label: '月結天數',
      before: displayVendorValue(profile.payment_terms.monthly_payment_days),
      after: displayVendorValue(importantDraft.monthly_payment_days),
    },
    {
      label: '付款方式',
      before: labelVendorPaymentMethod(profile.payment_terms.payment_method),
      after: labelVendorPaymentMethod(importantDraft.payment_method),
    },
    {
      label: '配送地點',
      before: labelVendorDeliveryScope(profile.delivery_terms.delivery_scope),
      after: labelVendorDeliveryScope(importantDraft.delivery_scope),
    },
    {
      label: '廠商配送週期',
      before: labelVendorDeliveryCycle(profile.delivery_terms.delivery_cycle),
      after: labelVendorDeliveryCycle(importantDraft.delivery_cycle),
    },
    {
      label: '固定配送星期',
      before: formatVendorWeekdays(profile.delivery_terms.delivery_weekdays),
      after: formatVendorWeekdays(importantDraft.delivery_weekdays),
    },
    {
      label: '最低訂購金額',
      before: formatVendorMoney(profile.delivery_terms.minimum_order_amount),
      after: formatVendorMoney(importantDraft.minimum_order_amount),
    },
    {
      label: '最低訂購數量',
      before: displayVendorValue(profile.delivery_terms.minimum_order_quantity),
      after: displayVendorValue(importantDraft.minimum_order_quantity),
    },
    {
      label: '廠商運費規則',
      before: labelVendorFreightRule(profile.delivery_terms.freight_rule),
      after: labelVendorFreightRule(importantDraft.freight_rule),
    },
    {
      label: '免運門檻金額',
      before: formatVendorMoney(profile.delivery_terms.freight_threshold_amount),
      after: formatVendorMoney(importantDraft.freight_threshold_amount),
    },
    {
      label: '固定運費金額',
      before: formatVendorMoney(profile.delivery_terms.freight_fixed_amount),
      after: formatVendorMoney(importantDraft.freight_fixed_amount),
    },
    {
      label: '其他運費規則說明',
      before: displayVendorValue(profile.delivery_terms.freight_other_note),
      after: displayVendorValue(importantDraft.freight_other_note),
    },
    {
      label: '退貨地點',
      before: labelVendorDeliveryScope(profile.return_terms.return_scope),
      after: labelVendorDeliveryScope(importantDraft.return_scope),
    },
    {
      label: '退貨／換貨處理方式',
      before: labelVendorReturnHandling(profile.return_terms.return_handling_method),
      after: labelVendorReturnHandling(importantDraft.return_handling_method),
    },
    {
      label: '退貨／換貨成立條件',
      before: formatVendorReturnConditions(profile.return_terms.return_condition_types),
      after: formatVendorReturnConditions(importantDraft.return_condition_types),
    },
    {
      label: '即期退換貨條件',
      before: displayVendorValue(profile.return_terms.return_near_expiry_condition),
      after: displayVendorValue(importantDraft.return_near_expiry_condition),
    },
    {
      label: '瑕疵退換貨條件',
      before: displayVendorValue(profile.return_terms.return_defect_condition),
      after: displayVendorValue(importantDraft.return_defect_condition),
    },
    {
      label: '吃不合退換貨條件',
      before: displayVendorValue(profile.return_terms.return_intolerance_condition),
      after: displayVendorValue(importantDraft.return_intolerance_condition),
    },
    {
      label: '其他退換貨條件',
      before: displayVendorValue(profile.return_terms.return_other_condition),
      after: displayVendorValue(importantDraft.return_other_condition),
    },
    {
      label: '退貨／換貨詳細說明',
      before: displayVendorValue(profile.return_terms.return_condition_description),
      after: displayVendorValue(importantDraft.return_condition_description),
    },
    {
      label: '退貨／換貨運費負擔',
      before: labelVendorReturnFreight(profile.return_terms.return_freight_responsibility),
      after: labelVendorReturnFreight(importantDraft.return_freight_responsibility),
    },
    {
      label: '退換貨運費負擔說明',
      before: displayVendorValue(profile.return_terms.return_freight_rule_note),
      after: displayVendorValue(importantDraft.return_freight_rule_note),
    },
    {
      label: '發票格式',
      before: labelVendorInvoiceFormat(profile.invoice_terms.invoice_format),
      after: labelVendorInvoiceFormat(importantDraft.invoice_format),
    },
    {
      label: '其他發票格式說明',
      before: displayVendorValue(profile.invoice_terms.invoice_format_other),
      after: displayVendorValue(importantDraft.invoice_format_other),
    },
    {
      label: '發票寄送方式',
      before: labelVendorInvoiceDelivery(profile.invoice_terms.invoice_delivery_method),
      after: labelVendorInvoiceDelivery(importantDraft.invoice_delivery_method),
    },
    {
      label: '其他發票寄送方式說明',
      before: displayVendorValue(profile.invoice_terms.invoice_delivery_other),
      after: displayVendorValue(importantDraft.invoice_delivery_other),
    },
    {
      label: '銀行名稱',
      before: displayVendorValue(currentBank?.bank_name),
      after: displayVendorValue(importantDraft.bank_name),
    },
    {
      label: '分行名稱',
      before: displayVendorValue(currentBank?.branch_name),
      after: displayVendorValue(importantDraft.branch_name),
    },
    {
      label: '戶名',
      before: displayVendorValue(currentBank?.bank_account_name),
      after: displayVendorValue(importantDraft.bank_account_name),
    },
    {
      label: '銀行帳號',
      before: displayVendorValue(
        currentBank?.bank_account_number ?? currentBank?.bank_account_number_masked,
      ),
      after: displayVendorValue(importantDraft.bank_account_number),
    },
  ];
  const previewRows = comparisonRows.filter((row) => row.before !== row.after);
  const focusPreviewRows = comparisonRows.filter((row) =>
    [
      '固定配送星期',
      '廠商運費規則',
      '退貨／換貨成立條件',
      '退貨／換貨運費負擔',
      '發票格式',
      '發票寄送方式',
    ].includes(row.label),
  );

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
          <div>
            <h2 className="text-xl font-extrabold text-ink">我的廠商資料</h2>
            <p className="mt-1 text-sm text-slate-600">
              目前正式廠商主檔：{displayVendorValue(profile.vendor_name)}／統一編號：
              {displayVendorValue(profile.business.tax_id)}
            </p>
          </div>
          <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">
            合作狀態：{labelVendorStatus(profile.vendor_status)}
          </span>
        </div>
      </Card>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">公司基本資料</h3>
        <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <VendorProfileValue label="公司名稱" value={profile.business.company_name} />
          <VendorProfileValue label="公司簡稱" value={profile.business.company_short_name} />
          <VendorProfileValue label="統一編號" value={profile.business.tax_id} />
          <VendorProfileValue label="公司電話" value={profile.business.phone} />
          <VendorProfileValue label="公司地址" value={profile.business.address} wide />
          <VendorProfileValue label="公司 Email" value={profile.business.company_email} />
          <VendorProfileValue label="公司網站" value={profile.business.website} />
          <VendorProfileValue label="傳真" value={profile.business.company_fax} />
          <VendorProfileValue label="公司簡介" value={profile.business.company_intro} wide />
          <VendorProfileValue label="備註" value={profile.business.notes} wide />
        </dl>
      </Card>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">代理品牌與供應商品類</h3>
        <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <VendorProfileValue
            label="主要代理／品牌"
            value={
              profile.business.principal_brands.length
                ? profile.business.principal_brands.join('、')
                : '未設定'
            }
          />
          <VendorProfileValue
            label="供應商品類"
            value={formatVendorCategories(profile.business.supplied_product_categories)}
          />
        </dl>
      </Card>

      <form onSubmit={saveContact}>
        <Card>
          <h3 className="mb-4 text-lg font-extrabold text-ink">
            主要聯絡人與一般聯絡資料（可直接更新）
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="公司電話"
              value={contactDraft.company_phone}
              onChange={(e) => updateContact('company_phone', e.target.value)}
            />
            <Input
              label="公司 Email"
              type="email"
              value={contactDraft.company_email}
              onChange={(e) => updateContact('company_email', e.target.value)}
            />
            <Textarea
              label="公司地址"
              value={contactDraft.company_address}
              onChange={(e) => updateContact('company_address', e.target.value)}
            />
            <Input
              label="公司網站"
              value={contactDraft.website}
              onChange={(e) => updateContact('website', e.target.value)}
            />
            <Input
              label="傳真"
              value={contactDraft.company_fax}
              onChange={(e) => updateContact('company_fax', e.target.value)}
            />
            <Input
              label="聯絡人姓名"
              value={contactDraft.contact_name}
              onChange={(e) => updateContact('contact_name', e.target.value)}
            />
            <Input
              label="聯絡人職稱"
              value={contactDraft.contact_job_title}
              onChange={(e) => updateContact('contact_job_title', e.target.value)}
            />
            <Input
              label="聯絡電話"
              value={contactDraft.contact_phone}
              onChange={(e) => updateContact('contact_phone', e.target.value)}
            />
            <Input
              label="聯絡 Email"
              type="email"
              value={contactDraft.contact_email}
              onChange={(e) => updateContact('contact_email', e.target.value)}
            />
          </div>
          <div className="mt-5 flex justify-end">
            <Button loading={savingContact}>儲存一般聯絡資料</Button>
          </div>
        </Card>
      </form>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">主要聯絡人</h3>
        <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <VendorProfileValue label="聯絡人姓名" value={profile.contact.contact_name} />
          <VendorProfileValue label="聯絡人職稱" value={profile.contact.contact_job_title} />
          <VendorProfileValue label="聯絡電話" value={profile.contact.contact_phone} />
          <VendorProfileValue label="聯絡 Email" value={profile.contact.contact_email} />
          <VendorProfileValue label="廠商結帳日" value={profile.contact.vendor_closing_day} />
          <VendorProfileValue
            label="廠商每日訂單截止時間"
            value={formatVendorTime(profile.contact.daily_order_cutoff_time)}
          />
        </dl>
      </Card>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">付款條件</h3>
        <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <VendorProfileValue
            label="付款條件"
            value={labelVendorPaymentTerm(profile.payment_terms.payment_term)}
          />
          <VendorProfileValue
            label="月結天數"
            value={
              profile.payment_terms.monthly_payment_days == null
                ? '未填寫'
                : `${profile.payment_terms.monthly_payment_days} 天`
            }
          />
          <VendorProfileValue
            label="付款方式"
            value={labelVendorPaymentMethod(profile.payment_terms.payment_method)}
          />
          <VendorProfileValue
            label="其他付款方式說明"
            value={profile.payment_terms.payment_method_other}
          />
        </dl>

        <h3 className="mb-4 mt-8 text-lg font-extrabold text-ink">配送條件</h3>
        <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <VendorProfileValue
            label="配送地點"
            value={labelVendorDeliveryScope(profile.delivery_terms.delivery_scope)}
          />
          <VendorProfileValue
            label="廠商配送週期"
            value={labelVendorDeliveryCycle(profile.delivery_terms.delivery_cycle)}
          />
          <VendorProfileValue
            label="固定配送星期"
            value={formatVendorWeekdays(profile.delivery_terms.delivery_weekdays)}
          />
          <VendorProfileValue
            label="最低訂購金額"
            value={formatVendorMoney(profile.delivery_terms.minimum_order_amount)}
          />
          <VendorProfileValue
            label="最低訂購數量"
            value={profile.delivery_terms.minimum_order_quantity}
          />
          <VendorProfileValue
            label="廠商運費規則"
            value={labelVendorFreightRule(profile.delivery_terms.freight_rule)}
          />
          <VendorProfileValue
            label="免運門檻"
            value={formatVendorMoney(profile.delivery_terms.freight_threshold_amount)}
          />
          <VendorProfileValue
            label="固定運費"
            value={formatVendorMoney(profile.delivery_terms.freight_fixed_amount)}
          />
          <VendorProfileValue
            label="其他運費規則說明"
            value={profile.delivery_terms.freight_other_note}
            wide
          />
        </dl>

        <h3 className="mb-4 mt-8 text-lg font-extrabold text-ink">退貨／換貨條件</h3>
        <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <VendorProfileValue
            label="退貨地點"
            value={labelVendorDeliveryScope(profile.return_terms.return_scope)}
          />
          <VendorProfileValue
            label="退貨／換貨處理方式"
            value={labelVendorReturnHandling(profile.return_terms.return_handling_method)}
          />
          <VendorProfileValue
            label="退貨／換貨成立條件"
            value={formatVendorReturnConditions(profile.return_terms.return_condition_types)}
            wide
          />
          <VendorProfileValue
            label="即期退換貨條件"
            value={profile.return_terms.return_near_expiry_condition}
          />
          <VendorProfileValue
            label="瑕疵退換貨條件"
            value={profile.return_terms.return_defect_condition}
          />
          <VendorProfileValue
            label="吃不合退換貨條件"
            value={profile.return_terms.return_intolerance_condition}
          />
          <VendorProfileValue
            label="其他退換貨條件"
            value={profile.return_terms.return_other_condition}
          />
          <VendorProfileValue
            label="退貨／換貨詳細說明"
            value={profile.return_terms.return_condition_description}
            wide
          />
          <VendorProfileValue
            label="退貨／換貨運費負擔"
            value={labelVendorReturnFreight(profile.return_terms.return_freight_responsibility)}
          />
          <VendorProfileValue
            label="運費負擔規則說明"
            value={profile.return_terms.return_freight_rule_note}
          />
        </dl>
      </Card>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">發票與對帳</h3>
        <dl className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <VendorProfileValue
            label="發票格式"
            value={labelVendorInvoiceFormat(profile.invoice_terms.invoice_format)}
          />
          <VendorProfileValue
            label="發票寄送方式"
            value={labelVendorInvoiceDelivery(profile.invoice_terms.invoice_delivery_method)}
          />
          <VendorProfileValue
            label="發票聯絡人"
            value={
              profile.invoice_terms.invoice_same_as_primary
                ? '同主要聯絡人'
                : profile.invoice_terms.invoice_contact_name
            }
          />
          <VendorProfileValue
            label="發票聯絡電話"
            value={profile.invoice_terms.invoice_contact_phone}
          />
          <VendorProfileValue label="發票 Email" value={profile.invoice_terms.invoice_email} />
          <VendorProfileValue
            label="對帳聯絡人"
            value={
              profile.invoice_terms.reconciliation_same_as_primary
                ? '同主要聯絡人'
                : profile.invoice_terms.reconciliation_contact_name
            }
          />
          <VendorProfileValue
            label="對帳聯絡電話"
            value={profile.invoice_terms.reconciliation_contact_phone}
          />
          <VendorProfileValue
            label="對帳 Email"
            value={profile.invoice_terms.reconciliation_email}
          />
        </dl>
      </Card>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">銀行資料</h3>
        {profile.bank_details.length === 0 ? (
          <p className="text-sm text-slate-500">未設定</p>
        ) : (
          <div className="space-y-4">
            {profile.bank_details.map((bank, index) => (
              <dl
                key={bank.id ?? `${bank.bank_name}-${index}`}
                className="grid gap-x-6 gap-y-4 rounded-xl border border-slate-200 p-4 md:grid-cols-2"
              >
                <VendorProfileValue label="銀行名稱" value={bank.bank_name} />
                <VendorProfileValue label="銀行代碼" value={bank.bank_code} />
                <VendorProfileValue label="分行名稱" value={bank.branch_name} />
                <VendorProfileValue label="分行代碼" value={bank.branch_code} />
                <VendorProfileValue label="帳戶名稱" value={bank.bank_account_name} />
                <VendorProfileValue
                  label="銀行帳號"
                  value={bank.bank_account_number ?? bank.bank_account_number_masked}
                />
              </dl>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">附件資料</h3>
        {profile.attachments.length === 0 ? (
          <EmptyState
            title="目前沒有附件資料"
            description="核准申請時承接的公司文件會顯示在這裡。"
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-bold text-slate-500">
                  <th className="px-4 py-3">文件類型</th>
                  <th className="px-4 py-3">檔案名稱</th>
                  <th className="px-4 py-3">上傳日期</th>
                  <th className="px-4 py-3">檔案大小</th>
                  <th className="px-4 py-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {profile.attachments.map((attachment) => (
                  <tr key={attachment.id} className="border-t border-slate-100 text-sm">
                    <td className="px-4 py-3">{labelVendorAttachmentType(attachment.type)}</td>
                    <td className="max-w-[320px] break-words px-4 py-3">
                      {displayVendorValue(attachment.original_name)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                      {formatVendorDate(attachment.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                      {formatVendorFileSize(attachment.size_bytes)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <a
                        className="mr-3 font-bold text-teal-700 hover:underline"
                        href={api.vendorProactive.profile.attachmentUrl(attachment.id, true)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        預覽
                      </a>
                      <a
                        className="font-bold text-teal-700 hover:underline"
                        href={api.vendorProactive.profile.attachmentUrl(attachment.id)}
                      >
                        下載
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <form onSubmit={submitImportantChange}>
        <Card>
          <div className="mb-4">
            <h3 className="text-lg font-extrabold text-ink">重要交易資料變更申請</h3>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              以下內容不會直接覆蓋正式主檔；送出後由採購、財務或管理員審核，核准後才會套用。
            </p>
          </div>
          <div className="mb-5 rounded-xl border border-teal-100 bg-teal-50/60 p-4">
            <h4 className="font-bold text-ink">目前正式資料／申請變更內容</h4>
            <div className="mt-3 grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg bg-white p-3">
                <p className="text-sm font-bold text-slate-700">目前正式資料</p>
                <dl className="mt-2 space-y-2 text-sm">
                  {focusPreviewRows.map((row) => (
                    <div
                      key={row.label}
                      className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3"
                    >
                      <dt className="font-semibold text-slate-500">{row.label}</dt>
                      <dd className="whitespace-pre-wrap break-words text-ink">{row.before}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="rounded-lg bg-white p-3">
                <p className="text-sm font-bold text-slate-700">申請變更內容</p>
                <dl className="mt-2 space-y-2 text-sm">
                  {focusPreviewRows.map((row) => (
                    <div
                      key={row.label}
                      className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3"
                    >
                      <dt className="font-semibold text-slate-500">{row.label}</dt>
                      <dd className="whitespace-pre-wrap break-words text-ink">{row.after}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="付款條件"
              value={importantDraft.payment_term}
              onChange={(e) => updateImportant('payment_term', e.target.value)}
            >
              <option value="">請選擇</option>
              {Object.entries(vendorLabels.paymentTerm).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Input
              label="月結天數"
              type="number"
              min="0"
              value={importantDraft.monthly_payment_days}
              onChange={(e) => updateImportant('monthly_payment_days', e.target.value)}
            />
            <Select
              label="付款方式"
              value={importantDraft.payment_method}
              onChange={(e) => updateImportant('payment_method', e.target.value)}
            >
              <option value="">請選擇</option>
              {Object.entries(vendorLabels.paymentMethod).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Input
              label="其他付款方式說明"
              value={importantDraft.payment_method_other}
              onChange={(e) => updateImportant('payment_method_other', e.target.value)}
            />
            <Select
              label="配送地點"
              value={importantDraft.delivery_scope}
              onChange={(e) => updateImportant('delivery_scope', e.target.value)}
            >
              <option value="">請選擇</option>
              {Object.entries(vendorLabels.deliveryScope).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="配送週期"
              value={importantDraft.delivery_cycle}
              onChange={(e) => updateImportant('delivery_cycle', e.target.value)}
            >
              <option value="">請選擇</option>
              {vendorOptions.deliveryCycles.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {importantDraft.delivery_cycle === 'FIXED_WEEKDAYS' && (
              <VendorOptionCheckboxGroup
                label="固定配送星期"
                hint="可複選"
                options={vendorOptions.weekdays}
                selected={importantDraft.delivery_weekdays}
                onChange={(value) => toggleImportantChoice('delivery_weekdays', value)}
              />
            )}
            <Input
              label="最低訂購金額"
              type="number"
              min="0"
              value={importantDraft.minimum_order_amount}
              onChange={(e) => updateImportant('minimum_order_amount', e.target.value)}
            />
            <Input
              label="最低訂購數量"
              type="number"
              min="0"
              value={importantDraft.minimum_order_quantity}
              onChange={(e) => updateImportant('minimum_order_quantity', e.target.value)}
            />
            <Select
              label="廠商運費規則"
              required
              value={importantDraft.freight_rule}
              onChange={(e) => updateImportant('freight_rule', e.target.value)}
            >
              <option value="">請選擇</option>
              {vendorOptions.freightRules.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {importantDraft.freight_rule === 'THRESHOLD' && (
              <Input
                label="免運門檻金額"
                type="number"
                min="0"
                required
                value={importantDraft.freight_threshold_amount}
                onChange={(e) => updateImportant('freight_threshold_amount', e.target.value)}
              />
            )}
            {importantDraft.freight_rule === 'FIXED' && (
              <Input
                label="固定運費金額"
                type="number"
                min="0"
                required
                value={importantDraft.freight_fixed_amount}
                onChange={(e) => updateImportant('freight_fixed_amount', e.target.value)}
              />
            )}
            {importantDraft.freight_rule === 'OTHER' && (
              <Textarea
                label="其他運費規則說明"
                required
                value={importantDraft.freight_other_note}
                onChange={(e) => updateImportant('freight_other_note', e.target.value)}
              />
            )}
            <Select
              label="退貨地點"
              value={importantDraft.return_scope}
              onChange={(e) => updateImportant('return_scope', e.target.value)}
            >
              <option value="">請選擇</option>
              {Object.entries(vendorLabels.deliveryScope).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="退貨／換貨處理方式"
              value={importantDraft.return_handling_method}
              onChange={(e) => updateImportant('return_handling_method', e.target.value)}
            >
              <option value="">請選擇</option>
              {vendorOptions.returnHandlingMethods.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <VendorOptionCheckboxGroup
              label="退貨／換貨成立條件"
              hint="可複選"
              options={vendorOptions.returnConditionTypes}
              selected={importantDraft.return_condition_types}
              onChange={(value) => toggleImportantChoice('return_condition_types', value)}
            />
            {importantDraft.return_condition_types.includes('NEAR_EXPIRY') && (
              <Textarea
                label="即期退換貨條件"
                required
                value={importantDraft.return_near_expiry_condition}
                onChange={(e) => updateImportant('return_near_expiry_condition', e.target.value)}
              />
            )}
            {importantDraft.return_condition_types.includes('DEFECT') && (
              <Textarea
                label="瑕疵退換貨條件"
                required
                value={importantDraft.return_defect_condition}
                onChange={(e) => updateImportant('return_defect_condition', e.target.value)}
              />
            )}
            {importantDraft.return_condition_types.includes('INTOLERANCE') && (
              <Textarea
                label="吃不合退換貨條件"
                required
                value={importantDraft.return_intolerance_condition}
                onChange={(e) => updateImportant('return_intolerance_condition', e.target.value)}
              />
            )}
            {importantDraft.return_condition_types.includes('OTHER') && (
              <Textarea
                label="其他退換貨條件"
                required
                value={importantDraft.return_other_condition}
                onChange={(e) => updateImportant('return_other_condition', e.target.value)}
              />
            )}
            <Select
              label="退貨／換貨運費負擔"
              required
              value={importantDraft.return_freight_responsibility}
              onChange={(e) => updateImportant('return_freight_responsibility', e.target.value)}
            >
              <option value="">請選擇</option>
              {vendorOptions.returnFreightResponsibilities.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {importantDraft.return_freight_responsibility === 'BY_REASON' && (
              <Textarea
                label="退換貨運費判定規則"
                required
                value={importantDraft.return_freight_rule_note}
                onChange={(e) => updateImportant('return_freight_rule_note', e.target.value)}
              />
            )}
            {importantDraft.return_freight_responsibility === 'AGREEMENT' && (
              <Textarea
                label="雙方協議說明"
                value={importantDraft.return_freight_rule_note}
                onChange={(e) => updateImportant('return_freight_rule_note', e.target.value)}
              />
            )}
            {importantDraft.return_freight_responsibility === 'OTHER' && (
              <Textarea
                label="其他運費負擔說明"
                required
                value={importantDraft.return_freight_rule_note}
                onChange={(e) => updateImportant('return_freight_rule_note', e.target.value)}
              />
            )}
            <Textarea
              className="md:col-span-2"
              label="退貨／換貨詳細說明"
              required
              value={importantDraft.return_condition_description}
              onChange={(e) => updateImportant('return_condition_description', e.target.value)}
            />
            <Select
              label="發票格式"
              required
              value={importantDraft.invoice_format}
              onChange={(e) => updateImportant('invoice_format', e.target.value)}
            >
              <option value="">請選擇</option>
              {vendorOptions.invoiceFormats.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {importantDraft.invoice_format === 'OTHER' && (
              <Input
                label="其他發票格式說明"
                required
                value={importantDraft.invoice_format_other}
                onChange={(e) => updateImportant('invoice_format_other', e.target.value)}
              />
            )}
            <Select
              label="發票寄送方式"
              required
              value={importantDraft.invoice_delivery_method}
              onChange={(e) => updateImportant('invoice_delivery_method', e.target.value)}
            >
              <option value="">請選擇</option>
              {vendorOptions.invoiceDeliveryMethods.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {importantDraft.invoice_delivery_method === 'OTHER' && (
              <Textarea
                label="其他發票寄送方式說明"
                required
                value={importantDraft.invoice_delivery_other}
                onChange={(e) => updateImportant('invoice_delivery_other', e.target.value)}
              />
            )}
          </div>
          <details className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <summary className="cursor-pointer font-bold text-ink">
              銀行資料（可一併提出變更）
            </summary>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Input
                label="銀行名稱"
                value={importantDraft.bank_name}
                onChange={(e) => updateImportant('bank_name', e.target.value)}
              />
              <Input
                label="銀行代碼"
                value={importantDraft.bank_code}
                onChange={(e) => updateImportant('bank_code', e.target.value)}
              />
              <Input
                label="分行名稱"
                value={importantDraft.branch_name}
                onChange={(e) => updateImportant('branch_name', e.target.value)}
              />
              <Input
                label="分行代碼"
                value={importantDraft.branch_code}
                onChange={(e) => updateImportant('branch_code', e.target.value)}
              />
              <Input
                label="戶名"
                value={importantDraft.bank_account_name}
                onChange={(e) => updateImportant('bank_account_name', e.target.value)}
              />
              <Input
                label="銀行帳號"
                value={importantDraft.bank_account_number}
                onChange={(e) => updateImportant('bank_account_number', e.target.value)}
              />
            </div>
          </details>
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <h4 className="font-bold text-ink">變更內容確認</h4>
            {previewRows.length === 0 ? (
              <p className="mt-2 text-sm text-slate-600">
                目前尚未偵測到變更，請先修改要申請的資料。
              </p>
            ) : (
              <div className="mt-3 space-y-3">
                {previewRows.map((row) => (
                  <div
                    key={row.label}
                    className="grid gap-1 text-sm md:grid-cols-[180px_1fr_1fr] md:gap-3"
                  >
                    <p className="font-bold text-slate-700">{row.label}</p>
                    <p className="whitespace-pre-wrap break-words">
                      <span className="font-semibold text-slate-500">原：</span>
                      {row.before}
                    </p>
                    <p className="whitespace-pre-wrap break-words">
                      <span className="font-semibold text-teal-700">新：</span>
                      {row.after}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Textarea
            className="mt-5"
            label="變更原因"
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            hint="至少 5 個字"
          />
          <div className="mt-5 flex justify-end">
            <Button loading={savingChange}>送出資料變更申請</Button>
          </div>
        </Card>
      </form>

      <Card>
        <h3 className="mb-4 text-lg font-extrabold text-ink">資料變更紀錄</h3>
        {changeRequests.length === 0 ? (
          <EmptyState
            title="目前沒有資料變更申請"
            description="提出重要資料變更後，申請與處理結果會顯示在這裡。"
          />
        ) : (
          <div className="space-y-3">
            {changeRequests.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
                  <p className="font-bold text-ink">
                    {VENDOR_CHANGE_REQUEST_TYPE_LABELS[item.change_type]}
                  </p>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                    {labelVendorChangeRequestStatus(item.status)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  變更欄位：
                  {item.changed_fields.map(labelVendorChangedField).join('、') || '未填寫'}
                </p>
                <p className="mt-1 text-sm text-slate-600">原因：{item.reason}</p>
                {item.review_reason && (
                  <p className="mt-1 text-sm text-amber-800">處理說明：{item.review_reason}</p>
                )}
                <p className="mt-2 text-xs text-slate-400">
                  提出日期：{formatVendorDate(item.created_at, true)}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export function VendorPortalPage({ notify }: PageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  type VendorEntryFlow =
    'TAX_ID' | 'EXISTING_VENDOR' | 'NEW_VENDOR' | 'EXISTING_APPLICATION' | 'BLOCKED';
  const [session, setSession] = useState<{
    vendor_name: string;
    tax_id: string;
    contact_email: string;
    vendor_id: string | null;
    vendor_status?: string | null;
    account_id?: string | null;
    user_role?: string | null;
    skip_email_verification?: boolean;
  } | null>(null);
  const [proposals, setProposals] = useState<VendorProposal[]>([]);
  const [invitations, setInvitations] = useState<
    Array<
      VendorProposalInvitation & {
        fill_status: string;
        invitation_status: string;
        expires_at: string;
        revoked_at: string | null;
        proposal_count: number;
      }
    >
  >([]);
  const [vendorProfile, setVendorProfile] = useState<VendorSelfServiceProfile | null>(null);
  const [changeRequests, setChangeRequests] = useState<VendorChangeRequest[]>([]);
  const [workspaceView, setWorkspaceView] = useState<'PROPOSALS' | 'PROFILE'>('PROPOSALS');
  const [portalApplication, setPortalApplication] = useState<VendorApplication | null>(null);
  const [entryFlow, setEntryFlow] = useState<VendorEntryFlow>('TAX_ID');
  const [entryApplicationStatus, setEntryApplicationStatus] = useState('');
  const [entryVendorStatus, setEntryVendorStatus] = useState('');
  const [entryAccountStatus, setEntryAccountStatus] = useState('UNKNOWN');
  const [form, setForm] = useState({ tax_id: '', vendor_code: '', email: '' });
  const [accountForm, setAccountForm] = useState({ username: '', password: '' });
  const [setupForm, setSetupForm] = useState({
    password: '',
    confirm_password: '',
  });
  const [setupToken, setSetupToken] = useState('');
  const [setupExpiresAt, setSetupExpiresAt] = useState<string | null>(null);
  const [setupTurnstileToken, setSetupTurnstileToken] = useState('');
  const [setupTurnstileResetKey, setSetupTurnstileResetKey] = useState(0);
  const [accountMode, setAccountMode] = useState<'LOGIN' | 'SETUP'>('LOGIN');
  const [challenge, setChallenge] = useState<{ id: string; dev_code?: string } | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const update = (key: string, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const updateAccount = (key: 'username' | 'password', value: string) =>
    setAccountForm((current) => ({ ...current, [key]: value }));
  const loadPortal = async () => {
    const [proposalResult, invitationResult, applicationResult, profileResult, changeResult] =
      await Promise.all([
        api.vendorProactive.proposals.list(),
        api.vendorProactive.invitations.list().catch((error) => {
          if (error instanceof ApiError && [403, 404].includes(error.status))
            return { invitations: [] };
          throw error;
        }),
        api.vendorProactive.applicationDetail().catch((error) => {
          if (error instanceof ApiError && error.status === 404) return null;
          throw error;
        }),
        api.vendorProactive.profile.get().catch((error) => {
          if (error instanceof ApiError && [403, 404].includes(error.status)) return null;
          throw error;
        }),
        api.vendorProactive.changeRequests.list().catch((error) => {
          if (error instanceof ApiError && [403, 404].includes(error.status))
            return { requests: [] };
          throw error;
        }),
      ]);
    setProposals(proposalResult.proposals);
    setInvitations(invitationResult.invitations);
    setPortalApplication(applicationResult);
    setVendorProfile(profileResult?.profile ?? null);
    setChangeRequests(changeResult.requests);
  };
  useEffect(() => {
    const challengeId = searchParams.get('challenge_id');
    const magicToken = searchParams.get('magic_token');
    const invitationToken = searchParams.get('invite_token');
    if (challengeId && !magicToken) {
      setEntryFlow('EXISTING_APPLICATION');
      setChallenge({ id: challengeId });
    }
    const taxId = searchParams.get('tax_id');
    const email = searchParams.get('email');
    if (taxId || email)
      setForm((current) => ({
        ...current,
        tax_id: taxId ?? current.tax_id,
        email: email ?? current.email,
      }));
    const bootstrap = async () => {
      try {
        if (invitationToken) {
          await api.vendorProactive.invitations.accept(invitationToken);
          const result = await api.vendorProactive.session();
          setSession(result);
          await loadPortal();
          navigate('/vendor/proposal', { replace: true });
          notify('已開啟惠生邀請，請選擇開始填報。', 'success');
          return;
        }
        const result = await api.vendorProactive.session();
        setSession(result);
        await loadPortal();
      } catch (error) {
        if (challengeId && magicToken) {
          try {
            await api.vendorProactive.verify({
              challenge_id: challengeId,
              magic_token: magicToken,
            });
            const result = await api.vendorProactive.session();
            setSession(result);
            await loadPortal();
          } catch (verifyError) {
            notify(errorMessage(verifyError), 'error');
          }
        } else if (!(error instanceof ApiError && error.status === 401))
          notify(errorMessage(error), 'error');
      }
    };
    void bootstrap().finally(() => setLoading(false));
    // Session is intentionally loaded once when the public page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, notify, searchParams]);
  const resolveEntry = async () => {
    const taxId = form.tax_id.replace(/\s|-/g, '').trim();
    if (!/^\d{8}$/.test(taxId)) {
      notify('請輸入 8 位數字的統一編號。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.vendorEntry.resolve(taxId);
      setForm((current) => ({ ...current, tax_id: taxId }));
      setEntryApplicationStatus(result.application_status ?? '');
      setEntryVendorStatus(result.vendor_status ?? '');
      setEntryAccountStatus(result.account_status ?? 'UNKNOWN');
      setAccountForm({ username: result.login_account ?? taxId, password: '' });
      setSetupToken(result.setup_token ?? '');
      setSetupExpiresAt(result.setup_expires_at ?? null);
      setSetupForm({ password: '', confirm_password: '' });
      setSetupTurnstileToken('');
      setSetupTurnstileResetKey((value) => value + 1);
      if (result.flow === 'SUSPENDED_VENDOR' || result.flow === 'INACTIVE_VENDOR') {
        setEntryFlow('BLOCKED');
        setAccountMode('LOGIN');
        notify(
          result.flow === 'SUSPENDED_VENDOR'
            ? '目前廠商狀態為暫停交易，暫時無法新增新品提案。'
            : '此廠商帳號目前已停用，無法建立新品提案。',
          'error',
        );
      } else if (
        result.flow === 'PASSWORD_REQUIRED' ||
        result.flow === 'FIRST_TIME_PASSWORD_SETUP' ||
        result.flow === 'PASSWORD_RESET_REQUIRED' ||
        result.flow === 'LOCKED' ||
        result.flow === 'DISABLED'
      ) {
        setEntryFlow('EXISTING_VENDOR');
        setAccountMode(
          result.flow === 'FIRST_TIME_PASSWORD_SETUP' || result.flow === 'PASSWORD_RESET_REQUIRED'
            ? 'SETUP'
            : 'LOGIN',
        );
        notify(
          result.flow === 'FIRST_TIME_PASSWORD_SETUP'
            ? '已辨識為既有合作廠商，請首次設定登入密碼。'
            : result.flow === 'PASSWORD_RESET_REQUIRED'
              ? '密碼已被重設，請重新設定登入密碼。'
              : '已辨識為既有合作廠商，請登入後繼續。',
          'info',
        );
      } else if (result.flow === 'NEW_VENDOR') {
        setEntryFlow('NEW_VENDOR');
      } else if (result.flow === 'EXISTING_APPLICATION') {
        setEntryFlow('EXISTING_APPLICATION');
      } else {
        setEntryFlow('BLOCKED');
      }
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const requestUnifiedVerification = async () => {
    if (!form.tax_id || !form.email) {
      notify('請輸入公司或主要聯絡人 Email。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.vendorProactive.requestVerification({
        mode: 'NEW_VENDOR',
        tax_id: form.tax_id,
        email: form.email,
      });
      if (result.direct_access) {
        const sessionResult = await api.vendorProactive.session();
        setSession(sessionResult);
        await loadPortal();
        navigate(
          '/vendor/apply?tax_id=' +
            encodeURIComponent(form.tax_id) +
            (entryApplicationStatus === 'REJECTED' ? '&edit=1' : ''),
        );
        return;
      }
      setChallenge({ id: result.challenge_id ?? '', dev_code: result.dev_code });
      notify('驗證碼已寄出，請完成身分驗證後繼續原申請。', 'info');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const loginExistingVendor = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.tax_id || !accountForm.password) {
      notify('請輸入密碼。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.vendorAccount.login(form.tax_id, accountForm.password);
      if (result.user.role !== 'VENDOR' || !result.user.vendor_id) {
        await api.auth.logout().catch(() => undefined);
        notify('此帳號不是已綁定正式廠商的帳號。', 'error');
        return;
      }
      const sessionResult = await api.vendorProactive.session();
      setSession(sessionResult);
      await loadPortal();
      notify('登入成功，已直接進入廠商工作台。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const setupVendorAccountPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!setupToken) {
      notify('密碼設定連結已失效，請重新輸入統一編號。', 'error');
      return;
    }
    if (!setupTurnstileToken) {
      notify('請完成安全驗證後再設定密碼。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.vendorAccount.setupPassword({
        setup_token: setupToken,
        password: setupForm.password,
        confirm_password: setupForm.confirm_password,
        turnstile_token: setupTurnstileToken,
      });
      setSession(await api.vendorProactive.session());
      await loadPortal();
      setSetupForm({ password: '', confirm_password: '' });
      setSetupToken('');
      setSetupExpiresAt(null);
      setSetupTurnstileToken('');
      notify('廠商登入密碼已完成設定，已進入工作台。', 'success');
      if (result.user.role !== 'VENDOR') await api.auth.logout().catch(() => undefined);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const verifyUnified = async () => {
    if (!challenge || !code) {
      notify('請輸入 Email 驗證碼。', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.vendorProactive.verify({ challenge_id: challenge.id, code });
      const sessionResult = await api.vendorProactive.session();
      setSession(sessionResult);
      await loadPortal();
      navigate('/vendor/apply?tax_id=' + encodeURIComponent(form.tax_id));
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const create = async () => {
    const accessMessage = session?.vendor_id
      ? vendorProposalAccessMessage(session.vendor_status)
      : null;
    if (accessMessage) {
      notify(accessMessage, 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.vendorProactive.proposals.create();
      setProposals((current) => [result, ...current]);
      navigate(`/vendor/proposal/${result.id}`);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const startInvitation = async (invitationId: string) => {
    setSaving(true);
    try {
      const proposal = await api.vendorProactive.invitations.start(invitationId);
      setProposals((current) => [proposal, ...current]);
      navigate(`/vendor/proposal/${proposal.id}`);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const deleteProposal = async (proposal: VendorProposal) => {
    if (!canVendorDeleteProposal(proposal.status)) return;
    const productName = proposal.product?.name_zh || '尚未填寫商品名稱';
    const statusLabel = PROACTIVE_STATUS_LABELS[proposal.status] ?? proposal.status;
    const previouslySubmitted = Boolean(proposal.submitted_at);
    const historyNotice = previouslySubmitted
      ? '\n\n此案件曾經提交並已有歷史處理紀錄。刪除後將不再顯示於廠商端主要列表，但系統仍會保留必要的稽核紀錄。'
      : '';
    const confirmed = window.confirm(
      `確定要刪除此新品提案嗎？\n\n商品名稱：${productName}\n目前狀態：${statusLabel}\n\n刪除後將不再顯示於您的新品提案列表。${historyNotice}`,
    );
    if (!confirmed) return;
    try {
      await api.vendorProactive.proposals.remove(proposal.id);
      setProposals((current) => current.filter((item) => item.id !== proposal.id));
      notify('新品提案已刪除。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const logout = async () => {
    try {
      await api.vendorProactive.logout();
      if (session?.account_id) await api.auth.logout();
    } finally {
      setSession(null);
      setProposals([]);
      setVendorProfile(null);
      setChangeRequests([]);
      setWorkspaceView('PROPOSALS');
      setPortalApplication(null);
      setChallenge(null);
      setCode('');
      notify('已登出廠商工作台。', 'info');
    }
  };
  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  return (
    <div className="min-h-screen bg-sand">
      <VendorPortalHeader>
        {session ? (
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>{session.vendor_name}</span>
            <button className="font-bold text-rose-600" onClick={logout}>
              登出
            </button>
          </div>
        ) : null}
      </VendorPortalHeader>
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-10">
        {session ? (
          <>
            {session.vendor_id &&
              session.vendor_status &&
              !canCreateVendorProposal(session.vendor_status) && (
                <Card className="mb-6 border-amber-200 bg-amber-50">
                  <p className="font-bold text-amber-950">
                    {vendorProposalAccessMessage(session.vendor_status)}
                  </p>
                </Card>
              )}
            {portalApplication?.status === 'REJECTED' && (
              <Card className="mb-6 border-rose-200 bg-rose-50">
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                  <div>
                    <p className="text-sm font-extrabold text-rose-900">廠商申請已退件</p>
                    <p className="mt-2 text-sm leading-6 text-rose-900">
                      退件原因：
                      {portalApplication.review_reason ||
                        '採購人員未提供文字說明，請確認申請資料是否完整。'}
                    </p>
                  </div>
                  <Button onClick={() => navigate('/vendor/apply?edit=1')}>查看原因並修改</Button>
                </div>
              </Card>
            )}
            {portalApplication?.status === 'DRAFT' && (
              <Card className="mb-6 border-teal-200 bg-teal-50">
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                  <div>
                    <p className="font-extrabold text-teal-950">新廠商申請草稿</p>
                    <p className="mt-1 text-sm text-teal-900">
                      資料尚未正式送出，可直接繼續填寫申請。
                    </p>
                  </div>
                  <Button onClick={() => navigate('/vendor/apply')}>繼續填寫申請</Button>
                </div>
              </Card>
            )}
            {session.vendor_id && (
              <Card className="mb-6 border-teal-100 bg-white">
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant={workspaceView === 'PROPOSALS' ? 'primary' : 'secondary'}
                    onClick={() => setWorkspaceView('PROPOSALS')}
                  >
                    我的新品提案
                  </Button>
                  <Button
                    type="button"
                    variant={workspaceView === 'PROFILE' ? 'primary' : 'secondary'}
                    onClick={() => setWorkspaceView('PROFILE')}
                  >
                    我的廠商資料
                    {changeRequests.some((item) =>
                      ['PENDING', 'NEEDS_INFO'].includes(item.status),
                    ) && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                        {
                          changeRequests.filter((item) =>
                            ['PENDING', 'NEEDS_INFO'].includes(item.status),
                          ).length
                        }
                      </span>
                    )}
                  </Button>
                </div>
              </Card>
            )}
            {session.vendor_id && invitations.length > 0 && (
              <Card className="mb-6 border-sky-200 bg-sky-50">
                <div>
                  <h2 className="text-xl font-extrabold text-sky-950">惠生邀請填報</h2>
                  <p className="mt-1 text-sm text-sky-900">
                    惠生邀請與廠商主動提案使用同一份完整新品提案表單；同一邀請可建立多筆商品提案。
                  </p>
                </div>
                <div className="mt-4 grid gap-3">
                  {invitations.map((invitation) => {
                    const statusLabel: Record<string, string> = {
                      NOT_STARTED: '尚未填報',
                      DRAFT: '已有草稿',
                      SUBMITTED: '已提交',
                      EXPIRED: '已逾期',
                      REVOKED: '已撤銷',
                    };
                    const closed = ['EXPIRED', 'REVOKED'].includes(invitation.fill_status);
                    return (
                      <div
                        key={invitation.id}
                        className="flex flex-col justify-between gap-3 rounded-2xl bg-white p-4 shadow-sm md:flex-row md:items-center"
                      >
                        <div>
                          <p className="font-bold text-ink">{invitation.name}</p>
                          <p className="mt-1 text-sm text-slate-600">
                            分類：{invitation.category || '未指定'} · 截止：
                            <DateText value={invitation.due_date} />
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            {statusLabel[invitation.fill_status] ?? invitation.fill_status} · 已建立{' '}
                            {invitation.proposal_count} 筆提案 · 已提交{' '}
                            {invitation.submitted_proposal_count ?? 0} 項商品
                          </p>
                        </div>
                        <Button
                          variant="secondary"
                          disabled={closed || saving}
                          loading={saving}
                          onClick={() => void startInvitation(invitation.id)}
                        >
                          {closed ? '無法填報' : '開始填報／新增商品'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
            {workspaceView === 'PROFILE' && session.vendor_id ? (
              <VendorSelfServicePanel
                profile={vendorProfile}
                changeRequests={changeRequests}
                onReload={loadPortal}
                notify={notify}
              />
            ) : (
              <>
                <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                      {VENDOR_PLATFORM_NAME}
                    </p>
                    <h1 className="mt-2 text-3xl font-extrabold text-ink">
                      {VENDOR_PLATFORM_NAME}
                    </h1>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      每一筆提案獨立保存，可建立多個新品；採購人員審核前不代表已進入正式採購流程。
                    </p>
                  </div>
                  <Button
                    onClick={create}
                    loading={saving}
                    disabled={Boolean(
                      session.vendor_id && !canCreateVendorProposal(session.vendor_status),
                    )}
                  >
                    ＋ 建立新品提案
                  </Button>
                </div>
                {proposals.length === 0 ? (
                  <Card>
                    <EmptyState
                      title="尚未建立新品提案"
                      description="建立第一筆草稿，填寫商品、價格、通路與附件後送出。"
                      action={
                        <Button
                          onClick={create}
                          disabled={Boolean(
                            session.vendor_id && !canCreateVendorProposal(session.vendor_status),
                          )}
                        >
                          建立第一筆提案
                        </Button>
                      }
                    />
                  </Card>
                ) : (
                  <Card>
                    <Table>
                      <thead>
                        <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                          <th className="px-4 py-3">提案編號</th>
                          <th className="px-4 py-3">商品</th>
                          <th className="px-4 py-3">品牌／分類</th>
                          <th className="px-4 py-3">完整度</th>
                          <th className="px-4 py-3">狀態</th>
                          <th className="px-4 py-3">更新時間</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {proposals.map((proposal) => (
                          <tr key={proposal.id} className="border-t border-slate-100 align-top">
                            <td className="px-4 py-4 font-bold text-teal-700">
                              {proposal.proposal_no}
                            </td>
                            <td className="max-w-[260px] whitespace-normal break-words px-4 py-4">
                              <p className="font-bold">
                                {proposal.product?.name_zh || '尚未填寫商品名稱'}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                {proposal.product?.specification || '尚未填寫規格'}
                              </p>
                              {proposal.product?.certificate_status === 'HAS_CERTIFICATE' ? (
                                <span className="inline-block mt-1 rounded bg-teal-50 px-1.5 py-0.5 text-[11px] font-bold text-teal-800 border border-teal-100">
                                  ✓ 已提供認證
                                </span>
                              ) : proposal.product?.certificate_status === 'NOT_REQUIRED' ? (
                                <span className="inline-block mt-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-700">
                                  免認證
                                </span>
                              ) : (
                                <span className="inline-block mt-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold text-amber-800">
                                  待補認證
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-600">
                              {proposal.product?.brand || '—'}
                              <br />
                              {productCategoryDisplay(proposal.product)}
                              <br />
                              <span className="text-xs text-slate-400">
                                {proposal.proposal_source === 'BUYER_INVITATION'
                                  ? '來源：惠生邀請'
                                  : '來源：廠商主動提案'}
                              </span>
                            </td>
                            <td className="px-4 py-4 font-bold text-teal-700">
                              {proposal.completeness_score}%
                            </td>
                            <td className="px-4 py-4">
                              <ProactiveStatusBadge status={proposal.status} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">
                              <DateText value={proposal.updated_at} withTime />
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="secondary"
                                  onClick={() => navigate(`/vendor/proposal/${proposal.id}`)}
                                >
                                  {canVendorEditProposal(proposal.status) ? '查看／編輯' : '查看'}
                                </Button>
                                {proposal.status === 'NOT_ADOPTED' && (
                                  <Button
                                    variant="ghost"
                                    onClick={async () => {
                                      try {
                                        const copy = await api.vendorProactive.proposals.copy(
                                          proposal.id,
                                        );
                                        setProposals((current) => [copy, ...current]);
                                        notify('已複製為新的提案草稿。');
                                      } catch (error) {
                                        notify(errorMessage(error), 'error');
                                      }
                                    }}
                                  >
                                    複製新提案
                                  </Button>
                                )}
                                {canVendorDeleteProposal(proposal.status) && (
                                  <Button
                                    variant="danger"
                                    onClick={() => void deleteProposal(proposal)}
                                  >
                                    刪除提案
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card>
                )}
              </>
            )}
          </>
        ) : (
          <div className="mx-auto max-w-3xl">
            <div className="mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                {VENDOR_PLATFORM_NAME}
              </p>
              <h1 className="mt-2 text-3xl font-extrabold text-ink md:text-4xl">
                {VENDOR_PLATFORM_NAME}
              </h1>
              <p className="mt-3 leading-7 text-slate-600">
                歡迎使用惠生合作供應商平台，請輸入公司統一編號以繼續。
              </p>
            </div>
            <Card>
              <h2 className="text-2xl font-extrabold text-ink">{VENDOR_PLATFORM_NAME}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                請先輸入公司統一編號，系統將自動辨識是否為既有合作廠商。
              </p>
              {entryFlow === 'TAX_ID' && (
                <form
                  className="mt-6 space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void resolveEntry();
                  }}
                >
                  <Input
                    label="統一編號"
                    required
                    value={form.tax_id}
                    onChange={(event) => update('tax_id', event.target.value)}
                    inputMode="numeric"
                    autoComplete="organization"
                  />
                  <p className="text-sm text-slate-500">請輸入 8 位數字，不含空白或符號。</p>
                  <Button className="w-full" loading={saving}>
                    下一步
                  </Button>
                </form>
              )}
              {entryFlow === 'EXISTING_VENDOR' && (
                <div className="mt-6 space-y-4">
                  <div className="rounded-xl bg-teal-50 p-4 text-sm font-bold text-teal-900">
                    已辨識為既有合作廠商；請使用統一編號與密碼登入，不需要 Email 二次驗證。
                  </div>
                  {entryVendorStatus === 'SUSPENDED' && (
                    <div className="rounded-xl bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      目前廠商狀態為暫停交易；登入後可查看既有案件，但暫時無法新增新品提案。
                    </div>
                  )}
                  {entryVendorStatus === 'INACTIVE' && (
                    <div className="rounded-xl bg-rose-50 p-4 text-sm leading-6 text-rose-900">
                      目前廠商帳號已停用；登入後無法建立新品提案，請聯繫採購人員。
                    </div>
                  )}
                  {entryAccountStatus === 'NOT_INITIALIZED' && accountMode === 'SETUP' && (
                    <div className="rounded-xl bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      此統一編號為既有合作廠商，請設定日後登入新品提案平台使用的密碼；不需要採購提供啟用碼，也不需要
                      Email 驗證。
                    </div>
                  )}
                  {entryAccountStatus === 'PASSWORD_RESET_REQUIRED' && accountMode === 'SETUP' && (
                    <div className="rounded-xl bg-orange-50 p-4 text-sm leading-6 text-orange-900">
                      原登入密碼已被採購人員重設，請設定新的登入密碼後繼續。
                    </div>
                  )}
                  {entryAccountStatus === 'LOCKED' && (
                    <div className="rounded-xl bg-rose-50 p-4 text-sm leading-6 text-rose-900">
                      密碼輸入錯誤次數過多，目前帳號已鎖定，請聯繫採購人員協助處理。
                    </div>
                  )}
                  {entryAccountStatus === 'DISABLED' && (
                    <div className="rounded-xl bg-rose-50 p-4 text-sm leading-6 text-rose-900">
                      此廠商登入帳號目前已停用，請聯繫採購人員重新啟用。
                    </div>
                  )}
                  {accountMode === 'LOGIN' && entryAccountStatus === 'ACTIVE' && (
                    <form className="space-y-4" onSubmit={loginExistingVendor}>
                      <Input label="帳號（統一編號）" value={form.tax_id} disabled />
                      <Input
                        label="密碼"
                        type="password"
                        required
                        value={accountForm.password}
                        onChange={(event) => updateAccount('password', event.target.value)}
                        autoComplete="current-password"
                      />
                      <div className="flex flex-wrap gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setEntryFlow('TAX_ID');
                            setAccountMode('LOGIN');
                          }}
                        >
                          更換統一編號
                        </Button>
                        <Button className="flex-1" loading={saving}>
                          登入廠商工作台
                        </Button>
                      </div>
                    </form>
                  )}
                  {accountMode === 'SETUP' &&
                    (entryAccountStatus === 'NOT_INITIALIZED' ||
                      entryAccountStatus === 'PASSWORD_RESET_REQUIRED') && (
                      <form className="space-y-4" onSubmit={setupVendorAccountPassword}>
                        <Input label="統一編號" value={form.tax_id} disabled />
                        <Input
                          label="新密碼"
                          type="password"
                          required
                          value={setupForm.password}
                          onChange={(event) =>
                            setSetupForm((current) => ({
                              ...current,
                              password: event.target.value,
                            }))
                          }
                          autoComplete="new-password"
                        />
                        <Input
                          label="確認新密碼"
                          type="password"
                          required
                          value={setupForm.confirm_password}
                          onChange={(event) =>
                            setSetupForm((current) => ({
                              ...current,
                              confirm_password: event.target.value,
                            }))
                          }
                          autoComplete="new-password"
                        />
                        <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                          密碼至少 8 個字元，且需包含英文字母與數字。
                        </p>
                        {setupExpiresAt && (
                          <p className="text-xs text-slate-500">
                            本次設定連結有效至：
                            <DateText value={setupExpiresAt} withTime />
                          </p>
                        )}
                        <TurnstileWidget
                          onToken={setSetupTurnstileToken}
                          resetKey={setupTurnstileResetKey}
                        />
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                              setEntryFlow('TAX_ID');
                              setAccountMode('LOGIN');
                            }}
                          >
                            返回統編輸入
                          </Button>
                          <Button className="flex-1" loading={saving}>
                            設定密碼並進入平台
                          </Button>
                        </div>
                      </form>
                    )}
                </div>
              )}
              {entryFlow === 'NEW_VENDOR' && (
                <div className="mt-6 space-y-4">
                  <div className="rounded-xl bg-sky-50 p-4 text-sm leading-6 text-sky-900">
                    目前尚無此統一編號的正式廠商資料，請先完成新廠商申請。
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setEntryFlow('TAX_ID')}
                    >
                      更換統一編號
                    </Button>
                    <Button
                      type="button"
                      className="flex-1"
                      onClick={() =>
                        navigate('/vendor/apply?tax_id=' + encodeURIComponent(form.tax_id))
                      }
                    >
                      開始新廠商申請
                    </Button>
                  </div>
                </div>
              )}
              {entryFlow === 'EXISTING_APPLICATION' && (
                <div className="mt-6 space-y-4">
                  {entryApplicationStatus === 'PENDING_REVIEW' ? (
                    <div className="rounded-xl bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      您的廠商申請目前正在審核中，請等待採購完成審核。
                    </div>
                  ) : (
                    <>
                      <div className="rounded-xl bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                        此統一編號已有進行中的廠商申請，請完成身分驗證後繼續原申請。
                      </div>
                      <Input
                        label="公司或主要聯絡人 Email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(event) => update('email', event.target.value)}
                      />
                      <Button
                        type="button"
                        className="w-full"
                        onClick={requestUnifiedVerification}
                        loading={saving}
                      >
                        驗證後繼續原申請
                      </Button>
                    </>
                  )}
                  <Button type="button" variant="secondary" onClick={() => setEntryFlow('TAX_ID')}>
                    返回重新輸入
                  </Button>
                  {challenge && (
                    <div className="border-t border-slate-100 pt-4">
                      <p className="font-bold">請輸入 Email 驗證碼</p>
                      {challenge.dev_code && (
                        <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                          測試驗證碼：
                          <strong className="tracking-[0.2em]">{challenge.dev_code}</strong>
                        </p>
                      )}
                      <div className="mt-4 flex gap-3">
                        <Input
                          aria-label="Email 驗證碼"
                          placeholder="6 位數驗證碼"
                          value={code}
                          onChange={(event) => setCode(event.target.value)}
                        />
                        <Button type="button" onClick={verifyUnified} loading={saving}>
                          驗證並繼續
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {entryFlow === 'BLOCKED' && (
                <div className="mt-6 space-y-4">
                  <div className="rounded-xl bg-rose-50 p-4 text-sm leading-6 text-rose-900">
                    {entryVendorStatus === 'SUSPENDED'
                      ? '目前廠商狀態為暫停交易，暫時無法新增新品提案，請聯繫採購人員。'
                      : '目前廠商帳號已停用，如有新品合作需求請聯繫採購人員。'}
                  </div>
                  <Button type="button" variant="secondary" onClick={() => setEntryFlow('TAX_ID')}>
                    返回重新輸入
                  </Button>
                </div>
              )}
            </Card>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white/70 p-5 text-sm leading-6 text-slate-600">
              <p className="font-bold text-ink">使用說明</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>新廠商先填寫申請資料，建立後即可開始建立新品提案。</li>
                <li>既有廠商使用統一編號與密碼登入，不需要 Email 二次驗證。</li>
                <li>草稿可反覆編輯；送出後進入新品提案採購審核。</li>
                <li>若要求補件，只能修改採購指定的內容，所有版本都會保留。</li>
              </ol>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export function VendorProposalEditorLegacyPage({ notify }: PageProps) {
  const { id = '' } = useParams();
  const [proposal, setProposal] = useState<VendorProposal | null>(null);
  const [product, setProduct] = useState<VendorProposalProduct>(blankProactiveProduct);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [channels, setChannels] = useState<
    Array<{ channel_code: ProactiveChannelCode; channel_name: string; is_primary: boolean }>
  >([]);
  const [currentChannels, setCurrentChannels] = useState<
    Array<{ channel_code: ProactiveChannelCode; channel_name: string; is_primary: boolean }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey] = useState(0);
  useUnsavedPrompt(dirty);
  const load = async () => {
    try {
      const [result, categoryResult] = await Promise.all([
        api.vendorProactive.proposals.detail(id),
        api.productCategories.publicList(),
      ]);
      setProposal(result);
      setProductCategories(categoryResult.categories);
      setProduct(normalizeVendorProposalProduct(result.product));
      setChannels(
        (result.expected_sales_channels ?? result.channels ?? []).map((channel) => ({
          channel_code: channel.channel_code as ProactiveChannelCode,
          channel_name: channel.channel_name,
          is_primary: Boolean('is_primary' in channel ? channel.is_primary : false),
        })),
      );
      setCurrentChannels(
        (result.current_sales_channels ?? []).map((channel) => ({
          channel_code: channel.channel_code as ProactiveChannelCode,
          channel_name: channel.channel_name,
          is_primary: false,
        })),
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, [id]);
  const update = (key: keyof VendorProposalProduct, value: unknown) => {
    setDirty(true);
    setProduct((current) => ({ ...current, [key]: value }) as VendorProposalProduct);
  };
  const toggleChannel = (code: ProactiveChannelCode) => {
    setDirty(true);
    setChannels((current) =>
      current.some((channel) => channel.channel_code === code)
        ? current.filter((channel) => channel.channel_code !== code)
        : [...current, { channel_code: code, channel_name: '', is_primary: current.length === 0 }],
    );
  };
  const save = async (): Promise<VendorProposal | null> => {
    setSaving(true);
    try {
      const normalizedProduct = normalizeVendorProposalProduct(product);
      const result = await api.vendorProactive.proposals.save(id, {
        product: {
          ...normalizedProduct,
          barcodes: normalizedProduct.barcodes.filter(Boolean),
        },
        channels,
        current_sales_channels: currentChannels,
        expected_sales_channels: channels,
      });
      setProposal(result);
      setProduct(normalizeVendorProposalProduct(result.product));
      setDirty(false);
      notify('主動提案草稿已儲存。');
      return result;
    } catch (error) {
      notify(errorMessage(error), 'error');
      return null;
    } finally {
      setSaving(false);
    }
  };
  const upload = async (type: string, file: File) => {
    try {
      await api.vendorProactive.upload(id, type, file);
      await load();
      notify('附件已上傳。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const submit = async () => {
    if (!proposal) return;
    if (!turnstileToken) {
      notify('請先完成安全驗證。', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const saved = dirty ? await save() : proposal;
      if (!saved) return;
      const result = await api.vendorProactive.proposals.submit(id, turnstileToken);
      setProposal(result);
      setDirty(false);
      notify('主動新品提案已提交，等待採購審核。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSubmitting(false);
    }
  };
  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  if (!proposal)
    return (
      <div className="grid min-h-screen place-items-center bg-sand p-6">
        <EmptyState
          title="找不到這筆提案"
          action={
            <Link to="/vendor/proposal">
              <Button>返回提案列表</Button>
            </Link>
          }
        />
      </div>
    );
  const locked = !['DRAFT', 'VENDOR_REVISING', 'REVISION_REQUIRED'].includes(proposal.status);
  const field = (key: keyof VendorProposalProduct) => product[key] as string | number | null;
  return (
    <div className="min-h-screen bg-sand">
      <VendorPortalHeader>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>{proposal.proposal_no}</span>
          <ProactiveStatusBadge status={proposal.status} />
        </div>
      </VendorPortalHeader>
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-10">
        <p className="mb-3 text-sm text-slate-500">
          <span className="font-bold text-rose-600">*</span> 為必填欄位
        </p>
        <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <Link to="/vendor/proposal" className="text-sm font-bold text-teal-700">
              ← 返回提案列表
            </Link>
            <h1 className="mt-3 text-3xl font-extrabold text-ink">
              {proposal.product?.name_zh || '新品提案草稿'}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              提案編號 {proposal.proposal_no} · 完整度{' '}
              <strong className="text-teal-700">{proposal.completeness_score}%</strong>
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setPreview((current) => !current)}>
              {preview ? '返回編輯' : '預覽提案'}
            </Button>
            {!locked && (
              <Button onClick={save} loading={saving}>
                儲存草稿
              </Button>
            )}
          </div>
        </div>
        {proposal.status === 'REVISION_REQUIRED' && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <p className="font-bold text-orange-900">採購人員要求補件</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-orange-900">
              {proposal.vendor_visible_response || '請依下方補件項目更新資料。'}
            </p>
            {proposal.revision_requests
              ?.filter((item) => !item.resolved_at)
              .map((item) => (
                <div key={item.id} className="mt-3 rounded-xl bg-white/70 p-3 text-sm">
                  <p className="font-bold">
                    需補項目：
                    {item.item_codes
                      .map((code) => PROACTIVE_REVISION_ITEM_LABELS[code] ?? code)
                      .join('、')}
                  </p>
                  <p className="mt-1">
                    期限：{item.due_at ? <DateText value={item.due_at} /> : '未設定'}
                  </p>
                </div>
              ))}
          </Card>
        )}
        {proposal.invitation && (
          <Card className="mb-6 border-sky-200 bg-sky-50">
            <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-700">
                  惠生邀請原始內容
                </p>
                <h2 className="mt-1 text-lg font-extrabold text-sky-950">
                  {proposal.invitation.name}
                </h2>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-sky-800">
                來源：惠生邀請
              </span>
            </div>
            <div className="mt-4 grid gap-3 text-sm text-sky-950 md:grid-cols-2">
              <p>商品分類／主題：{proposal.invitation.category || '未指定'}</p>
              <p>
                填報期限：
                <DateText value={proposal.invitation.due_date} />
              </p>
              <p className="whitespace-pre-wrap break-words md:col-span-2">
                邀請說明：{proposal.invitation.description || '未提供說明'}
              </p>
              <p className="md:col-span-2">
                必要附件：
                {proposal.invitation.required_attachment_types.length
                  ? proposal.invitation.required_attachment_types
                      .map((type) => labelProactiveAttachmentType(type))
                      .join('、')
                  : '依目前附件規則'}
              </p>
            </div>
          </Card>
        )}
        {proposal.missing_items?.length ? (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <p className="font-bold text-amber-900">提交前需補齊</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {proposal.missing_items.map((item) => (
                <span
                  key={item}
                  className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-900"
                >
                  {item}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs text-amber-800">
              完整度門檻由系統設定控制，後端提交時會再次檢查。
            </p>
          </Card>
        ) : null}
        {preview ? (
          <Card>
            <h2 className="text-2xl font-extrabold">提案預覽</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <Info label="商品名稱" value={product.name_zh} />
              <Info
                label="品牌／製造商"
                value={`${product.brand || '—'}／${product.manufacturer || '—'}`}
              />
              <Info
                label="規格／單位"
                value={`${product.specification || '—'}／${product.unit || '—'}`}
              />
              <Info
                label="產地／分類"
                value={`${product.origin || '—'}／${productCategoryDisplay(product)}`}
              />
              <Info label="未稅進價" value={<Money value={product.ex_tax_price} />} />
              <Info
                label="含稅進價／建議售價"
                value={
                  <>
                    <Money value={product.tax_included_price} />／
                    <Money value={product.suggested_retail_price} />
                  </>
                }
              />
              <Info
                label="預估毛利"
                value={
                  product.estimated_margin_rate == null
                    ? '儲存後計算'
                    : `${(product.estimated_margin_rate * 100).toFixed(2)}%`
                }
              />
              <Info
                label="主要通路"
                value={
                  channels
                    .map(
                      (channel) =>
                        PROACTIVE_CHANNEL_LABELS[channel.channel_code] ?? channel.channel_code,
                    )
                    .join('、') || '—'
                }
              />
              <Info label="商品特色" value={product.key_features || product.product_features} />
              <Info label="競品差異" value={product.competitor_difference} />
              <Info label="適用客群" value={product.target_customers} />
              <Info label="上市計畫" value={product.launch_plan} />
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card>
              <h2 className="mb-5 text-xl font-extrabold">商品基本資料</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <Input
                  label="廠商商品編號"
                  disabled={locked}
                  value={field('vendor_product_code') ?? ''}
                  onChange={(event) => update('vendor_product_code', event.target.value)}
                />
                <Input
                  label="商品中文名稱"
                  required
                  disabled={locked}
                  value={field('name_zh') ?? ''}
                  onChange={(event) => update('name_zh', event.target.value)}
                />
                <Input
                  label="商品英文名稱"
                  disabled={locked}
                  value={field('name_en') ?? ''}
                  onChange={(event) => update('name_en', event.target.value)}
                />
                <Input
                  label="品牌"
                  required
                  disabled={locked}
                  value={field('brand') ?? ''}
                  onChange={(event) => update('brand', event.target.value)}
                />
                <Input
                  label="製造商"
                  disabled={locked}
                  value={field('manufacturer') ?? ''}
                  onChange={(event) => update('manufacturer', event.target.value)}
                />
                <Input
                  label="產地"
                  required
                  disabled={locked}
                  value={field('origin') ?? ''}
                  onChange={(event) => update('origin', event.target.value)}
                />
                <ProductCategorySelect
                  categories={productCategories}
                  value={product.product_category_code}
                  currentName={product.product_category_name}
                  disabled={locked}
                  onChange={(value) => update('product_category_code', value)}
                />
                <Input
                  label="子分類"
                  disabled={locked}
                  value={field('subcategory') ?? ''}
                  onChange={(event) => update('subcategory', event.target.value)}
                />
                <Input
                  label="規格"
                  required
                  disabled={locked}
                  value={field('specification') ?? ''}
                  onChange={(event) => update('specification', event.target.value)}
                />
                <Input
                  label="單位"
                  required
                  disabled={locked}
                  value={field('unit') ?? ''}
                  onChange={(event) => update('unit', event.target.value)}
                />
                <Input
                  label="箱入數"
                  type="number"
                  min="0"
                  required
                  disabled={locked}
                  value={field('case_pack') ?? ''}
                  onChange={(event) =>
                    update('case_pack', event.target.value ? Number(event.target.value) : null)
                  }
                />
                <Select
                  label="商品生命週期"
                  disabled={locked}
                  value={product.lifecycle_type}
                  onChange={(event) => update('lifecycle_type', event.target.value)}
                >
                  {Object.entries(PROACTIVE_LIFECYCLE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Textarea
                  label="成分"
                  disabled={locked}
                  value={product.ingredients}
                  onChange={(event) => update('ingredients', event.target.value)}
                />
                <Textarea
                  label="使用方式"
                  disabled={locked}
                  value={product.usage}
                  onChange={(event) => update('usage', event.target.value)}
                />
                <Textarea
                  label="保存方式／保存期限"
                  disabled={locked}
                  value={`${product.storage}${product.shelf_life ? `\n保存期限：${product.shelf_life}` : ''}`}
                  onChange={(event) => {
                    const [storage, ...shelf] = event.target.value.split('\n保存期限：');
                    update('storage', storage);
                    update('shelf_life', shelf.join('\n保存期限：'));
                  }}
                />
                <Textarea
                  label="有效日期要求／注意事項"
                  disabled={locked}
                  value={`${product.expiry_requirement}${product.precautions ? `\n注意事項：${product.precautions}` : ''}`}
                  onChange={(event) => {
                    const [expiry, ...precautions] = event.target.value.split('\n注意事項：');
                    update('expiry_requirement', expiry);
                    update('precautions', precautions.join('\n注意事項：'));
                  }}
                />
                <Input
                  label="預計上市日期"
                  type="date"
                  disabled={locked}
                  value={product.planned_launch_date ?? ''}
                  onChange={(event) => update('planned_launch_date', event.target.value || null)}
                />
              </div>
            </Card>
            <Card>
              <h2 className="mb-5 text-xl font-extrabold">條碼</h2>
              <p className="mb-4 text-sm text-slate-500">
                國際條碼最多六組；同一提案內不得重複。若系統發現其他提案已有相同條碼，會標示疑似重複但不會自動拒絕。
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <Input
                    key={index}
                    label={`國際條碼 ${index + 1}`}
                    disabled={locked}
                    value={product.barcodes[index] ?? ''}
                    onChange={(event) => {
                      const next = [...product.barcodes];
                      next[index] = event.target.value;
                      update('barcodes', next);
                    }}
                  />
                ))}
                <Input
                  label="外箱條碼"
                  disabled={locked}
                  value={product.outer_barcode}
                  onChange={(event) => update('outer_barcode', event.target.value)}
                />
                <Input
                  label="內盒條碼"
                  disabled={locked}
                  value={product.inner_barcode}
                  onChange={(event) => update('inner_barcode', event.target.value)}
                />
              </div>
            </Card>
            <Card>
              <h2 className="mb-5 text-xl font-extrabold">價格與交易條件</h2>
              <div className="grid gap-4 md:grid-cols-4">
                <Input
                  label="未稅進價"
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={locked}
                  value={product.ex_tax_price ?? ''}
                  onChange={(event) =>
                    update('ex_tax_price', event.target.value ? Number(event.target.value) : null)
                  }
                />
                <Input
                  label="含稅進價"
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={locked}
                  value={product.tax_included_price ?? ''}
                  onChange={(event) =>
                    update(
                      'tax_included_price',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="稅率"
                  required
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  disabled={locked}
                  value={product.tax_rate}
                  onChange={(event) => update('tax_rate', Number(event.target.value))}
                />
                <Input
                  label="建議售價"
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={locked}
                  value={product.suggested_retail_price ?? ''}
                  onChange={(event) =>
                    update(
                      'suggested_retail_price',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="活動售價"
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={locked}
                  value={product.promotional_price ?? ''}
                  onChange={(event) =>
                    update(
                      'promotional_price',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="最低訂購量"
                  required
                  type="number"
                  min="0"
                  step="1"
                  disabled={locked}
                  value={product.minimum_order_quantity ?? ''}
                  onChange={(event) =>
                    update(
                      'minimum_order_quantity',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="最低訂購金額"
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={locked}
                  value={product.minimum_order_amount ?? ''}
                  onChange={(event) =>
                    update(
                      'minimum_order_amount',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="最小配送單位"
                  required
                  disabled={locked}
                  value={product.minimum_delivery_unit}
                  onChange={(event) => update('minimum_delivery_unit', event.target.value)}
                />
                <Input
                  label="交貨天數"
                  required
                  type="number"
                  min="0"
                  step="1"
                  disabled={locked}
                  value={product.delivery_days ?? ''}
                  onChange={(event) =>
                    update('delivery_days', event.target.value ? Number(event.target.value) : null)
                  }
                />
                <Input
                  label="付款方式"
                  required
                  disabled={locked}
                  value={product.payment_method}
                  onChange={(event) => update('payment_method', event.target.value)}
                />
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Textarea
                  label="退貨條件"
                  required
                  disabled={locked}
                  value={product.return_terms}
                  onChange={(event) => update('return_terms', event.target.value)}
                />
                <Textarea
                  label="即期品處理方式"
                  required
                  disabled={locked}
                  value={product.near_expiry_handling}
                  onChange={(event) => update('near_expiry_handling', event.target.value)}
                />
                <Textarea
                  label="首批優惠"
                  disabled={locked}
                  value={product.first_order_offer}
                  onChange={(event) => update('first_order_offer', event.target.value)}
                />
                <Textarea
                  label="贈品及搭贈"
                  disabled={locked}
                  value={product.gifts}
                  onChange={(event) => update('gifts', event.target.value)}
                />
                <Textarea
                  label="促銷／陳列／試用支援"
                  disabled={locked}
                  value={`${product.promotion_support}\n${product.display_support}\n${product.sample_support}`}
                  onChange={(event) => {
                    const [promotionSupport = '', displaySupport = '', sampleSupport = ''] =
                      event.target.value.split('\n');
                    update('promotion_support', promotionSupport);
                    update('display_support', displaySupport);
                    update('sample_support', sampleSupport);
                  }}
                />
              </div>
            </Card>
            <Card>
              <h2 className="mb-5 text-xl font-extrabold">銷售與上市提案</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <Textarea
                  label="商品特色／關鍵賣點"
                  required
                  disabled={locked}
                  value={product.key_features}
                  onChange={(event) => update('key_features', event.target.value)}
                />
                <Textarea
                  label="市場定位"
                  required
                  disabled={locked}
                  value={product.market_positioning}
                  onChange={(event) => update('market_positioning', event.target.value)}
                />
                <Textarea
                  label="適用客群"
                  required
                  disabled={locked}
                  value={product.target_customers}
                  onChange={(event) => update('target_customers', event.target.value)}
                />
                <Textarea
                  label="競品差異"
                  required
                  disabled={locked}
                  value={product.competitor_difference}
                  onChange={(event) => update('competitor_difference', event.target.value)}
                />
                <Textarea
                  label="建議售價理由"
                  required
                  disabled={locked}
                  value={product.suggested_price_rationale}
                  onChange={(event) => update('suggested_price_rationale', event.target.value)}
                />
                <Textarea
                  label="市場銷售情形"
                  required
                  disabled={locked}
                  value={product.market_sales}
                  onChange={(event) => update('market_sales', event.target.value)}
                />
                <Input
                  label="預估月銷量"
                  required
                  type="number"
                  min="0"
                  step="1"
                  disabled={locked}
                  value={product.estimated_monthly_sales ?? ''}
                  onChange={(event) =>
                    update(
                      'estimated_monthly_sales',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="建議門市類型"
                  required
                  disabled={locked}
                  value={product.suggested_store_type}
                  onChange={(event) => update('suggested_store_type', event.target.value)}
                />
                <Textarea
                  label="建議陳列位置／方式"
                  required
                  disabled={locked}
                  value={`${product.display_location}\n${product.display_method}`}
                  onChange={(event) => {
                    const [displayLocation = '', displayMethod = ''] =
                      event.target.value.split('\n');
                    update('display_location', displayLocation);
                    update('display_method', displayMethod);
                  }}
                />
                <Textarea
                  label="行銷支援"
                  required
                  disabled={locked}
                  value={product.marketing_support}
                  onChange={(event) => update('marketing_support', event.target.value)}
                />
                <Textarea
                  label="上市計畫"
                  required
                  disabled={locked}
                  value={product.launch_plan}
                  onChange={(event) => update('launch_plan', event.target.value)}
                />
              </div>
              <div className="mt-5">
                <p className="mb-2 text-sm font-semibold text-ink">預計銷售通路（可複選）</p>
                <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                  {(Object.keys(PROACTIVE_CHANNEL_LABELS) as ProactiveChannelCode[]).map((code) => (
                    <label
                      key={code}
                      className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-sm"
                    >
                      <input
                        type="checkbox"
                        disabled={locked}
                        checked={channels.some((channel) => channel.channel_code === code)}
                        onChange={() => toggleChannel(code)}
                      />
                      {PROACTIVE_CHANNEL_LABELS[code]}
                    </label>
                  ))}
                </div>
                <Input
                  className="mt-3"
                  label="主要通路名稱／其他補充"
                  disabled={locked}
                  value={channels
                    .map((channel) => channel.channel_name)
                    .filter(Boolean)
                    .join('、')}
                  onChange={(event) =>
                    setChannels((current) =>
                      current.map((channel, index) =>
                        index === 0 ? { ...channel, channel_name: event.target.value } : channel,
                      ),
                    )
                  }
                />
              </div>
            </Card>
            <Card>
              <h2 className="mb-5 text-xl font-extrabold">附件</h2>
              <p className="mb-4 text-sm leading-6 text-slate-500">
                檔案會使用 UUID 物件鍵存入 R2，前端只取得經權限檢查的下載 API，不直接暴露儲存網址。
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {(Object.keys(PROACTIVE_ATTACHMENT_LABELS) as string[]).map((type) => (
                  <label
                    key={type}
                    className="rounded-2xl border border-dashed border-slate-200 bg-white p-4"
                  >
                    <span className="block text-sm font-bold">
                      {labelProactiveAttachmentType(type)}
                    </span>
                    <input
                      type="file"
                      className="mt-3 block w-full text-sm"
                      disabled={locked}
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.ppt,.pptx"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void upload(type, file);
                      }}
                    />
                  </label>
                ))}
              </div>
              <div className="mt-4 space-y-2">
                {proposal.attachments?.map((attachment) => (
                  <a
                    key={attachment.id}
                    href={api.vendorProactive.downloadUrl(attachment.id)}
                    className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm hover:bg-teal-50"
                  >
                    <span className="min-w-0 truncate">
                      {labelProactiveAttachmentType(attachment.type)} · {attachment.original_name}
                    </span>
                    <span className="text-xs font-bold text-teal-700">查看附件</span>
                  </a>
                ))}
              </div>
            </Card>
            <div className="flex flex-col items-end justify-end gap-3 border-t border-slate-200 pt-6 sm:flex-row">
              {!locked && (
                <Button variant="secondary" onClick={save} loading={saving}>
                  儲存草稿
                </Button>
              )}
              {!locked && (
                <>
                  <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileResetKey} />
                  <Button onClick={submit} loading={submitting} disabled={submitting}>
                    提交主動提案
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export function VendorProposalEditorPage({ notify }: PageProps) {
  const { id = '' } = useParams();
  const [proposal, setProposal] = useState<VendorProposal | null>(null);
  const [versions, setVersions] = useState<VendorProposalVersion[]>([]);
  const [proposalChangeDraft, setProposalChangeDraft] = useState({
    name_zh: '',
    brand: '',
    specification: '',
    ex_tax_price: '',
    tax_included_price: '',
    tax_rate: '',
    suggested_retail_price: '',
    barcodes: '',
    return_terms: '',
  });
  const [proposalChangeReason, setProposalChangeReason] = useState('');
  const [requestingProposalChange, setRequestingProposalChange] = useState(false);
  const [product, setProduct] = useState<VendorProposalProduct>(blankProactiveProduct);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [currentChannels, setCurrentChannels] = useState<
    Array<{ channel_code: ProactiveChannelCode; channel_name: string; is_primary: boolean }>
  >([]);
  const [expectedChannels, setExpectedChannels] = useState<
    Array<{ channel_code: ProactiveChannelCode; channel_name: string; is_primary: boolean }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [quantifiedContentConfirmed, setQuantifiedContentConfirmed] = useState(false);
  const [barcodeImageTarget, setBarcodeImageTarget] = useState('');
  const [autosaveStatus, setAutosaveStatus] = useState<'DIRTY' | 'SAVING' | 'SAVED' | 'ERROR'>(
    'SAVED',
  );
  const [lastAutosavedAt, setLastAutosavedAt] = useState<string | null>(null);
  const [editRevision, setEditRevision] = useState(0);
  const editRevisionRef = useRef(0);
  const saveSequenceRef = useRef(0);
  const latestSaveSequenceRef = useRef(0);
  const saveRef = useRef<((options?: { auto?: boolean }) => Promise<VendorProposal | null>) | null>(
    null,
  );
  useUnsavedPrompt(dirty);

  const load = async () => {
    const loadSequence = ++saveSequenceRef.current;
    latestSaveSequenceRef.current = loadSequence;
    editRevisionRef.current = 0;
    setEditRevision(0);
    setDirty(false);
    setAutosaveStatus('SAVED');
    setLastAutosavedAt(null);
    try {
      const [detail, categoryResult, versionResult] = await Promise.all([
        api.vendorProactive.proposals.detail(id),
        api.productCategories.publicList(),
        api.vendorProactive.proposals.versions(id).catch(() => ({ versions: [] })),
      ]);
      setProposal(detail);
      setVersions(versionResult.versions);
      setProductCategories(categoryResult.categories);
      const loadedProduct = normalizeVendorProposalProduct(detail.product);
      setProduct(loadedProduct);
      setProposalChangeDraft({
        name_zh: loadedProduct.name_zh,
        brand: loadedProduct.brand,
        specification: loadedProduct.specification,
        ex_tax_price: loadedProduct.ex_tax_price?.toString() ?? '',
        tax_included_price: loadedProduct.tax_included_price?.toString() ?? '',
        tax_rate: loadedProduct.tax_rate?.toString() ?? '',
        suggested_retail_price: loadedProduct.suggested_retail_price?.toString() ?? '',
        barcodes: loadedProduct.barcodes.filter(Boolean).join(', '),
        return_terms: loadedProduct.return_terms,
      });
      setCurrentChannels(
        (detail.current_sales_channels ?? []).map((channel) => ({
          channel_code: channel.channel_code as ProactiveChannelCode,
          channel_name: channel.channel_name,
          is_primary: false,
        })),
      );
      setExpectedChannels(
        (detail.expected_sales_channels ?? detail.channels ?? []).map((channel) => ({
          channel_code: channel.channel_code as ProactiveChannelCode,
          channel_name: channel.channel_name,
          is_primary: 'is_primary' in channel ? Boolean(channel.is_primary) : false,
        })),
      );
      setQuantifiedContentConfirmed(false);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, [id]);

  const markDirty = () => {
    editRevisionRef.current += 1;
    setEditRevision(editRevisionRef.current);
    setDirty(true);
    setAutosaveStatus('DIRTY');
  };

  const update = (key: keyof VendorProposalProduct, value: unknown) => {
    markDirty();
    if (
      [
        'initial_order_offer',
        'initial_order_offer_enabled',
        'initial_order_offer_content',
        'gift_bundle_support',
        'gift_bundle_support_enabled',
        'gift_bundle_support_content',
        'promotion_support',
        'promotion_support_enabled',
        'promotion_content',
        'promotion_start_date',
        'promotion_end_date',
        'sample_support',
        'trial_support_enabled',
        'trial_support_content',
      ].includes(key)
    )
      setQuantifiedContentConfirmed(false);
    setProduct((current) => ({ ...current, [key]: value }) as VendorProposalProduct);
  };
  const updateTradeTerms = (key: string, value: unknown) => {
    markDirty();
    setProduct(
      (current) =>
        ({
          ...current,
          trade_terms_draft: {
            ...(current.trade_terms_draft ?? current.trade_terms ?? {}),
            [key]: value,
          } as VendorProposalProduct['trade_terms_draft'],
        }) as VendorProposalProduct,
    );
  };
  const requestProposalChange = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!proposal || !canVendorRequestProposalChange(proposal.status)) return;
    if (proposalChangeReason.trim().length < 5) {
      notify('請填寫至少 5 個字的修改原因。', 'error');
      return;
    }
    const proposedProduct = {
      name_zh: proposalChangeDraft.name_zh.trim(),
      brand: proposalChangeDraft.brand.trim(),
      specification: proposalChangeDraft.specification.trim(),
      ex_tax_price: proposalChangeDraft.ex_tax_price
        ? Number(proposalChangeDraft.ex_tax_price)
        : null,
      tax_included_price: proposalChangeDraft.tax_included_price
        ? Number(proposalChangeDraft.tax_included_price)
        : null,
      tax_rate: proposalChangeDraft.tax_rate
        ? Number(proposalChangeDraft.tax_rate)
        : product.tax_rate,
      suggested_retail_price: proposalChangeDraft.suggested_retail_price
        ? Number(proposalChangeDraft.suggested_retail_price)
        : null,
      barcodes: proposalChangeDraft.barcodes
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      return_terms: proposalChangeDraft.return_terms.trim(),
    };
    setRequestingProposalChange(true);
    try {
      await api.vendorProactive.proposals.requestChange(id, {
        reason: proposalChangeReason.trim(),
        proposed: { product: proposedProduct },
      });
      setProposalChangeReason('');
      await load();
      notify('新品資料／報價變更申請已送出。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setRequestingProposalChange(false);
    }
  };
  const updateLifecycle = (value: string) => {
    update('product_lifecycle', value);
    update('lifecycle_type', value);
  };
  const toggleChannel = (
    setter: React.Dispatch<
      React.SetStateAction<
        Array<{ channel_code: ProactiveChannelCode; channel_name: string; is_primary: boolean }>
      >
    >,
    code: ProactiveChannelCode,
  ) => {
    markDirty();
    setter((current) =>
      current.some((channel) => channel.channel_code === code)
        ? current.filter((channel) => channel.channel_code !== code)
        : [...current, { channel_code: code, channel_name: '', is_primary: current.length === 0 }],
    );
  };
  const productValue = (key: keyof VendorProposalProduct) =>
    (product[key] ?? '') as string | number | null | undefined;
  const selectionValue = (enabled: number | undefined, legacy: string | undefined) =>
    legacy === '有' || legacy === 'YES' || enabled === 1
      ? 'YES'
      : legacy === '無' || legacy === 'NO'
        ? 'NO'
        : '';
  const removedFields = new Set([
    'promotional_price',
    'delivery_days',
    'payment_method',
    'payment_terms',
    'return_terms',
    'near_expiry_handling',
    'expiry_requirement',
    'display_location',
    'estimated_monthly_sales',
    'suggested_store_type',
    'display_method',
    'display_support',
    'trade_terms_draft',
  ]);
  const normalizedProduct = normalizeVendorProposalProduct(product);
  const certificateAttachment = useMemo(() => {
    return (
      proposal?.attachments?.find((att) => att.id === product.certificate_attachment_id) ||
      proposal?.attachments?.find((att) => ['LICENSE', 'CERTIFICATION'].includes(att.type))
    );
  }, [proposal?.attachments, product.certificate_attachment_id]);
  const saveProduct = {
    ...Object.fromEntries(
      Object.entries(normalizedProduct).filter(([key]) => !removedFields.has(key)),
    ),
    barcodes: normalizedProduct.barcodes.filter(Boolean),
    no_international_barcode: Boolean(normalizedProduct.no_international_barcode),
    launch_date_is_tentative: Boolean(normalizedProduct.launch_date_is_tentative),
    initial_order_offer_enabled: normalizedProduct.initial_order_offer_enabled === 1,
    gift_bundle_support_enabled: normalizedProduct.gift_bundle_support_enabled === 1,
    promotion_support_enabled: normalizedProduct.promotion_support_enabled === 1,
    trial_support_enabled: normalizedProduct.trial_support_enabled === 1,
    trade_terms: normalizeProductTradeTermsPayload(
      normalizedProduct.trade_terms_draft ?? normalizedProduct.trade_terms,
    ),
  };
  const save = async (options: { auto?: boolean } = {}): Promise<VendorProposal | null> => {
    const auto = options.auto === true;
    const startedRevision = editRevisionRef.current;
    const sequence = ++saveSequenceRef.current;
    latestSaveSequenceRef.current = sequence;
    setSaving(true);
    if (auto) setAutosaveStatus('SAVING');
    try {
      const result = await api.vendorProactive.proposals.save(id, {
        product: saveProduct,
        channels: expectedChannels,
        current_sales_channels: currentChannels,
        expected_sales_channels: expectedChannels,
      });
      const canApply = shouldApplyAutosaveResponse({
        responseSequence: sequence,
        latestSequence: latestSaveSequenceRef.current,
        startedRevision,
        currentRevision: editRevisionRef.current,
      });
      if (!canApply) {
        if (!auto) notify('內容在儲存期間有更新，請再儲存一次。', 'error');
        return null;
      }
      setProposal(result);
      setProduct(normalizeVendorProposalProduct(result.product));
      setDirty(false);
      setAutosaveStatus('SAVED');
      setLastAutosavedAt(new Date().toISOString());
      if (!auto) notify('新品提案草稿已儲存。', 'success');
      return result;
    } catch (error) {
      if (
        sequence === latestSaveSequenceRef.current &&
        startedRevision === editRevisionRef.current
      ) {
        setAutosaveStatus('ERROR');
        notify(errorMessage(error), 'error');
      }
      return null;
    } finally {
      if (sequence === latestSaveSequenceRef.current) setSaving(false);
    }
  };
  saveRef.current = save;

  useEffect(() => {
    if (
      !dirty ||
      !proposal ||
      !['DRAFT', 'VENDOR_REVISING', 'REVISION_REQUIRED'].includes(proposal.status)
    )
      return;
    const timer = window.setTimeout(() => {
      if (saveRef.current) void saveRef.current({ auto: true });
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [dirty, editRevision, id, proposal, proposal?.status]);

  const upload = async (type: string, file: File, barcodeValue?: string) => {
    try {
      const attachment = await api.vendorProactive.upload(id, type, file, barcodeValue);
      setProposal((current) => {
        if (!current) return current;
        const nextAttachments = (current.attachments ?? []).filter((item) => {
          if (item.type !== attachment.type) return true;
          if (attachment.type === 'BARCODE_IMAGE')
            return item.barcode_value !== attachment.barcode_value;
          return false;
        });
        return { ...current, attachments: [...nextAttachments, attachment] };
      });
      if (type === 'LICENSE' || type === 'CERTIFICATION') {
        update('certificate_attachment_id', attachment.id);
        update('certificate_attachment_url', api.vendorProactive.downloadUrl(attachment.id));
        update('certificate_attachment_name', attachment.original_name);
      }
      notify('附件已上傳。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };

  const handleCertificateUpload = async (file: File) => {
    try {
      const attachment = await api.vendorProactive.upload(id, 'LICENSE', file);
      setProposal((current) => {
        if (!current) return current;
        const nextAttachments = (current.attachments ?? []).filter(
          (item) => item.type !== 'LICENSE' && item.type !== 'CERTIFICATION',
        );
        return { ...current, attachments: [...nextAttachments, attachment] };
      });
      update('certificate_attachment_id', attachment.id);
      update('certificate_attachment_url', api.vendorProactive.downloadUrl(attachment.id));
      update('certificate_attachment_name', attachment.original_name);
      notify('認證／許可證證明文件已上傳。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };

  const deleteAttachment = async (
    attachment: NonNullable<VendorProposal['attachments']>[number],
    confirmBefore = true,
  ): Promise<boolean> => {
    if (confirmBefore) {
      const label = labelProactiveAttachmentType(attachment.type);
      if (
        !window.confirm(
          `確定要刪除此附件嗎？\n\n文件類型：${label}\n檔案名稱：${attachment.original_name}`,
        )
      )
        return false;
    }
    try {
      const result = await api.vendorProactive.deleteAttachment(id, attachment.id);
      setProposal((current) =>
        current ? { ...current, attachments: result.attachments } : current,
      );
      if (
        attachment.id === product.certificate_attachment_id ||
        ['LICENSE', 'CERTIFICATION'].includes(attachment.type)
      ) {
        update('certificate_attachment_id', null);
        update('certificate_attachment_url', null);
        update('certificate_attachment_name', null);
      }
      if (confirmBefore) notify('附件已刪除。', 'success');
      return true;
    } catch (error) {
      notify(errorMessage(error), 'error');
      return false;
    }
  };
  const handleNoBarcodeToggle = async (checked: boolean) => {
    if (!checked) {
      update('no_international_barcode', 0);
      update('no_international_barcode_reason', '');
      update('no_international_barcode_other_reason', '');
      return;
    }
    const barcodeAttachments = (proposal?.attachments ?? []).filter(
      (attachment) => attachment.type === 'BARCODE_IMAGE',
    );
    if (
      barcodeAttachments.length > 0 &&
      !window.confirm(
        '目前已上傳條碼圖片。設定為無國際條碼後，這些條碼圖片將從本次提報中移除，是否繼續？',
      )
    )
      return;
    for (const attachment of barcodeAttachments) {
      const deleted = await deleteAttachment(attachment, false);
      if (!deleted) return;
    }
    update('no_international_barcode', 1);
    update('barcodes', ['']);
    update('outer_barcode', '');
    update('inner_barcode', '');
  };
  const submit = async () => {
    if (!proposal) return;
    if (!turnstileToken) {
      notify('請先完成安全驗證。', 'error');
      return;
    }
    if (!quantifiedContentConfirmed) {
      notify('請確認優惠及支援內容已清楚載明實際提供條件與數量。', 'error');
      return;
    }
    const certValidation = validateProposalCertificate(
      {
        ...product,
        hasCertificateAttachment: Boolean(
          product.certificate_attachment_id ||
          proposal.attachments?.some((item) => ['LICENSE', 'CERTIFICATION'].includes(item.type)),
        ),
      },
      true,
    );
    if (!certValidation.valid) {
      notify(certValidation.errors[0] || '請完成認證／許可證必填資料。', 'error');
      return;
    }
    const barcodeErrors = validateProductBarcodes({
      barcodes: saveProduct.barcodes,
      outer_barcode: product.outer_barcode,
      inner_barcode: product.inner_barcode,
    });
    const hasAnyBarcode = Boolean(
      saveProduct.barcodes.length || product.outer_barcode || product.inner_barcode,
    );
    if (product.no_international_barcode && hasAnyBarcode) {
      notify('勾選無國際條碼時不可同時填寫條碼資料。', 'error');
      return;
    }
    if (!product.no_international_barcode && barcodeErrors.length) {
      notify('條碼資料有誤：' + barcodeErrors.join('、'), 'error');
      return;
    }
    setSubmitting(true);
    try {
      const saved = dirty ? await save() : proposal;
      if (!saved) return;
      const result = await api.vendorProactive.proposals.submit(
        id,
        turnstileToken,
        crypto.randomUUID(),
        quantifiedContentConfirmed,
      );
      setProposal(result);
      setDirty(false);
      notify('新品提案已提交，等待採購審核。', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
      setTurnstileToken('');
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-sand">
        <Loading />
      </div>
    );
  if (!proposal)
    return (
      <div className="grid min-h-screen place-items-center bg-sand p-6">
        <EmptyState
          title="找不到新品提案"
          action={
            <Link to="/vendor/proposal">
              <Button>返回廠商入口</Button>
            </Link>
          }
        />
      </div>
    );
  const locked = !['DRAFT', 'VENDOR_REVISING', 'REVISION_REQUIRED'].includes(proposal.status);
  const noBarcode = Boolean(product.no_international_barcode);
  const tradeTerms = product.trade_terms_draft ?? product.trade_terms;
  const barcodeValues = [
    ...product.barcodes.filter(Boolean),
    product.outer_barcode,
    product.inner_barcode,
  ].filter(Boolean);
  const internationalBarcodeValues = product.barcodes
    .filter(Boolean)
    .map((value) => value.replace(/[-\s]/g, '').trim());
  const quantifiedContentWarnings = getQuantifiedContentWarnings(product);
  const attachmentRequirements: AttachmentRequirement[] = proposal.attachment_requirements?.length
    ? proposal.attachment_requirements
    : ([
        {
          id: 'front',
          category_code: '*',
          type: 'PRODUCT_FRONT',
          label: '商品正面照',
          required: 1,
          is_active: 1,
          sort_order: 10,
        },
        {
          id: 'back',
          category_code: '*',
          type: 'PRODUCT_BACK',
          label: '商品背面照',
          required: 1,
          is_active: 1,
          sort_order: 20,
        },
        {
          id: 'quotation',
          category_code: '*',
          type: 'QUOTATION',
          label: '報價單',
          required: 1,
          is_active: 1,
          sort_order: 30,
        },
      ] as AttachmentRequirement[]);
  const attachmentTypes = new Set<string>(
    proposal.attachments?.map((attachment) => attachment.type) ?? [],
  );
  const effectiveAttachmentRequirements = attachmentRequirements.filter(
    (requirement) => !(requirement.type === 'BARCODE_IMAGE' && noBarcode),
  );
  const missingRequiredAttachments = effectiveAttachmentRequirements.filter(
    (requirement) => requirement.required === 1 && !attachmentTypes.has(requirement.type),
  );
  const selectedBarcodeImageTarget = internationalBarcodeValues.includes(barcodeImageTarget)
    ? barcodeImageTarget
    : (internationalBarcodeValues[0] ?? '');
  const toggleSelection = (
    key: keyof VendorProposalProduct,
    legacyKey: keyof VendorProposalProduct,
    value: string,
  ) => {
    update(key, value === 'YES' ? 1 : value === 'NO' ? 0 : undefined);
    update(legacyKey, value === 'YES' ? '有' : value === 'NO' ? '無' : '');
  };
  const channelSelector = (
    title: string,
    channels: Array<{
      channel_code: ProactiveChannelCode;
      channel_name: string;
      is_primary: boolean;
    }>,
    setter: React.Dispatch<
      React.SetStateAction<
        Array<{ channel_code: ProactiveChannelCode; channel_name: string; is_primary: boolean }>
      >
    >,
    required: boolean,
  ) => (
    <div className="md:col-span-2">
      <p className="mb-2 text-sm font-semibold text-ink">
        {title}
        {required && <span className="ml-1 text-rose-600">*</span>}
      </p>
      <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
        {(Object.keys(PROACTIVE_CHANNEL_LABELS) as ProactiveChannelCode[]).map((code) => (
          <label key={code} className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-sm">
            <input
              type="checkbox"
              disabled={locked}
              checked={channels.some((channel) => channel.channel_code === code)}
              onChange={() => toggleChannel(setter, code)}
            />
            <span>{PROACTIVE_CHANNEL_LABELS[code]}</span>
          </label>
        ))}
      </div>
      {channels.some((channel) => channel.channel_code === 'OTHER') && (
        <Input
          className="mt-3"
          label="其他通路名稱"
          required={required}
          disabled={locked}
          value={channels.find((channel) => channel.channel_code === 'OTHER')?.channel_name ?? ''}
          onChange={(event) => {
            markDirty();
            setter((current) =>
              current.map((channel) =>
                channel.channel_code === 'OTHER'
                  ? { ...channel, channel_name: event.target.value }
                  : channel,
              ),
            );
          }}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-sand">
      <VendorPortalHeader>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>{proposal.proposal_no}</span>
          <ProactiveStatusBadge status={proposal.status} />
        </div>
      </VendorPortalHeader>
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-10">
        <p className="mb-3 text-sm text-slate-500">
          <span className="font-bold text-rose-600">*</span> 為必填欄位
        </p>
        <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <Link to="/vendor/proposal" className="text-sm font-bold text-teal-700">
              返回廠商入口
            </Link>
            <h1 className="mt-3 text-3xl font-extrabold text-ink">
              {product.name_zh || '新品提案填報'}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              提案編號：{proposal.proposal_no} 完成度：
              <strong>{proposal.completeness_score}%</strong>
            </p>
          </div>
          {!locked && (
            <div className="flex flex-col items-end gap-2">
              <span
                className={`text-xs font-semibold ${autosaveStatus === 'ERROR' ? 'text-rose-600' : 'text-slate-500'}`}
              >
                {autosaveStatus === 'DIRTY' && '尚未儲存'}
                {autosaveStatus === 'SAVING' && '儲存中…'}
                {autosaveStatus === 'SAVED' && '已自動儲存'}
                {autosaveStatus === 'ERROR' && '儲存失敗'}
                {lastAutosavedAt && ` · ${formatTaipeiDate(lastAutosavedAt, true)}`}
              </span>
              <Button variant="secondary" onClick={() => void save()} loading={saving}>
                儲存草稿
              </Button>
            </div>
          )}
        </div>
        {proposal.status === 'REVISION_REQUIRED' && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <p className="font-bold text-orange-900">採購補件／修改要求</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-orange-900">
              {proposal.vendor_visible_response || '請依採購人員要求補齊資料。'}
            </p>
          </Card>
        )}
        {proposal.invitation && (
          <Card className="mb-6 border-sky-200 bg-sky-50">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-bold text-sky-900">惠生邀請填報原始內容</p>
                <p className="mt-1 text-sm text-sky-800">
                  本提案來源：{proposal.invitation.name}
                  。以下邀請內容僅供查看，完整提案資料請於本頁填寫。
                </p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-sky-800">
                {proposal.invitation.category || '未指定分類'}
              </span>
            </div>
            <div className="mt-4 grid gap-3 text-sm text-sky-950 md:grid-cols-3">
              <p>
                填報期限：
                <DateText value={proposal.invitation.due_date} />
              </p>
              <p>
                預計上市：
                <DateText value={proposal.invitation.launch_date} />
              </p>
              <p>
                必填附件：
                {proposal.invitation.required_attachment_types.length
                  ? proposal.invitation.required_attachment_types
                      .map((type) => labelProactiveAttachmentType(type))
                      .join('、')
                  : '無指定'}
              </p>
            </div>
            {proposal.invitation.description && (
              <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-sky-950">
                邀請說明：{proposal.invitation.description}
              </p>
            )}
          </Card>
        )}
        {proposal.missing_items?.length ? (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <p className="font-bold text-amber-900">
              尚有 {proposal.missing_items.length} 項必填資料未完成
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {proposal.missing_items.map((item) => (
                <span
                  key={item}
                  className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-900"
                >
                  {item}
                </span>
              ))}
            </div>
          </Card>
        ) : null}
        {versions.length > 0 && (
          <Card className="mb-6">
            <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
              <div>
                <h2 className="text-lg font-extrabold text-ink">報價版本</h2>
                <p className="mt-1 text-sm text-slate-500">
                  正式送出及核准時保留快照，歷史版本不會被覆蓋。
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {versions.map((version) => (
                  <span
                    key={version.id}
                    className="rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-teal-800"
                  >
                    {version.version_label}・
                    {PROACTIVE_STATUS_LABELS[version.status_snapshot] ?? version.status_snapshot}
                  </span>
                ))}
              </div>
            </div>
          </Card>
        )}
        {canVendorRequestProposalChange(proposal.status) && (
          <form onSubmit={requestProposalChange}>
            <Card className="mb-6 border-amber-200 bg-amber-50">
              <h2 className="text-lg font-extrabold text-ink">提出新品資料／報價變更</h2>
              <p className="mt-2 text-sm leading-6 text-amber-900">
                此案件已送出或已核准，不能直接覆寫原始版本。請填寫變更後內容與原因，送交採購審核。
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <Input
                  label="商品中文名稱"
                  value={proposalChangeDraft.name_zh}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      name_zh: event.target.value,
                    }))
                  }
                />
                <Input
                  label="品牌"
                  value={proposalChangeDraft.brand}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({ ...current, brand: event.target.value }))
                  }
                />
                <Input
                  label="商品規格"
                  value={proposalChangeDraft.specification}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      specification: event.target.value,
                    }))
                  }
                />
                <Input
                  label="未稅進價"
                  type="number"
                  min="0"
                  value={proposalChangeDraft.ex_tax_price}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      ex_tax_price: event.target.value,
                    }))
                  }
                />
                <Input
                  label="含稅進價"
                  type="number"
                  min="0"
                  value={proposalChangeDraft.tax_included_price}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      tax_included_price: event.target.value,
                    }))
                  }
                />
                <Input
                  label="稅率"
                  type="number"
                  min="0"
                  value={proposalChangeDraft.tax_rate}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      tax_rate: event.target.value,
                    }))
                  }
                />
                <Input
                  label="建議售價"
                  type="number"
                  min="0"
                  value={proposalChangeDraft.suggested_retail_price}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      suggested_retail_price: event.target.value,
                    }))
                  }
                />
                <Input
                  className="md:col-span-2"
                  label="國際條碼（多組請用逗號分隔）"
                  value={proposalChangeDraft.barcodes}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      barcodes: event.target.value,
                    }))
                  }
                />
                <Textarea
                  className="md:col-span-3"
                  label="退貨／換貨條件"
                  value={proposalChangeDraft.return_terms}
                  onChange={(event) =>
                    setProposalChangeDraft((current) => ({
                      ...current,
                      return_terms: event.target.value,
                    }))
                  }
                />
                <Textarea
                  className="md:col-span-3"
                  label="修改原因 *"
                  required
                  value={proposalChangeReason}
                  onChange={(event) => setProposalChangeReason(event.target.value)}
                  hint="請說明變更原因，至少 5 個字。"
                />
              </div>
              <div className="mt-5 flex justify-end">
                <Button loading={requestingProposalChange}>送出變更申請</Button>
              </div>
            </Card>
          </form>
        )}
        <div className="space-y-6">
          <Card>
            <h2 className="mb-5 text-xl font-extrabold">一、商品基本資料</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="廠商商品編號"
                disabled={locked}
                value={productValue('vendor_product_code') ?? ''}
                onChange={(event) => update('vendor_product_code', event.target.value)}
              />
              <Input
                label="商品中文名稱"
                required
                disabled={locked}
                value={productValue('name_zh') ?? ''}
                onChange={(event) => update('name_zh', event.target.value)}
              />
              <Input
                label="商品英文名稱"
                disabled={locked}
                value={productValue('name_en') ?? ''}
                onChange={(event) => update('name_en', event.target.value)}
              />
              <Input
                label="品牌"
                required
                disabled={locked}
                value={productValue('brand') ?? ''}
                onChange={(event) => update('brand', event.target.value)}
              />
              <Input
                label="製造商"
                disabled={locked}
                value={productValue('manufacturer') ?? ''}
                onChange={(event) => update('manufacturer', event.target.value)}
              />
              <Input
                label="原產地"
                disabled={locked}
                value={productValue('origin') ?? ''}
                onChange={(event) => update('origin', event.target.value)}
              />
              <ProductCategorySelect
                categories={productCategories}
                value={product.product_category_code}
                currentName={product.product_category_name}
                disabled={locked}
                onChange={(code) => {
                  update('product_category_code', code);
                  update(
                    'product_category_name',
                    productCategories.find((category) => category.code === code)?.name ?? '',
                  );
                }}
              />
              <div className="rounded-xl bg-slate-50 p-3 text-sm">
                <p className="font-semibold text-ink">商品子分類</p>
                <p className="mt-2 text-slate-600">
                  {product.product_subcategory_code
                    ? product.product_subcategory_code + '｜' + product.product_subcategory_name
                    : '由採購審核時填寫'}
                </p>
              </div>
              <Input
                label="商品規格"
                required
                disabled={locked}
                value={productValue('specification') ?? ''}
                onChange={(event) => update('specification', event.target.value)}
              />
              <Input
                label="銷售單位"
                required
                disabled={locked}
                value={productValue('unit') ?? ''}
                onChange={(event) => update('unit', event.target.value)}
              />
              <Input
                label="箱入數"
                required
                type="number"
                min="0"
                disabled={locked}
                value={productValue('case_pack') ?? ''}
                onChange={(event) =>
                  update('case_pack', event.target.value ? Number(event.target.value) : null)
                }
              />
              <Select
                label="商品生命週期"
                required
                disabled={locked}
                value={product.product_lifecycle ?? product.lifecycle_type}
                onChange={(event) => updateLifecycle(event.target.value)}
              >
                <option value="">請選擇</option>
                {Object.entries(PROACTIVE_LIFECYCLE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
              {product.product_lifecycle === 'OTHER' && (
                <Input
                  label="其他生命週期說明"
                  required
                  disabled={locked}
                  value={product.product_lifecycle_other_note ?? ''}
                  onChange={(event) => update('product_lifecycle_other_note', event.target.value)}
                />
              )}
              <Input
                label="保存方式"
                disabled={locked}
                value={product.storage_method ?? ''}
                onChange={(event) => update('storage_method', event.target.value)}
              />
              <Input
                label="保存期限"
                required
                disabled={locked}
                value={product.shelf_life}
                onChange={(event) => update('shelf_life', event.target.value)}
              />
              <Input
                label="預計上市日期"
                required
                type="date"
                disabled={locked}
                value={product.expected_launch_date ?? ''}
                onChange={(event) => update('expected_launch_date', event.target.value || null)}
              />
              <label className="flex items-center gap-2 self-end rounded-xl bg-slate-50 p-3 text-sm font-semibold">
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={Boolean(product.launch_date_is_tentative)}
                  onChange={(event) =>
                    update('launch_date_is_tentative', event.target.checked ? 1 : 0)
                  }
                />
                上市日期為暫定
              </label>
              <Textarea
                className="md:col-span-3"
                label="商品特色"
                required
                disabled={locked}
                value={product.key_features}
                onChange={(event) => update('key_features', event.target.value)}
              />
              <Textarea
                label="成分"
                disabled={locked}
                value={product.ingredients}
                onChange={(event) => update('ingredients', event.target.value)}
              />
              <Textarea
                label="使用方式"
                disabled={locked}
                value={product.usage}
                onChange={(event) => update('usage', event.target.value)}
              />
              <Textarea
                label="注意事項"
                disabled={locked}
                value={product.precautions}
                onChange={(event) => update('precautions', event.target.value)}
              />

              <div className="md:col-span-3 rounded-2xl border border-teal-200/80 bg-teal-50/60 p-5 mt-3 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-white text-xs font-black">
                        證
                      </span>
                      認證／許可證
                      <span className="text-rose-500 font-bold">*</span>
                    </h3>
                    <p className="text-xs text-slate-600 mt-1">
                      請填報商品符合之主管機關許可證或認證字號，並上傳官方證明文件（支援 PDF / JPG /
                      PNG）。若依法無需許可證，請選擇該狀態並填寫說明。
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 mb-4">
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => update('certificate_status', 'HAS_CERTIFICATE')}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      product.certificate_status === 'HAS_CERTIFICATE'
                        ? 'border-teal-600 bg-white shadow-sm ring-2 ring-teal-600/20'
                        : 'border-slate-200 bg-white/90 hover:border-slate-300'
                    }`}
                  >
                    <span className="text-2xl mt-0.5">🛡️</span>
                    <div>
                      <div className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                        有認證／許可證
                        {product.certificate_status === 'HAS_CERTIFICATE' && (
                          <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[11px] font-bold text-teal-800">
                            已選擇
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 leading-normal">
                        藥品、醫療器材、健康食品、化妝品或相關檢驗認證
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => update('certificate_status', 'NOT_REQUIRED')}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      product.certificate_status === 'NOT_REQUIRED'
                        ? 'border-teal-600 bg-white shadow-sm ring-2 ring-teal-600/20'
                        : 'border-slate-200 bg-white/90 hover:border-slate-300'
                    }`}
                  >
                    <span className="text-2xl mt-0.5">📄</span>
                    <div>
                      <div className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                        依法無需認證／許可證
                        {product.certificate_status === 'NOT_REQUIRED' && (
                          <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[11px] font-bold text-teal-800">
                            已選擇
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 leading-normal">
                        一般生活日用品、日雜等非主管機關列管商品
                      </div>
                    </div>
                  </button>
                </div>

                {product.certificate_status === 'HAS_CERTIFICATE' && (
                  <div className="space-y-4 pt-3 border-t border-teal-200/60">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Select
                        label="認證／許可證類型"
                        required
                        disabled={locked}
                        value={product.certificate_type ?? ''}
                        onChange={(event) => update('certificate_type', event.target.value)}
                      >
                        <option value="">請選擇許可證類型</option>
                        {Object.entries(CERTIFICATE_TYPE_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </Select>

                      <Input
                        label="認證／許可證字號"
                        required
                        disabled={locked}
                        placeholder="例如：衛部醫器製壹字第001234號、衛部健食字第A00123號"
                        hint="允許填寫中文、英文、數字及符號；請勿填寫「無」、「N/A」、「待補」等假資料"
                        value={product.certificate_number ?? ''}
                        onChange={(event) => update('certificate_number', event.target.value)}
                      />
                    </div>

                    {product.certificate_type === 'OTHER' && (
                      <Input
                        label="其他認證／許可證說明"
                        required
                        disabled={locked}
                        placeholder="請說明所屬認證單位及許可證名稱"
                        value={product.certificate_other_description ?? ''}
                        onChange={(event) =>
                          update('certificate_other_description', event.target.value)
                        }
                      />
                    )}

                    <div className="rounded-xl bg-white p-4 border border-teal-200/70 shadow-2xs">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                            <span>📎 認證／許可證證明文件</span>
                            <span className="text-rose-500 font-bold">*</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            請上傳主管機關核准之許可證、登錄證明或認證合格證書（支援 PDF / JPG /
                            PNG，上限 10MB）
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          {certificateAttachment ? (
                            <div className="flex items-center gap-2">
                              <a
                                href={api.vendorProactive.downloadUrl(
                                  certificateAttachment.id,
                                  true,
                                )}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100"
                              >
                                👁️ 預覽文件
                              </a>
                              {!locked && (
                                <>
                                  <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100">
                                    更換
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        if (file) void handleCertificateUpload(file);
                                        event.currentTarget.value = '';
                                      }}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="px-2 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-lg"
                                    onClick={() => void deleteAttachment(certificateAttachment)}
                                  >
                                    刪除
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            !locked && (
                              <label className="cursor-pointer inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-teal-600 rounded-xl hover:bg-teal-700 shadow-xs transition-colors">
                                <span>📤 上傳許可證文件</span>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) void handleCertificateUpload(file);
                                    event.currentTarget.value = '';
                                  }}
                                />
                              </label>
                            )
                          )}
                        </div>
                      </div>

                      {certificateAttachment && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                          <span className="font-semibold text-teal-800">✓ 已附加檔案：</span>
                          <span className="truncate">{certificateAttachment.original_name}</span>
                          <span className="text-slate-400">
                            ({Math.ceil(certificateAttachment.size_bytes / 1024)} KB)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {product.certificate_status === 'NOT_REQUIRED' && (
                  <div className="pt-3 border-t border-teal-200/60">
                    <Textarea
                      label="依法無需認證／許可證說明原因"
                      required
                      disabled={locked}
                      placeholder="例如：一般生活用品，非屬藥事法或醫療器材管理法規範之列管商品，依法無需申請許可證或辦理登錄。"
                      hint="請簡要說明商品免列管法規或屬性依據"
                      value={product.certificate_not_required_reason ?? ''}
                      onChange={(event) =>
                        update('certificate_not_required_reason', event.target.value)
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="mb-5 text-xl font-extrabold">二、條碼</h2>
            <p className="mb-4 text-sm text-slate-500">
              國際條碼最多六組；條碼會驗證格式且不得重複。
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Input
                  key={index}
                  label={'國際條碼 ' + (index + 1)}
                  disabled={locked || noBarcode}
                  value={product.barcodes[index] ?? ''}
                  onChange={(event) => {
                    const next = [...product.barcodes];
                    next[index] = event.target.value;
                    update('barcodes', next);
                  }}
                />
              ))}
              <Input
                label="外箱條碼"
                disabled={locked || noBarcode}
                value={product.outer_barcode}
                onChange={(event) => update('outer_barcode', event.target.value)}
              />
              <Input
                label="內盒條碼"
                disabled={locked || noBarcode}
                value={product.inner_barcode}
                onChange={(event) => update('inner_barcode', event.target.value)}
              />
            </div>
            <label className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-sm font-semibold">
              <input
                type="checkbox"
                disabled={locked}
                checked={noBarcode}
                onChange={(event) => void handleNoBarcodeToggle(event.target.checked)}
              />
              本商品無國際條碼
            </label>
            {noBarcode && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Select
                  label="無國際條碼原因"
                  required
                  disabled={locked}
                  value={product.no_international_barcode_reason ?? ''}
                  onChange={(event) =>
                    update('no_international_barcode_reason', event.target.value)
                  }
                >
                  <option value="">請選擇</option>
                  <option value="NOT_APPLICABLE">商品不適用</option>
                  <option value="NOT_ASSIGNED">尚未編碼</option>
                  <option value="OTHER">其他</option>
                </Select>
                {product.no_international_barcode_reason === 'OTHER' && (
                  <Textarea
                    label="其他原因說明"
                    required
                    disabled={locked}
                    value={product.no_international_barcode_other_reason ?? ''}
                    onChange={(event) =>
                      update('no_international_barcode_other_reason', event.target.value)
                    }
                  />
                )}
              </div>
            )}
            {barcodeValues.length > 0 && noBarcode && (
              <p className="mt-3 text-sm text-rose-600">已勾選無國際條碼時，不得同時填寫條碼。</p>
            )}
          </Card>

          <Card>
            <h2 className="mb-5 text-xl font-extrabold">三、價格與交易條件</h2>
            <div className="grid gap-4 md:grid-cols-4">
              <Input
                label="未稅進價"
                required
                type="number"
                min="0"
                step="0.01"
                disabled={locked}
                value={product.ex_tax_price ?? ''}
                onChange={(event) =>
                  update('ex_tax_price', event.target.value ? Number(event.target.value) : null)
                }
              />
              <Input
                label="含稅進價"
                required
                type="number"
                min="0"
                step="0.01"
                disabled={locked}
                value={product.tax_included_price ?? ''}
                onChange={(event) =>
                  update(
                    'tax_included_price',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <Input
                label="稅率（％）"
                required
                type="number"
                min="0"
                max="100"
                step="0.1"
                disabled={locked}
                value={product.tax_rate}
                onChange={(event) => update('tax_rate', Number(event.target.value))}
              />
              <Input
                label="建議售價"
                required
                type="number"
                min="0"
                step="0.01"
                disabled={locked}
                value={product.suggested_retail_price ?? ''}
                onChange={(event) =>
                  update(
                    'suggested_retail_price',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <Input
                label="最低訂購量"
                required
                type="number"
                min="0"
                step="1"
                disabled={locked}
                value={product.minimum_order_quantity ?? ''}
                onChange={(event) =>
                  update(
                    'minimum_order_quantity',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <Input
                label="最低訂購金額"
                required
                type="number"
                min="0"
                step="0.01"
                disabled={locked}
                value={product.minimum_order_amount ?? ''}
                onChange={(event) =>
                  update(
                    'minimum_order_amount',
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              />
              <Input
                label="最小配送單位"
                required
                disabled={locked}
                value={product.minimum_delivery_unit}
                onChange={(event) => update('minimum_delivery_unit', event.target.value)}
              />
            </div>
            <div className="mt-5 rounded-2xl border border-teal-100 bg-teal-50 p-4">
              <p className="font-bold text-teal-900">廠商標準交易條件</p>
              <p className="mt-1 text-sm text-teal-800">
                以下交易條件已依目前廠商資料自動帶入；資料來源：
                {proposal.trade_terms_source?.source_type === 'VENDOR_APPLICATION'
                  ? '新廠商申請'
                  : '廠商主檔'}
                。
              </p>
              <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                <p>
                  付款條件：{displayProductTradeTerm(proposal.trade_terms_source?.payment_term)}
                </p>
                <p>月結天數：{proposal.trade_terms_source?.monthly_payment_days ?? '不適用'}</p>
                <p>
                  付款方式：{displayProductTradeTerm(proposal.trade_terms_source?.payment_method)}
                </p>
                <p>
                  退貨地點：{displayProductTradeTerm(proposal.trade_terms_source?.return_scope)}
                </p>
                <p>
                  退換貨方式：
                  {displayProductTradeTerm(proposal.trade_terms_source?.return_handling_method)}
                </p>
                <p>
                  退換貨條件：
                  {displayProductTradeTerm(proposal.trade_terms_source?.return_conditions)}
                </p>
                <p className="md:col-span-2">
                  退換貨說明：{proposal.trade_terms_source?.return_description || '尚未設定'}
                </p>
                <p>
                  退貨運費：
                  {displayProductTradeTerm(
                    proposal.trade_terms_source?.return_freight_responsibility,
                  )}
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Select
                label="本商品是否有特殊交易條件？"
                required
                disabled={locked}
                value={
                  normalizeSpecialTradeTermsFlag(tradeTerms?.special_trade_terms) ? 'YES' : 'NO'
                }
                onChange={(event) => {
                  const nextEnabled = event.target.value === 'YES';
                  if (!canChangeSpecialTradeTerms(tradeTerms, nextEnabled)) return;
                  updateTradeTerms('special_trade_terms', nextEnabled);
                }}
              >
                <option value="NO">否，沿用廠商交易條件</option>
                <option value="YES">是，本商品有特殊交易條件</option>
              </Select>
              {tradeTerms && normalizeSpecialTradeTermsFlag(tradeTerms.special_trade_terms) && (
                <Textarea
                  label="特殊條件原因"
                  required
                  disabled={locked}
                  value={tradeTerms.special_reason ?? ''}
                  onChange={(event) => updateTradeTerms('special_reason', event.target.value)}
                />
              )}
            </div>
            {tradeTerms && normalizeSpecialTradeTermsFlag(tradeTerms.special_trade_terms) && (
              <div className="mt-4 grid gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 md:grid-cols-2">
                <Input
                  label="特殊付款條件"
                  required
                  disabled={locked}
                  value={tradeTerms.special_payment_term ?? ''}
                  onChange={(event) => updateTradeTerms('special_payment_term', event.target.value)}
                />
                <Input
                  label="特殊月結天數"
                  type="number"
                  min="1"
                  disabled={locked}
                  value={tradeTerms.special_monthly_payment_days ?? ''}
                  onChange={(event) =>
                    updateTradeTerms(
                      'special_monthly_payment_days',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                <Input
                  label="特殊付款方式"
                  required
                  disabled={locked}
                  value={tradeTerms.special_payment_method ?? ''}
                  onChange={(event) =>
                    updateTradeTerms('special_payment_method', event.target.value)
                  }
                />
                <Input
                  label="特殊退換貨期限"
                  disabled={locked}
                  value={tradeTerms.special_return_deadline ?? ''}
                  onChange={(event) =>
                    updateTradeTerms('special_return_deadline', event.target.value)
                  }
                />
                <Textarea
                  label="特殊退換貨詳細說明"
                  required
                  disabled={locked}
                  value={tradeTerms.special_return_description ?? ''}
                  onChange={(event) =>
                    updateTradeTerms('special_return_description', event.target.value)
                  }
                />
                <Textarea
                  label="特殊退換貨條件（請以逗號分隔）"
                  required
                  disabled={locked}
                  value={(tradeTerms.special_return_conditions ?? []).join('、')}
                  onChange={(event) =>
                    updateTradeTerms(
                      'special_return_conditions',
                      event.target.value
                        .split(/[、,，]/)
                        .map((item) => item.trim())
                        .filter(Boolean),
                    )
                  }
                />
              </div>
            )}
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Select
                label="首批優惠"
                required
                disabled={locked}
                value={selectionValue(
                  product.initial_order_offer_enabled,
                  product.initial_order_offer,
                )}
                onChange={(event) =>
                  toggleSelection(
                    'initial_order_offer_enabled',
                    'initial_order_offer',
                    event.target.value,
                  )
                }
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.initial_order_offer_enabled === 1 && (
                <div>
                  <Textarea
                    label="首批優惠內容"
                    required
                    disabled={locked}
                    value={product.initial_order_offer_content ?? ''}
                    onChange={(event) => update('initial_order_offer_content', event.target.value)}
                  />
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    請填寫具體且可計算的優惠內容，內容需包含明確數量、金額、折扣、比例或買送條件。
                  </p>
                  {quantifiedContentWarnings.some((warning) =>
                    warning.startsWith('首批優惠內容：'),
                  ) && (
                    <p className="mt-2 rounded-lg bg-amber-100 p-2 text-xs leading-5 text-amber-900">
                      目前內容可能缺少明確量化資訊，請確認是否已填寫實際數量、金額、折扣或比例。
                    </p>
                  )}
                </div>
              )}
              <Select
                label="贈品及搭贈"
                required
                disabled={locked}
                value={selectionValue(
                  product.gift_bundle_support_enabled,
                  product.gift_bundle_support,
                )}
                onChange={(event) =>
                  toggleSelection(
                    'gift_bundle_support_enabled',
                    'gift_bundle_support',
                    event.target.value,
                  )
                }
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.gift_bundle_support_enabled === 1 && (
                <div>
                  <Textarea
                    label="贈品及搭贈內容"
                    required
                    disabled={locked}
                    value={product.gift_bundle_support_content ?? ''}
                    onChange={(event) => update('gift_bundle_support_content', event.target.value)}
                  />
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    請填寫具體搭贈方式及數量，例如：買 12 盒送 1 盒、試用包 × 10 包。
                  </p>
                  {quantifiedContentWarnings.some((warning) =>
                    warning.startsWith('贈品及搭贈內容：'),
                  ) && (
                    <p className="mt-2 rounded-lg bg-amber-100 p-2 text-xs leading-5 text-amber-900">
                      目前內容可能缺少明確量化資訊，請確認是否已填寫實際數量、金額、折扣或比例。
                    </p>
                  )}
                </div>
              )}
              <Select
                label="促銷支援"
                required
                disabled={locked}
                value={selectionValue(product.promotion_support_enabled, product.promotion_support)}
                onChange={(event) =>
                  toggleSelection(
                    'promotion_support_enabled',
                    'promotion_support',
                    event.target.value,
                  )
                }
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.promotion_support_enabled === 1 && (
                <>
                  <div>
                    <Textarea
                      label="促銷內容"
                      required
                      disabled={locked}
                      value={product.promotion_content ?? ''}
                      onChange={(event) => update('promotion_content', event.target.value)}
                    />
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      請填寫具體促銷方式及量化內容，需包含價格、折扣、數量、比例或買送條件。
                    </p>
                    {quantifiedContentWarnings.some((warning) =>
                      warning.startsWith('促銷內容：'),
                    ) && (
                      <p className="mt-2 rounded-lg bg-amber-100 p-2 text-xs leading-5 text-amber-900">
                        目前內容可能缺少明確量化資訊，請確認是否已填寫實際數量、金額、折扣或比例。
                      </p>
                    )}
                  </div>
                  <Input
                    label="促銷開始日期"
                    required
                    type="date"
                    disabled={locked}
                    value={product.promotion_start_date ?? ''}
                    onChange={(event) => update('promotion_start_date', event.target.value || null)}
                  />
                  <Input
                    label="促銷結束日期"
                    required
                    type="date"
                    disabled={locked}
                    value={product.promotion_end_date ?? ''}
                    onChange={(event) => update('promotion_end_date', event.target.value || null)}
                  />
                </>
              )}
              <Select
                label="試用／體驗支援"
                required
                disabled={locked}
                value={selectionValue(product.trial_support_enabled, product.sample_support)}
                onChange={(event) =>
                  toggleSelection('trial_support_enabled', 'sample_support', event.target.value)
                }
              >
                <option value="">請選擇</option>
                <option value="YES">有</option>
                <option value="NO">無</option>
              </Select>
              {product.trial_support_enabled === 1 && (
                <div>
                  <Textarea
                    label="試用／體驗支援內容"
                    required
                    disabled={locked}
                    value={product.trial_support_content ?? ''}
                    onChange={(event) => update('trial_support_content', event.target.value)}
                  />
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    請填寫實際提供的試用品、體驗品或活動支援數量，需包含明確數量或規模。
                  </p>
                  {quantifiedContentWarnings.some((warning) =>
                    warning.startsWith('試用／體驗支援內容：'),
                  ) && (
                    <p className="mt-2 rounded-lg bg-amber-100 p-2 text-xs leading-5 text-amber-900">
                      目前內容可能缺少明確量化資訊，請確認是否已填寫實際數量、金額、折扣或比例。
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>

          <Card>
            <h2 className="mb-5 text-xl font-extrabold">四、商品資訊與銷售規劃</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <Textarea
                label="商品特色補充"
                required
                disabled={locked}
                value={product.key_features}
                onChange={(event) => update('key_features', event.target.value)}
              />
              <Textarea
                label="適用客群"
                required
                disabled={locked}
                value={product.target_customers}
                onChange={(event) => update('target_customers', event.target.value)}
              />
              <Textarea
                label="與競品差異"
                required
                disabled={locked}
                value={product.competitor_difference}
                onChange={(event) => update('competitor_difference', event.target.value)}
              />
              <Textarea
                label="建議售價理由"
                required
                disabled={locked}
                value={product.suggested_price_rationale}
                onChange={(event) => update('suggested_price_rationale', event.target.value)}
              />
              <Textarea
                label="市場定位"
                required
                disabled={locked}
                value={product.market_positioning}
                onChange={(event) => update('market_positioning', event.target.value)}
              />
              <Textarea
                label="上市計畫"
                required
                disabled={locked}
                value={product.launch_plan}
                onChange={(event) => update('launch_plan', event.target.value)}
              />
              {channelSelector('目前銷售通路', currentChannels, setCurrentChannels, false)}
              {channelSelector('預計銷售通路', expectedChannels, setExpectedChannels, true)}
            </div>
          </Card>

          <Card>
            <h2 className="mb-5 text-xl font-extrabold">五、附件</h2>
            <p className="mb-4 text-sm text-slate-500">
              檔名只作為顯示用途；儲存鍵由系統產生。每個必填附件完成後才可正式提交。
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {effectiveAttachmentRequirements
                .filter((requirement) => requirement.type !== 'BARCODE_IMAGE')
                .map((requirement) => (
                  <label
                    key={requirement.id}
                    className="rounded-2xl border border-dashed border-slate-200 p-4"
                  >
                    <span className="block text-sm font-bold">
                      {labelProactiveAttachmentType(requirement.type, requirement.label)}
                      {requirement.required === 1 && (
                        <span className="ml-2 text-rose-600">*【必填】</span>
                      )}
                    </span>
                    <input
                      type="file"
                      className="mt-3 block w-full text-sm"
                      disabled={locked}
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void upload(requirement.type, file);
                      }}
                    />
                  </label>
                ))}
              {!noBarcode ? (
                <div className="rounded-2xl border border-dashed border-teal-200 bg-teal-50 p-4 md:col-span-2">
                  <p className="text-sm font-bold text-teal-950">
                    條碼圖片 <span className="ml-2 text-rose-600">*【必填】</span>
                  </p>
                  <p className="mt-2 text-xs leading-5 text-teal-900">
                    請上傳可清楚辨識商品國際條碼及條碼下方數字的圖片。支援
                    JPG、JPEG、PNG、WEBP，單檔大小上限依系統設定。
                  </p>
                  {internationalBarcodeValues.length ? (
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <Select
                        label="對應國際條碼"
                        required
                        disabled={locked}
                        value={selectedBarcodeImageTarget}
                        onChange={(event) => setBarcodeImageTarget(event.target.value)}
                      >
                        {internationalBarcodeValues.map((barcode) => (
                          <option key={barcode} value={barcode}>
                            {barcode}
                          </option>
                        ))}
                      </Select>
                      <label className="block text-sm font-semibold text-ink">
                        上傳條碼圖片
                        <input
                          type="file"
                          multiple
                          className="mt-2 block w-full text-sm"
                          disabled={locked}
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          onChange={(event) => {
                            const files = Array.from(event.target.files ?? []);
                            if (files.length && selectedBarcodeImageTarget)
                              void (async () => {
                                for (const file of files)
                                  await upload('BARCODE_IMAGE', file, selectedBarcodeImageTarget);
                              })();
                            event.currentTarget.value = '';
                          }}
                        />
                      </label>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm font-semibold text-rose-700">
                      請先填寫至少一組國際條碼，才能指定條碼圖片對應資料。
                    </p>
                  )}
                </div>
              ) : (
                <p className="rounded-xl bg-slate-100 p-4 text-sm text-slate-700 md:col-span-2">
                  本商品已申報無國際條碼，因此不要求上傳條碼圖片。
                </p>
              )}
            </div>
            {missingRequiredAttachments.length > 0 && (
              <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                尚缺附件：
                {missingRequiredAttachments
                  .map((item) => labelProactiveAttachmentType(item.type, item.label))
                  .join('、')}
              </p>
            )}
            <div className="mt-4 space-y-2">
              {proposal.attachments?.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex flex-col gap-3 rounded-xl bg-slate-50 p-3 text-sm md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-ink">
                      {labelProactiveAttachmentType(attachment.type)}
                      {attachment.barcode_value ? `（${attachment.barcode_value}）` : ''}
                    </p>
                    <p className="mt-1 break-words text-xs text-slate-500">
                      {attachment.original_name}（{Math.ceil(attachment.size_bytes / 1024)} KB）
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={api.vendorProactive.downloadUrl(attachment.id, true)}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg px-3 py-2 text-xs font-bold text-teal-700 hover:bg-teal-100"
                    >
                      預覽
                    </a>
                    <a
                      href={api.vendorProactive.downloadUrl(attachment.id)}
                      className="rounded-lg px-3 py-2 text-xs font-bold text-teal-700 hover:bg-teal-100"
                    >
                      下載
                    </a>
                    {!locked && (
                      <>
                        <label className="cursor-pointer rounded-lg px-3 py-2 text-xs font-bold text-teal-700 hover:bg-teal-100">
                          更換
                          <input
                            type="file"
                            className="hidden"
                            disabled={locked}
                            accept={
                              attachment.type === 'BARCODE_IMAGE'
                                ? '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp'
                                : '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx'
                            }
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file)
                                void upload(
                                  attachment.type,
                                  file,
                                  attachment.barcode_value ?? undefined,
                                );
                              event.currentTarget.value = '';
                            }}
                          />
                        </label>
                        <Button
                          type="button"
                          variant="danger"
                          className="min-h-9 px-3 py-1.5 text-xs"
                          onClick={() => void deleteAttachment(attachment)}
                        >
                          刪除
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {(quantifiedContentWarnings.length > 0 || !quantifiedContentConfirmed) && (
            <Card className="border-amber-200 bg-amber-50">
              <p className="font-bold text-amber-950">優惠與支援內容確認</p>
              <p className="mt-2 text-sm leading-6 text-amber-900">
                請提供具體量化數字，例如數量、金額、折扣、比例、買 X 送 Y、每店數量或活動總數量。
              </p>
              {quantifiedContentWarnings.length > 0 && (
                <div className="mt-3 space-y-1 text-sm text-amber-900">
                  {quantifiedContentWarnings.map((warning) => (
                    <p key={warning}>⚠ {warning}</p>
                  ))}
                </div>
              )}
              <label className="mt-4 flex items-start gap-2 text-sm font-semibold text-amber-950">
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={quantifiedContentConfirmed}
                  onChange={(event) => setQuantifiedContentConfirmed(event.target.checked)}
                />
                <span>
                  我確認以上優惠及支援內容已清楚載明實際提供條件與數量。{' '}
                  <span className="text-rose-600">*</span>
                </span>
              </label>
            </Card>
          )}

          {!locked && (
            <div className="flex flex-col items-end justify-end gap-3 border-t border-slate-200 pt-6 sm:flex-row">
              <Button variant="secondary" onClick={() => void save()} loading={saving}>
                儲存草稿
              </Button>
              <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileResetKey} />
              <Button
                onClick={() => void submit()}
                loading={submitting}
                disabled={missingRequiredAttachments.length > 0 || submitting}
              >
                正式提交新品提案
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function ProactiveInboxPage({ notify }: PageProps) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '');
  const [proposalSource, setProposalSource] = useState(
    () => searchParams.get('proposal_source') ?? '',
  );
  const [productCategoryCode, setProductCategoryCode] = useState('');
  const [productSubcategoryCode, setProductSubcategoryCode] = useState('');
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [productSubcategories, setProductSubcategories] = useState<ProductSubcategory[]>([]);
  const [query, setQuery] = useState('');
  const [proposals, setProposals] = useState<VendorProposal[]>([]);
  const [metrics, setMetrics] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [listResult, dashboardResult] = await Promise.allSettled([
        api.proactive.list({
          ...(status ? { status } : {}),
          ...(proposalSource ? { proposal_source: proposalSource } : {}),
          ...(productCategoryCode ? { product_category_code: productCategoryCode } : {}),
          ...(productSubcategoryCode ? { product_subcategory_code: productSubcategoryCode } : {}),
          ...(query ? { q: query } : {}),
          ...(includeDeleted ? { include_deleted: '1' } : {}),
        }),
        api.proactive.dashboard(),
      ]);
      if (listResult.status === 'fulfilled') setProposals(listResult.value.proposals);
      else {
        const message = errorMessage(listResult.reason);
        setProposals([]);
        setLoadError(message);
      }
      if (dashboardResult.status === 'fulfilled') setMetrics(dashboardResult.value.metrics);
      else setMetrics({});
      if (listResult.status === 'rejected') notify(errorMessage(listResult.reason), 'error');
      else if (dashboardResult.status === 'rejected')
        notify(`審核統計暫時無法載入：${errorMessage(dashboardResult.reason)}`, 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    api.productCategories
      .publicList()
      .then((result) => setProductCategories(result.categories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, []);
  useEffect(() => {
    setProductSubcategoryCode('');
    if (!productCategoryCode) {
      setProductSubcategories([]);
      return;
    }
    api.productSubcategories
      .publicList(productCategoryCode)
      .then((result) => setProductSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [productCategoryCode, notify]);
  const metricLabels: Record<string, string> = {
    weekly_new: '本週新增',
    waiting_buyer_review: '待採購審核',
    invitation_pending_review: '惠生邀請待審',
    revision: '補件中',
    overdue_7_days: '超過 7 天',
    waiting_meeting: '待會議',
    product_master_pending: '待建立商品主檔',
    monthly_approved: '本月決議上市',
    monthly_not_adopted: '本月不採用',
  };
  return (
    <>
      <PageHeader
        eyebrow="PROPOSAL REVIEW"
        title="新品提案採購審核"
        description="集中處理惠生邀請與廠商主動提案；廠商送出邀請商品後會自動出現在待採購審核。"
        actions={
          <>
            <Link to="/proactive-reports">
              <Button variant="secondary">新品提案報表</Button>
            </Link>
            <Button onClick={load}>重新整理</Button>
          </>
        }
      />
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {Object.entries(metricLabels).map(([key, label]) => (
          <StatCard
            key={key}
            label={label}
            value={metrics[key] ?? 0}
            tone={
              key.includes('revision') || key.includes('overdue')
                ? 'amber'
                : key.includes('approved')
                  ? 'teal'
                  : undefined
            }
          />
        ))}
      </div>
      <Card className="no-print mb-6">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_190px_190px_190px_220px_auto]">
          <Input
            label="搜尋提案／商品／品牌／分類"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Select label="狀態" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部狀態</option>
            {PROACTIVE_ACTIVE_STATUS_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {PROACTIVE_STATUS_LABELS[key]}
              </option>
            ))}
          </Select>
          <Select
            label="提案來源"
            value={proposalSource}
            onChange={(event) => setProposalSource(event.target.value)}
          >
            <option value="">全部來源</option>
            <option value="BUYER_INVITATION">惠生邀請</option>
            <option value="VENDOR_PROACTIVE">廠商主動提案</option>
          </Select>
          <Select
            label="商品分類"
            value={productCategoryCode}
            onChange={(event) => setProductCategoryCode(event.target.value)}
          >
            <option value="">全部商品分類</option>
            {productCategories.map((category) => (
              <option key={category.code} value={category.code}>
                {formatProductCategory(category.code, category.name)}
              </option>
            ))}
          </Select>
          <Select
            label="商品子分類"
            value={productSubcategoryCode}
            onChange={(event) => setProductSubcategoryCode(event.target.value)}
            disabled={!productCategoryCode}
          >
            <option value="">全部商品子分類</option>
            {productSubcategories.map((subcategory) => (
              <option key={subcategory.code} value={subcategory.code}>
                {subcategory.code}｜{subcategory.name}
              </option>
            ))}
          </Select>
          <div className="flex items-end">
            <Button className="w-full" onClick={load} loading={loading}>
              套用篩選
            </Button>
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(event) => setIncludeDeleted(event.target.checked)}
            />
            顯示已刪除提案
          </label>
        </div>
      </Card>
      <Card>
        {loading ? (
          <Loading />
        ) : loadError ? (
          <EmptyState
            title="新品提案列表暫時無法載入"
            description={loadError}
            action={<Button onClick={load}>重新載入</Button>}
          />
        ) : proposals.length === 0 ? (
          <EmptyState
            title="目前沒有符合條件的新品提案"
            description="廠商送出惠生邀請商品或主動提案後，案件會出現在這裡。"
          />
        ) : (
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">提案／提交日期</th>
                <th className="px-4 py-3">廠商</th>
                <th className="px-4 py-3">商品／品牌</th>
                <th className="px-4 py-3">分類／來源</th>
                <th className="px-4 py-3">價格／毛利</th>
                <th className="px-4 py-3">完整度／警示</th>
                <th className="px-4 py-3">負責採購</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {proposals.map((proposal) => (
                <tr key={proposal.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-4">
                    <Link
                      to={`/proactive/${proposal.id}${proposal.is_deleted ? '?include_deleted=1' : ''}`}
                      className="font-bold text-teal-700 hover:underline"
                    >
                      {proposal.proposal_no}
                    </Link>
                    <p className="mt-1 text-xs text-slate-500">
                      提交：
                      {proposal.submitted_at ? (
                        <DateText value={proposal.submitted_at} withTime />
                      ) : (
                        '尚未提交'
                      )}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      更新：
                      <DateText value={proposal.updated_at} withTime />
                    </p>
                  </td>
                  <td className="max-w-[180px] whitespace-normal break-words px-4 py-4">
                    <p className="font-bold">{proposal.vendor_name}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {proposal.tax_id}
                      {proposal.vendor_application_id ? ' · 新廠商申請' : ''}
                    </p>
                  </td>
                  <td className="max-w-[240px] whitespace-normal break-words px-4 py-4">
                    <p className="font-bold">{proposal.product?.name_zh || '未填商品名稱'}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      品牌：{proposal.product?.brand || '—'}
                    </p>
                    {proposal.product?.certificate_status === 'HAS_CERTIFICATE' ? (
                      <span className="inline-block mt-1 rounded bg-teal-50 px-1.5 py-0.5 text-[11px] font-bold text-teal-800 border border-teal-100">
                        ✓ 已提供認證
                      </span>
                    ) : proposal.product?.certificate_status === 'NOT_REQUIRED' ? (
                      <span className="inline-block mt-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-700">
                        免認證
                      </span>
                    ) : (
                      <span className="inline-block mt-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold text-amber-800">
                        待補認證
                      </span>
                    )}
                  </td>
                  <td className="max-w-[220px] whitespace-normal break-words px-4 py-4">
                    <p className="font-bold">{productCategoryDisplay(proposal.product)}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      子分類：
                      {proposal.product?.product_subcategory_code
                        ? `${proposal.product.product_subcategory_code}｜${proposal.product.product_subcategory_name}`
                        : '未設定'}
                    </p>
                    <p className="mt-2 text-xs font-bold text-sky-700">
                      {FORMAL_SUBMISSION_SOURCE_LABELS[proposal.proposal_source ?? ''] ??
                        '廠商主動提案'}
                    </p>
                    {proposal.quotation_round_name ? (
                      <p className="mt-1 text-xs text-slate-500">
                        活動：{proposal.quotation_round_name}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-4">
                    <Money value={proposal.product?.ex_tax_price} />
                    <p className="mt-1 text-xs font-bold text-teal-700">
                      毛利{' '}
                      {proposal.product?.estimated_margin_rate == null
                        ? '—'
                        : `${(proposal.product.estimated_margin_rate * 100).toFixed(1)}%`}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-bold text-teal-700">{proposal.completeness_score}%</p>
                    {proposal.duplicate_proposal_flag ? (
                      <p className="mt-1 text-xs font-bold text-orange-700">疑似重複提案</p>
                    ) : null}
                    {proposal.duplicate_barcode_flag ? (
                      <p className="mt-1 text-xs font-bold text-orange-700">條碼可能重複</p>
                    ) : null}
                    {proposal.missing_items?.length ? (
                      <p className="mt-1 text-xs text-rose-700">
                        缺 {proposal.missing_items.length} 項
                      </p>
                    ) : null}
                  </td>
                  <td className="max-w-[150px] whitespace-normal break-words px-4 py-4 text-sm">
                    {proposal.assigned_buyer_name || '尚未指派'}
                  </td>
                  <td className="px-4 py-4">
                    <ProactiveStatusBadge status={proposal.status} />
                    {proposal.is_deleted ? (
                      <p className="mt-2 text-xs font-bold text-rose-700">
                        已刪除：
                        {proposal.deleted_at ? (
                          <DateText value={proposal.deleted_at} withTime />
                        ) : null}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Link
                      to={`/proactive/${proposal.id}${proposal.is_deleted ? '?include_deleted=1' : ''}`}
                    >
                      <Button variant="secondary">
                        {proposal.is_deleted ? '查看紀錄' : '開啟審核'}
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

export function ProactiveReviewPage({ notify }: PageProps) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const includeDeleted = searchParams.get('include_deleted') === '1';
  const [proposal, setProposal] = useState<VendorProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classificationSaving, setClassificationSaving] = useState(false);
  const [revisionItems, setRevisionItems] = useState<string[]>(['PRODUCT_BASIC']);
  const [revisionExplanation, setRevisionExplanation] = useState('');
  const [revisionDueAt, setRevisionDueAt] = useState('');
  const [rejectionCode, setRejectionCode] = useState('DOCUMENT_INCOMPLETE');
  const [vendorReason, setVendorReason] = useState('');
  const [pauseReason, setPauseReason] = useState('');
  const [reconsiderationDate, setReconsiderationDate] = useState('');
  const [masterSaving, setMasterSaving] = useState(false);
  const [masterForm, setMasterForm] = useState({
    large_code: '',
    middle_code: '',
    small_code: '',
    vip_price: '',
    employee_price: '',
    in_store_promotion: '',
  });
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [productSubcategories, setProductSubcategories] = useState<ProductSubcategory[]>([]);
  const [reviewCategoryCode, setReviewCategoryCode] = useState('');
  const [reviewSubcategoryCode, setReviewSubcategoryCode] = useState('');
  const [tradeTermsReason, setTradeTermsReason] = useState('');
  const [tradeTermsSaving, setTradeTermsSaving] = useState(false);
  const load = async () => {
    try {
      const result = await api.proactive.detail(id, includeDeleted);
      setProposal(result);
      setReviewCategoryCode(result.product?.product_category_code ?? '');
      setReviewSubcategoryCode(result.product?.product_subcategory_code ?? '');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    api.productCategories
      .publicList()
      .then((result) => setProductCategories(result.categories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [id, includeDeleted]);
  useEffect(() => {
    if (!reviewCategoryCode) {
      setProductSubcategories([]);
      return;
    }
    api.productSubcategories
      .list({ category_code: reviewCategoryCode, include_inactive: '1' })
      .then((result) => setProductSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [reviewCategoryCode, notify]);
  const reload = async () => {
    const result = await api.proactive.detail(id, includeDeleted);
    setProposal(result);
  };
  const saveClassification = async () => {
    if (proposal?.status === 'WAITING_VENDOR_APPROVAL') {
      notify('新廠商資格尚未核准，暫不能修改商品分類。', 'error');
      return;
    }
    if (!reviewCategoryCode) {
      notify('請選擇商品分類。', 'error');
      return;
    }
    setClassificationSaving(true);
    try {
      const result = await api.proactive.updateClassification(id, {
        product_category_code: reviewCategoryCode,
        product_subcategory_code: reviewSubcategoryCode,
      });
      setProposal(result);
      notify('商品分類與子分類已儲存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setClassificationSaving(false);
    }
  };
  const reviewSpecialTradeTerms = async (decision: 'ACCEPT' | 'REJECT' | 'REQUEST_REVISION') => {
    if (proposal?.status === 'WAITING_VENDOR_APPROVAL') {
      notify('新廠商資格尚未核准，暫不能審核特殊交易條件。', 'error');
      return;
    }
    if (decision !== 'ACCEPT' && !tradeTermsReason.trim()) {
      notify('拒絕或要求補件時必須填寫原因。', 'error');
      return;
    }
    setTradeTermsSaving(true);
    try {
      const result = await api.proactive.reviewTradeTerms(id, {
        decision,
        reason: tradeTermsReason,
      });
      setProposal(result);
      setTradeTermsReason('');
      notify('特殊交易條件審核已更新。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setTradeTermsSaving(false);
    }
  };
  const requestRevision = async () => {
    if (proposal?.status === 'WAITING_VENDOR_APPROVAL') {
      notify('新廠商資格尚未核准，暫不能要求商品提報補件。', 'error');
      return;
    }
    if (!revisionExplanation.trim()) {
      notify('請填寫補件說明。', 'error');
      return;
    }
    if (!revisionItems.length) {
      notify('請至少選擇一項補件項目。', 'error');
      return;
    }
    if (!revisionDueAt) {
      notify('請選擇補件期限。', 'error');
      return;
    }
    const today = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Taipei',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    if (revisionDueAt < today) {
      notify('補件期限不得早於目前日期。', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.proactive.requestRevision(id, {
        item_codes: revisionItems,
        explanation: revisionExplanation,
        due_at: revisionDueAt,
      });
      await reload();
      setRevisionExplanation('');
      notify('補件要求已送出。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const changeStatus = async (status: ProactiveProposalStatus) => {
    if (proposal?.status === 'WAITING_VENDOR_APPROVAL') {
      notify('新廠商資格尚未核准，暫不能變更商品提報狀態。', 'error');
      return;
    }
    if (status === 'PAUSED' && !pauseReason.trim()) {
      notify('請填寫暫緩原因。', 'error');
      return;
    }
    if (status === 'NOT_ADOPTED' && !vendorReason.trim()) {
      notify('請填寫廠商可見的不採用說明。', 'error');
      return;
    }
    if (
      status === 'APPROVED' &&
      !window.confirm('確定將此新品提案決議上市嗎？系統將建立待建立正式商品主檔工作項目。')
    )
      return;
    setSaving(true);
    try {
      const result = await api.proactive.status(id, {
        status,
        reason:
          status === 'PAUSED'
            ? pauseReason
            : status === 'APPROVED'
              ? '採購決議上市。'
              : status === 'WAITING_MEETING'
                ? '送入會議審議。'
                : vendorReason,
        reconsideration_date: status === 'PAUSED' ? reconsiderationDate || null : null,
        rejection_reason_code: status === 'NOT_ADOPTED' ? rejectionCode : undefined,
        internal_rejection_reason: status === 'NOT_ADOPTED' ? `固定原因：${rejectionCode}` : '',
        vendor_visible_reason: status === 'NOT_ADOPTED' ? vendorReason : '',
      });
      setProposal(result);
      notify(status === 'APPROVED' ? '新品提案已決議上市。' : '新品提案狀態已更新。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  const createProductMaster = async () => {
    if (
      !masterForm.large_code.trim() ||
      !masterForm.middle_code.trim() ||
      !masterForm.small_code.trim() ||
      masterForm.vip_price === ''
    ) {
      notify('請填寫商品大碼、商品中碼、商品小碼及 VIP 價。', 'error');
      return;
    }
    setMasterSaving(true);
    try {
      const result = await api.proactive.createProductMaster(id, {
        large_code: masterForm.large_code,
        middle_code: masterForm.middle_code,
        small_code: masterForm.small_code,
        vip_price: Number(masterForm.vip_price),
        employee_price: masterForm.employee_price === '' ? null : Number(masterForm.employee_price),
        in_store_promotion: masterForm.in_store_promotion,
      });
      setProposal(result);
      notify('正式商品主檔已建立。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setMasterSaving(false);
    }
  };
  const toggleRevision = (code: string) =>
    setRevisionItems((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    );
  if (loading) return <Loading />;
  if (!proposal)
    return (
      <EmptyState
        title="找不到主動提案"
        action={
          <Link to="/proactive">
            <Button>返回收件匣</Button>
          </Link>
        }
      />
    );
  const product = proposal.product;
  const tradeTerms =
    product?.trade_terms ?? product?.trade_terms_draft ?? proposal.trade_terms_source ?? null;
  const deliveryTerms = proposal.trade_terms_source;
  const vendorApprovalPending = proposal.status === 'WAITING_VENDOR_APPROVAL';
  const classificationChanged = Boolean(
    product &&
    (product.product_category_code !== reviewCategoryCode ||
      product.product_subcategory_code !== reviewSubcategoryCode),
  );
  const todayTaipei = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const missingRequiredAttachments = (proposal.attachment_requirements ?? []).filter(
    (requirement) =>
      requirement.required === 1 &&
      !(requirement.type === 'BARCODE_IMAGE' && Boolean(product?.no_international_barcode)) &&
      !proposal.attachments?.some(
        (attachment) => !attachment.deleted_at && attachment.type === requirement.type,
      ),
  );
  const effectiveReviewAttachmentRequirements = (proposal.attachment_requirements ?? []).filter(
    (requirement) =>
      !(requirement.type === 'BARCODE_IMAGE' && Boolean(product?.no_international_barcode)),
  );
  const barcodeImageAttachments = (proposal.attachments ?? []).filter(
    (attachment) => !attachment.deleted_at && attachment.type === 'BARCODE_IMAGE',
  );
  return (
    <>
      <PageHeader
        eyebrow="PROPOSAL REVIEW"
        title={`新品提案採購審核 · ${proposal.proposal_no}`}
        description="完整檢查商品、價格、交易條件、附件與邀請來源；內部意見不會顯示給廠商。"
        actions={
          <Button variant="secondary" onClick={() => navigate('/proactive')}>
            返回收件匣
          </Button>
        }
      />
      <div className="space-y-6">
        <Card>
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
            <div>
              <p className="text-xs font-bold text-teal-700">
                {proposal.vendor_name} · {proposal.tax_id}
              </p>
              <h2 className="mt-1 text-2xl font-extrabold">{product?.name_zh || '未填商品名稱'}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {product?.brand || '—'} · {productCategoryDisplay(product)} · 建立於{' '}
                <DateText value={proposal.created_at} withTime />
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <ProactiveStatusBadge status={proposal.status} />
              {proposal.duplicate_proposal_flag ? (
                <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800">
                  疑似重複提案
                </span>
              ) : null}
              {proposal.duplicate_barcode_flag ? (
                <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800">
                  條碼可能已存在
                </span>
              ) : null}
            </div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-4">
            <Info label="完整度" value={`${proposal.completeness_score}%`} />
            <Info
              label="提案來源"
              value={
                FORMAL_SUBMISSION_SOURCE_LABELS[proposal.proposal_source ?? ''] ?? '廠商主動提案'
              }
            />
            <Info label="負責採購" value={proposal.assigned_buyer_name || '尚未指派'} />
            <Info
              label="提交日期"
              value={
                proposal.submitted_at ? (
                  <DateText value={proposal.submitted_at} withTime />
                ) : (
                  '尚未提交'
                )
              }
            />
            <Info label="未稅進價" value={<Money value={product?.ex_tax_price} />} />
            <Info label="建議售價" value={<Money value={product?.suggested_retail_price} />} />
            <Info
              label="預估毛利率"
              value={
                product?.estimated_margin_rate == null
                  ? '—'
                  : `${(product.estimated_margin_rate * 100).toFixed(2)}%`
              }
            />
          </div>
          {proposal.missing_items?.length ? (
            <div className="mt-5 rounded-2xl border border-orange-200 bg-orange-50 p-4">
              <p className="font-bold text-orange-900">完整度檢查提示</p>
              <p className="mt-2 text-sm leading-6 text-orange-900">
                {proposal.missing_items.join('、')}
              </p>
            </div>
          ) : null}
        </Card>
        {vendorApprovalPending ? (
          <Card className="border-amber-200 bg-amber-50">
            <p className="font-extrabold text-amber-950">等待廠商資格核准</p>
            <p className="mt-2 text-sm leading-6 text-amber-900">
              廠商已完成商品提報，但新廠商申請尚未核准。資料、條碼、附件與交易條件 Snapshot
              已保留；核准後系統會沿用同一筆提案自動進入待採購審核。
            </p>
          </Card>
        ) : null}
        {proposal.proposal_source === 'BUYER_INVITATION' && proposal.invitation ? (
          <Card className="border-sky-200 bg-sky-50">
            <h3 className="text-lg font-extrabold text-sky-950">惠生邀請原始內容</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Info label="報價活動" value={proposal.invitation.name} />
              <Info
                label="指定分類"
                value={
                  proposal.invitation.product_category_code
                    ? formatProductCategory(
                        proposal.invitation.product_category_code,
                        proposal.invitation.product_category_name || proposal.invitation.category,
                      )
                    : proposal.invitation.category || '未指定'
                }
              />
              <Info label="開始日期" value={<DateText value={proposal.invitation.start_date} />} />
              <Info label="截止日期" value={<DateText value={proposal.invitation.due_date} />} />
            </div>
            <div className="mt-4 rounded-xl bg-white/80 p-4 text-sm leading-6 text-sky-950">
              <p className="font-bold">邀請說明</p>
              <p className="mt-1 whitespace-pre-wrap">
                {proposal.invitation.description || '未填寫說明'}
              </p>
              <p className="mt-3 text-xs text-sky-800">
                必填附件：
                {proposal.invitation.required_attachment_types.length
                  ? proposal.invitation.required_attachment_types
                      .map((type) => labelProactiveAttachmentType(type))
                      .join('、')
                  : '依商品分類規則'}
              </p>
            </div>
          </Card>
        ) : null}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <h3 className="mb-4 text-lg font-extrabold">商品與銷售資料</h3>
            <div className="mb-5 grid gap-4 rounded-2xl border border-teal-100 bg-teal-50 p-4 md:grid-cols-2">
              <ProductCategorySelect
                categories={productCategories}
                value={reviewCategoryCode}
                currentName={product?.product_category_name ?? ''}
                onChange={(value) => {
                  setReviewCategoryCode(value);
                  setReviewSubcategoryCode('');
                  notify('商品分類已變更，請重新選擇商品子分類。', 'info');
                }}
              />
              <ProductSubcategorySelect
                subcategories={productSubcategories}
                value={reviewSubcategoryCode}
                currentName={product?.product_subcategory_name ?? ''}
                disabled={!reviewCategoryCode}
                onChange={setReviewSubcategoryCode}
              />
              {!reviewSubcategoryCode && reviewCategoryCode ? (
                <p className="text-sm font-semibold text-rose-700 md:col-span-2">
                  商品分類已變更時，請重新選擇商品子分類。
                </p>
              ) : null}
              {classificationChanged ? (
                <div className="flex items-center justify-between gap-3 md:col-span-2">
                  <p className="text-sm text-teal-900">
                    系統會自動記錄原值、新值、修改人與修改時間，不需要填寫修改原因。
                  </p>
                  <Button onClick={saveClassification} loading={classificationSaving}>
                    儲存分類
                  </Button>
                </div>
              ) : null}
            </div>
            {product ? (
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <h4 className="font-extrabold text-teal-900 sm:col-span-2">商品基本資料</h4>
                <Info label="商品中文名稱" value={product.name_zh} />
                <Info label="商品英文名稱" value={product.name_en} />
                <Info label="品牌" value={product.brand} />
                <Info label="製造商" value={product.manufacturer} />
                <Info label="產地" value={product.origin} />
                <Info label="商品分類" value={productCategoryDisplay(product)} />
                <Info
                  label="商品子分類"
                  value={
                    product.product_subcategory_code
                      ? `${product.product_subcategory_code}｜${product.product_subcategory_name}`
                      : '尚未指定'
                  }
                />
                <Info label="商品規格" value={product.specification} />
                <Info label="銷售單位" value={product.unit} />
                <Info label="箱入數" value={product.case_pack ?? '—'} />
                <Info
                  label="商品生命週期"
                  value={
                    product.product_lifecycle ||
                    PROACTIVE_LIFECYCLE_LABELS[product.lifecycle_type] ||
                    product.lifecycle_type
                  }
                />
                <Info label="廠商商品編號" value={product.vendor_product_code} />
                <Info label="保存方式" value={product.storage_method || product.storage} />
                <Info label="保存期限" value={product.shelf_life} />
                <Info
                  label="預計上市日期"
                  value={`${product.expected_launch_date || product.planned_launch_date || '—'}${
                    product.launch_date_is_tentative ? '（暫定）' : ''
                  }`}
                />
                <Info label="成分" value={product.ingredients} />
                <Info label="使用方式" value={product.usage} />
                <Info label="注意事項" value={product.precautions} />

                <h4 className="mt-3 font-extrabold text-teal-900 sm:col-span-2 flex items-center justify-between">
                  <span>認證／許可證資料</span>
                  {product.certificate_status === 'HAS_CERTIFICATE' ? (
                    <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-bold text-teal-800 border border-teal-200">
                      ✓ 有認證／許可證
                    </span>
                  ) : product.certificate_status === 'NOT_REQUIRED' ? (
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-700 border border-slate-200">
                      依法無需認證／許可證
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-800 border border-amber-200">
                      未填寫
                    </span>
                  )}
                </h4>

                {product.certificate_status === 'HAS_CERTIFICATE' ? (
                  <>
                    <Info
                      label="許可證類型"
                      value={
                        labelCertificateType(product.certificate_type) +
                        (product.certificate_type === 'OTHER' &&
                        product.certificate_other_description
                          ? `（${product.certificate_other_description}）`
                          : '')
                      }
                    />
                    <Info label="認證／許可證字號" value={product.certificate_number || '—'} />
                    <div className="rounded-xl bg-teal-50/70 border border-teal-200/80 p-3 sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold text-teal-900">認證／許可證證明文件</p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          {product.certificate_attachment_name ||
                            proposal?.attachments?.find(
                              (att) =>
                                att.id === product.certificate_attachment_id ||
                                ['LICENSE', 'CERTIFICATION'].includes(att.type),
                            )?.original_name ||
                            '已附加證明文件'}
                        </p>
                      </div>
                      {(product.certificate_attachment_id ||
                        proposal?.attachments?.some((att) =>
                          ['LICENSE', 'CERTIFICATION'].includes(att.type),
                        )) && (
                        <div className="flex items-center gap-2">
                          <a
                            href={api.vendorProactive.downloadUrl(
                              product.certificate_attachment_id ||
                                proposal.attachments?.find((att) =>
                                  ['LICENSE', 'CERTIFICATION'].includes(att.type),
                                )?.id ||
                                '',
                              true,
                            )}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-lg bg-teal-600 text-white px-3 py-1.5 text-xs font-bold hover:bg-teal-700 shadow-2xs"
                          >
                            👁️ 查看附件
                          </a>
                          <a
                            href={api.vendorProactive.downloadUrl(
                              product.certificate_attachment_id ||
                                proposal.attachments?.find((att) =>
                                  ['LICENSE', 'CERTIFICATION'].includes(att.type),
                                )?.id ||
                                '',
                            )}
                            className="rounded-lg bg-white border border-teal-300 text-teal-800 px-3 py-1.5 text-xs font-bold hover:bg-teal-50 shadow-2xs"
                          >
                            下載
                          </a>
                        </div>
                      )}
                    </div>
                  </>
                ) : product.certificate_status === 'NOT_REQUIRED' ? (
                  <div className="sm:col-span-2">
                    <Info
                      label="依法無需認證說明"
                      value={product.certificate_not_required_reason || '—'}
                    />
                  </div>
                ) : null}

                <h4 className="mt-3 font-extrabold text-teal-900 sm:col-span-2">條碼資料</h4>
                {product.no_international_barcode ? (
                  <Info
                    label="國際條碼"
                    value={`本商品無國際條碼\n原因：${
                      product.no_international_barcode_reason || '未填寫'
                    }${
                      product.no_international_barcode_other_reason
                        ? `\n補充：${product.no_international_barcode_other_reason}`
                        : ''
                    }`}
                  />
                ) : (
                  Array.from({ length: 6 }, (_, index) => (
                    <Info
                      key={`barcode-${index + 1}`}
                      label={`國際條碼 ${index + 1}`}
                      value={product.barcodes[index] || '—'}
                    />
                  ))
                )}
                {!product.no_international_barcode ? (
                  <>
                    <Info label="外箱條碼" value={product.outer_barcode} />
                    <Info label="內盒條碼" value={product.inner_barcode} />
                  </>
                ) : null}

                <h4 className="mt-3 font-extrabold text-teal-900 sm:col-span-2">銷售與上市資料</h4>
                <Info
                  label="商品主要特色"
                  value={product.key_features || product.product_features}
                />
                <Info label="商品市場定位" value={product.market_positioning} />
                <Info label="主要消費客群" value={product.target_customers} />
                <Info label="與競品差異" value={product.competitor_difference} />
                <Info label="建議售價理由" value={product.suggested_price_rationale} />
                <Info label="市場銷售實績" value={product.market_sales} />
                <Info label="上市推廣計畫" value={product.launch_plan} />
                <Info
                  label="商品編號／規格"
                  value={`${product.vendor_product_code || '—'}／${product.specification || '—'}`}
                />
                <Info
                  label="產地／單位"
                  value={`${product.origin || '—'}／${product.unit || '—'}`}
                />
                <Info label="商品分類" value={productCategoryDisplay(product)} />
                <Info
                  label="商品子分類"
                  value={
                    product.product_subcategory_code
                      ? `${product.product_subcategory_code}｜${product.product_subcategory_name}`
                      : '尚未指定'
                  }
                />
                <Info
                  label="條碼"
                  value={
                    [...product.barcodes, product.outer_barcode, product.inner_barcode]
                      .filter(Boolean)
                      .join('、') || '—'
                  }
                />
                <Info
                  label="商品生命週期"
                  value={
                    product.product_lifecycle ||
                    PROACTIVE_LIFECYCLE_LABELS[product.lifecycle_type] ||
                    product.lifecycle_type ||
                    '—'
                  }
                />
                <Info
                  label="保存方式／保存期限"
                  value={`${product.storage_method || product.storage || '—'}／${product.shelf_life || '—'}`}
                />
                <Info
                  label="預計上市日期"
                  value={`${product.expected_launch_date || product.planned_launch_date || '—'}${product.launch_date_is_tentative ? '（暫定）' : ''}`}
                />
                <Info
                  label="國際條碼聲明"
                  value={
                    product.no_international_barcode
                      ? `無（${product.no_international_barcode_reason || '未填原因'}${product.no_international_barcode_other_reason ? `：${product.no_international_barcode_other_reason}` : ''}）`
                      : [...product.barcodes, product.outer_barcode, product.inner_barcode]
                          .filter(Boolean)
                          .join('、') || '—'
                  }
                />
                <Info
                  label="最低訂購量／金額"
                  value={`${product.minimum_order_quantity ?? '—'}／${product.minimum_order_amount == null ? '—' : product.minimum_order_amount}`}
                />
                <Info
                  label="廠商交易條件"
                  value={`${displayProductTradeTerm(proposal.trade_terms_source?.payment_term)}／${displayProductTradeTerm(proposal.trade_terms_source?.payment_method)}`}
                />
                <Info
                  label="退貨條件"
                  value={displayProductTradeTerm(proposal.trade_terms_source?.return_conditions)}
                />
                <Info
                  label="特殊交易條件"
                  value={
                    normalizeSpecialTradeTermsFlag(product.trade_terms?.special_trade_terms)
                      ? '待採購確認'
                      : '無，沿用廠商交易條件'
                  }
                />
                <Info
                  label="首批優惠"
                  value={
                    product.initial_order_offer ||
                    (product.initial_order_offer_enabled ? '有' : '無')
                  }
                />
                <Info
                  label="贈品及搭贈"
                  value={
                    product.gift_bundle_support ||
                    (product.gift_bundle_support_enabled ? '有' : '無')
                  }
                />
                <Info
                  label="促銷支援"
                  value={
                    product.promotion_support_enabled
                      ? `有${product.promotion_content ? `：${product.promotion_content}` : ''}`
                      : '無'
                  }
                />
                <Info
                  label="試用／試吃支援"
                  value={
                    product.trial_support_enabled
                      ? `有${product.trial_support_content ? `：${product.trial_support_content}` : ''}`
                      : '無'
                  }
                />
                <Info label="商品特色" value={product.key_features || product.product_features} />
                <Info label="目標客群" value={product.target_customers} />
                <Info label="競品差異" value={product.competitor_difference} />
                <Info label="上市計畫" value={product.launch_plan} />
                <Info
                  label="目前銷售通路"
                  value={
                    (proposal.current_sales_channels ?? [])
                      .map(
                        (channel) =>
                          PROACTIVE_CHANNEL_LABELS[channel.channel_code] ?? channel.channel_code,
                      )
                      .join('、') || '尚未填寫'
                  }
                />
                <Info
                  label="預計銷售通路"
                  value={
                    (proposal.expected_sales_channels ?? proposal.channels ?? [])
                      .map(
                        (channel) =>
                          PROACTIVE_CHANNEL_LABELS[channel.channel_code] ?? channel.channel_code,
                      )
                      .join('、') || '尚未填寫'
                  }
                />
              </div>
            ) : (
              <EmptyState title="廠商尚未填寫商品資料" />
            )}
          </Card>
          <Card>
            <h3 className="mb-4 text-lg font-extrabold">價格與交易條件</h3>
            {product ? (
              <div className="space-y-5">
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <h4 className="font-extrabold text-teal-900 sm:col-span-2">商品價格</h4>
                  <Info label="未稅進價" value={<Money value={product.ex_tax_price} />} />
                  <Info label="含稅進價" value={<Money value={product.tax_included_price} />} />
                  <Info label="稅率" value={`${product.tax_rate ?? 0}%`} />
                  <Info label="建議售價" value={<Money value={product.suggested_retail_price} />} />
                  <Info label="最低訂購量" value={product.minimum_order_quantity ?? '—'} />
                  <Info label="最小配送單位" value={product.minimum_delivery_unit} />
                  <Info
                    label="最低訂購金額"
                    value={<Money value={product.minimum_order_amount} />}
                  />

                  <h4 className="mt-3 font-extrabold text-teal-900 sm:col-span-2">付款條件</h4>
                  <Info
                    label="資料來源"
                    value={
                      tradeTerms?.source_type === 'VENDOR_MASTER'
                        ? '廠商主檔'
                        : tradeTerms?.source_type === 'VENDOR_APPLICATION'
                          ? '新廠商申請'
                          : tradeTerms?.source_type === 'PRODUCT_SPECIAL'
                            ? '商品特殊交易條件'
                            : '尚未設定'
                    }
                  />
                  <Info label="付款條件" value={labelVendorPaymentTerm(tradeTerms?.payment_term)} />
                  <Info
                    label="月結天數"
                    value={
                      tradeTerms?.monthly_payment_days == null
                        ? '不適用'
                        : `${tradeTerms.monthly_payment_days} 天`
                    }
                  />
                  <Info
                    label="付款方式"
                    value={labelVendorPaymentMethod(tradeTerms?.payment_method)}
                  />

                  <h4 className="mt-3 font-extrabold text-teal-900 sm:col-span-2">配送條件</h4>
                  <Info
                    label="配送地點"
                    value={labelVendorDeliveryScope(deliveryTerms?.delivery_scope)}
                  />
                  <Info
                    label="廠商配送週期"
                    value={labelVendorDeliveryCycle(deliveryTerms?.delivery_cycle)}
                  />
                  <Info
                    label="固定配送星期"
                    value={formatVendorWeekdays(deliveryTerms?.delivery_weekdays)}
                  />
                  <Info
                    label="最低訂購金額"
                    value={<Money value={deliveryTerms?.delivery_minimum_order_amount} />}
                  />
                  <Info
                    label="最低訂購數量"
                    value={deliveryTerms?.delivery_minimum_order_quantity ?? '—'}
                  />
                  <Info
                    label="廠商運費規則"
                    value={`${labelVendorFreightRule(deliveryTerms?.freight_rule)}${
                      deliveryTerms?.freight_threshold_amount != null
                        ? `（門檻 ${formatVendorMoney(deliveryTerms.freight_threshold_amount)}）`
                        : deliveryTerms?.freight_fixed_amount != null
                          ? `（固定運費 ${formatVendorMoney(deliveryTerms.freight_fixed_amount)}）`
                          : deliveryTerms?.freight_other_note
                            ? `（${deliveryTerms.freight_other_note}）`
                            : ''
                    }`}
                  />

                  <h4 className="mt-3 font-extrabold text-teal-900 sm:col-span-2">
                    退貨／換貨條件
                  </h4>
                  <Info
                    label="退貨地點"
                    value={labelVendorDeliveryScope(tradeTerms?.return_scope)}
                  />
                  <Info
                    label="退貨／換貨處理方式"
                    value={labelVendorReturnHandling(tradeTerms?.return_handling_method)}
                  />
                  <Info
                    label="退貨／換貨成立條件"
                    value={formatVendorReturnConditions(tradeTerms?.return_conditions)}
                  />
                  <Info label="即期退換貨條件" value={tradeTerms?.return_near_expiry_condition} />
                  <Info label="瑕疵退換貨條件" value={tradeTerms?.return_defect_condition} />
                  <Info label="吃不合退換貨條件" value={tradeTerms?.return_intolerance_condition} />
                  <Info label="其他退換貨條件" value={tradeTerms?.return_other_condition} />
                  <Info label="退貨／換貨詳細說明" value={tradeTerms?.return_description} />
                  <Info
                    label="退貨／換貨運費負擔"
                    value={`${labelVendorReturnFreight(tradeTerms?.return_freight_responsibility)}${
                      tradeTerms?.return_freight_rule_note
                        ? `（${tradeTerms.return_freight_rule_note}）`
                        : ''
                    }`}
                  />
                </div>
              </div>
            ) : (
              <EmptyState title="尚無價格與交易條件" />
            )}
          </Card>
          <Card>
            <h3 className="mb-4 text-lg font-extrabold">優惠與行銷支援</h3>
            {product ? (
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <Info label="首批優惠" value={product.initial_order_offer_enabled ? '有' : '無'} />
                <Info label="首批優惠內容" value={product.initial_order_offer_content} />
                <Info
                  label="贈品及搭贈"
                  value={product.gift_bundle_support_enabled ? '有' : '無'}
                />
                <Info label="贈品及搭贈內容" value={product.gift_bundle_support_content} />
                <Info label="促銷支援" value={product.promotion_support_enabled ? '有' : '無'} />
                <Info label="促銷內容" value={product.promotion_content} />
                <Info label="促銷開始日期" value={product.promotion_start_date} />
                <Info label="促銷結束日期" value={product.promotion_end_date} />
                <Info label="試用／體驗支援" value={product.trial_support_enabled ? '有' : '無'} />
                <Info label="試用／體驗支援內容" value={product.trial_support_content} />
              </div>
            ) : (
              <EmptyState title="尚無優惠與行銷支援資料" />
            )}
          </Card>
          <Card>
            <h3 className="mb-4 text-lg font-extrabold">附件與補件歷程</h3>
            {effectiveReviewAttachmentRequirements.length ? (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm">
                <p className="font-bold text-amber-900">必填附件檢查</p>
                <div className="mt-2 space-y-1">
                  {effectiveReviewAttachmentRequirements.map((requirement) => {
                    const uploaded = proposal.attachments?.some(
                      (attachment) =>
                        !attachment.deleted_at && attachment.type === requirement.type,
                    );
                    return (
                      <p
                        key={requirement.id}
                        className={uploaded ? 'text-emerald-700' : 'text-rose-700'}
                      >
                        {uploaded ? '✓' : '!'}{' '}
                        {labelProactiveAttachmentType(requirement.type, requirement.label)}
                      </p>
                    );
                  })}
                </div>
                {missingRequiredAttachments.length ? (
                  <p className="mt-2 font-semibold text-rose-700">
                    尚缺 {missingRequiredAttachments.length} 項必要附件。
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="mb-4 rounded-2xl border border-teal-100 bg-teal-50 p-4">
              <p className="font-bold text-teal-950">條碼資料核對</p>
              {product?.no_international_barcode ? (
                <p className="mt-2 text-sm text-teal-900">
                  本商品已申報無國際條碼，不要求條碼圖片。
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  {(product?.barcodes ?? []).filter(Boolean).map((barcode) => {
                    const images = barcodeImageAttachments.filter(
                      (attachment) => attachment.barcode_value === barcode,
                    );
                    return (
                      <div key={barcode} className="rounded-xl bg-white p-3">
                        <p className="text-sm font-bold text-ink">國際條碼：{barcode}</p>
                        {images.length ? (
                          <div className="mt-3 flex flex-wrap gap-3">
                            {images.map((attachment) => (
                              <a
                                key={attachment.id}
                                href={`/api/vendor-proposals/attachments/${attachment.id}?preview=1`}
                                target="_blank"
                                rel="noreferrer"
                                className="block rounded-lg border border-slate-200 bg-white p-2"
                              >
                                {attachment.content_type.startsWith('image/') ? (
                                  <img
                                    src={`/api/vendor-proposals/attachments/${attachment.id}?preview=1`}
                                    alt={`條碼圖片 ${barcode}`}
                                    className="h-28 w-40 object-contain"
                                  />
                                ) : null}
                                <span className="mt-1 block max-w-40 break-words text-xs text-teal-700">
                                  {attachment.original_name}
                                </span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-rose-700">尚未上傳對應條碼圖片。</p>
                        )}
                      </div>
                    );
                  })}
                  {barcodeImageAttachments.some((attachment) => !attachment.barcode_value) && (
                    <p className="text-sm text-amber-800">有條碼圖片尚未指定對應的國際條碼。</p>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              {proposal.attachments?.some((attachment) => !attachment.deleted_at) ? (
                proposal.attachments
                  .filter((attachment) => !attachment.deleted_at)
                  .map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex flex-col gap-3 rounded-xl bg-slate-50 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                    >
                      <span className="min-w-0 break-words">
                        {labelProactiveAttachmentType(attachment.type)} · {attachment.original_name}
                      </span>
                      <span className="flex shrink-0 gap-3 text-xs font-bold">
                        <a
                          href={`/api/vendor-proposals/attachments/${attachment.id}?preview=1`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-teal-700"
                        >
                          預覽
                        </a>
                        <a
                          href={`/api/vendor-proposals/attachments/${attachment.id}`}
                          className="text-teal-700"
                        >
                          下載
                        </a>
                      </span>
                    </div>
                  ))
              ) : (
                <p className="text-sm text-slate-500">尚無附件。</p>
              )}
            </div>
            {proposal.revision_requests?.map((item) => (
              <div
                key={item.id}
                className="mt-4 rounded-xl border border-orange-100 bg-orange-50 p-3 text-sm"
              >
                <p className="font-bold">
                  補件：
                  {item.item_codes
                    .map((code) => PROACTIVE_REVISION_ITEM_LABELS[code] ?? code)
                    .join('、')}
                </p>
                <p className="mt-1 whitespace-pre-wrap leading-6">{item.explanation}</p>
                <p className="mt-1 text-xs text-slate-500">
                  期限：{item.due_at ? <DateText value={item.due_at} /> : '未設定'}
                </p>
              </div>
            ))}
          </Card>
        </div>
        {product?.trade_terms_draft &&
        normalizeSpecialTradeTermsFlag(product.trade_terms_draft.special_trade_terms) ? (
          <Card>
            <h3 className="mb-2 text-lg font-extrabold">本商品特殊交易條件</h3>
            <p className="text-sm text-slate-600">
              目前狀態：
              {product.trade_terms_draft.special_status === 'PENDING_BUYER_REVIEW'
                ? '待採購確認'
                : product.trade_terms_draft.special_status}
            </p>
            <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
              <Info
                label="特殊付款條件"
                value={displayProductTradeTerm(product.trade_terms_draft.special_payment_term)}
              />
              <Info
                label="特殊付款方式"
                value={displayProductTradeTerm(product.trade_terms_draft.special_payment_method)}
              />
              <Info
                label="特殊退換貨條件"
                value={displayProductTradeTerm(product.trade_terms_draft.special_return_conditions)}
              />
              <Info label="特殊條件原因" value={product.trade_terms_draft.special_reason} />
              <Info
                label="特殊退換貨說明"
                value={product.trade_terms_draft.special_return_description}
              />
            </div>
            <Textarea
              className="mt-4"
              label="審核意見／要求補件原因"
              value={tradeTermsReason}
              onChange={(event) => setTradeTermsReason(event.target.value)}
            />
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => void reviewSpecialTradeTerms('REJECT')}
                loading={tradeTermsSaving}
              >
                拒絕特殊條件，沿用標準條件
              </Button>
              <Button
                variant="danger"
                onClick={() => void reviewSpecialTradeTerms('REQUEST_REVISION')}
                loading={tradeTermsSaving}
              >
                要求廠商重新提出
              </Button>
              <Button
                onClick={() => void reviewSpecialTradeTerms('ACCEPT')}
                loading={tradeTermsSaving}
              >
                接受特殊條件
              </Button>
            </div>
          </Card>
        ) : null}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <h3 className="mb-4 text-lg font-extrabold">要求補件</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {Object.entries(PROACTIVE_REVISION_ITEM_LABELS).map(([code, label]) => (
                <label
                  key={code}
                  className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={revisionItems.includes(code)}
                    onChange={() => toggleRevision(code)}
                  />
                  {label}
                </label>
              ))}
            </div>
            <Textarea
              className="mt-4"
              label="補件說明（廠商可見）"
              required
              value={revisionExplanation}
              onChange={(event) => setRevisionExplanation(event.target.value)}
            />
            <Input
              className="mt-4"
              label="補件期限"
              required
              type="date"
              min={todayTaipei}
              value={revisionDueAt}
              onChange={(event) => setRevisionDueAt(event.target.value)}
            />
            <div className="mt-4 flex justify-end">
              <Button variant="secondary" onClick={requestRevision} loading={saving}>
                送出補件要求
              </Button>
            </div>
          </Card>
          <Card>
            <h3 className="mb-4 text-lg font-extrabold">狀態與會議流程</h3>
            <p className="mb-4 text-sm text-slate-600">
              目前狀態：{PROACTIVE_STATUS_LABELS[proposal.status] ?? proposal.status}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => changeStatus('APPROVED')} loading={saving}>
                決議上市
              </Button>
              <Button
                variant="secondary"
                onClick={() => changeStatus('WAITING_MEETING')}
                loading={saving}
              >
                進入會議
              </Button>
            </div>
            <div className="mt-5 border-t border-slate-100 pt-5">
              <Textarea
                label="暫緩原因"
                required
                value={pauseReason}
                onChange={(event) => setPauseReason(event.target.value)}
              />
              <Input
                className="mt-3"
                label="預計重新評估日期"
                type="date"
                min={todayTaipei}
                value={reconsiderationDate}
                onChange={(event) => setReconsiderationDate(event.target.value)}
              />
              <Button
                variant="secondary"
                className="mt-3"
                onClick={() => changeStatus('PAUSED')}
                loading={saving}
              >
                暫緩
              </Button>
            </div>
            <div className="mt-5 border-t border-slate-100 pt-5">
              <Select
                label="不採用原因"
                value={rejectionCode}
                onChange={(event) => setRejectionCode(event.target.value)}
              >
                <option value="DUPLICATE_PRODUCT">商品重複或已有同類商品</option>
                <option value="MARGIN_TOO_LOW">毛利不符合採購條件</option>
                <option value="MARKET_FIT_LOW">市場定位或客群不符</option>
                <option value="DOCUMENT_INCOMPLETE">文件或法規資料不足</option>
                <option value="MOQ_UNSUITABLE">最低訂購量或交易條件不合適</option>
                <option value="DELIVERY_UNSUITABLE">交貨或配送條件不合適</option>
                <option value="OTHER">其他原因</option>
              </Select>
              <Textarea
                className="mt-3"
                label="廠商可見說明"
                value={vendorReason}
                onChange={(event) => setVendorReason(event.target.value)}
              />
              <Button
                variant="danger"
                className="mt-3"
                onClick={() => changeStatus('NOT_ADOPTED')}
                loading={saving}
              >
                標示不採用
              </Button>
            </div>
          </Card>
        </div>
        {proposal.status === 'APPROVED' || proposal.product_master ? (
          <Card className={proposal.product_master ? 'border-emerald-200' : 'border-teal-200'}>
            <h3 className="mb-2 text-lg font-extrabold">
              {proposal.product_master ? '正式商品主檔' : '待建立正式商品主檔'}
            </h3>
            {proposal.product_master ? (
              <div className="mt-4 grid gap-3 text-sm md:grid-cols-3">
                <Info label="商品大碼" value={proposal.product_master.large_code} />
                <Info label="商品中碼" value={proposal.product_master.middle_code} />
                <Info label="商品小碼" value={proposal.product_master.small_code} />
                <Info label="VIP 價" value={<Money value={proposal.product_master.vip_price} />} />
                <Info
                  label="員工價"
                  value={<Money value={proposal.product_master.employee_price} />}
                />
                <Info
                  label="店內促銷活動"
                  value={proposal.product_master.in_store_promotion || '無'}
                />
              </div>
            ) : (
              <>
                <p className="text-sm leading-6 text-slate-600">
                  本提案已決議上市。商品資料會自動帶入，請補齊以下正式主檔欄位。
                </p>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <Input
                    label="商品大碼"
                    required
                    value={masterForm.large_code}
                    onChange={(event) =>
                      setMasterForm({ ...masterForm, large_code: event.target.value })
                    }
                  />
                  <Input
                    label="商品中碼"
                    required
                    value={masterForm.middle_code}
                    onChange={(event) =>
                      setMasterForm({ ...masterForm, middle_code: event.target.value })
                    }
                  />
                  <Input
                    label="商品小碼"
                    required
                    value={masterForm.small_code}
                    onChange={(event) =>
                      setMasterForm({ ...masterForm, small_code: event.target.value })
                    }
                  />
                  <Input
                    label="VIP 價"
                    required
                    type="number"
                    min="0"
                    step="0.01"
                    value={masterForm.vip_price}
                    onChange={(event) =>
                      setMasterForm({ ...masterForm, vip_price: event.target.value })
                    }
                  />
                  <Input
                    label="員工價"
                    type="number"
                    min="0"
                    step="0.01"
                    value={masterForm.employee_price}
                    onChange={(event) =>
                      setMasterForm({ ...masterForm, employee_price: event.target.value })
                    }
                  />
                  <Textarea
                    label="店內促銷活動"
                    value={masterForm.in_store_promotion}
                    onChange={(event) =>
                      setMasterForm({ ...masterForm, in_store_promotion: event.target.value })
                    }
                  />
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={createProductMaster} loading={masterSaving}>
                    建立正式商品主檔
                  </Button>
                </div>
              </>
            )}
          </Card>
        ) : null}
        <Card>
          <h3 className="mb-4 text-lg font-extrabold">審核歷史</h3>
          <div className="space-y-3">
            {proposal.review_history?.length
              ? proposal.review_history.map((item) => (
                  <div key={item.id} className="flex gap-3 border-l-2 border-teal-200 pl-4">
                    <div>
                      <p className="text-sm font-bold">
                        {item.proposal_version} ·{' '}
                        {PROPOSAL_REVIEW_ACTION_LABELS[item.action] ?? item.action}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {item.old_status
                          ? `${PROACTIVE_STATUS_LABELS[item.old_status] ?? item.old_status} → `
                          : ''}
                        {PROACTIVE_STATUS_LABELS[item.new_status] ?? item.new_status}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
                        {item.review_note || '—'}
                      </p>
                      {item.supplement_deadline ? (
                        <p className="mt-1 text-xs font-semibold text-orange-700">
                          補件期限：
                          <DateText value={item.supplement_deadline} />
                        </p>
                      ) : null}
                      <p className="mt-1 text-xs text-slate-400">
                        <DateText value={item.reviewed_at} withTime /> ·{' '}
                        {item.reviewed_by_name || item.reviewed_role || '系統'}
                      </p>
                    </div>
                  </div>
                ))
              : proposal.status_history?.map((item, index) => (
                  <div
                    key={`${item.created_at}-${index}`}
                    className="flex gap-3 border-l-2 border-teal-200 pl-4"
                  >
                    <div>
                      <p className="text-sm font-bold">
                        {item.from_status
                          ? `${PROACTIVE_STATUS_LABELS[item.from_status] ?? item.from_status} → `
                          : ''}
                        {PROACTIVE_STATUS_LABELS[item.to_status] ?? item.to_status}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
                        {item.reason || '—'}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        <DateText value={item.created_at} withTime /> ·{' '}
                        {item.changed_role ?? '系統'}
                      </p>
                    </div>
                  </div>
                ))}
          </div>
        </Card>
      </div>
    </>
  );
}

const PRODUCT_MASTER_QUICK_FILTERS: Array<{
  key: string;
  label: string;
  summaryKey: string;
}> = [
  { key: '', label: '全部', summaryKey: 'total' },
  { key: 'PENDING', label: '待建立', summaryKey: 'pending' },
  { key: 'IN_PROGRESS', label: '建檔中', summaryKey: 'in_progress' },
  { key: 'DATA_INCOMPLETE', label: '資料待補', summaryKey: 'data_incomplete' },
  { key: 'WITHOUT_ANNOUNCEMENT', label: '已建檔未公告', summaryKey: 'without_announcement' },
  { key: 'ANNOUNCEMENT_LINKED', label: '已加入公告', summaryKey: 'announcement_linked' },
  { key: 'COMPLETED', label: '已完成', summaryKey: 'completed' },
];

function ProductMasterWorkBadge({ status }: { status: ProductMasterWorkStatus }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
        PRODUCT_MASTER_WORK_STATUS_TONE[status] ?? 'bg-slate-100 text-slate-700'
      }`}
    >
      {PRODUCT_MASTER_WORK_STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function ProductMasterWorkItemsPage({ notify }: PageProps) {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<ProductMasterWorkItem[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [subcategories, setSubcategories] = useState<ProductSubcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [quick, setQuick] = useState(searchParams.get('quick') ?? '');
  const [decisionFrom, setDecisionFrom] = useState('');
  const [decisionTo, setDecisionTo] = useState('');
  const [vendor, setVendor] = useState('');
  const [brand, setBrand] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [subcategoryCode, setSubcategoryCode] = useState('');
  const [assignedBuyerId, setAssignedBuyerId] = useState('');
  const [workStatus, setWorkStatus] = useState('');
  const [announcementStatus, setAnnouncementStatus] = useState('');
  const [proposalSource, setProposalSource] = useState('');
  const [buyerOptions, setBuyerOptions] = useState<Array<{ id: string; name: string }>>([]);

  const buildParams = (quickOverride = quick) => ({
    ...(quickOverride ? { quick: quickOverride } : {}),
    ...(decisionFrom ? { decision_date_from: decisionFrom } : {}),
    ...(decisionTo ? { decision_date_to: decisionTo } : {}),
    ...(vendor ? { vendor } : {}),
    ...(brand ? { brand } : {}),
    ...(categoryCode ? { product_category_code: categoryCode } : {}),
    ...(subcategoryCode ? { product_subcategory_code: subcategoryCode } : {}),
    ...(assignedBuyerId ? { assigned_buyer_id: assignedBuyerId } : {}),
    ...(workStatus ? { status: workStatus } : {}),
    ...(announcementStatus ? { announcement_status: announcementStatus } : {}),
    ...(proposalSource ? { proposal_source: proposalSource } : {}),
  });

  const load = async (quickOverride = quick) => {
    setLoading(true);
    try {
      const result = await api.productMasterWorkItems.list(buildParams(quickOverride));
      setItems(result.work_items);
      setSummary(result.summary);
      setBuyerOptions((current) => {
        const options = new Map(current.map((option) => [option.id, option.name]));
        result.work_items.forEach((item) => {
          if (item.assigned_buyer_id)
            options.set(item.assigned_buyer_id, item.assigned_buyer_name || item.assigned_buyer_id);
        });
        return [...options.entries()].map(([id, name]) => ({ id, name }));
      });
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    api.productCategories
      .publicList()
      .then((result) => setCategories(result.categories))
      .catch((error) => notify(errorMessage(error), 'error'));
    // 初次載入採網址快捷篩選；後續由使用者按「套用篩選」。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSubcategoryCode('');
    if (!categoryCode) {
      setSubcategories([]);
      return;
    }
    api.productSubcategories
      .publicList(categoryCode)
      .then((result) => setSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [categoryCode, notify]);

  const selectQuick = (value: string) => {
    setQuick(value);
    setWorkStatus('');
    void load(value);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="PRODUCT MASTER WORKFLOW"
        title="待建立正式商品主檔"
        description="追蹤決議上市後的商品建檔、上市公告製作與發布進度；建檔完成後案件仍會保留至公告發布。"
        actions={
          <Button onClick={() => void load()} loading={loading}>
            重新整理
          </Button>
        }
      />
      <div className="flex flex-wrap gap-2">
        {PRODUCT_MASTER_QUICK_FILTERS.map((filter) => (
          <button
            key={filter.key || 'all'}
            type="button"
            onClick={() => selectQuick(filter.key)}
            className={`rounded-full border px-4 py-2 text-sm font-bold transition ${
              quick === filter.key
                ? 'border-teal-600 bg-teal-600 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-teal-300'
            }`}
          >
            {filter.label} {summary[filter.summaryKey] ?? 0}
          </button>
        ))}
      </div>
      <Card className="no-print">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            label="決議日期（起）"
            type="date"
            value={decisionFrom}
            onChange={(event) => setDecisionFrom(event.target.value)}
          />
          <Input
            label="決議日期（迄）"
            type="date"
            value={decisionTo}
            onChange={(event) => setDecisionTo(event.target.value)}
          />
          <Input
            label="廠商／統一編號"
            value={vendor}
            onChange={(event) => setVendor(event.target.value)}
          />
          <Input label="品牌" value={brand} onChange={(event) => setBrand(event.target.value)} />
          <Select
            label="商品分類"
            value={categoryCode}
            onChange={(event) => setCategoryCode(event.target.value)}
          >
            <option value="">全部商品分類</option>
            {categories.map((category) => (
              <option key={category.code} value={category.code}>
                {formatProductCategory(category.code, category.name)}
              </option>
            ))}
          </Select>
          <Select
            label="商品子分類"
            value={subcategoryCode}
            disabled={!categoryCode}
            onChange={(event) => setSubcategoryCode(event.target.value)}
          >
            <option value="">全部商品子分類</option>
            {subcategories.map((subcategory) => (
              <option key={subcategory.code} value={subcategory.code}>
                {subcategory.code}｜{subcategory.name}
              </option>
            ))}
          </Select>
          <Select
            label="負責採購"
            value={assignedBuyerId}
            onChange={(event) => setAssignedBuyerId(event.target.value)}
          >
            <option value="">全部負責採購</option>
            {buyerOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
          <Select
            label="商品建檔狀態"
            value={workStatus}
            onChange={(event) => setWorkStatus(event.target.value)}
          >
            <option value="">全部建檔狀態</option>
            {Object.entries(PRODUCT_MASTER_WORK_STATUS_LABELS).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            label="公告狀態"
            value={announcementStatus}
            onChange={(event) => setAnnouncementStatus(event.target.value)}
          >
            <option value="">全部公告狀態</option>
            <option value="DRAFT">已加入公告草稿</option>
            <option value="PENDING_PUBLICATION">公告待發布</option>
            <option value="PUBLISHED">公告已發布</option>
            <option value="WITHDRAWN">公告已撤回</option>
            <option value="ARCHIVED">公告已封存</option>
          </Select>
          <Select
            label="提案來源"
            value={proposalSource}
            onChange={(event) => setProposalSource(event.target.value)}
          >
            <option value="">全部提案來源</option>
            <option value="BUYER_INVITATION">惠生邀請</option>
            <option value="VENDOR_PROACTIVE">廠商主動提案</option>
          </Select>
          <div className="flex items-end">
            <Button className="w-full" onClick={() => void load()} loading={loading}>
              套用篩選
            </Button>
          </div>
        </div>
      </Card>
      <Card>
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <EmptyState
            title="目前沒有符合條件的建檔工作項目"
            description="新品決議上市後，系統會自動在此建立工作項目。"
          />
        ) : (
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">提案／決議</th>
                <th className="px-4 py-3">來源／活動</th>
                <th className="px-4 py-3">廠商</th>
                <th className="px-4 py-3">商品／品牌</th>
                <th className="px-4 py-3">分類／子分類</th>
                <th className="px-4 py-3">規格／單位</th>
                <th className="px-4 py-3">價格</th>
                <th className="px-4 py-3">決議／負責採購</th>
                <th className="px-4 py-3">建檔狀態</th>
                <th className="px-4 py-3">公告狀態</th>
                <th className="px-4 py-3">最後更新</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-slate-100 align-top">
                  <td className="whitespace-normal px-4 py-4">
                    <p className="font-bold text-teal-700">{item.proposal_no}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      <DateText value={item.decision_at} withTime />
                    </p>
                  </td>
                  <td className="max-w-[180px] whitespace-normal break-words px-4 py-4 text-sm">
                    <p className="font-bold">
                      {item.proposal_source === 'BUYER_INVITATION' ? '惠生邀請' : '廠商主動提案'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {item.quote_activity_name || '不屬於報價活動'}
                    </p>
                  </td>
                  <td className="max-w-[180px] whitespace-normal break-words px-4 py-4">
                    <p className="font-bold">{item.vendor_name}</p>
                    <p className="mt-1 text-xs text-slate-500">{item.tax_id}</p>
                  </td>
                  <td className="max-w-[220px] whitespace-normal break-words px-4 py-4">
                    <p className="font-bold">{item.product_name || '未填商品名稱'}</p>
                    <p className="mt-1 text-xs text-slate-500">品牌：{item.brand || '—'}</p>
                  </td>
                  <td className="max-w-[210px] whitespace-normal break-words px-4 py-4 text-sm">
                    <p>
                      {formatProductCategory(
                        item.product_category_code,
                        item.product_category_name,
                      )}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {item.product_subcategory_code
                        ? `${item.product_subcategory_code}｜${item.product_subcategory_name}`
                        : '子分類未設定'}
                    </p>
                  </td>
                  <td className="max-w-[170px] whitespace-normal break-words px-4 py-4 text-sm">
                    {item.specification || '—'}
                    <p className="mt-1 text-xs text-slate-500">單位：{item.sales_unit || '—'}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm">
                    <p>
                      含稅：
                      <Money value={item.tax_included_price} />
                    </p>
                    <p className="mt-1">
                      建議：
                      <Money value={item.suggested_retail_price} />
                    </p>
                  </td>
                  <td className="max-w-[150px] whitespace-normal break-words px-4 py-4 text-sm">
                    <p>{item.decision_by_name || '—'}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      採購：{item.assigned_buyer_name || '尚未指派'}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <ProductMasterWorkBadge status={item.status} />
                    {item.missing_items.length ? (
                      <p className="mt-2 max-w-[180px] whitespace-normal text-xs text-rose-700">
                        缺少：{item.missing_items.join('、')}
                      </p>
                    ) : null}
                    {item.product_code ? (
                      <p className="mt-2 text-xs font-bold text-teal-700">{item.product_code}</p>
                    ) : null}
                  </td>
                  <td className="max-w-[180px] whitespace-normal break-words px-4 py-4 text-sm">
                    <p className="font-bold">
                      {PRODUCT_MASTER_ANNOUNCEMENT_PROGRESS_LABELS[item.announcement_status] ??
                        item.announcement_status}
                    </p>
                    {item.announcement_title ? (
                      <p className="mt-1 text-xs text-slate-500">{item.announcement_title}</p>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">
                    <DateText value={item.updated_at} withTime />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col gap-2">
                      <Link to={`/proactive/${item.proposal_id}`}>
                        <Button variant="secondary" className="w-full">
                          查看原提案
                        </Button>
                      </Link>
                      {item.status === 'PENDING' ||
                      item.status === 'IN_PROGRESS' ||
                      item.status === 'DATA_INCOMPLETE' ? (
                        <Link to={`/product-master-work-items/${item.id}`}>
                          <Button className="w-full">建立商品主檔</Button>
                        </Link>
                      ) : (
                        <Link to={`/product-master-work-items/${item.id}`}>
                          <Button variant="secondary" className="w-full">
                            查看商品主檔
                          </Button>
                        </Link>
                      )}
                      {item.announcement_id ? (
                        <Link to={`/launch-announcements?announcement_id=${item.announcement_id}`}>
                          <Button variant="secondary" className="w-full">
                            查看上市公告
                          </Button>
                        </Link>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export function ProductMasterWorkItemPage({ notify }: PageProps) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [workItem, setWorkItem] = useState<ProductMasterWorkItem | null>(null);
  const [proposal, setProposal] = useState<VendorProposal | null>(null);
  const [draftAnnouncements, setDraftAnnouncements] = useState<LaunchAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState('');
  const [allocationQuantity, setAllocationQuantity] = useState('');
  const [form, setForm] = useState({
    large_code: '',
    middle_code: '',
    small_code: '',
    vip_price: '',
    employee_price: '',
    in_store_promotion: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [result, announcementResult] = await Promise.all([
        api.productMasterWorkItems.start(id),
        api.launchAnnouncements.list(),
      ]);
      setWorkItem(result.work_item);
      setProposal(result.proposal);
      setDraftAnnouncements(
        announcementResult.announcements.filter((announcement) => announcement.status === 'DRAFT'),
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const createProduct = async () => {
    if (
      !form.large_code.trim() ||
      !form.middle_code.trim() ||
      !form.small_code.trim() ||
      form.vip_price === ''
    ) {
      notify('請填寫商品大碼、商品中碼、商品小碼及 VIP價。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.productMasterWorkItems.createProduct(id, {
        large_code: form.large_code,
        middle_code: form.middle_code,
        small_code: form.small_code,
        vip_price: Number(form.vip_price),
        employee_price: form.employee_price === '' ? null : Number(form.employee_price),
        in_store_promotion: form.in_store_promotion,
      });
      setWorkItem(result.workItem);
      setProposal((current) =>
        current
          ? {
              ...current,
              status: 'MASTER_CREATED',
              product_master: result.productMaster,
            }
          : current,
      );
      notify('正式商品主檔已建立，可接續建立或加入新品上市公告。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const addToAnnouncement = async () => {
    if (!workItem?.product_master_id || !selectedAnnouncementId) {
      notify('請先選擇公告草稿。', 'error');
      return;
    }
    if (!allocationQuantity || Number(allocationQuantity) <= 0) {
      notify('請填寫大於 0 的配貨數量。', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.launchAnnouncements.addItem(selectedAnnouncementId, {
        product_master_id: workItem.product_master_id,
        allocation_mode: 'UNIFORM',
        allocation_quantity: Number(allocationQuantity),
        store_allocations: [],
      });
      const refreshed = await api.productMasterWorkItems.detail(id);
      setWorkItem(refreshed.work_item);
      setProposal(refreshed.proposal);
      notify('商品已加入上市公告草稿。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;
  if (!workItem || !proposal)
    return <EmptyState title="找不到建檔工作項目" description="請返回待建檔清單重新開啟。" />;

  const product = proposal.product;
  const productCodePreview = formatProductCode({
    largeCode: form.large_code,
    mediumCode: form.middle_code,
    smallCode: form.small_code,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="PRODUCT MASTER"
        title={proposal.product_master ? '正式商品主檔' : '建立正式商品主檔'}
        description={`${proposal.proposal_no}｜${proposal.vendor_name}｜${product?.name_zh || '未填商品名稱'}`}
        actions={
          <Button variant="secondary" onClick={() => navigate('/product-master-work-items')}>
            返回待建檔清單
          </Button>
        }
      />
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <ProductMasterWorkBadge status={workItem.status} />
          <span className="text-sm text-slate-500">
            決議日期：
            <DateText value={workItem.decision_at} withTime />
          </span>
          <span className="text-sm text-slate-500">
            來源：{workItem.proposal_source === 'BUYER_INVITATION' ? '惠生邀請' : '廠商主動提案'}
          </span>
        </div>
        {workItem.missing_items.length ? (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            資料待補：{workItem.missing_items.join('、')}
          </div>
        ) : null}
      </Card>
      <Card>
        <h2 className="text-lg font-extrabold">廠商資料</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <Info label="廠商 ID" value={proposal.vendor_id || '—'} />
          <Info label="廠商名稱" value={proposal.vendor_name} />
          <Info label="統一編號" value={proposal.tax_id} />
          <Info label="廠商商品編號" value={product?.vendor_product_code || '—'} />
        </div>
      </Card>
      <Card>
        <h2 className="text-lg font-extrabold">商品基本資料</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Info label="商品中文名稱" value={product?.name_zh || '—'} />
          <Info label="商品英文名稱" value={product?.name_en || '—'} />
          <Info label="品牌" value={product?.brand || '—'} />
          <Info label="製造商" value={product?.manufacturer || '—'} />
          <Info label="產地" value={product?.origin || '—'} />
          <Info
            label="商品分類"
            value={formatProductCategory(
              product?.product_category_code,
              product?.product_category_name,
            )}
          />
          <Info
            label="商品子分類"
            value={
              product?.product_subcategory_code
                ? `${product.product_subcategory_code}｜${product.product_subcategory_name}`
                : '—'
            }
          />
          <Info label="商品規格" value={product?.specification || '—'} />
          <Info label="銷售單位" value={product?.unit || '—'} />
          <Info label="箱入數" value={product?.case_pack ?? '—'} />
          <Info
            label="商品生命週期"
            value={
              PROACTIVE_LIFECYCLE_LABELS[product?.lifecycle_type ?? ''] ??
              product?.lifecycle_type ??
              '—'
            }
          />
          <Info label="保存方式" value={product?.storage_method || product?.storage || '—'} />
          <Info label="保存期限" value={product?.shelf_life || '—'} />
          <Info
            label="預計上市日期"
            value={product?.expected_launch_date || product?.planned_launch_date || '—'}
          />
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-lg font-extrabold">條碼資料</h2>
          <div className="mt-4 space-y-2 text-sm">
            <Info
              label="國際條碼"
              value={
                Number(product?.no_international_barcode ?? 0) === 1
                  ? `無國際條碼：${product?.no_international_barcode_reason || '未填原因'}`
                  : product?.barcodes?.join('、') || '—'
              }
            />
            <Info label="外箱條碼" value={product?.outer_barcode || '—'} />
            <Info label="內盒條碼" value={product?.inner_barcode || '—'} />
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-extrabold">價格資料</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Info label="未稅進價" value={<Money value={product?.ex_tax_price} />} />
            <Info label="含稅進價" value={<Money value={product?.tax_included_price} />} />
            <Info label="稅率" value={product ? `${product.tax_rate}%` : '—'} />
            <Info label="建議售價" value={<Money value={product?.suggested_retail_price} />} />
          </div>
        </Card>
      </div>
      <Card>
        <h2 className="text-lg font-extrabold">商品與銷售資料</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Info
            label="商品主要特色"
            value={product?.key_features || product?.product_features || '—'}
          />
          <Info label="商品市場定位" value={product?.market_positioning || '—'} />
          <Info label="主要消費客群" value={product?.target_customers || '—'} />
          <Info label="與競品差異" value={product?.competitor_difference || '—'} />
          <Info
            label="目前銷售通路"
            value={
              (proposal.current_sales_channels ?? []).map((item) => item.channel_name).join('、') ||
              '—'
            }
          />
          <Info
            label="預計銷售通路"
            value={
              (proposal.expected_sales_channels ?? proposal.channels ?? [])
                .map((item) => item.channel_name)
                .join('、') || '—'
            }
          />
          <Info
            label="上市推廣計畫"
            value={product?.launch_plan || product?.marketing_support || '—'}
          />
        </div>
      </Card>
      <Card>
        <h2 className="text-lg font-extrabold">交易條件與優惠</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Info
            label="付款條件"
            value={displayProductTradeTerm(
              product?.trade_terms?.payment_term ??
                product?.trade_terms_draft?.payment_term ??
                product?.payment_terms,
            )}
          />
          <Info
            label="付款方式"
            value={displayProductTradeTerm(
              product?.trade_terms?.payment_method ??
                product?.trade_terms_draft?.payment_method ??
                product?.payment_method,
            )}
          />
          <Info
            label="退貨／換貨條件"
            value={displayProductTradeTerm(
              product?.trade_terms?.return_description ??
                product?.trade_terms_draft?.return_description ??
                product?.return_terms,
            )}
          />
          <Info
            label="首批優惠"
            value={product?.initial_order_offer_content || product?.first_order_offer || '無'}
          />
          <Info
            label="贈品及搭贈"
            value={product?.gift_bundle_support_content || product?.gifts || '無'}
          />
          <Info
            label="促銷支援"
            value={product?.promotion_content || product?.promotion_support || '無'}
          />
          <Info
            label="試用／體驗支援"
            value={product?.trial_support_content || product?.sample_support || '無'}
          />
        </div>
      </Card>
      <Card>
        <h2 className="text-lg font-extrabold">附件</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {(proposal.attachments ?? [])
            .filter((attachment) => !attachment.deleted_at)
            .map((attachment) => (
              <a
                key={attachment.id}
                href={`/api/vendor-proposals/attachments/${attachment.id}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-slate-200 p-4 text-sm hover:border-teal-300"
              >
                <strong className="block text-teal-700">
                  {labelProactiveAttachmentType(attachment.type)}
                </strong>
                <span className="mt-1 block break-all text-slate-500">
                  {attachment.original_name}
                </span>
              </a>
            ))}
          {!proposal.attachments?.some((attachment) => !attachment.deleted_at) ? (
            <p className="text-sm text-slate-500">無附件</p>
          ) : null}
        </div>
      </Card>
      {proposal.product_master ? (
        <Card className="border-emerald-200">
          <h2 className="text-lg font-extrabold">正式商品主檔已建立</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <Info
              label="商品代號"
              value={proposal.product_master.product_code || workItem.product_code || '—'}
            />
            <Info label="商品大碼" value={proposal.product_master.large_code} />
            <Info label="商品中碼" value={proposal.product_master.middle_code} />
            <Info label="商品小碼" value={proposal.product_master.small_code} />
            <Info label="VIP價" value={<Money value={proposal.product_master.vip_price} />} />
            <Info label="員工價" value={<Money value={proposal.product_master.employee_price} />} />
            <Info label="店內促銷活動" value={proposal.product_master.in_store_promotion || '無'} />
          </div>
          {workItem.announcement_id ? (
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to={`/launch-announcements?announcement_id=${workItem.announcement_id}`}>
                <Button>查看上市公告</Button>
              </Link>
              <span className="self-center text-sm font-bold text-teal-700">
                {PRODUCT_MASTER_ANNOUNCEMENT_PROGRESS_LABELS[workItem.announcement_status]}
              </span>
            </div>
          ) : (
            <div className="mt-6 border-t border-slate-100 pt-6">
              <h3 className="font-extrabold">接續製作新品上市公告</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={() =>
                    navigate(
                      `/launch-announcements?product_master_id=${workItem.product_master_id}&mode=new`,
                    )
                  }
                >
                  建立新品上市公告
                </Button>
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-[1fr_180px_auto]">
                <Select
                  label="加入既有公告草稿"
                  value={selectedAnnouncementId}
                  onChange={(event) => setSelectedAnnouncementId(event.target.value)}
                >
                  <option value="">請選擇公告草稿</option>
                  {draftAnnouncements.map((announcement) => (
                    <option key={announcement.id} value={announcement.id}>
                      {announcement.title}
                    </option>
                  ))}
                </Select>
                <Input
                  label="配貨數量"
                  type="number"
                  min="1"
                  step="1"
                  value={allocationQuantity}
                  onChange={(event) => setAllocationQuantity(event.target.value)}
                />
                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={() => void addToAnnouncement()}
                    loading={saving}
                  >
                    加入公告草稿
                  </Button>
                </div>
              </div>
              {!draftAnnouncements.length ? (
                <p className="mt-3 text-xs text-slate-500">目前沒有可加入的公告草稿。</p>
              ) : null}
            </div>
          )}
        </Card>
      ) : (
        <Card className="border-teal-200">
          <h2 className="text-lg font-extrabold">正式商品主檔補填欄位</h2>
          <p className="mt-2 text-sm text-slate-600">
            原提案資料會保存為建檔 Snapshot；以下欄位由採購補填。
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <Input
              label="商品大碼"
              required
              value={form.large_code}
              onChange={(event) => setForm({ ...form, large_code: event.target.value })}
            />
            <Input
              label="商品中碼"
              required
              value={form.middle_code}
              onChange={(event) => setForm({ ...form, middle_code: event.target.value })}
            />
            <Input
              label="商品小碼"
              required
              value={form.small_code}
              onChange={(event) => setForm({ ...form, small_code: event.target.value })}
            />
            <Input
              label="VIP價"
              required
              type="number"
              min="0"
              step="0.01"
              value={form.vip_price}
              onChange={(event) => setForm({ ...form, vip_price: event.target.value })}
            />
            <Input
              label="員工價"
              type="number"
              min="0"
              step="0.01"
              value={form.employee_price}
              onChange={(event) => setForm({ ...form, employee_price: event.target.value })}
            />
            <Textarea
              label="店內促銷活動"
              value={form.in_store_promotion}
              onChange={(event) => setForm({ ...form, in_store_promotion: event.target.value })}
            />
          </div>
          <p className="mt-4 text-sm font-bold text-teal-700">
            商品代號預覽：{productCodePreview || '請填寫三段商品代碼'}
          </p>
          <div className="mt-5 flex justify-end">
            <Button onClick={() => void createProduct()} loading={saving}>
              建立正式商品主檔
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export function ProactiveReportsPage({ notify }: PageProps) {
  const [status, setStatus] = useState('');
  const [productCategoryCode, setProductCategoryCode] = useState('');
  const [productSubcategoryCode, setProductSubcategoryCode] = useState('');
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [productSubcategories, setProductSubcategories] = useState<ProductSubcategory[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [categoryStats, setCategoryStats] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const params: Record<string, string> = {
    ...(status ? { status } : {}),
    ...(productCategoryCode ? { product_category_code: productCategoryCode } : {}),
    ...(productSubcategoryCode ? { product_subcategory_code: productSubcategoryCode } : {}),
  };
  const load = async () => {
    setLoading(true);
    try {
      const result = await api.proactive.reports(params);
      setRows(result.rows);
      setCategoryStats(result.category_stats);
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    api.productCategories
      .publicList()
      .then((result) => setProductCategories(result.categories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, []);
  useEffect(() => {
    setProductSubcategoryCode('');
    if (!productCategoryCode) {
      setProductSubcategories([]);
      return;
    }
    api.productSubcategories
      .publicList(productCategoryCode)
      .then((result) => setProductSubcategories(result.subcategories))
      .catch((error) => notify(errorMessage(error), 'error'));
  }, [productCategoryCode, notify]);
  const exportJson = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '新品提案報表.json';
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <PageHeader
        eyebrow="PROACTIVE REPORTING"
        title="新品提案報表"
        description="統計惠生邀請與廠商主動提案的完整度、重複警示、毛利與審核狀態；匯出會套用目前篩選條件。"
        actions={
          <>
            <Button variant="secondary" onClick={() => window.print()}>
              列印／PDF
            </Button>
            <Button variant="secondary" onClick={exportJson}>
              JSON
            </Button>
            <a href={api.proactive.reportCsvUrl(params)}>
              <Button>CSV</Button>
            </a>
          </>
        }
      />
      <Card className="no-print mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="狀態" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部狀態</option>
            {PROACTIVE_ACTIVE_STATUS_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {PROACTIVE_STATUS_LABELS[key]}
              </option>
            ))}
          </Select>
          <Select
            label="商品分類"
            value={productCategoryCode}
            onChange={(event) => setProductCategoryCode(event.target.value)}
          >
            <option value="">全部商品分類</option>
            {productCategories.map((category) => (
              <option key={category.code} value={category.code}>
                {formatProductCategory(category.code, category.name)}
              </option>
            ))}
          </Select>
          <Select
            label="商品子分類"
            value={productSubcategoryCode}
            onChange={(event) => setProductSubcategoryCode(event.target.value)}
            disabled={!productCategoryCode}
          >
            <option value="">全部商品子分類</option>
            {productSubcategories.map((subcategory) => (
              <option key={subcategory.code} value={subcategory.code}>
                {subcategory.code}｜{subcategory.name}
              </option>
            ))}
          </Select>
          <Button onClick={load} loading={loading}>
            套用篩選
          </Button>
        </div>
      </Card>
      {categoryStats.length > 0 && (
        <Card className="mb-6">
          <h2 className="mb-4 text-lg font-extrabold">商品分類統計</h2>
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">商品分類</th>
                <th className="px-4 py-3">提案件數</th>
                <th className="px-4 py-3">會議審議</th>
                <th className="px-4 py-3">核准</th>
                <th className="px-4 py-3">不採用</th>
                <th className="px-4 py-3">核准率</th>
              </tr>
            </thead>
            <tbody>
              {categoryStats.map((stat) => (
                <tr key={String(stat.product_category_code)} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-bold">
                    {formatProductCategory(
                      String(stat.product_category_code || ''),
                      String(stat.product_category_name || ''),
                    )}
                    <span className="mt-1 block text-xs font-semibold text-slate-500">
                      {stat.subcategory_code
                        ? `${String(stat.subcategory_code)}｜${String(stat.subcategory_name || '')}`
                        : '子分類未設定'}
                    </span>
                  </td>
                  <td className="px-4 py-3">{String(stat.proposal_count ?? 0)}</td>
                  <td className="px-4 py-3">{String(stat.meeting_count ?? 0)}</td>
                  <td className="px-4 py-3">{String(stat.approved_count ?? 0)}</td>
                  <td className="px-4 py-3">{String(stat.not_adopted_count ?? 0)}</td>
                  <td className="px-4 py-3">{Number(stat.approval_rate ?? 0).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      <Card>
        {loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <EmptyState title="沒有符合條件的提案" />
        ) : (
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">提案編號</th>
                <th className="px-4 py-3">廠商／商品</th>
                <th className="px-4 py-3">價格／毛利</th>
                <th className="px-4 py-3">完整度</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">建立時間</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={String(row.proposal_no)} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-4 font-bold text-teal-700">{String(row.proposal_no)}</td>
                  <td className="max-w-[360px] whitespace-normal break-words px-4 py-4">
                    <p className="font-bold">{String(row.vendor_name)}</p>
                    <p className="mt-1">
                      {String(row.product_name || '—')} · {String(row.brand || '—')}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {formatProductCategory(
                        String(row.product_category_code || ''),
                        String(row.product_category_name || row.category || ''),
                      )}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      商品子分類：
                      {row.product_subcategory_code
                        ? `${String(row.product_subcategory_code)}｜${String(row.product_subcategory_name || '')}`
                        : '未設定'}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <Money value={row.ex_tax_price as number | null} />
                    <p className="mt-1 text-xs font-bold text-teal-700">
                      毛利{' '}
                      {row.margin_rate == null
                        ? '—'
                        : `${(Number(row.margin_rate) * 100).toFixed(1)}%`}
                    </p>
                  </td>
                  <td className="px-4 py-4 font-bold text-teal-700">
                    {String(row.completeness_score)}%
                  </td>
                  <td className="px-4 py-4">
                    <ProactiveStatusBadge status={String(row.status) as ProactiveProposalStatus} />
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500">
                    <DateText value={String(row.created_at)} withTime />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

type LaunchEditorItem = {
  submission_id: string;
  product_name: string;
  product_code: string;
  vip_price: string;
  allocation_mode: LaunchAllocationMode;
  allocation_quantity: string;
  store_allocations: Record<string, string>;
};

type LaunchEditor = {
  id?: string;
  title: string;
  launch_date: string;
  audience_scope: 'ALL' | 'DESIGNATED';
  store_ids: string[];
  items: LaunchEditorItem[];
};

const emptyLaunchEditor = (): LaunchEditor => ({
  title: '新品上市配貨公告',
  launch_date: '',
  audience_scope: 'ALL',
  store_ids: [],
  items: [],
});

function launchEditorFromAnnouncement(announcement: LaunchAnnouncement): LaunchEditor {
  return {
    id: announcement.id,
    title: announcement.title,
    launch_date: announcement.launch_date ?? '',
    audience_scope: announcement.audience_scope,
    store_ids: (announcement.stores ?? []).map((store) => store.id),
    items: (announcement.items ?? []).map((item) => ({
      submission_id: item.proposal_id,
      product_name: item.product_name,
      product_code: item.product_code,
      vip_price: item.vip_price == null ? '' : String(item.vip_price),
      allocation_mode: item.allocation_mode,
      allocation_quantity: item.allocation_quantity == null ? '' : String(item.allocation_quantity),
      store_allocations: Object.fromEntries(
        (item.store_allocations ?? []).map((allocation) => [
          allocation.store_id,
          String(allocation.allocation_quantity),
        ]),
      ),
    })),
  };
}

function launchEditorPayload(editor: LaunchEditor) {
  return {
    title: editor.title,
    launch_date: editor.launch_date || null,
    audience_scope: editor.audience_scope,
    store_ids: editor.audience_scope === 'DESIGNATED' ? editor.store_ids : [],
    items: editor.items.map((item) => ({
      submission_id: item.submission_id,
      vip_price: item.vip_price === '' ? null : Number(item.vip_price),
      allocation_mode: item.allocation_mode,
      allocation_quantity:
        item.allocation_mode === 'UNIFORM' && item.allocation_quantity !== ''
          ? Number(item.allocation_quantity)
          : null,
      store_allocations:
        item.allocation_mode === 'BY_STORE'
          ? Object.entries(item.store_allocations)
              .filter(([, quantity]) => quantity !== '')
              .map(([store_id, quantity]) => ({
                store_id,
                allocation_quantity: Number(quantity),
              }))
          : [],
    })),
  };
}

export function LaunchAnnouncementsPage({ notify }: PageProps) {
  const [launchSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [announcements, setAnnouncements] = useState<LaunchAnnouncement[]>([]);
  const [candidates, setCandidates] = useState<LaunchProductCandidate[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selected, setSelected] = useState<LaunchAnnouncement | null>(null);
  const [editor, setEditor] = useState<LaunchEditor | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isBuyer = user?.role === 'ADMIN' || user?.role === 'BUYER';

  const load = async () => {
    setLoading(true);
    try {
      const me = await api.auth.me();
      setUser(me.user);
      const announcementResult = await api.launchAnnouncements.list();
      setAnnouncements(announcementResult.announcements);
      if (me.user.role === 'ADMIN' || me.user.role === 'BUYER') {
        const [candidateResult, storeResult] = await Promise.all([
          api.launchAnnouncements.candidates(),
          api.stores(),
        ]);
        setCandidates(candidateResult.candidates);
        setStores(storeResult.stores);
        const requestedProductMasterId = launchSearchParams.get('product_master_id');
        if (requestedProductMasterId && launchSearchParams.get('mode') === 'new') {
          const candidate = candidateResult.candidates.find(
            (item) => item.product_master_id === requestedProductMasterId,
          );
          if (candidate) {
            setSelected(null);
            setEditor({
              ...emptyLaunchEditor(),
              items: [
                {
                  submission_id: candidate.source_id,
                  product_name: candidate.product_name || '未命名商品',
                  product_code: candidate.product_code,
                  vip_price: candidate.vip_price == null ? '' : String(candidate.vip_price),
                  allocation_mode: 'UNIFORM',
                  allocation_quantity: '',
                  store_allocations: {},
                },
              ],
            });
          }
        }
      }
      const requestedAnnouncementId = launchSearchParams.get('announcement_id');
      if (requestedAnnouncementId) {
        const detail = await api.launchAnnouncements.detail(requestedAnnouncementId);
        setSelected(detail.announcement);
        setEditor(null);
      }
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // The page reloads explicitly after every mutation; the load function is intentionally stable for this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAnnouncement = async (id: string) => {
    setLoading(true);
    try {
      const result = await api.launchAnnouncements.detail(id);
      setSelected(result.announcement);
      if (user?.role === 'STORE' && !result.announcement.read_at)
        await api.launchAnnouncements.markRead(id);
      if (user?.role === 'STORE')
        setAnnouncements((current) =>
          current.map((item) =>
            item.id === id ? { ...item, read_at: new Date().toISOString() } : item,
          ),
        );
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setSelected(null);
    setEditor(emptyLaunchEditor());
  };

  const toggleCandidate = (candidate: LaunchProductCandidate) => {
    setEditor((current) => {
      if (!current) return current;
      const exists = current.items.some((item) => item.submission_id === candidate.source_id);
      return {
        ...current,
        items: exists
          ? current.items.filter((item) => item.submission_id !== candidate.source_id)
          : [
              ...current.items,
              {
                submission_id: candidate.source_id,
                product_name: candidate.product_name || '未命名商品',
                product_code: candidate.product_code,
                vip_price: candidate.vip_price == null ? '' : String(candidate.vip_price),
                allocation_mode: 'UNIFORM',
                allocation_quantity: '',
                store_allocations: {},
              },
            ],
      };
    });
  };

  const save = async () => {
    if (!editor) return;
    if (!editor.items.length) {
      notify('請至少選擇一項新品。', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = editor.id
        ? await api.launchAnnouncements.update(editor.id, launchEditorPayload(editor))
        : await api.launchAnnouncements.create(launchEditorPayload(editor));
      setSelected(result.announcement);
      setEditor(launchEditorFromAnnouncement(result.announcement));
      await load();
      notify('上市公告草稿已儲存。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const result = await api.launchAnnouncements.publish(selected.id);
      setSelected(result.announcement);
      setEditor(null);
      await load();
      notify('新品上市公告已發布給指定對象。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const revise = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const result = await api.launchAnnouncements.revise(selected.id);
      setSelected(result.announcement);
      setEditor(launchEditorFromAnnouncement(result.announcement));
      await load();
      notify('已建立修正版公告草稿。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async () => {
    if (!selected) return;
    const reason = window.prompt('請填寫撤回原因。', '公告內容需要修正。')?.trim();
    if (!reason) return;
    setSaving(true);
    try {
      const result = await api.launchAnnouncements.withdraw(selected.id, reason);
      setSelected(result.announcement);
      await load();
      notify('公告已撤回。');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const setEditorItem = (submissionId: string, patch: Partial<LaunchEditorItem>) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) =>
              item.submission_id === submissionId ? { ...item, ...patch } : item,
            ),
          }
        : current,
    );
  };

  const renderAnnouncementTable = (announcement: LaunchAnnouncement) => (
    <Table>
      <thead>
        <tr className="bg-slate-50 text-xs font-bold text-slate-500">
          <th className="px-4 py-3">商品代號</th>
          <th className="px-4 py-3">商品名稱</th>
          <th className="px-4 py-3">單位</th>
          <th className="px-4 py-3">進價</th>
          <th className="px-4 py-3">VIP價</th>
          <th className="px-4 py-3">配貨數量</th>
          <th className="px-4 py-3">是否退換貨</th>
          <th className="px-4 py-3">促銷活動</th>
        </tr>
      </thead>
      <tbody>
        {(announcement.items ?? []).map((item) => (
          <tr key={item.id} className="border-t border-slate-100 align-top">
            <td className="whitespace-nowrap px-4 py-4 font-bold text-teal-700">
              {item.product_code || '尚未建立'}
            </td>
            <td className="max-w-[220px] whitespace-normal break-words px-4 py-4 font-bold">
              {item.product_name || '—'}
            </td>
            <td className="px-4 py-4">{item.sales_unit || '—'}</td>
            <td className="px-4 py-4">
              <Money value={item.approved_cost} />
            </td>
            <td className="px-4 py-4">
              <Money value={item.vip_price} />
            </td>
            <td className="whitespace-pre-wrap px-4 py-4">
              {item.allocation_mode === 'BY_STORE'
                ? '依門市配貨明細'
                : (item.allocation_quantity ?? '—')}
              {item.allocation_mode === 'BY_STORE' && item.store_allocations?.length ? (
                <div className="mt-2 space-y-1 text-xs text-slate-500">
                  {item.store_allocations.map((allocation) => (
                    <div key={allocation.store_id}>
                      {allocation.store_name ?? allocation.store_id}：
                      {allocation.allocation_quantity}
                    </div>
                  ))}
                </div>
              ) : null}
            </td>
            <td className="max-w-[180px] whitespace-normal break-words px-4 py-4 text-sm">
              {item.return_display || '—'}
            </td>
            <td className="max-w-[220px] whitespace-normal break-words px-4 py-4 text-sm">
              {item.promotion_display || '無'}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="LAUNCH ANNOUNCEMENTS"
        title="新品上市公告"
        description={
          isBuyer
            ? '從核准上市新品建立配貨公告，預覽確認後發布給門市；已發布內容保留版本與撤回紀錄。'
            : '查看採購發布的新品上市配貨資訊；門市僅需閱讀公告，不參與審核或投票。'
        }
        actions={
          isBuyer ? (
            <Button onClick={startNew}>＋製作上市公告</Button>
          ) : (
            <Button variant="secondary" onClick={() => window.print()}>
              列印公告
            </Button>
          )
        }
      />
      {loading && !editor && !selected ? <Loading /> : null}
      {!loading && announcements.length === 0 && !editor && !selected ? (
        <Card>
          <EmptyState
            title={isBuyer ? '尚未建立上市公告' : '尚無新品上市公告'}
            description={
              isBuyer
                ? '新品完成核准上市與商品主檔設定後，即可製作公告。'
                : '採購發布公告後會顯示在這裡。'
            }
          />
        </Card>
      ) : null}
      {!editor && !selected && announcements.length > 0 && (
        <Card>
          <Table>
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500">
                <th className="px-4 py-3">公告標題</th>
                <th className="px-4 py-3">上市時間</th>
                <th className="px-4 py-3">商品數量</th>
                <th className="px-4 py-3">發布對象</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((announcement) => (
                <tr key={announcement.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-4 font-bold text-teal-700">
                    {announcement.title}
                    {announcement.read_at ? null : announcement.status === 'PUBLISHED' ? (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-1 text-[11px] text-amber-900">
                        未讀
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-600">
                    {announcement.launch_date || '待設定'}
                  </td>
                  <td className="px-4 py-4">{announcement.item_count}</td>
                  <td className="px-4 py-4">
                    {LAUNCH_ANNOUNCEMENT_AUDIENCE_LABELS[announcement.audience_scope]}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${LAUNCH_ANNOUNCEMENT_STATUS_TONE[announcement.status]}`}
                    >
                      {LAUNCH_ANNOUNCEMENT_STATUS_LABELS[announcement.status]}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Button
                      variant="secondary"
                      onClick={() => void openAnnouncement(announcement.id)}
                    >
                      查看公告
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      {isBuyer && editor && (
        <Card>
          <div className="mb-6 flex flex-col justify-between gap-3 border-b border-slate-100 pb-5 md:flex-row md:items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                ANNOUNCEMENT DRAFT
              </p>
              <h2 className="mt-1 text-2xl font-extrabold">公告預覽與編輯</h2>
            </div>
            <Button variant="secondary" onClick={() => setEditor(null)}>
              返回公告列表
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <Input
              className="md:col-span-2"
              label="公告標題"
              required
              value={editor.title}
              onChange={(event) => setEditor({ ...editor, title: event.target.value })}
            />
            <Input
              label="上市時間"
              type="date"
              required
              value={editor.launch_date}
              onChange={(event) => setEditor({ ...editor, launch_date: event.target.value })}
              hint="發布前必填"
            />
            <Select
              label="發布對象"
              value={editor.audience_scope}
              onChange={(event) =>
                setEditor({
                  ...editor,
                  audience_scope: event.target.value as LaunchEditor['audience_scope'],
                  store_ids: event.target.value === 'ALL' ? [] : editor.store_ids,
                })
              }
            >
              <option value="ALL">全門市</option>
              <option value="DESIGNATED">指定門市</option>
            </Select>
          </div>
          {editor.audience_scope === 'DESIGNATED' && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-bold">指定門市 *</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {stores.map((store) => (
                  <label key={store.id} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={editor.store_ids.includes(store.id)}
                      onChange={() =>
                        setEditor({
                          ...editor,
                          store_ids: editor.store_ids.includes(store.id)
                            ? editor.store_ids.filter((id) => id !== store.id)
                            : [...editor.store_ids, store.id],
                        })
                      }
                      className="accent-teal-600"
                    />
                    {store.code}｜{store.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
              <div>
                <p className="text-sm font-bold">公告商品</p>
                <p className="mt-1 text-xs text-slate-500">
                  僅能選擇已核准導入或已建立商品主檔的新品；商品代號由正式商品主檔帶入。
                </p>
              </div>
              <span className="text-xs font-bold text-teal-700">已選 {editor.items.length} 項</span>
            </div>
            <div className="mt-4 grid gap-2 md:grid-cols-2">
              {candidates.map((candidate) => {
                const checked = editor.items.some(
                  (item) => item.submission_id === candidate.source_id,
                );
                return (
                  <label
                    key={candidate.source_id}
                    className={`flex items-start gap-3 rounded-xl border p-3 text-sm ${checked ? 'border-teal-300 bg-teal-50' : 'border-slate-200 bg-white'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCandidate(candidate)}
                      className="mt-1 accent-teal-600"
                    />
                    <span>
                      <strong className="block">{candidate.product_name || '未命名商品'}</strong>
                      <span className="mt-1 block text-xs text-slate-500">
                        {candidate.vendor_name} · {candidate.product_code || '商品代號尚未建立'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="mt-6 space-y-4">
            {editor.items.map((item, index) => (
              <div key={item.submission_id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
                  <div>
                    <p className="font-bold">
                      {index + 1}. {item.product_name}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      商品代號：{item.product_code || '尚未建立'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      toggleCandidate({
                        source_id: item.submission_id,
                        source_type: 'PRODUCT_MASTER',
                        product_master_id: '',
                        product_name: item.product_name,
                        product_code: item.product_code,
                        sales_unit: '',
                        vendor_name: '',
                        approved_cost: null,
                        vip_price: item.vip_price === '' ? null : Number(item.vip_price),
                      })
                    }
                  >
                    移除
                  </Button>
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <Input
                    label="VIP價"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={item.vip_price}
                    onChange={(event) =>
                      setEditorItem(item.submission_id, { vip_price: event.target.value })
                    }
                  />
                  <Select
                    label="配貨設定"
                    value={item.allocation_mode}
                    onChange={(event) =>
                      setEditorItem(item.submission_id, {
                        allocation_mode: event.target.value as LaunchAllocationMode,
                      })
                    }
                  >
                    <option value="UNIFORM">統一配貨</option>
                    <option value="BY_STORE" disabled={editor.audience_scope !== 'DESIGNATED'}>
                      依門市設定
                    </option>
                  </Select>
                  {item.allocation_mode === 'UNIFORM' ? (
                    <Input
                      label="每店配貨數量"
                      type="number"
                      min="1"
                      step="1"
                      required
                      value={item.allocation_quantity}
                      onChange={(event) =>
                        setEditorItem(item.submission_id, {
                          allocation_quantity: event.target.value,
                        })
                      }
                    />
                  ) : null}
                </div>
                {item.allocation_mode === 'BY_STORE' && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {editor.store_ids.map((storeId) => {
                      const store = stores.find((candidate) => candidate.id === storeId);
                      return (
                        <Input
                          key={storeId}
                          label={`${store?.code ?? storeId} 配貨數量`}
                          type="number"
                          min="1"
                          step="1"
                          required
                          value={item.store_allocations[storeId] ?? ''}
                          onChange={(event) =>
                            setEditorItem(item.submission_id, {
                              store_allocations: {
                                ...item.store_allocations,
                                [storeId]: event.target.value,
                              },
                            })
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <Button variant="secondary" onClick={() => setEditor(null)}>
              取消
            </Button>
            <Button onClick={() => void save()} loading={saving}>
              儲存公告草稿
            </Button>
          </div>
        </Card>
      )}
      {selected && !editor && (
        <div className="space-y-6">
          <Card className="print:border-0 print:shadow-none">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                  惠生合作供應商平台
                </p>
                <h2 className="mt-2 text-3xl font-extrabold">{selected.title}</h2>
                <p className="mt-2 text-sm text-slate-600">
                  上市時間：{selected.launch_date || '尚未設定'} ／ 發布對象：
                  {LAUNCH_ANNOUNCEMENT_AUDIENCE_LABELS[selected.audience_scope]}
                </p>
              </div>
              <div className="no-print flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => window.print()}>
                  列印／PDF
                </Button>
                {isBuyer && selected.status === 'DRAFT' && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setEditor(launchEditorFromAnnouncement(selected))}
                    >
                      編輯草稿
                    </Button>
                    <Button onClick={() => void publish()} loading={saving}>
                      確認發布
                    </Button>
                  </>
                )}
                {isBuyer && selected.status === 'PUBLISHED' && (
                  <>
                    <Button variant="secondary" onClick={() => void revise()} loading={saving}>
                      發布修正版
                    </Button>
                    <Button variant="danger" onClick={() => void withdraw()} loading={saving}>
                      撤回公告
                    </Button>
                  </>
                )}
              </div>
            </div>
            {selected.status === 'WITHDRAWN' && selected.withdrawal_reason && (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                撤回原因：{selected.withdrawal_reason}
              </div>
            )}
          </Card>
          <Card className="overflow-x-auto print:border-0 print:shadow-none">
            <h3 className="mb-4 text-xl font-extrabold">新品上市配貨公告</h3>
            <p className="mb-5 text-sm leading-7 text-slate-600">
              各門市同仁您好，以下為本次新品上市配貨資訊，請依照建議數量進行訂購或陳列；如有任何異動或問題，請及時回報採購部。
            </p>
            {renderAnnouncementTable(selected)}
          </Card>
        </div>
      )}
    </div>
  );
}
